import { createContext, useContext, useEffect, useState } from "react";
import { getCountry } from "@/country";

// Denetim testleri (ör. lib/acilBildirim.test.js) bu tabloyu dosya metnini
// ayrıştırmadan okuyabilsin diye dışa veriliyor. Uygulama kodu hâlâ
// `useI18n().t()` kullanır — doğrudan bu nesneye dokunmaz.
export const translations = {
  en: {
    kediBildir: {
      baslik: "Report a cat",
      altBaslik: "We are here for the cats in your neighbourhood. What would you like to do?",
      kaydetBaslik: "Register a cat",
      kaydetAciklama: "Add a cat from your neighbourhood to the system",
      gordumBaslik: "I saw a cat",
      gordumAciklama: "Report that you have seen an already registered cat",
      acilBaslik: "Report an emergency",
      acilAciklama: "Call for help for an injured, sick or endangered cat",
      mamaSuBaslik: "Add a food/water spot",
      mamaSuAciklama: "Share the location of a food or water spot",
      dipnot: "Every report reaches the volunteers in your neighbourhood.",
    },
    indir: {
      baslik: "Get the app",
      altBaslik: "Install StrayPaw on your phone — see the animals around you, report a lost pet, answer a call for help.",
      appStore: "Download on the App Store",
      playStore: "Get it on Google Play",
      yonlendiriliyor: "Taking you to the store…",
      qr: "Scan with your phone's camera",
      yakinda: "The app is coming to the stores soon.",
      androidYakinda: "The Android version is coming soon.",
      zatenVar: "You already have the app.",
      anaSayfa: "Back to home",
    },
    terms: {
      overline: "Legal",
      title: "Terms of Use",
      updated: "Last updated: 18 February 2026",
      s1t: "1. What this service is",
      s1a: "StrayPaw is a community app for sharing sightings, photographs, messages and volunteer coordination about street animals. By creating an account you agree to use it under these terms.",
      s2t: "2. User-generated content",
      s2a: "You alone are responsible for everything you upload — photographs, messages, comments and reports included. You confirm that what you upload is lawful and does not infringe anyone else's rights.",
      s2zero: "Zero tolerance",
      s2b: ": insults, harassment, hate speech, sexual content, false reports, fraud and unlawful content are strictly forbidden. ",
      s2c: "See the ",
      s2d: " for details.",
      s3t: "3. Moderation, reporting and blocking",
      s3a: "Every surface that carries user content has a visible “Report” action. Reported content is reviewed by a human moderator within 24 hours; if it breaks the rules it is removed and the account may be suspended. You can block other users from their profile or from the message window.",
      s4t: "4. Account security",
      s4a: "Keeping your password private is your responsibility. If you notice someone else using your account, contact support immediately.",
      s5t: "5. How your data is processed",
      s5a: "How your personal data is handled is explained in the ",
      s5b: ".",
      s6t: "6. Suspension and closure",
      s6a: "Accounts that break these terms may be suspended or permanently closed without warning. You may close your own account at any time.",
      s7t: "7. Disclaimer",
      s7a: "The app is provided “as is”. Do not treat it as a substitute for professional veterinary advice. In an emergency, contact your local emergency services or a veterinarian.",
      s8t: "8. Changes",
      s8a: "These terms may be updated from time to time. The current version is always published on this page.",
      linkCommunity: "Community Rules",
      linkPrivacy: "Privacy Policy",
    },
    communityRules: {
      back: "Sign in",
      overline: "Community",
      title: "Community Rules",
      intro: "StrayPaw should be a safe and respectful place. Please follow the rules below.",
      r1: "Harassment, threats, hate speech and misleading reports are forbidden.",
      r2: "Inappropriate content can be reported.",
      r3: "Users can block anyone who is abusive.",
      r4: "Reported content is reviewed and removed if necessary.",
      r5: "In an emergency, contact your local authorities or a veterinarian.",
      footer: "Reporting and blocking: available from the message screen or from a user's profile.",
    },
    aboutPage: {
      overline: "About",
      title: "StrayPaw",
      lede: "An app that brings together the people who recognise, follow and — when it matters — reach the street animals in their neighbourhood.",
      whyT: "Why it exists",
      why1: "You feed an animal on the street. The next week you cannot tell whether it is the same one. You see an injured animal and you cannot tell whether anyone has seen it before. Was it neutered, was it vaccinated — nobody knows.",
      why2: "There is no shortage of people who care. What is missing is knowing that we are talking about the same animal.",
      whatT: "What we do",
      what1a: "When you photograph an animal, the app searches the database for it. Not what kind of animal it is — ",
      what1s: "which animal",
      what1b: " it is. The notch in an ear, the mark above an eye, the patch on a chin: those are what tell one animal from another, not its colour.",
      what2a: "This is a hard problem and we do not always get it right. When we are not sure, we say so. We explain it plainly on the ",
      what2l: "how it works",
      what2b: " page.",
      whoT: "Who makes it",
      who: "A small, independent team. We are not a municipality, an association or a company, and we have no official partnership with anyone. The app was written in Istanbul and is tested on Istanbul streets.",
      moneyT: "Money",
      money: "The core of the app is free: registering an animal, searching, reporting an emergency, messaging. Premium covers extras such as AI portraits, and is never needed to help an animal.",
      wrongT: "Seen something wrong?",
      wrong: "A wrong match, incorrect information, inappropriate content — tell us about all of it. Knowing about a fault is better than assuming there is none.",
      linkHelp: "Help",
      linkCommunity: "Community Rules",
      linkPrivacy: "Privacy",
      linkTerms: "Terms of Use",
    },
    howItWorks: {
      overline: "Guide",
      title: "How it works",
      stepLabel: "Step",
      lede1: "Most apps tell you ",
      ledeEm: "what kind",
      lede2: " of animal you are looking at: tabby, black, young. We try to work out ",
      ledeStrong: "which animal",
      lede3: " it is. That is the difference between recognising the animal you feed and listing a thousand that look like it.",
      s1t: "Take the photograph",
      s1a: "Photograph the animal where you find it. The app captures by itself once an animal is actually in frame — it asks for three angles: front, side, top.",
      s1b: "Three angles are not a whim: the things that tell one animal apart — the notch in an ear, the mark above an eye, the patch on a chin — are often invisible in a single frame.",
      s2t: "The database is searched automatically",
      s2a1: "The search ",
      s2aStrong: "starts on its own",
      s2a2: " the moment the first photograph is taken. You do not have to press anything, because you would not have — and then the app would fill up with six profiles of the same animal.",
      s2b1: "The system compares written features first, then ",
      s2bStrong: "actually looks",
      s2b2: " at the photographs of the strongest candidates. Photographs decide, not words.",
      s3t: "If we are not sure, we do not match",
      s3a: "Missing a match is a delay: a volunteer looks again tomorrow. A wrong match is a lie — it merges two animals into one profile and stops someone searching for an animal that is still out there.",
      s3b1: "So when the system is undecided it tells you ",
      s3bStrong: "“I am not sure”",
      s3b2: ". The last word is yours.",
      s4t: "The animal goes on the map",
      s4a: "Location, feeding records, vaccinations, neutering, health status — all of it collects on that animal's profile. In time the profile becomes the story of an animal: who fed it, who took it to a vet, when it went missing.",
      s5t: "The neighbourhood sees it",
      s5a: "When you report an emergency it reaches the volunteers who cover that neighbourhood. The person who knows the animal is the person closest to it.",
      boxT: "Why photo quality matters",
      boxA: "A blurred, distant or badly lit frame hides the marks that tell an animal apart. The system then either misses it or hesitates. A good photograph is not just a nicer profile — it is a correct match.",
      ctaScan: "Identify an animal",
      ctaFaq: "Frequently asked",
    },
    helpPage: {
      overline: "Support",
      title: "Help",
      lede: "Frequently asked questions. If what you need is not here, write to us — a real person reads it.",
      q1: "Why does the app sometimes fail to recognise the same animal?",
      a1: "What tells one animal apart are its marks: the notch in an ear, the mark above an eye, the patch on a chin. If the photograph is blurred, distant or dark, those marks are invisible and the system cannot be sure.\n\nWhen we are not sure we prefer not to match. A wrong match merges two animals into one profile and stops someone searching for an animal that is still out there. Missing a match is only a delay.",
      q2: "I registered the same animal twice by mistake. What should I do?",
      a2: "It happens, and it is fine. Open the animal's profile, mark the correct one, and report the wrong record to us. As the database grows the system will catch these duplicates by itself — every correction trains it.",
      q3: "I took the photograph but the search did not start.",
      a3: "The search starts by itself right after the first photograph; you do not have to press anything. If it did not start, the connection most likely dropped. Close the app, open it again and retake the photograph.",
      q4: "Is my location shared?",
      a4: "The animal's location is shared, not yours. Where the animal was seen stays on the map — that is all a volunteer needs in order to find it. See the Privacy Policy for details.",
      q5: "I reported an emergency. Who sees it?",
      a5: "It reaches the volunteers who cover that neighbourhood. You can choose your coverage area under Profile → Edit. The person who knows the animal is the person closest to it.",
      q6: "How do I delete my account?",
      a6: "At the very bottom of the profile page there is “Delete my account”. After your request the account is deleted within 30 days; you can cancel within that period if you change your mind.",
      q7: "I saw inappropriate content or a user.",
      a7: "Every profile and every piece of content has “Report” and “Block”. Reports are reviewed within 24 hours. The Community Rules list what we have zero tolerance for.",
      q8: "I cannot find an animal; the app is slow.",
      a8: "Large photographs take longer to upload. The app now shrinks photographs before sending them. If it is still slow, write to us — which phone, which screen, what you were doing. Without that we cannot find the fault.",
      contactT: "Contact us",
      contactA: "Bug reports, suggestions, complaints — they all arrive at the same address.",
      linkHow: "How it works",
      linkCommunity: "Community Rules",
      linkPrivacy: "Privacy",
      linkTerms: "Terms of Use",
    },
    privacyPage: {
      back: "Home",
      overline: "Legal",
      title: "Privacy Policy",
      updated: "Last updated: September 2026",
      intro1: "StrayPaw (",
      intro2: ") is a volunteer community platform built to help people look after the street animals in their neighbourhoods. This page explains, in plain language, what data is collected when you use the service, why it is processed and what rights you have.",
      s1t: "1. Data we collect",
      s1i1L: "Account details:",
      s1i1: " email address, name, password (stored only as a secure hash).",
      s1i2L: "Profile details:",
      s1i2: " profile photograph, district and area preferences, badges earned, Paw Points.",
      s1i3L: "Location:",
      s1i3: " only at the moment you allow it — used for reporting an animal, emergencies, viewing the map and the “Find” button. Location is never tracked in the background.",
      s1i4L: "Photo uploads:",
      s1i4: " images of the animals you add, your neighbourhood or your avatar. They are compressed on your device before being sent.",
      s1i5L: "Message content:",
      s1i5: " your conversations with other volunteers and users. Visible only to the parties to the conversation and, where necessary, to the moderation team.",
      s1i6L: "Usage data:",
      s1i6: " session information, which pages you open, error logs and device type (anonymous technical statistics).",
      s1i7L: "Advertising measurement:",
      s1i7: " To understand whether you installed the app through a Meta (Facebook/Instagram) promotion, your app install and app open events are sent to Meta. On iOS, if you allow tracking, your device's advertising identifier is included; if you decline, no identifier is sent and measurement happens only in aggregate, with no link to you. You can withdraw permission at any time under Settings \u2192 Privacy & Security \u2192 Tracking.",
      s2t: "2. Why we use this data",
      s2i1: "Showing street animals on the map, following them and keeping their health history.",
      s2i2: "Notifying the nearest volunteers about emergencies (an injured or sick animal).",
      s2i3: "Getting lost-animal notices to users in the relevant area.",
      s2i4: "The neighbourhood feed, comments, likes and volunteer contact.",
      s2i5: "Account security, fraud prevention and abuse moderation.",
      s2i6: "Anonymous performance and error monitoring so the app works better.",
      s3t: "3. Who we share it with",
      s3aA: "Your personal data is ",
      s3aStrong: "never sold to third parties",
      s3aB: " The limited data shared with Meta for advertising measurement is stated explicitly below. Apart from that, processing occurs only in these cases:",
      s3i1: "Infrastructure providers required for the service to run (servers, database, email) — only the minimum data needed to do the job.",
      s3i2: "In response to a lawful, official request from a competent authority.",
      s3i3: "Unless you specifically allow it in your messages, no user can see you personally (phone, email and similar are not shown on your profile).",
      s3i4: "Advertising measurement \u2014 Meta Platforms Ireland Ltd.: app install and app open events only, plus your device advertising identifier if you allowed tracking. Your name, email, location, photos and messages are not shared with Meta.",
      s4t: "4. About photographs",
      s4a: "The animal photographs you upload become visible to other users so the community can recognise that animal. AI analysis (breed, health assessment) runs only at the moment of upload and is not stored as separate biometric data. You can remove a photograph you uploaded at any time.",
      s5t: "5. Message privacy",
      s5a: "Your one-to-one messages are visible only to you and the person you are writing to. Content is stored on our server but is not shared with third parties. If a conversation is reported, the moderation team can review that conversation only. You can block or report a user, or delete your message history, at any time.",
      s6t: "6. Your rights",
      s6a: "Under Türkiye's KVKK and comparable data-protection laws you have the following rights:",
      s6i1: "To learn which of your data is processed.",
      s6i2: "To ask for incorrect or incomplete information to be corrected.",
      s6i3L: "To request deletion of your account or of individual data.",
      s6i3: " Your request is carried out within 30 days.",
      s6i4: "To object to the processing of your data or ask for it to be restricted.",
      s6i5: "To receive your data in a portable format (JSON).",
      s6b: "To exercise these rights, simply write to us at the address below.",
      s7t: "7. Data security",
      s7a: "Passwords are hashed with bcrypt, sessions are signed with JWT, and all traffic travels over HTTPS. Backups are kept encrypted. Even so, no transfer over the internet is 100% secure — so we recommend using a strong password and not sharing your account with anyone.",
      s8t: "8. Children's privacy",
      s8a: "Our app is not designed for children under 13 and we do not knowingly collect data from that age group. If we notice that a child under 13 has opened an account, we remove it.",
      s9t: "9. Changes to this policy",
      s9a: "We may update this policy from time to time. You will be told about significant changes by an in-app notice or by email. The “Last updated” date at the top of this page always reflects the current version.",
      contactT: "Contact",
      contactA: "For the privacy policy, data deletion requests or any data-protection question:",
      footer: "StrayPaw · Istanbul, Türkiye",
    },
    // TUR-10: `volunteers` blogu HIC yazilmamisti. Volunteers.jsx alti
    // anahtar istiyordu, t() bulamayinca anahtarin kendisini donduruyordu —
    // yani sayfanin basligi ekranda 'volunteers.title' yaziyordu, uc dilde de.
    volunteers: {
      title: "Volunteers",
      subtitle: "The people who feed, treat and rescue the animals in your neighbourhood. Every name here is someone who showed up.",
      becomeVolunteer: "Become a volunteer",
      individualsTitle: "Volunteers and vets",
      individualsSubtitle: "Ranked by contribution — feeding, rescues, vaccinations and reports.",
      noIndividuals: "No volunteers yet. Be the first one in your neighbourhood.",
    },
    dogUi: {
      dbTitleCat: "Cat Database",
      dbTitleDog: "Dog Database",
      dbOverlineCat: "CAT DATABASE",
      dbOverlineDog: "DOG DATABASE",
      dbSubCat: "Browse every catalogued cat in our community.",
      dbSubDog: "Browse every catalogued dog in our community.",
      dbSearchCat: "Search by name, color, breed or neighbourhood",
      dbSearchDog: "Search by name, colour or neighbourhood",
      dbLostCat: "Find your lost cat",
      dbLostDog: "Find your lost dog",
      dbRegisteredCat: "Registered",
      dbRegisteredDog: "Registered",
      dbInjuredCat: "Injured",
      dbInjuredDog: "Injured",
      dbEarTagged: "Ear-tagged",
      dbEarTaggedHint: "Sterilised — ear notch / tag",
      dbTitleAll: "Animal Database",
      dbOverlineAll: "ANIMAL DATABASE",
      dbSubAll: "Browse every catalogued animal in our community.",
      dbSearchAll: "Search by name, colour or neighbourhood",
      dbLostAll: "Find your lost animal",
      dbRegisteredAll: "Registered",
      dbInjuredAll: "Injured",
      lostConfirmCat: "Create a lost listing for {ad}? It goes live immediately and neighbours are notified.",
      lostConfirmDog: "Create a lost listing for {ad}? It goes live immediately and neighbours are notified.",
      lostOpenCat: "A listing is already open — taking you there.",
      lostOpenDog: "A listing is already open — taking you there.",
      deleteCat: "Delete this cat?",
      deleteDog: "Delete this dog?",
      shareCat: "Share this cat",
      shareDog: "Share this dog",
      editCat: "Edit cat",
      editDog: "Edit dog",
      reportCat: "Report this cat profile",
      reportDog: "Report this dog profile",
      noGpsCat: "GPS was unavailable when this cat was registered.",
      noGpsDog: "GPS was unavailable when this dog was registered.",
      lostCat: "My cat is lost \u2014 create listing",
      lostDog: "My dog is lost \u2014 create listing",
      newCat: "New cat",
      newDog: "New dog",
      saveCat: "Save cat",
      saveDog: "Save dog",
      saving: "Saving\u2026",
      dogOptional: "Add dog details (optional)",
      dogOptionalHint: "Not required. Two photos, a name and a neighbourhood are enough.",
    },
    dog: {
      section: "Dog details",
      unknownHint: "Leave as Unknown if you cannot see it clearly",
      matchingSoon: "Dog matching is not available yet — the profile is saved and will be searchable once it opens.",
      earCarriage: "Ear carriage",
      ear: { dik: "Erect", yari_dik: "Semi-erect", dusuk: "Drop", kesik: "Cropped", belirsiz: "Unknown" },
      tailType: "Tail",
      tail: { uzun_duz: "Long, straight", kivrik: "Curled", orak: "Sickle", kisa: "Short", kesik: "Docked", puskullu: "Plumed", belirsiz: "Unknown" },
      coatLength: "Coat length",
      coatLen: { kisa: "Short", orta: "Medium", uzun: "Long", belirsiz: "Unknown" },
      coatTexture: "Coat texture",
      coatTex: { duz: "Straight", dalgali: "Wavy", kivircik: "Curly", sert_tel: "Wiry", belirsiz: "Unknown" },
      muzzle: "Muzzle",
      muz: { basik: "Short", orta: "Medium", uzun: "Long", belirsiz: "Unknown" },
      build: "Build",
      bod: { ince: "Slender", orta: "Medium", iri: "Stocky", belirsiz: "Unknown" },
      earTagNo: "Ear tag number",
      earTagHint: "Only if the dog has a sterilisation ear tag",
      registering: "You are registering a dog",
      registeringCat: "You are registering a cat",
      switchToCat: "Register as cat",
      switchToDog: "Register as dog",
      myLostDog: "This is my lost dog",
    },
    appName: "StrayPaw",
    tagline: "Street animals of your neighbourhood",
    // Panodaki baslik slogani. `tagline` bundan AYRI: o sayfa basliklarinda
    // ve paylasim kartlarinda kullaniliyor, bu ise uygulama basliginin ikinci
    // satiri. Ikisini birlestirmek, panoda olmayan bir metni basliga sokardi.
    baslikSlogani: "A stronger neighbourhood.",
    arama: {
      rowPlace: "Neighbourhood",
      otherNeighborhoods: "Other neighbourhoods",
      row_kediler: "cats",
      row_harita: "on the map",
      row_kayip: "lost listings",
      row_besleme: "feeding points",
      rowGo_kediler: "See the cats",
      rowGo_harita: "What's in the area?",
      rowGo_kayip: "See the listings",
      rowGo_besleme: "See the points",
      unit_kediler: "results",
      unit_harita: "",
      unit_kayip: "listings",
      unit_besleme: "points",
      tab_ilanlar: "Lost listings",
      title: "Search",
      placeholder: "Search a cat, neighbourhood or district…",
      back: "Back",
      clear: "Clear",
      recent: "Recent searches",
      clearHistory: "Clear",
      mostCats: "Neighbourhoods with the most cats",
      hint: "Search by cat name, neighbourhood or district to reach registered cats, lost listings and everything in your area.",
      neighborhoods: "Neighbourhoods",
      cats: "Cats",
      catWord: "cats",
      searching: "Searching…",
      empty: "Nothing matched. Try a neighbourhood name.",
      searchFor: "Search for",
      tab_hepsi: "All",
      tab_kediler: "Cats",
      tab_mahalleler: "Neighbourhoods",
    },
    anasayfaV2: {
      odul: "reward",
      slogan: "A better life, for every paw.",
      heroTitle: "What is happening in your neighbourhood today?",
      heroSub: "Safer streets for street cats are possible.",
      heroMark: "Every paw matters",
      statCats: "Registered cats",
      statEmergencies: "Emergencies",
      statFeeding: "Feeding spots",
      statVaccinated: "Vaccinated",
      locationPick: "Change location",
      search: "Search a cat, neighbourhood or district…",
      actionsTitle: "What would you like to do?",
      actionsAside: "A small step, a big difference",
      a1t: "I saw a cat",
      a1s: "Report location",
      a2t: "Find a lost cat",
      a2s: "Search the listings",
      aramaVeritabani: "Cat database",
      a3t: "Emergency Hub",
      a3s: "Cats awaiting help",
      a4t: "Identify with AI",
      a4s: "Compare by photo",
      vtBaslik: "Cat Database",
      vtAlt: "Browse registered cats, see the details.",
      vtAdetSonEk: "cats",
      lostTitle: "A cat is missing near you",
      casesTitle: "Important cases near you",
      casesSub: "Cats that need help most in the field",
      casesAll: "See all",
      casesEmpty: "No open cases near you — good news.",
      casesFarAway: "None nearby — showing the newest records.",
      tabLost: "Lost cats",
      tabEmergency: "Urgent cases",
      tabAll: "All",
      emergencyBadge: "URGENT",
      chipVet: "Needs a vet",
      chipInjured: "Waiting for help",
      chipFood: "Food/water needed",
      chipDanger: "Danger reported",
      chipVolunteer: "Volunteer wanted",
      chipLost: "Has anyone seen it?",
      lostAll: "See all",
      lostBadge: "LOST",
      lostEmpty: "No lost listings near you — good news.",
      mapTitle: "Neighbourhood map",
      mapDesc: "Discover cats, emergencies and feeding spots near you.",
      mapOpen: "Open the map",
      aiTitle: "AI · Cat recognition",
      aiDesc: "Photograph the cat you saw and compare it with registered cats.",
      aiPhoto: "Take a photo",
      aiGallery: "Choose from gallery",
      healthTitle: "Cat health and support",
      healthDesc: "Vaccines, neutering, rabies and health notes.",
      communityTitle: "Join the community",
      communityDesc: "Volunteers, events and more.",
      communityMark: "Stronger together",
      navReport: "Report a cat",
      navReportShort: "Report",
      justNow: "just now",
      hoursAgo: "{n}h ago",
      hoursAgoLong: "{n} hours ago",
      minutesAgoLong: "{n} minutes ago",
      pickPlaceAria: "Choose your neighbourhood",
      pickPlaceCta: "Choose your area",
      pickPlaceTitle: "Choose your area",
      pickPlaceSub: "Home and My Neighbourhood screens follow this choice.",
      pickPlaceNear: "Based on your location",
      pickPlaceClose: "Close",
      pickPlaceSave: "Save",
      pickPlaceSaving: "Saving\u2026",
      yesterday: "yesterday",
      daysAgo: "{n}d ago",
    },
    kayipIlan: {
      title: "Lost cat",
      lost: "LOST",
      odul: "reward",
      poster: "Make poster",
      posterTitle: "Lost cat poster",
      posterErr: "Could not create the poster. Try again in a moment.",
      share: "Share",
      shareTitle: "Lost cat 🔍",
      shareCopied: "Link copied — paste it wherever you like.",
      shareErr: "Could not share. Try again in a moment.",
      seen: "I saw this cat",
      seenSub: "Report the location on the map",
      aiTitle: "Seen a similar cat?",
      aiDesc: "Take a photo or pick one from your gallery, AI will compare.",
      whereTitle: "Where could it be?",
      whereSub: "Estimated search area",
      openMap: "Open map",
      teamTitle: "Search team",
      teamCount: "{n} people searching",
      teamJoin: "Join the search",
      teamLeave: "Leave the search",
      photoCount: "Photos",
      seenCount: "Sighting reports",
      owner: "Listing owner",
      call: "Call",
      message: "Message",
      details: "Listing details",
      reunited: "Reunited — close listing",
      bKisir: "Neutered",
      bKisirDegil: "Not neutered",
      bErkek: "Male",
      bDisi: "Female",
      bUrkek: "Shy",
      bSokulgan: "Friendly",
      bSaldirgan: "Defensive",
      bSakin: "Calm",
      noPhoto: "No photo",
      notFound: "Listing not found.",
      closed: "This listing is closed.",
    },
    aramaAlani: {
      title: "Search area",
      areaTitle: "Estimated search area",
      areaSub: "Built from the last known location.",
      allReports: "See all",
      directions: "Get directions to this area",
      legend: "Map key",
      sonGorulen: "Last seen",
      aramaAlaniHalka: "Search area",
      ihbarlar: "Reported sightings",
      gonulluler: "Volunteers",
      reports: "Recent reports",
      noReports: "No reports yet. Be the first.",
      cFoto: "Photo added",
      cBenzer: "Could be similar",
      cEminDegil: "Not sure",
      addReport: "Add a report",
      join: "Join the search team",
      leave: "Leave the search team",
      team: "{n} people searching",
      locHidden: "Sign in to see where volunteers are",
      away: "{d} away",
      noCoords: "This listing has no map location.",
      closed: "This listing is closed.",
      notFound: "Listing not found.",
      back: "Back",
    },
    mahallem: {
      anOnerilerAcikGorev: "These already have open tasks",
      anHeroBaslik: "How is {yer}?",
      anHeroAlt: "Last 30 days of data",
      anHeroAltAi: "30-day data & AI analysis",
      anSkorEtiketi: "Neighborhood Health Score",
      anSkorBilgiAc: "What does the score measure?",
      anSkorBilgi: "The score measures the neighborhood's resources (vaccination records, feeding and water points, open emergencies, response speed) — not the cats' health.",
      anAzVeri: "little data",
      anBalonYok: "No summary yet: not enough measurements in this area.",
      anDurumKayit: "Cats are being recorded",
      anDurumKayitAlt: "{n} new in 30 days",
      anDurumAcilYok: "No open emergencies",
      anDurumAcilYokAlt: "All cases resolved",
      anDurumSuVar: "Water points in place",
      anDurumSuVarAlt: "{n} active points",
      anDurumMamaVar: "Feeding points in place",
      anDurumMamaVarAlt: "{n} active points",
      anDurumBeslenme: "Not enough feeding support",
      anDurumBeslenmeAlt: "New points needed",
      anDurumSu: "Not enough water points",
      anDurumSuAlt: "New points needed",
      anDurumGonullu: "Low volunteer support",
      anDurumGonulluAlt: "More volunteers needed",
      anDurumAsi: "Weak vaccine tracking",
      anDurumAsiAlt: "Few records",
      anDurumBarinak: "No cat houses",
      anDurumBarinakAlt: "Shelter needed",
      anDurumYarali: "Injured cat reports",
      anDurumYaraliAlt: "In the last 30 days",
      anDurumAcil: "{n} emergencies waiting",
      anDurumAcilBir: "1 emergency waiting",
      anDurumAcilAlt: "Needs action",
      anKonularBaslik: "{n} key issues right now",
      anTumunuGor: "See all",
      anKonuAcil: "Emergency waiting",
      anKonuYarali: "Injured cats reported",
      anKonuBeslenme: "Feeding point missing",
      anKonuSu: "Water point missing",
      anKonuGonullu: "Low volunteer support",
      anKonuAsi: "Vaccine tracking weak",
      anKonuBarinak: "No cat house",
      anKonuBolgede: "Most in {yer}.",
      anKonuYok: "No clear gap in the measured areas.",
      anVeriYok: "Data could not be loaded.",
      anDugmeVakalar: "See cases",
      anDugmeGonulluler: "See volunteers",
      anDugmeKediler: "See cats",
      anHaritaBaslik: "Neighborhood Analysis Map",
      anAralikSec: "Choose period",
      anAralik7: "Last 7 days",
      anAralik30: "Last 30 days",
      anAralik90: "Last 3 months",
      anAralik365: "Last year",
      anAralikTumu: "All time",
      anKatmanYogunluk: "Cat Density",
      anKatmanIhtiyac: "Needs",
      anKatmanAcil: "Emergencies",
      anKatmanBeslenme: "Feeding",
      anKatmanSu: "Water Points",
      anYogunlukDusuk: "Low",
      anYogunlukYuksek: "High",
      anEtiketKedi: "{n} cats",
      anEnCokIhtiyac: "Area most in need:",
      anHaritaBos: "No area with a boundary to draw yet.",
      anDegisimBaslik: "What changed in 30 days?",
      anDetayliGor: "Details",
      anSekmeKediler: "Cats",
      anSekmeAcil: "Emergencies",
      anSekmeKayip: "Lost Cats",
      anSekmeBeslenme: "Feeding",
      anSekmeSu: "Water Points",
      anSatirKedi: "Cat records",
      anSatirAcil: "Emergencies",
      anSatirBeslenme: "Feeding points",
      anSatirSu: "Water points",
      anRozet30: "in 30 days",
      anGrafikNotu: "By record date: cats = total registered at the end of each day; others = added that day.",
      anGrafikYok: "No records in this period yet.",
      anOnerilerBaslik: "AI Suggestions",
      anOnerilerAlt: "Ideas to improve our neighborhood.",
      anTumOneriler: "All suggestions",
      anOneriYok: "No suggestions right now.",
      // ÖNCELİK-1 (18 Eylül 2026) — Mahalle Öncelikleri sayfası.
      ocBaslik: "Neighborhood Priorities",
      ocAlt: "Let's act together for a better neighborhood.",
      ocGeri: "Back",
      ocNot: "These priorities were set by AI analysis from your neighborhood's data over the last 30 days.",
      ocEtiketAcil: "EMERGENCIES",
      ocEtiketBeslenme: "FEEDING",
      ocEtiketSu: "WATER POINTS",
      ocEtiketGonullu: "VOLUNTEER SUPPORT",
      ocEtiketDiger: "OTHER NEEDS",
      ocAcilBaslik: "Cats waiting for help",
      ocAcilAlt: "{n} emergency case open in your neighborhood.",
      ocAcilYokBaslik: "No open emergencies",
      ocAcilYokAlt: "No case is waiting for response right now.",
      ocAcilBirim: "open case",
      ocBeslenmeBaslik: "Feeding support needed",
      ocBeslenmeAlt: "Feeding points fall short in {n} area(s).",
      ocBeslenmeYokBaslik: "Feeding points are enough",
      ocBeslenmeYokAlt: "{n} feeding point(s) on record.",
      ocBeslenmeBirim: "new point",
      ocSuBaslik: "Water points missing",
      ocSuAlt: "Water support needed in {n} area(s).",
      ocSuYokBaslik: "Water points are enough",
      ocSuYokAlt: "{n} water point(s) on record.",
      ocSuBirim: "point",
      ocGonulluBaslik: "We need support",
      ocGonulluAlt: "Volunteers wanted for {n} cats.",
      ocGonulluYokBaslik: "Volunteer support is enough",
      ocGonulluYokAlt: "{g} volunteer(s) looking after {n} cats.",
      ocGonulluBirim: "cats",
      ocDigerBaslik: "Lost and shelter matters",
      ocDigerAlt: "{k} active record(s), {b} shelter need.",
      ocDigerYokBaslik: "No lost or shelter issue",
      ocDigerYokAlt: "No open lost report or shelter need.",
      ocDigerBirim: "records",
      ocDugmeGorevler: "See Tasks",
      ocDugmeBolgeler: "See Areas",
      ocDugmeDetaylar: "See Details",
      gvKatBeslenme: "Feeding",
      gvKatSu: "Water point",
      gvKatKayip: "Lost cat",
      gvKatSaglik: "Health",
      gvKatKulube: "Shelter",
      gvDugmeDetay: "See details",
      gvSloganSade: "Let's act together for the cats.",
      gvOlusturBaslik: "Create your own task",
      gvOlusturAlt: "Create a new task for your neighbourhood. Together we reach more cats.",
      gvOlusturDugme: "Create task",
      gvOneriBaslikKisa: "AI suggestion",
      gvGorevlerimBag: "My tasks · {n}",
      baKatmanKisaAcil: "Urgent",
      baKatmanKisaBeslenme: "Feeding",
      baKatmanKisaSu: "Water",
      baKatmanKisaGonullu: "Volunteers",
      baKatmanKisaIsi: "Heat",
      gvBaslik: "Tasks",
      gvSlogan: "Things you can do for the cats in your neighbourhood. A small step, a big difference.",
      gvElYazisi: "Together, happier cats.",
      gvSuzTumu: "All",
      gvSuzAcil: "Urgent",
      gvSuzBugun: "Today",
      gvSuzYakin: "Nearby",
      gvFiltrele: "Filter",
      gvFiltreTur: "Task type",
      gvFiltreDurum: "Status",
      gvTemizle: "Clear",
      gvTamam: "Done",
      gvTur_yarali_kontrol: "Injured cat",
      gvTur_veteriner_destegi: "Vet support",
      gvTur_mama_istasyonu: "Feeding",
      gvTur_su_kabi: "Water",
      gvTur_kayip_arama: "Lost cat",
      gvTur_kulube: "Shelter",
      gvHata: "Tasks could not be loaded.",
      gvOncelikliBaslik: "Priority tasks",
      gvCipTumu: "All",
      gvCipAcil: "Urgent",
      gvCipIhtiyac: "Need",
      gvCipDestek: "Support",
      gvListeGeri: "My neighborhood",
      gvListeAlt: "All tasks in {yer}",
      gvListeSimdi: "Do these now",
      gvListeSimdiAlt: "Priority tasks in the neighbourhood",
      gvListeDiger: "Other tasks",
      gvListeDigerAlt: "Other tasks in the neighbourhood",
      gvListeAra: "Search",
      gvDetayGeri: "Tasks",
      gvDetayAcilRozet: "Urgent task",
      gvDetayBenimRozet: "My task",
      gvDetayBittiRozet: "Done",
      gvDetayBekliyor: "Waiting for help",
      gvDetayBekliyorAlt: "This task needs urgent attention.",
      gvDetayUstlendi: "You took this task!",
      gvDetayUstlendiAlt: "Thank you — we are stronger together.",
      gvDetayBittiBanner: "This task is done. Thank you for your work!",
      gvDetaySekmeDetay: "Details",
      gvDetaySekmeKonum: "Location",
      gvDetaySekmeYorum: "Comments",
      gvDetaySonGorulme: "Last seen",
      gvDetayKonum: "Location",
      gvDetayDurum: "Status",
      gvDetayBildiren: "Reported by",
      gvDetayAciklama: "Description",
      gvDetayHaritada: "See on map",
      gvDetayUstlen: "Take on task",
      gvDetayPaylas: "Share",
      gvDetayTamamla: "Mark as done",
      gvDetayFoto: "Add photo",
      gvDetayGelisme: "Progress photos",
      gvDetayBittiTarih: "This task was completed on {tarih}",
      gvDetayYorumBos: "No comments yet.",
      gvDetayEkip: "Team and steps",
      gvTumunuGor: "See all",
      gvRozetAcil: "Urgent",
      gvRozetOncelikli: "Priority",
      gvDugmeGor: "View task",
      gvDugmeUstlen: "Take on",
      gvDugmeKontrol: "Check",
      gvDugmeDestek: "Help out",
      gvYakinBaslik: "Tasks near you",
      gvMahalleBaslik: "Tasks in the neighbourhood",
      gvOneriBaslik: "AI suggestion",
      gvOneriDugme: "See suggestions",
      gvBenimBaslik: "My tasks",
      gvAktifSayi: "{n} active",
      gvTamamSayi: "{n} completed",
      gvBenimBos: "You haven't joined a task yet.",
      gvBos: "No task here right now.",
      gvKonumGerekli: "Location permission is needed for nearby tasks.",
      gvKonumReddedildi: "Location permission was denied. You can allow it in settings.",
      gvDurumAcik: "Open",
      gvDurumDevam: "In progress",
      gvDurumTamam: "Completed",
      gvDurumIptal: "Cancelled",
      gvZamanBugun: "Joined · Today {saat}",
      gvZamanDun: "Joined · Yesterday {saat}",
      gvZamanTarih: "Joined · {tarih}",
      gvZamanBugunKisa: "Today {saat}",
      gvZamanDunKisa: "Yesterday {saat}",
      m5Mahallem: "My neighbourhood",
      m5Paylas: "Share",
      m5GonulluOl: "Volunteer",
      m5HavaAcik: "Clear",
      m5HavaParcali: "Partly cloudy",
      m5HavaSisli: "Foggy",
      m5HavaYagmurlu: "Rainy",
      m5HavaKarli: "Snowy",
      m5HavaFirtinali: "Stormy",
      m5HavaBugun: "Today {min}° / {max}°",
      m5PanoKedi: "registered cats",
      m5EgilimYeni: "new",
      m5EgilimBaslik: "Last 30 days: {son} new records · previous 30 days: {once}",
      m5HaritayiAc: "Open map",
      baBaslik: "Area Analysis",
      baAltBaslik: "Understand your neighbourhood's needs together.",
      baAralikTumu: "All",
      baAralik7: "7 days",
      baAralik30: "30 days",
      baAralik90: "3 months",
      baAralik365: "1 year",
      baSayacKedi: "Total cats",
      baSayacAcil: "Open emergencies",
      baSayacBeslenme: "Feeding points",
      baSayacSu: "Water points",
      baSayacGonullu: "Volunteer joins",
      baSayacKediYeni: "New cats",
      baSayacAcilYeni: "New emergencies",
      baSayacBeslenmeYeni: "New feeding points",
      baSayacSuYeni: "New water points",
      baSayacGonulluYeni: "New volunteer joins",
      baSatirKedi: "{n} cats",
      baSatirAcil: "{n} emergencies",
      baSatirBeslenme: "{n} feeding points",
      baSatirSu: "{n} water points",
      baSatirGonullu: "{n} volunteer joins",
      baKatmanlar: "Layers",
      baKatmanKedi: "Cats",
      baKatmanAcil: "Emergencies",
      baKatmanBeslenme: "Feeding points",
      baKatmanSu: "Water points",
      baKatmanGonullu: "Volunteer tasks",
      baKatmanIsi: "Heat map",
      baCercevele: "Fit map to areas",
      baIlceNotu: "Few records have a neighbourhood, so areas are shown as districts.",
      baHata: "Couldn't load the area analysis. It will appear once the server is updated.",
      baHaritaBos: "No area has a location yet",
      baBolgeOzet: "Summary by Area",
      baTumBolgeler: "All areas",
      baKademeNotu: "Numbers show new records in this period; levels are for all time.",
      baDiger: "Other",
      baDigerAlt: "no neighbourhood",
      baBolgeyiIncele: "View area",
      baBolgeYok: "No areas to show yet.",
      baAiBaslik: "AI Area Analysis",
      baAiYok: "Today's AI summary isn't ready yet.",
      baAiDetay: "Detailed analysis",
      baOncelikli: "Priority Areas",
      baNedenYok: "Lowest score",
      baHaritadaGor: "See cats on map",
      baHaritadaAlt: "Map centred on these areas.",
      baTumKediler: "Neighbourhood cats",
      baTumKedilerAlt: "See every cat as a list.",
      baKisaKedi: "Total cats",
      baKisaAcil: "Urgent",
      baKisaBeslenme: "Feeding",
      baKisaSu: "Water",
      baKisaGonullu: "Volunteers",
      baKisaKediYeni: "New cats",
      baKisaAcilYeni: "New emerg.",
      baKisaBeslenmeYeni: "New feeding",
      baKisaSuYeni: "New water",
      baKisaGonulluYeni: "Joins",
      baSatirKisaKedi: "{n} cats",
      baSatirKisaAcil: "{n} emerg.",
      baSatirKisaBeslenme: "{n} feeding",
      baSatirKisaSu: "{n} water",
      baSatirKisaGonullu: "{n} joins",
      baIncele: "View",
      baAiDetayKisa: "Details",
      baKademeNotuKisa: "· new records",
      baIlceNotuKisa: "District level",
      gdBaslik: "Daily Detail",
      gdBugun: "Today",
      gdOncekiGun: "Previous day",
      gdSonrakiGun: "Next day",
      gdToplamBugun: "{n} records today.",
      gdToplamGun: "{n} records on this day.",
      gdToplamYok: "No records on this day.",
      gdToplamKarsilastir: "Previous day: {once} · this day: {simdi}.",
      gdDagilim: "Activity Breakdown",
      gdKisaKedi: "New cats",
      gdKisaAcil: "Emergency",
      gdKisaKayip: "Lost",
      gdKisaBeslenme: "Feeding",
      gdKisaSu: "Water",
      gdKisaGonullu: "Volunteers",
      gdBolge: "By Neighbourhood",
      gdKonumsuz: "No neighbourhood: {n}",
      gdKonumYok: "No located records",
      gdHaritadaGor: "View on map",
      gdFotoBugun: "Photos from Today",
      gdFotoGun: "Photos of the Day",
      gdFotoYok: "No photos",
      gdFazla: "more",
      gdKayitBugun: "Today's Records",
      gdKayitGun: "Records of the Day",
      gdFiltreHepsi: "All records",
      gdTumunuGoster: "Show all ({n})",
      gdKayitKedi: "New cat record",
      gdKayitAcil: "Emergency",
      gdKayitKayip: "Lost cat post",
      gdKayitBeslenme: "New feeding point",
      gdKayitSu: "New water point",
      gdKayitGonullu: "Volunteers joined",
      gdKatilim: "{n} joined",
      gdAltBaslik: "See your neighbourhood on the map.",
      gdAltMetin: "A safer life for your neighbourhood, together.",
      gdHata: "Couldn't load the daily detail. It will appear once the server is updated.",
      gdPaylasMetni: "{yer} · {tarih}: street cats, daily detail.",
      gdGunuAc: "Open daily detail for {tarih}",
      s7Baslik: "Last 7 Days",
      s7PaylasMetni: "{yer}: the last 7 days for street cats.",
      s7GrafikBaslik: "New records per day",
      s7Yeni: "new",
      s7Sabit: "no change",
      s7SayacKedi: "New cat records",
      s7SayacAcil: "Emergencies",
      s7SayacKayip: "Lost cat posts",
      s7SayacBeslenme: "New feeding points",
      s7SayacSu: "New water points",
      s7SayacGonullu: "Volunteer joins",
      s7Gunluk: "Daily details",
      s7KartKotu: "{seri} increased in the last 7 days.",
      s7KartIyi: "{seri} increased in the last 7 days.",
      s7KartSabit: "No clear change compared with the previous 7 days.",
      s7KartOnceYok: "Comparison with the previous week appears after the server update.",
      s7KartKarsilastir: "Previous 7 days: {once} · this week: {simdi}.",
      s7IstatistikDetay: "See statistics",
      s7SeriAcil: "Emergencies",
      s7SeriKayip: "Lost cat posts",
      s7SeriKedi: "New cat records",
      s7SeriBeslenme: "New feeding points",
      s7SeriSu: "New water points",
      s7SeriGonullu: "Volunteer joins",
      s7Ac: "Open last 7 days",
      zekaGonullu: "Volunteers needed",
      azVeriKisa: "Few records",
      heroYanSlogan: "Same neighbourhood, happier cats.",
      haritaBaslikOzet: "Neighbourhood map (overview)",
      lejantAcil: "Emergencies",
      lejantKayip: "Lost cats",
      lejantSu: "Water points",
      lejantBeslenme: "Feeding",
      lejantGonullu: "Volunteers",
      zekaSu: "Too few water points",
      zekaMama: "Too few feeding points",
      zekaAsi: "Vaccine records are weak",
      zekaYarali: "Injured cats reported",
      zekaAcil: "Open emergency cases",
      zekaBarinak: "No cat shelter recorded",
      zekaTamam: "No clear gap",
      yasBilinmiyorKisa: "Unknown",
      dagilimIpucuBilinmiyor: "{p} of cats have no age on record.",
      dagilimIpucuCogunluk: "Largest group: {grup} ({p}).",
      dagilimYakinda: "Available after the server update.",
      ustBaslik: "My Neighbourhood",
      paylas: "Share",
      paylasMetni: "{yer}: cats, needs and volunteers in the neighbourhood.",
      paylasKopyalandi: "Link copied",
      paylasOlmadi: "Could not share",
      sekmeGenel: "Overview",
      sekmeAnaliz: "Analysis",
      sekmeGorevler: "Tasks",
      sekmeKediler: "Cats",
      sekmeGonulluler: "Volunteers",
      lejantKedi: "Cats",
      zekaOzet: "We analysed your neighbourhood. Highlights:",
      azVeri: "Few records yet — the score may change quickly.",
      detayliAnaliz: "See detailed analysis",
      dagilimBaslik: "Cats by age",
      kediKisa: "cats",
      yasYavru: "Kitten",
      yasYetiskin: "Adult",
      yasYasli: "Senior",
      yasBilinmiyor: "Age unknown",
      dagilimYok: "No cats recorded yet.",
      ihtiyacBaslik: "Top needs",
      ihtiyacYok: "No clear need right now.",
      oncelikKritik: "Critical",
      oncelikYuksek: "High",
      oncelikOrta: "Medium",
      oncelikDusuk: "Low",
      son7Baslik: "Last 7 days",
      son7Yok: "No new records in the last 7 days.",
      seriKedi: "Cat",
      seriAcil: "Emergency",
      seriKayip: "Lost",
      seriBeslenme: "Feeding",
      seriSu: "Water",
      ctaBaslik: "Stronger together as a neighbourhood.",
      ctaAlt: "Help change the lives of cats around you.",
      ctaDugme: "Volunteer",
      zekaAlt: "See what your neighbourhood needs and act on it.",
      zekaBaslik: "AI Neighbourhood Analysis",
      panoBeslenme: "feeding points",
      panoGonullu: "active volunteers",
      hizliAcilMerkezi: "Emergency Centre",
      onemliBaslik: "Nearby alerts",
      rozetKayip: "LOST",
      rozetAcil: "URGENT",
      rozetMama: "FOOD",
      rozetTehlike: "DANGER",
      rozetGonullu: "HELP",
      durumYok: "Nothing urgent nearby right now.",
      sonFotograflar: "Latest photos",
      sonAktiviteler: "Latest activity",
      tumuSayili: "All ({n})",
      paylasimYok: "No posts in your neighbourhood yet.",
      yakinKediler: "Cats near you",
      haritayiAc: "Open map",
      cipBesleniyor: "Feeding points",
      cipSu: "Water points",
      cipAlisik: "Used to people",
      cipBiliniyor: "Known locally",
      haritaEnYakin: "nearest {k} of {n} cats",
      panoBaslik: "In Your Neighbourhood Today",
      panoAlt: "More cats, a better neighbourhood.",
      panoKedi: "cats tracked",
      panoAcil: "emergencies",
      panoKayip: "lost cats",
      panoSu: "water points",
      mahallemiGor: "See my neighbourhood",
      hizliBaslik: "Quick actions",
      hizliKaydet: "Report a cat",
      hizliGordum: "I saw a cat",
      hizliAcil: "Report emergency",
      hizliMamaSu: "Food / Water",
      title: "My neighbourhood",
      bolgelerBaslik: "Areas that need the most help",
      heroKedi: "{n} cats registered in your neighbourhood.",
      heroYeniKedi: "{n} new cats registered in the last 30 days.",
      heroYardimBekleyen: "{n} cats in your neighbourhood are waiting for help.",
      enOnemli: "Biggest need:",
      iSu: "Water points",
      iMama: "Food points",
      iAsi: "Vaccination tracking",
      iYarali: "Injured cats",
      iAcil: "Open emergencies",
      iBarinak: "Shelter",
      iTamam: "No clear gap",
      haritaAlt: "Explore cats and areas that need help.",
      tamEkran: "Full screen",
      lYuksek: "High need",
      lOrta: "Moderate",
      lYeterli: "Sufficient",
      lMama: "Food point",
      lSu: "Water point",
      tumu: "All",
      tumGorevler: "All tasks",
      ilerlemeTamam: "{n}/{m} done",
      kayipRozet: "LOST",
      kayipAdsiz: "Lost cat",
      kayipYok: "No lost listings near you.",
      azOnce: "just now",
      saatOnce: "{n}h ago",
      gunOnce: "{n}d ago",
      son30: "Last 30 days",
      sAcil: "Emergencies",
      sMama: "Feeding points",
      sSu: "Water points",
      sGonullu: "Active volunteers",
      aiBaslik: "AI neighbourhood analysis",
      beta: "BETA",
      aiGor: "See AI suggestions",
      haritaBaslik: "Neighbourhood map",
      kayipBaslik: "Lost cats near you",
      yardimBaslik: "Help your neighbourhood",
      gorevYok: "No open tasks right now.",
      gorevKatil: "Join",
      ilerleme: "{n}/{m} volunteers",
      skorBaslik: "Neighbourhood score",
      skorYaklasik: "change is approximate",
      olculmedi: "—",
      yukleniyor: "Loading…",
      hata: "The neighbourhood report could not be loaded.",
      mahalleYok: "Set your home neighbourhood to see this page.",
      secBaslik: "Choose your neighbourhood",
      secCumle: "Once you pick it, the cats, needs and tasks here follow your own neighbourhood.",
      secKaydet: "Save",
      vazgec: "Cancel",
      secHata: "Could not save your neighbourhood.",
      tekrarDene: "Try again",
    },
    bolge: {
      title: "Area detail",
      bolgesi: "{n} area",
      tumunuGor: "See all",
      detayliAnaliz: "Detailed analysis",
      tumu: "All",
      gecenAy: "{n} vs last month",
      yaklasik: "approx.",
      kediAdet: "{n} cats",
      geri: "Back",
      kapat: "Close",
      son30: "Last 30 days",
      iKritik: "Critical need",
      iYuksek: "High need",
      iOrta: "Moderate need",
      iDusuk: "Low need",
      sKedi: "Registered cats",
      sMama: "Food points",
      sSu: "Water points",
      yKuzey: "North",
      yGuney: "South",
      yDogu: "East",
      yBati: "West",
      aiBaslik: "AI Area Analysis",
      beta: "BETA",
      kedilerBaslik: "Cats in this area",
      kediYok: "No cats registered in this area.",
      skorBaslik: "Neighbourhood score",
      skorYaklasik: "change is approximate",
      dagilimBaslik: "Distribution by direction",
      dagilimAdet: "{n} cats",
      bildirimBaslik: "Recent activity",
      bildirimYok: "No activity in the last 30 days.",
      olculmedi: "—",
      olcumYok: "No daily measurements have accumulated for this area yet.",
      hata: "Could not load the area report.",
    },
    mahalleListesi: {
      title: "Neighborhoods",
      ara: "Search neighborhood",
      mahalle: "neighborhoods",
      paylasim: "posts",
      kedi: "cats",
      alinamadi: "Could not load the neighborhood list.",
      tekrarDene: "Try again",
      aramaBos: "No neighborhood matches this search.",
      hicYok: "No neighborhoods to show yet.",
      kuralNotu: "This list shows neighborhoods with posts, coverage areas or registered animals.",
    },
    aiAnaliz: {
      sekmeOzet: "Overview",
      sekmeDetay: "Detailed Data",
      sekmeOneriler: "Suggestions",
      sonNGun: "Last {n} days",
      paylas: "Share",
      paylasMetni: "{yer}: neighbourhood analysis for street cats.",
      paylasKopyalandi: "Link copied",
      paylasOlmadi: "Couldn't share",
      detayBaslik: "Scores by area",
      veriYok: "No measurement yet.",
      skorEtiketi: "Neighbourhood health score",
      genelDurum: "Overall status",
      genelSkor: "Resources in the neighbourhood are rated \"{etiket}\".",
      genelKediArtti: "{n} more cats registered in 30 days.",
      genelKediAzaldi: "{n} fewer registered cats than 30 days ago.",
      genelKediSabit: "The number of registered cats did not change in 30 days.",
      genelBulgu: "{n} gaps were found.",
      genelBulguYok: "No clear gap in the measured areas.",
      farkRozeti: "{d} points vs last month",
      aiOzeti: "AI Summary",
      ozetSablon: "Summary",
      tumu: "All",
      bulgularBaslik: "Key findings",
      bulguAcil: "Open emergencies",
      bulguYarali: "Injured cat reports",
      bulguMama: "Not enough feeding points",
      bulguSu: "Not enough water points",
      bulguAsi: "Weak vaccine tracking",
      bulguBarinak: "No cat house",
      bulguGonullu: "Volunteer support needed",
      eylemHaritada: "View on map",
      eylemGorev: "Create task",
      eylemDetaylar: "See details",
      eylemGonullu: "Call volunteers",
      eylemVakalar: "See cases",
      son7AcilEk: "{n} new in the last 7 days.",
      bulguYok: "No clear gap in the measured areas.",
      haritadaGorBag: "See on map",
      bolgeBaslik: "Area analysis",
      kademeMukemmel: "Excellent",
      kademeIyi: "Good",
      kademeGelistir: "Needs work",
      kademeKritik: "Critical",
      ilceDuzeyiNotu: "District level",
      notSu: "Few water points",
      notMama: "Feeding point short",
      notAsi: "Vaccine tracking",
      notYarali: "Injured reports",
      notAcil: "Open emergency",
      notBarinak: "No cat house",
      notGonullu: "Volunteers needed",
      notTamam: "Overall good",
      bolgeYok: "Area scores appear once there is enough location data.",
      aralikSec: "Choose range",
      trendBaslik: "Trend analysis",
      seriKedi: "Cats",
      seriAcil: "Emergency",
      seriKayip: "Lost",
      seriBeslenme: "Feeding",
      seriSu: "Water",
      trendBirikiyor: "Daily measurements are building up; the chart appears after two days.",
      oneriBaslik: "AI Suggestion",
      bakilanBaslik: "What this analysis checked",
      bakilanKedi: "Registered cats",
      bakilanSu: "Water points",
      bakilanMama: "Feeding points",
      bakilanKulube: "Cat houses",
      bakilanAcil: "Cats in urgent condition",
      bakilanYarali: "Injured reports (30 days)",
      oneriAlinamadi: "Suggestions could not be loaded — the server did not answer. This does not mean there are no gaps.",
      tekrarDene: "Try again",
      acikGorevNotu: "A suggestion is hidden while an open task of the same kind exists.",
      sayacKedi: "cats",
      sayacSu: "water points",
      sayacMama: "feeding points",
      sayacKulube: "cat houses",
      sayacYarali: "injured reports (30 days)",
      sekmeBolge: "Region Analysis",
      sekmeKedi: "Cat Recognition",
      ozetBaslikPano: "Overall assessment",
      ozetCumle: "Neighbourhood standing in {yer} is {durum}. {n} priority gap(s) found.",
      durumBilinmiyor: "not measured",
      yerYok: "Neighbourhood not selected",
      balon: "A better neighbourhood is possible!",
      eksiklerBaslik: "Priority gaps",
      eksikSu: "Not enough water points",
      eksikMama: "Not enough feeding points",
      eksikYarali: "Injured cat reports",
      eksikKulube: "No registered cat house",
      eksikVet: "Cats needing urgent care",
      bugunBaslik: "3 things you can do today",
      eylemSu: "Add a water point",
      eylemMama: "Set up a feeding point",
      eylemYarali: "Check on an injured cat",
      eylemKulube: "Build a cat house",
      eylemVet: "Get vet support",
      haritadaGor: "See on map",
      eylemDetay: "See details",
      eylemListe: "See list",
      yogunBolge: "Densest cat area in the neighbourhood",
      gorevOlustur: "Create task",
      etkiOnEk: "Expected impact:",
      eksikYok: "No priority gap right now.",
      title: "AI Analysis",
      beta: "BETA",
      sekmeTani: "Identify by photo",
      sekmeSonuc: "Analysis results",
      sekmeIstatistik: "Statistics",
      taniBaslik: "Identify a cat from a photo",
      taniAciklama: "Upload a photo; it is compared with registered cats and open lost listings.",
      taniGit: "Go to photo identification",
      sonAnalizler: "Recent analyses",
      analizYok: "You haven't run an analysis yet.",
      olcumBaslamadi: "Analysis history has only just started being kept; earlier runs were not recorded.",
      eslesti: "Match",
      eslesmedi: "No match",
      adsizIlan: "Name not given",
      ozetBaslik: "Analysis summary for this area",
      sAnaliz: "Analyses",
      sEslesme: "Matches",
      sYeniKedi: "New cats",
      oran: "{n}/{m} analyses reached the threshold",
      oranNotu: "This is not an accuracy rate: how many of the matches were correct has not been measured.",
      son30: "Last 30 days",
      yukleniyor: "Loading…",
      hata: "Could not load analyses.",
    },
    kayipFormu: {
      baslik: "About the cat",
      adEtiket: "Name",
      adYerTutucu: "e.g. Boncuk",
      adIpucu: "The name neighbours will call out — it helps more than anything else.",
      kisirEtiket: "Neutered",
      kEvet: "Yes",
      kHayir: "No",
      cinsiyetEtiket: "Sex",
      cErkek: "Male",
      cDisi: "Female",
      cBilinmiyor: "Don't know",
      yasEtiket: "Age",
      yasIpucu: "e.g. 1.5 years old",
      mizacEtiket: "Temperament",
      mUrkek: "Shy",
      mSokulgan: "Friendly",
      mSaldirgan: "Defensive",
      mSakin: "Calm",
      iletisimEtiket: "Contact (optional)",
      iletisimIpucu: "Only shown behind the Call button, to signed-in people. Do not put it in the description.",
      notNumaraUyarisi: "There seems to be a phone number in the description. Put it in the contact field instead — that way it is not shown in the open.",
    },
    kayipBildir: {
      title: "Lost cat report",
      subtitle: "Add photos and what your cat looks like. Location comes next.",
      fotoZorunlu: "At least one photo is needed — it is the only thing a finder can compare.",
      varsa: "if known",
      renkEtiket: "Colour",
      konumTitle: "Where and when last seen",
      konumSubtitle: "Mark the spot on the map. Without it your listing cannot reach nearby people.",
      konumumuKullan: "Use my current location",
      konumAliniyor: "Getting location…",
      konumZorunlu: "A location is required. If GPS won't open, tap the map to mark the spot.",
      konumIsaretlendi: "Location marked",
      konumYok: "No location yet",
      yaricapEtiket: "Search radius",
      yaricapIpucu: "A lost house cat usually hides within a few hundred metres in the first days.",
      sonGorulmeEtiket: "Last seen",
      ekEtiket: "Extra information",
      ekYerTutucu: "Anything that helps: a habit, where it usually hides, who it trusts…",
      mahalleEtiket: "Neighbourhood",
      duzenle: "Edit",
      ozetAdsiz: "Unnamed cat",
      yayinla: "Publish lost listing",
      yayinlaniyor: "Publishing…",
      errSend: "The listing could not be published. Please try again.",
      okTitle: "Your listing is live",
      okDesc: "People nearby have been notified. Every sighting will reach you.",
      okView: "Open my listing",
      okList: "All lost cats",
      acikIlanBaslik: "You already have an open listing",
      acikIlanMetin: "A second listing splits the search. Is this the same cat?",
      acikIlanGit: "Open that listing",
      acikIlanBaska: "No, this is another cat",
      vazgec: "Cancel",
      renkYerTutucu: "e.g. tabby, white chest",
      ayirtEtiket: "Distinguishing features",
      ayirtYerTutucu: "Collar, scar, notched ear, the way it walks…",
    },
    kayipListe: {
      odul: "reward",
      baslik: "Lost cats",
      rozet: "LOST",
      ilanAc: "Post a lost listing",
      fotoAra: "Search by photo",
      yukleniyor: "Loading…",
      yeniden: "Try again",
      hata: "The listings could not be loaded.",
      bos: "No open lost listings right now — good news.",
      yakindaYok: "Nothing nearby, so listings from further away are shown.",
    },
    acilDetay: {
      durumBaslik: "Cat’s condition",
      aciklamaBaslik: "Description",
      bildirenBaslik: "Reported by",
      bildirdi: "reported {n}",
      konumBaslik: "Location",
      haritadaAc: "Open in map",
      paylas: "Share",
      sil: "Delete report",
      silOnayBaslik: "Delete this report?",
      silOnayMetin: "It disappears from the list and the map. This can't be undone.",
      silEvet: "Yes, delete",
      vazgec: "Cancel",
      siliniyor: "Deleting…",
      silHata: "The report could not be deleted.",
      silYetki: "Only the person who reported it can delete it.",
      fotoSayaci: "{a} / {b}",
      yorumlar: "Comments",
      yorumYok: "No comments yet — neighbours coordinate here.",
      yorumYaz: "Write a comment…",
      yorumGonder: "Send",
      yorumGidiyor: "Sending…",
      yorumHata: "The comment could not be sent.",
      yorumOkunmadi: "The comments could not be loaded.",
      yorumGiris: "Sign in to comment.",
      yorumSil: "Delete comment",
      yorumSilHata: "The comment could not be deleted.",
      begen: "Support",
      begenHata: "That didn't go through.",
    },
    anasayfaAlt: {
      baslik: "About Sokak Kedileri",
      hakkinda: "About us",
      yardim: "Help",
      gizlilik: "Privacy",
      sartlar: "Terms of use",
      telif: "Made with love for street cats.",
    },
    ihbarFormu: {
      title: "Add a report",
      sub: "Where did you see the cat?",
      locTitle: "Location",
      locUse: "Use my current location",
      locPicked: "Location marked",
      locHelp: "Tap the map to move the pin.",
      locNeeded: "A location is required — a report without one can't be drawn on the map.",
      noteTitle: "What did you see?",
      notePh: "Under a white car, very shy. Ran toward the park.",
      photoTitle: "Photo (optional)",
      photoAdd: "Add a photo",
      photoRemove: "Remove photo",
      photoHint: "A photo turns your report into evidence — AI can compare it.",
      guvenTitle: "How sure are you?",
      gBenzer: "Could be similar",
      gEminDegil: "Not sure",
      gFoto: "Photo added",
      guvenFotoNote: "With a photo, your report is marked “Photo added”.",
      send: "Send report",
      sending: "Sending…",
      okTitle: "Report sent 🧡",
      okBody: "The owner has been notified. Thank you.",
      okBack: "Back to search area",
      errSend: "The report could not be sent. Please try again.",
      back: "Back",
    },
    aiKars: {
      title: "AI comparison",
      seenSimilar: "Seen a similar cat?",
      seenSimilarDesc: "If you saw a cat, take a photo or pick one from your gallery. AI will compare it with the lost cat.",
      camera: "Camera",
      gallery: "Gallery",
      aiNote: "AI is an assistant. The final call is yours.",
      resultTitle: "Comparison result",
      new: "New",
      yourPhoto: "Your photo",
      lostCat: "Lost cat",
      levelHigh: "High similarity",
      levelMid: "Some similarity",
      levelLow: "Low similarity",
      levelHighDesc: "This cat looks quite like the lost one.",
      levelMidDesc: "Some features match, others do not.",
      levelLowDesc: "The features mostly do not match.",
      fPattern: "Coat pattern",
      fMarks: "Distinctive marks",
      fVisual: "Overall look",
      fAge: "Age range",
      fLocation: "Location proximity",
      stateSimilar: "similar",
      statePartial: "partly similar",
      stateDifferent: "differs",
      stateUnmeasured: "not measured",
      report: "Report this cat",
      unsureTitle: "Not sure?",
      unsureDesc: "Report it anyway. Every report helps.",
      tipsTitle: "Tips for a better result",
      tip1: "Get the face in sharp focus",
      tip2: "If you can, shoot from several angles",
      tip3: "Shoot in good light",
      tip4: "Show the surroundings too",
      comparing: "Comparing…",
      errCompare: "Comparison failed. Try again.",
      notMeasuredNote: "Features the AI could not measure are marked as such — unmeasured is not the same as mismatched.",
    },
    acil: {
      merkezHeroNot: "Every cat matters",
      merkezBaslik: "Emergency Centre",
      merkezHeroBaslik: "Let's save lives together",
      merkezHeroAlt: "Let's act together for cats that are injured, in danger or in need.",
      merkezVakalariGor: "See emergencies",
      merkezVakalariGorAlt: "Look at the cats waiting for help right now.",
      merkezVakaBildir: "Report an emergency",
      merkezVakaBildirAlt: "Create a new emergency.",
      merkezYakinVakalar: "Emergencies near me",
      merkezKategoriler: "Emergency categories",
      merkezKatYarali: "Injured cats",
      merkezKatTehlike: "Danger zones",
      merkezKatKayip: "Lost cats",
      merkezKatMamaSu: "Food / Water",
      merkezKatGonullu: "Volunteer help",
      merkezAltBaslik: "Every report can save a life.",
      merkezAltMetin: "By reporting even uncertain situations you help cats live in safer neighbourhoods.",
      merkezVakaYok: "No open emergencies near you right now.",
      title: "Report an emergency",
      subtitle: "Seen a cat that needs help? Report it quickly and reach the nearest volunteers.",
      safetyNote: "In an emergency, please put your own safety first. Do not put yourself at risk.",
      stepInfo: "Details",
      stepLocation: "Location",
      stepSend: "Send",
      catInjured: "Injured cat",
      catInjuredDesc: "Injured, sick or suffering cat",
      catLost: "Report a lost cat",
      catLostDesc: "Your cat is missing — post a search listing",
      catFood: "Food / water need",
      catFoodDesc: "No feeding spot, or one that needs support",
      catDanger: "Dangerous area",
      catDangerDesc: "Construction, traffic, poisoning risk and so on",
      catVolunteer: "Volunteer help",
      catVolunteerDesc: "When transport, fostering or on-site help is needed",
      injuredTitle: "Injured cat report",
      injuredDesc: "Share the condition of the cat you saw. We will tell volunteers right away.",
      injuredPlaceholder: "The cat's condition, behaviour, injury…",
      severityLabel: "Urgency",
      severityHigh: "Urgent",
      severityMid: "Medium",
      severityLow: "Low",
      foodTitle: "Food / water need",
      foodDesc: "Create a report for cats in this area that need food or water.",
      needLabel: "What is needed",
      needFood: "Food",
      needWater: "Water",
      needBoth: "Both",
      foodPlaceholder: "E.g. street cats in this area need food support…",
      dangerTitle: "Dangerous area",
      dangerDesc: "Is there an area that puts cats at risk? Let other animal lovers know too.",
      dangerTypeLabel: "Type of danger",
      multiSelect: "You can pick more than one",
      dTraffic: "Traffic risk",
      dConstruction: "Construction / demolition",
      dPoison: "Poisoning risk",
      dViolence: "Violence / mistreatment",
      dWell: "Well / enclosed space",
      dOther: "Other",
      dangerPlaceholder: "Add details about the area…",
      volunteerTitle: "Volunteer help",
      volunteerDesc: "Does this cat need a volunteer? Share your request so nearby volunteers hear about it.",
      supportLabel: "Type of help",
      sTransport: "Transport",
      sFoster: "Temporary fostering",
      sOnSite: "On-site help",
      sVet: "Vet support",
      sSupplies: "Supplies",
      sOther: "Other",
      volunteerPlaceholder: "What kind of help is needed?",
      photoLabel: "Add photos",
      optional: "(optional)",
      photoTake: "Camera",
      photoPick: "Choose from gallery",
      photoMax: "Up to {n} photos",
      descLabel: "Description",
      minChars: "At least {n} characters",
      continue: "Continue",
      back: "Back",
      locTitle: "Choose location",
      locDesc: "Mark the cat's location on the map or use your current location.",
      locUseCurrent: "Use my current location",
      locAddress: "Address (optional)",
      locNeighborhood: "Neighbourhood",
      locNeighborhoodHint: "e.g. Moda, Kadıköy",
      locUnsupported: "This browser does not support location.",
      locDenied: "Location permission was declined. Type the neighbourhood instead.",
      locFailed: "Couldn't get your location. Tap the map or type the neighbourhood.",
      okNotified: "volunteers notified",
      errSend: "Could not send the report. Please try again.",
      errLogin: "Please sign in to send the report. Your form is kept.",
      errNetwork: "No connection. Check your network and try again.",
      errList: "Could not load the list.",
      retry: "Try again",
      detayGiris: "Sign in to see this report.",
      detayYok: "This report is no longer available.",
      detayKonumsuz: "No location was saved with this report.",
      detayBildiren: "Reported by {ad}",
      detaySilmeYok: "Deleting your own report isn't available yet.",
      locRequired: "Location is required so helpers can find the spot.",
      reviewTitle: "Review your report",
      reviewDesc: "Does everything look right? Check before sending.",
      rType: "Type",
      rLocation: "Location",
      rPhotos: "Photos ({n})",
      submit: "Send report",
      okTitle: "Report sent!",
      okDesc: "Thank you! The nearest volunteers have been notified. You can follow what happens.",
      okMap: "See on the map",
      okList: "View emergencies",
      okAgain: "Make another report",
      okAvgLabel: "Average response time",
      okAvgMinutes: "{n} minutes",
      listTitle: "Emergencies",
      fAll: "All",
      fInjured: "Injured",
      fFood: "Food / water",
      fDanger: "Danger",
      fVolunteer: "Volunteer",
      badgeHigh: "URGENT",
      badgeMid: "MEDIUM",
      badgeLow: "LOW",
      badgeNew: "NEW",
      listEmpty: "No open emergencies near you — good news.",
      errNoCategory: "First choose what you are reporting.",
      sending: "Sending…",
      kaydet: "Save",
      kaydedildi: "Saved",
      kaydettiklerim: "Saved cases",
      kaydetHata: "That didn't go through.",
      kaydetBos: "Nothing saved yet. Tap the heart on a case to follow it.",
      kaydetGiris: "Sign in to save cases.",
      yakinFiltre: "Showing cases within {n} km of you.",
      tumunuGoster: "Show all",
      yakiniGoster: "Only nearby",
      tumVakalar: "All open cases are shown.",
    },
    footer: {
      discover: "Discover",
      community: "Community",
      connect: "Connect",
      brandDescription:
        "We bring volunteers, care points and urgent support together to help stray cats live safer, healthier and more loved lives.",
    },
    assistant: {
      name: "Paw Assistant",
      readyStatus: "Ready · zero-cost first",
      napStatus: "Napping until tomorrow 😴",
      placeholder: "Ask me about street cats…",
      napHint: "I'm napping — see you tomorrow!",
      suggestionsTitle: "Try one of these:",
      disclaimer: "AI advice · not a substitute for a vet",
      networkError: "Network is grumpy right now 🐾 try again in a moment.",
      guestPrompt: "Hi! I'd love to chat but I need to recognise you first. Sign in or join — it's free and takes a few seconds.",
      guestLoginCta: "Sign in to chat with me",
    },
    nav: {
      home: "Home",
      database: "Animal Database",
      identify: "AI Identify",
      identifyShort: "AI Scan",
      map: "Map",
      sightings: "Report emergency",
      emergencies: "Report emergency",
      volunteers: "Volunteers",
      feeding: "Feeding Spots",
      leaderboard: "Leaderboard",
      photoContest: "Photo Contest",
      badges: "Badges",
      safety: "Safety",
      observationHouse: "Observation House",
      neighborhood: "My Neighborhood",
      messages: "Messages",
      notifications: "Notifications",
      dashboard: "Dashboard",
      profile: "Profile",
      login: "Sign in",
      register: "Join",
      logout: "Sign out",
      addCat: "Add a cat",
      admin: "Admin",
      adminEmergencies: "Emergency Queue",
    },
    landing: {
      overline: "Community Animal Welfare",
      heroTitle: "Every street cat has a story.",
      heroSubtitle:
        "Identify, track and help protect them.",
      liveBadge: "Community live",
      ctaPrimary: "Register a cat",
      ctaSecondary: "Cat Database",
      statCats: "Cats catalogued",
      statSightings: "Emergencies reported",
      statFeeding: "Feeding spots",
      statVaccinated: "Vaccinated",
      featuresOverline: "What you can do",
      f1Title: "AI photo identification",
      f1Desc:
        "Upload a photo and our AI describes coat, breed, age and distinctive features — then matches against the local database.",
      f2Title: "Neighborhood map",
      f2Desc: "Pin animals, emergencies and feeding stations across your city. Filter by district, colour or health status.",
      f3Title: "Health & vaccinations",
      f3Desc:
        "Track rabies, FVRCP, spay/neuter and notes. Empower local volunteers and vets with real records.",
      f4Title: "Emergency reports",
      f4Desc:
        "Spotted a kitten in trouble? Drop a location pin — or add a photo with the location — in 30 seconds and alert nearby volunteers and vets.",
      finalCta: "Help your neighborhood cats today.",
      finalCtaSub: "It only takes a photo to start.",
    },
    auth: {
      loginTitle: "Welcome back",
      registerTitle: "Create your account",
      registerSuccess: "Account created!",
      email: "Email",
      password: "Password",
      name: "Full name",
      role: "Account type",
      rolePublic: "Public — Browse and report emergencies",
      roleVolunteer: "Volunteer — Manage cats & feeding spots",
      roleAdmin: "Admin — Full access",
      volunteerType: "Volunteer specialty",
      volunteerTypeVet: "Volunteer veterinarian — treat injured strays",
      volunteerTypeIndividual: "Volunteer individual — feed, foster, rescue",
      submitLogin: "Sign in",
      submitRegister: "Create account",
      switchToRegister: "No account yet? Join",
      switchToLogin: "Have an account? Sign in",
      forgotPassword: "Forgot your password?",
      forgotLink: "Forgot password",
      backToLogin: "Back to sign in",
      forgotTitle: "Reset your password",
      forgotIntro: "Enter your email and we'll send you a link to set a new password.",
      forgotSubmit: "Send reset link",
      forgotSentBody: "If we found an account with that email, a reset link is on its way. Check your inbox (and spam folder).",
      forgotDevTitle: "Dev mode",
      forgotDevBody: "Email isn't configured yet — use the link below to continue:",
      newPassword: "New password",
      confirmPassword: "Confirm password",
      resetTitle: "Set a new password",
      resetIntro: "Pick something you'll remember — at least 8 characters.",
      resetSubmit: "Update password",
      resetSuccess: "Password updated. You can sign in now.",
      resetFailed: "Something went wrong — request a new reset link.",
      resetMinLen: "Password must be at least 8 characters.",
      resetMismatch: "Passwords don't match.",
      resetNoToken: "This link is missing a token. Request a fresh reset email.",
      invalid: "Invalid credentials",
      emailTaken: "Email already registered",
    },
    cats: {
      title: "Cat Database",
      loadMore: "Load more",
      subtitle: "Browse every catalogued cat in our community.",
      search: "Search by name, color, breed…",
      filterNeighborhood: "Neighborhood",
      filterHealth: "Health status",
      add: "Add a cat",
      empty: "No cats yet. Be the first to add one!",
      details: "Details",
      visits: "visits",
      aiPortraitShortcut: "Create AI portrait",
      aiPortraitRegenerate: "Regenerate AI portrait",
      aiPortraitShortcutHint: "Generate a studio-style portrait from this cat's real photos",
      aiBadgeHasTooltip: "AI portrait ready",
      aiBadgeMissingTooltip: "No AI portrait yet — open and generate one",
      health: {
        healthy: "Healthy",
        minor_issues: "Minor issues",
        needs_attention: "Needs attention",
        critical: "Critical",
        injured: "Injured",
        sick: "Sick",
        recovering: "Recovering",
        unknown: "Unknown",
      },
    },
    addCat: {
      title: "Add a cat",
      subtitle: "Upload a photo — our AI will pre-fill the details for you.",
      uploadCta: "Upload photo",
      analyzing: "Analyzing photo with AI…",
      analyzeAgain: "Re-analyze",
      name: "Name",
      color: "Color",
      pattern: "Pattern",
      breed: "Breed",
      age: "Age estimate",
      size: "Size",
      features: "Distinctive features",
      neighborhood: "Neighborhood / District",
      lat: "Latitude",
      lng: "Longitude",
      health: "Health status",
      spayed: "Spayed / Neutered",
      notes: "Notes",
      save: "Save cat",
      success: "Cat saved!",
      error: "Could not save the cat",
      aiError: "AI analysis failed",
      tooLarge: "Photo is too large (max 6 MB). Please pick a smaller crop.",
      invalidFields: "Required fields are missing or invalid (location / name).",
      serverDown: "Could not create profile — please check your location and required fields.",
    },
    identify: {
      // TUR-10: bu yedi anahtar ayri bir 'identify' blogundaydi.
      // Ayni nesnede ayni anahtar iki kez yazilinca JS ikincisini
      // alir ve birincisini TAMAMEN atar — birlestirmez. Sonuc:
      // AI Kimlik ekraninda kullanici 'identify.registerCat' gibi
      // ham anahtarlar goruyordu. Uc dilde de boyleydi.
      registerCat: "Register this cat",
      registerDog: "Register this dog",
      noMatch: "No match found",
      noMatchCat: "This cat may already be registered and we may have missed it.",
      noMatchDog: "This dog may already be registered and we may have missed it.",
      reallyNewCat: "This really is a new cat",
      reallyNewDog: "This really is a new dog",

      title: "AI Identify",
      subtitle:
        "Upload a photo of a stray cat. Our AI will describe it and find possible matches in the database.",
      upload: "Upload a cat photo",
      analyze: "Analyze & match",
      analyzing: "AI is looking at the photo…",
      neighborhood: "Filter by neighborhood (optional)",
      analysis: "AI analysis",
      matches: "Possible matches",
      noMatches: "No similar cats found. Want to add this as a new cat?",
      addAsNew: "Save as new cat",
      score: "Similarity",
      topMatch: "Best match",
      otherMatches: "Other possible matches",
      matchFound: "Match found",
      health: {
        title: "Health assessment",
        statusLabel: "Health status",
        urgencyLabel: "Priority",
        issuesLabel: "Visible signs",
        adviceLabel: "What to do now",
        bodyLabel: "Body condition",
        statuses: {
          healthy: "Healthy",
          minor_issues: "Minor issues",
          needs_attention: "Needs attention",
          critical: "Critical",
          unknown: "Could not assess",
        },
        urgencies: {
          URGENT: "Urgent — vet now",
          MEDIUM: "Medium — vet in 1–3 days",
          STABLE: "Stable",
          UNKNOWN: "Unclear",
        },
        body: {
          emaciated: "Emaciated",
          thin: "Thin",
          ideal: "Ideal",
          overweight: "Overweight",
          unknown: "Unknown",
        },
        disclaimer: "AI assessment only — not a substitute for a veterinarian.",
      },
    },
    liveIdentify: {
      overline: "Live AI",
      title: "Point your camera at a cat",
      subtitle: "Aim at a stray cat's face — our AI will recognise it from your live camera feed and match against the database.",
      tryLive: "Try with camera",
      startCamera: "Start camera",
      stopCamera: "Stop camera",
      scanNow: "Scan now",
      pauseAuto: "Pause auto-scan",
      resumeAuto: "Resume auto-scan",
      autoOn: "Auto-scanning",
      autoOff: "Manual mode",
      cameraOff: "Camera is off — tap Start to begin.",
      aimAtCat: "Aim at a cat's face and the AI will start analysing…",
      startToBegin: "Tap Start camera to begin.",
      freeHint: "Free plan: tap Scan once per attempt (uses 1 of your 10 monthly AI calls). Upgrade for non-stop auto-scan.",
      upsell: "Auto-scan with Premium",
    },
    scanCat: {
      overline: "AI Profile Wizard",
      title: "Cat passport photo",
      subtitle: "Aim the camera at the cat. The AI checks the database first, then guides you through three angles — front, side and top — to build a perfect profile.",
      navLabel: "Scan a cat",
      startCamera: "Start camera",
      startCameraNative: "Take photos (3)",
      pickFromGallery: "Pick 3 photos from gallery",
      galleryFallbackHint: "If the camera doesn't open, you can still create a profile by picking 3 photos from your gallery.",
      galleryPrimaryHint: "Pick 3 photos from your gallery to create a cat profile. AI match, auto-crop, and Premium portraits all work the same way.",
      galleryNeed3: "Please pick exactly 3 photos.",
      tryCameraBeta: "Try camera",
      cameraUnavailableFallback: "Camera couldn't open, you can pick 3 photos from your gallery.",
      startUnavailableFallback: "Camera couldn't start, you can pick photos from your gallery.",
      partialFallback: "Only {n}/3 photos captured. Continuing with the gallery picker for the rest.",
      cameraCannotOpen: "Camera could not be opened. Please check your camera app or choose photos from gallery.",
      nativeCaptureStep: "Photo captured",
      nativeAngleStart: "Now capturing angle",
      partialCaptures: "{n}/3 photos captured. Tap to retry the remaining angles.",
      buttonTapped: "Camera button tapped",
      stopCamera: "Stop camera",
      cameraDenied: "Camera permission denied. Please grant camera access for this site in your browser settings.",
      cameraDeniedNative: "Camera permission denied. Please enable the camera permission in your phone settings.",
      cameraDeniedPWA: "Camera permission couldn't be obtained in the installed app. You can open it in Chrome to scan with the camera.",
      cameraUnsupported: "This device doesn't support the camera API.",
      cameraGenericError: "Could not access the camera.",
      openInChrome: "Open in Chrome",
      openAppSettings: "Open settings",
      cameraRetry: "Try again",
      cameraWaiting: "Waiting for camera permission…",
      matching: "Searching the database…",
      matchFound: "A match was found!",
      matchPrompt: "Is this the same cat?",
      matchYes: "Yes, open profile",
      matchNo: "No, new profile",
      notFound: "Profile not found",
      notFoundSub: "Let's create a new one — three angles, AI-guided.",
      startWizard: "Start scanning now",
      stages: {
        front: "Front view",
        side: "Side view",
        top: "Top view",
      },
      stageHint: {
        front: "Point the camera straight at the cat's face. Both eyes visible.",
        side: "Move to the side — full profile, only one eye/ear visible.",
        top: "Hold the camera above the head, looking down at the skull.",
      },
      captured: "Captured!",
      retake: "Retake",
      tooFast: "Hold steady…",
      holdCatOnScreen: "Keep the cat in frame",
      timeoutCloser: "Move a bit closer",
      timeoutSteady: "Hold the camera steady",
      timeoutLight: "Lighting is insufficient — move to a brighter spot",
      timeoutShade: "Too much glare — move into shade",
      collectingFrames: "Picking the best frame…",
      locationCaptured: "Location captured",
      locationNeighborhoodOnly: "Continuing with neighborhood",
      locationMissing: "No location — get GPS or type a neighborhood",
      locationRequired: "Please capture GPS or type a neighborhood.",
      locating: "Getting location…",
      gpsRefresh: "Refresh GPS",
      gpsLocked: "Location captured",
      gpsDenied: "Location permission denied. Allow in phone settings or type a neighborhood.",
      gpsUnavailable: "Couldn't get location (no signal). You can continue with a neighborhood.",
      gpsTimeout: "Location request timed out. You can continue with a neighborhood.",
      gpsError: "Couldn't get location. You can continue with a neighborhood.",
      gpsUnsupported: "This device doesn't support location services.",
      autoStarting: "Cat detected, scan starting…",
      autoDetectActive: "Detecting cat… Keep its face in frame. Scan starts automatically.",
      autoProfileTitle: "Auto Profile Photo",
      autoFree: "Free",
      autoProcessing: "Processing…",
      premiumOnly: "Premium",
      aiPortraitSectionTitle: "AI Profile Portrait",
      aiPortraitOpenBtn: "Generate AI portrait",
      aiPortraitFreeHint: "Premium members can generate an AI front-facing portrait from your photos.",
      aiPortraitTitle: "Create AI portrait",
      aiPortraitOverline: "Premium feature",
      aiPortraitBody: "AI generates ONE front-facing portrait using your 3 real photos as reference. Identity (color, pattern, eyes) is preserved. This uses 1 AI portrait credit.",
      aiPortraitLoading: "Loading credit info…",
      aiPortraitQuota: "Credits left",
      aiPortraitTierLabel: "Tier",
      aiPortraitFreeNotice: "AI portraits are for Premium / Super Premium members. The free auto profile photo is already generated.",
      aiPortraitConfirm: "Generate",
      aiPortraitGenerating: "Generating…",
      aiPortraitReady: "AI portrait ready!",
      aiPortraitCached: "From cache — no credit used.",
      aiPortraitFailed: "Couldn't generate AI portrait.",
      aiPortraitNotEnoughPhotos: "Not enough quality photos for AI portrait. Get the cat closer and sharper.",
      aiBadge: "AI",
      originalPhotosTitle: "Real Photos",
      blockedAi: "AI-generated images are blocked.",
      blockedInappropriate: "Inappropriate image detected.",
      finalize: "Finalize profile",
      finalizeTitle: "One last thing",
      finalizeSub: "Give the cat a name (and neighborhood if you know it).",
      submit: "Create profile",
      submitting: "Saving…",
      done: "Profile created!",
      doneSub: "All three angles have been added to the gallery.",
      goProfile: "Go to profile",
      manualCapture: "Capture now",
      angleOf: "Angle {n} of 3",
      voiceOn: "Voice on",
      voiceOff: "Voice off",
      directions: {
        left: "Move left",
        right: "Move right",
        up: "Tilt up",
        down: "Tilt down",
        closer: "Move closer",
        farther: "Step back",
        center: "Perfect, hold still",
        unclear: "Aim at the cat's face",
      },
      overallProgress: "Profile progress",
      bestPhotoChosen: "AI picked the best front shot as profile",
      detectorLoading: "Loading on-device AI… (one-time download)",
      detectorReady: "On-device AI ready · zero-cost scanning",
    },
    thinking: {
      title: "AI is thinking",
      hint: "This usually takes 5–15 seconds. You can keep using the app.",
      stages: {
        compress: "Preparing your photo…",
        vision: "Looking at the cat with GPT-Vision…",
        matching: "Searching the database for similar cats…",
        polishing: "Polishing the results…",
      },
    },
    safety: {
      overline: "Community Care",
      title: "Cat Safety Guide",
      subtitle:
        "Before you approach, feed, or rescue a street cat — read these short, practical guidelines. They protect both you and the cats.",
      disclaimerTitle: "Medical disclaimer",
      disclaimerText:
        "This page is general safety education for community volunteers, not medical advice. For any bite, deep scratch, swelling, or fever after a cat contact, please consult a qualified healthcare professional within 24 hours.",
      footerNote:
        "Stay safe out there. The kindest thing you can do for a stray cat is to keep yourself healthy enough to keep helping.",
      before: {
        eyebrow: "Before approaching",
        title: "Approach safely",
        intro:
          "Most street cats in Istanbul are tolerant of humans, but every cat has a boundary. Reading body language and moving slowly prevents 90% of incidents.",
        items: [
          {
            h: "Move slowly, blink softly, never reach over the head",
            p: "Crouch low, extend a finger and let the cat smell it first. A direct hand to the top of the skull triggers a defensive swipe. Slow blinks are feline body language for 'I'm not a threat'.",
          },
          {
            h: "Read the tail and ears",
            p: "Whipping tail, flattened ears, sideways body, low growl = back off. Upright tail with a slight curl at the tip = friendly hello.",
          },
          {
            h: "Feed from a distance the first time",
            p: "Drop food and step back 1–2 metres. Let the cat eat without leaning over it. Hand-feeding is for cats that have already chosen to trust you.",
          },
          {
            h: "Never reach into a mother cat's kittens",
            p: "Even the friendliest mother will defend kittens. Observe from at least 2 metres for a few days; if the kittens are sick or abandoned, contact a vet or volunteer instead.",
          },
          {
            h: "Wear closed shoes and long sleeves for rescues",
            p: "Injured or trapped cats panic. Thick fabric protects against deep scratches. Carry a towel — wrapping the cat reduces stress for both of you.",
          },
        ],
      },
      scratch: {
        eyebrow: "After a scratch",
        title: "If a cat scratches you",
        intro:
          "Most superficial scratches heal cleanly. But cat claws carry bacteria like Bartonella henselae (Cat-Scratch Disease). Quick, thorough washing prevents most infections.",
        items: [
          {
            h: "Rinse under running water for 5+ minutes",
            p: "Hold the wound under cool or lukewarm tap water and let it bleed freely for a minute or two — bleeding helps flush bacteria out.",
          },
          {
            h: "Wash with soap for at least 15 minutes",
            p: "Use plain soap, no need for anything fancy. Lather thoroughly into and around the wound.",
          },
          {
            h: "Stop the bleeding with clean gauze",
            p: "Apply gentle pressure with sterile gauze or a clean cloth. Don't use cotton balls — fibres stick to the wound.",
          },
          {
            h: "Apply an antiseptic and cover loosely",
            p: "Povidone-iodine (Batticon) or chlorhexidine work well. Use a non-stick dressing and change it daily.",
          },
          {
            h: "See a doctor within 24 hours if anything looks off",
            p: "Redness spreading, swelling, pus, fever, swollen lymph nodes — go to your nearest Aile Sağlığı Merkezi or hospital. Mention the date of the scratch and whether the cat looked healthy.",
          },
        ],
        warning:
          "If your last tetanus booster was more than 10 years ago — or you can't remember — request one. It is free at any Aile Sağlığı Merkezi.",
      },
      bite: {
        eyebrow: "After a bite",
        title: "If a cat bites you",
        intro:
          "Bites are more serious than scratches. Cat teeth puncture deep and inject bacteria into tissue. In Turkey, rabies prophylaxis is also a real consideration. Treat every bite as a medical event.",
        items: [
          {
            h: "Rinse for 15+ minutes under running water",
            p: "This is the single most important step. Long, vigorous rinsing dramatically lowers infection and rabies risk. Squeeze the area gently to encourage bleeding.",
          },
          {
            h: "Wash with soap and apply antiseptic",
            p: "Soap + water for at least 15 minutes, then povidone-iodine. Do not bandage tightly — bite wounds need air.",
          },
          {
            h: "Go to a hospital or sağlık ocağı the same day",
            p: "A doctor will assess depth, location, and infection risk. Antibiotics are almost always prescribed for cat bites.",
          },
          {
            h: "Ask about rabies prophylaxis",
            p: "If the cat was unfamiliar, behaving abnormally, or cannot be observed for 10 days, post-exposure rabies vaccination may be required. This is free at state hospitals in Turkey.",
          },
          {
            h: "If you know the cat, keep it under observation",
            p: "A healthy domestic or community cat that bit you should be observed for 10 days. If it stays healthy, rabies is ruled out. Note the cat's location and photo on this app to help if needed.",
          },
        ],
        warning:
          "Bites to the hand, face, or near joints are higher risk. Do NOT delay — go to the emergency department (acil servis) the same day.",
      },
      contacts: {
        eyebrow: "Emergency",
        title: "Phone numbers to know",
        subtitle: "In Turkey these lines are free, 24/7, and answered in Turkish (English is often available on 112).",
        l112: "Acil Çağrı Merkezi · ambulance, fire, police",
        l184: "Sağlık Bakanlığı · health advice line",
        l153: "Belediye · municipal animal services",
      },
      ctaTitle: "Were you scratched or bitten?",
      ctaSubtitle: "Log the incident so you remember to follow the 10-day rabies observation window — and so the community can help if needed.",
      ctaScratch: "I was scratched",
      ctaBite: "I was bitten",
    },
    incident: {
      title: "Log an incident",
      subtitle: "Start the 10-day observation window. You can edit the status later (cat healthy, symptoms appeared, treatment complete).",
      types: { scratch: "Scratch", bite: "Bite" },
      severity: "Severity",
      sev: { mild: "Mild — superficial", moderate: "Moderate — bleeding", severe: "Severe — deep / multiple" },
      cat: "Which cat (optional)",
      catPlaceholder: "Pick a cat or leave unknown",
      catHint: "Linking the cat helps the community track 10-day observation publicly.",
      unknownCat: "Unknown / stray I don't know",
      date: "Incident date",
      location: "Where (optional)",
      locationPlaceholder: "e.g. neighbourhood and street",
      notes: "Notes",
      notesPlaceholder: "What happened? Did you wash the wound? Any symptoms?",
      photo: "Wound or cat photo (optional)",
      privacyNote: "Your notes and photo are private to you. Only the linked cat (if any) gets a public 'under observation' badge and counter — no PII is exposed.",
      useGps: "Use my location (notifies nearby community)",
      gpsCaptured: "Location captured. We'll notify nearby community members.",
      gpsHint: "Volunteers in this area will see this in their notifications.",
      submit: "Start 10-day observation",
      submitting: "Saving…",
      created: "Incident recorded. 10-day observation started.",
      error: "Could not save incident.",
      loginRequired: "Please log in first.",
      observation: {
        title: "Active observations",
        subtitle: "Watch for any unusual behaviour in yourself OR the cat for 10 days after a bite/scratch.",
        empty: "No active observations.",
        daysLeft: "{n} days left",
        complete: "Observation complete",
        scratchOn: "Scratch",
        biteOn: "Bite",
        markHealthy: "Cat stayed healthy",
        markSymptoms: "Symptoms appeared",
        markResolved: "Treatment complete",
        statusUpdated: "Status updated.",
        statuses: {
          observing: "Observing",
          cat_healthy: "Cat stayed healthy ✓",
          symptoms_appeared: "Symptoms appeared",
          incident_developed: "Complication developed",
          incident_resolved: "Treatment complete ✓",
        },
        bannerObserving: "{n} active observation",
        bannerObservingPlural: "{n} active observations",
        notified: "neighbours notified",
        feedbackList: "Community feedback",
      },
      catBadge: {
        observing: "Under observation",
        sub: "{n} community member{plural} watching — {days}d left",
      },
      feedbackPage: {
        eyebrow: "Community feedback",
        notFound: "Incident not found.",
        intro: "A nearby community member reported this scratch/bite. If you've seen the cat or know something useful, share a quick observation — it helps the reporter complete the 10-day observation safely.",
        communityReplies: "community replies",
        observationLabel: "What did you observe?",
        options: {
          cat_seen_healthy: "I saw the cat — it looks healthy",
          cat_not_seen: "I haven't seen the cat",
          cat_seen_concerning: "I saw it — something looked off",
          other: "Other observation",
        },
        notePlaceholder: "Anything else? Time, location, what the cat was doing…",
        photo: "Photo of the cat (optional)",
        submit: "Send feedback",
        thanks: "Thanks for helping the community!",
        thanksSub: "The reporter has been notified of your observation.",
        othersTitle: "Other observations",
      },
    },
    observationHouse: {
      overline: "Community Safety",
      title: "Observation House",
      subtitle: "Every cat currently in a 10-day observation window after a reported scratch or bite. Help your neighbours by leaving a quick sighting.",
      allTypes: "All",
      neighborhoodPlaceholder: "Filter by neighborhood…",
      urgentOnly: "Urgent only (≤3 days)",
      nearbyOn: "Near me",
      nearbyOff: "Nearby on",
      results: "results",
      empty: "No active observations right now. 🐾",
      unknownCat: "Unknown cat",
      locationUnknown: "Location not shared",
      loadFailed: "Could not load observations.",
      gpsUnavailable: "Geolocation not supported.",
      gpsActive: "Filtering to 5 km around you.",
      gpsFailed: "Could not get location.",
    },
    weekly: {
      eyebrow: "Email Digest",
      title: "Weekly Observation Summary",
      subtitle: "Every Monday at 09:00 — a personal digest of this week's incidents and observations in your coverage area.",
      on: "On",
      off: "Off",
      optedIn: "Weekly summary turned on.",
      optedOut: "Weekly summary turned off.",
      noScope: "Set a coverage area on your profile first so we can tailor the digest to your neighbourhood.",
      statNew: "New incidents",
      statActive: "Active",
      statUrgent: "Urgent",
      statFeedbacks: "Feedbacks",
      highlightsTitle: "Top active cases this week",
      sendTest: "Send test now",
      sending: "Sending…",
      scheduleHint: "Auto-sent every Monday 09:00 (when enabled).",
      testSentEmail: "Test sent! Check your inbox.",
      testSentInAppOnly: "In-app notification sent. Email is disabled on this server.",
      error: "Could not send the digest.",
    },
    map: {
      title: "Animal Map",
      subtitle:
        "Every pin is a known cat, emergency or feeding station around the city.",
      layerCats: "Cats",
      layerSightings: "Emergencies",
      layerFeeding: "Feeding spots",
      layerObservations: "Observation House",
      layerVaccinated: "Vaccinated",
      layerCommunity: "Community",
      tabMap: "Map",
      tabNeighborhood: "My neighborhood",
      locate: "Locate",
    },
    sightings: {
      title: "Emergency Cases",
      subtitle: "Community-reported urgent cases of animals in need across your city.",
      report: "Report an emergency",
      notes: "What's happening?",
      reporter: "Reporter",
      when: "When",
      submit: "Submit emergency report",
      success: "Thanks for reporting — volunteers near you have been notified.",
      pendingReview: "Thanks — your emergency is under admin review and will go public after approval.",
      empty: "No emergency cases reported yet.",
      locationOnly: "Or just drop a quick location pin",
      photoAndLocation: "Add a photo to help responders identify the cat",
    },
    feeding: {
      title: "Feeding Spots",
      subtitle: "Where volunteers feed cats around the city.",
      add: "Add feeding spot",
      name: "Spot name",
      schedule: "Schedule (e.g. Mon/Wed/Fri 19:00)",
      notes: "Notes",
      empty: "No feeding spots yet.",
      success: "Feeding spot added",
      active: "Active",
      inactive: "Inactive",
      statusLabel: "Status",
      photo: "Photo of the spot",
      findNearest: "Find nearest",
      nearestFound: "Nearest spot is",
      nearestBadge: "Nearest to you",
      noNearest: "No feeding spots found.",
      geoUnsupported: "Geolocation is not supported by your browser.",
      geoDenied: "Please allow location to find the nearest spot.",
      openDirections: "Open in maps",
      maintainedBy: "Maintained by",
      requireNameLoc: "Name and GPS coordinates are required",
    },
    dashboard: {
      title: "Dashboard",
      ilkKediBaslik: "Add your first cat",
      ilkKediAlt: "The street cats you photograph help other people find them, feed them and look after them.",
      recentCats: "Recently added",
      welcome: "Welcome back",
      myRole: "Your role",
      quickAdd: "Quick add",
      addCat: "Add a cat",
      identify: "AI identify",
      reportSighting: "Report emergency",
      addFeeding: "Add feeding spot",
    },
    profile: {
      title: "My profile",
      settingsTitle: "Settings & help",
      settingsSubtitle: "Manage your account, notifications, language and privacy. Everything in one place.",
      aiPortraitBannerTitle: "AI Profile Portraits",
      aiPortraitBannerRemaining: "Credits left this month",
      aiPortraitBannerUnlimited: "Unlimited",
      aiPortraitBannerCta: "Generate a portrait",
      aiPortraitBannerUsed: "{used} of {limit} used",
      aiPortraitBannerExhausted: "You've used all credits — resets next month.",
      aiCoverageTitle: "Community AI portrait coverage",
      aiCoverageSubtitle: "{with} of {total} cats have an AI portrait",
      aiCoverageEmpty: "Be the first to add an AI portrait!",
      myAccount: "My account",
      myAccountHint: "Dashboard, my cats, contributions",
      notificationSettings: "Notification settings",
      notificationSettingsHint: "What to receive, how and when",
      countryTitle: "Country",
      countryHint: "Which community you belong to",
      languageTitle: "Language",
      languageHint: "Choose your interface language",
      privacyKvkk: "Privacy & data protection",
      privacyKvkkHint: "Data protection rights & cookies",
      terms: "Terms of use",
      termsHint: "Rules for using the app",
      contact: "Contact us",
      contactHint: "destek@sokakkedileri.tr",
      helpFaq: "Help & FAQ",
      helpFaqHint: "Getting started, common questions, contact",
      safetyHint: "Street cat safety guides",
      points: "Paw Points",
      badgesEarned: "earned badges",
      seeAllBadges: "See all badges",
      nextBadge: "Next badge",
      nextLevel: "Next level",
      nextLevelIn: "To next level",
      contributions: "contributions",
      notificationsTitle: "Notifications",
      notificationsHint: "Latest updates and replies",
      notificationsEmpty: "No notifications yet",
      seeAllNotifications: "See all notifications",
      settingsGroup: "Settings",
      settingsGroupHint: "Language, theme, permissions",
      helpInfoGroup: "Help & info",
      helpInfoHint: "How it works, FAQ, about, privacy",
      howItWorks: "How it works?",
      howItWorksHint: "Discover features in 3 steps",
      aboutUs: "About us",
      aboutUsHint: "Our mission, team and contact",
      themeTitle: "Theme",
      themeHint: "Light or dark interface",
      themeLight: "Light",
      themeDark: "Dark",
      locationPermTitle: "Location permission",
      locationPermHint: "Used to suggest cats near you",
      notifPermTitle: "Notification permission",
      notifPermHint: "Receive urgent emergency alerts",
      permRequest: "Allow",
      permGranted: "Allowed",
      permDenied: "Blocked",
      pointsShort: "🐾",
      catsDescribed: "Cats described",
      sightingsReported: "Emergencies reported",
      feedingSpots: "Feeding spots",
      vaccinations: "Vaccinations",
      // TUR-10: eskiden bu da `howItWorks` idi ve ayni nesnedeki
      // yardim baglantisinin metnini eziyordu (JS ikinciyi alir).
      // Farkli kavram, farkli anahtar.
      pointsHowItWorks: "How rating works",
      ruleCat: "Describe a new cat: +5 Paw Points",
      ruleFeeding: "Add a feeding spot: +3 Paw Points",
      ruleVaccination: "Record a vaccination: +10 Paw Points",
      ruleSighting: "Report an emergency: +2 Paw Points",
      namingNote: "The first person who describes a cat earns the privilege of naming it.",
      myCats: "Cats I described",
      noCatsYet: "You haven't described any cats yet.",
      rank: "Rank",
      privilegesTitle: "Privileges unlocked at your tier",
      nextTier: "to reach",
      keepGoing: "Be the first to recognize a cat — earn 5 Paw Points and unlock your describer ribbon.",
      photoUpdated: "Photo updated",
      photoError: "Could not update photo",
    },
    common: {
      showPassword: "Show password",
      hidePassword: "Hide password",
      deletePhoto: "Delete photo",
      cancel: "Cancel",
      save: "Save",
      delete: "Delete",
      edit: "Edit",
      back: "Back",
      required: "Required",
      loading: "Loading…",
      locateMe: "Use my location",
      tryAi: "Try AI",
      describedBy: "Described by",
      optional: "optional",
    },
    leaderboard: {
      title: "Community Leaderboard",
      subtitle: "Be the first to recognize a cat. Climb the ranks. Unlock privileges.",
      contributor: "Contributor",
      badge: "Tier",
      contributions: "Contributions",
      points: "Paw Points",
      empty: "No contributors yet — be the first!",
      you: "You",
      tabVolunteers: "Volunteers",
      tabNeighborhoods: "Neighborhood AI portraits",
      hoodEmpty: "Not enough AI portraits yet — be the first neighborhood to lead!",
      hoodWithAi: "AI portraits",
      hoodCoverage: "Coverage",
      hoodTopContributors: "Top contributors this week",
    },
    messenger: {
      title: "Messages",
      empty: "No conversations yet. Message a volunteer from the directory.",
      selectThread: "Pick a conversation to start chatting.",
      placeholder: "Type a message…",
      send: "Send",
    },
    notifications: {
      title: "Notifications",
      subtitle: "Emergencies near you, claims on your reports, and new messages.",
      empty: "You're all caught up.",
      markAllRead: "Mark all read",
      markRead: "Mark read",
      open: "Open",
    },
    severity: {
      minor: "Minor",
      urgent: "Urgent",
      critical: "Critical",
      label: "Severity",
    },
    emergencyActions: {
      claim: "I'm on the way",
      claimed: "Claimed by",
      resolve: "Mark resolved",
      resolved: "Resolved",
      status: "Status",
      open: "Open",
      message: "Message",
    },
    emergencyCategories: {
      injured: "Injured cat",
      injuredDesc: "Visible wound, limping, or in pain",
      food: "Food / water needed",
      foodDesc: "Cats around with no food or water nearby",
      danger: "Hazardous area",
      dangerDesc: "Construction, traffic, weather threat",
      volunteer: "Need a volunteer",
      volunteerDesc: "Need help on the ground (transport, fostering)",
      lost: "Lost cat",
      lostDesc: "Search by photo — AI checks every registered cat",
    },
    premium: {
      // TUR-10: Profile.jsx `t("premium.active") || "Aktif"` diyordu;
      // anahtar olmayinca Ingilizce ve Endonezce arayuze TURKCE "Aktif" siziyordu.
      active: "Active",
      navLink: "Premium",
      title: "Premium membership",
      subtitle: "Unlimited AI, custom themes, verification badge & saved feeding spots — for the cats.",
      priceLine: "Monthly subscription",
      cta: "Go Premium",
      comingSoonPlay: "Coming soon on Google Play",
      comingSoonHint: "Premium membership will be available once our Android app is live on Google Play. We'll notify you here on launch day.",
      ctaActive: "You're a Premium member",
      perksHeading: "What you unlock",
      perk1: "Unlimited cat identifications (free tier: {limit}/month)",
      perk2: "Unlimited paws on cat photos",
      perk3: "Direct messaging between Premium members",
      perk4: "Priority placement on emergency reports",
      perk5: "Premium badge — your voice counts more in the community",
      perk6: "Apply for the official volunteer programme",
      perk7: "Save favourite feeding spots",
      activeUntil: "Active until",
      usedThisMonth: "AI used this month",
      remaining: "remaining",
      unlimited: "Unlimited",
      themeTitle: "Theme",
      themeWarm: "Warm Stone",
      themeMidnight: "Midnight",
      themeSage: "Sage",
      themeBosphorus: "Deep Blue",
      verificationTitle: "Verification badge",
      verificationDesc: "Premium members can apply to have their organization verified by admins.",
      verificationOrgLabel: "Organization name (municipality, association, vet clinic…)",
      verificationRequest: "Request verification",
      verificationPending: "Pending admin review",
      verificationVerified: "Verified ✓",
      verificationOrgIs: "Verified as",
      adminApprovals: "Pending verification requests",
      adminApprove: "Approve",
      adminEmpty: "No pending verification requests.",
      paymentSuccess: "Welcome to Premium!",
      paymentSuccessSub: "Thank you for supporting the street animals in your city. Your perks are unlocked.",
      paymentChecking: "Confirming your payment…",
      paymentExpired: "Your payment session expired. Please try again.",
      paymentCancelled: "Payment cancelled.",
      paymentCancelSub: "No charge was made. You can return to upgrade any time.",
      backHome: "Back to dashboard",
      tryAgain: "Try again",
      checkoutError: "Could not start checkout. Please try again.",
      quotaReachedTitle: "Free monthly AI limit reached",
      quotaReachedSub: "Upgrade to Premium for unlimited identifications, or wait until next month.",
      seePricing: "See Premium",
      favoriteAdd: "Save spot",
      favoriteRemove: "Saved",
      favoritePremiumOnly: "Saving feeding spots is a Premium perk.",
    },
    lounge: {
      overline: "Premium Lounge",
      title: "Premium Lounge",
      subtitle: "Connect with the other premium supporters keeping your city's animals safe. Send a direct message to anyone here.",
      memberCount: "premium members",
      empty: "Be the first member of the Premium Lounge!",
      message: "Message",
      youAreHere: "This is you",
      vet: "Vet",
      gatedTitle: "Premium Lounge is for Premium members",
      gatedSub: "Go Premium to join the lounge and chat directly with verified volunteers and vets.",
    },
    city: {
      generating: "Painting your city…",
      aiGenerated: "AI scene",
    },
    coverage: {
      title: "Coverage areas",
      heading: "Where you take care",
      subtitle: "Pick the districts you live in or feel responsible for. We'll prioritise emergencies and feeding spots in those areas.",
      empty: "You haven't picked any districts yet. Add a city below to start.",
      addCity: "Add a city",
      removeCity: "Remove city",
      districtsCount: "districts selected",
      save: "Save coverage",
      saved: "Coverage saved",
    },
    trending: {
      overline: "Hall of paws",
      title: "Cats of the week",
      subtitle: "The most visited stray cats right now. Every 25 visits earns +5 Paw Points to the person who first described the cat.",
      week: "Week",
      month: "Month",
      all: "All time",
      seeAll: "See all cats",
    },
    badges: {
      overline: "Collection",
      title: "Paw Badges",
      subtitle: "Paw-themed collectible badges. Earn them by helping cats. Tap any badge to learn how to unlock it.",
      collected: "Collected",
      earnedOn: "Earned on",
      filterAll: "All",
      filterEarned: "Earned",
      filterLocked: "Locked",
      emptyFilter: "No badges in this filter yet.",
      next: "Next",
      allEarned: "All badges earned!",
      celebrationTitle: "New badge earned",
      howToTitle: "How to earn",
      howToFallback: "Keep being active in the community to unlock this badge.",
      pageOverline: "Collection",
      pageTitle: "Paw Badges",
      pageSubtitle: "Tap any badge to see how to unlock it. Earn badges by helping cats, photographing them, and lifting up the community.",
      shareBtn: "Share",
      shareText: "I just earned the {badge} paw badge on Pati! 🐾",
      remainingMetric: {
        cats: { one: "more cat", many: "more cats" },
        feeding: { one: "more spot", many: "more spots" },
        vax: { one: "more vaccination", many: "more vaccinations" },
        sightings: { one: "more emergency", many: "more emergencies" },
        claimed: { one: "more claim", many: "more claims" },
        messages: { one: "more message", many: "more messages" },
        districts: { one: "more district", many: "more districts" },
        cities: { one: "more city", many: "more cities" },
      },
      category: {
        describer: "Describer",
        feeding: "Feeding",
        health: "Health",
        emergency: "Emergency",
        fame: "Fame",
        coverage: "Coverage",
        social: "Social",
        engagement: "Engagement",
        special: "Special",
        paw: "Paw",
      },
    },
    donate: {
      navLink: "Donate",
      overline: "Live Transparency",
      title: "Help us heal them",
      subtitle: "Every kuruş is traceable. See exactly how donations turn into vet visits, surgeries and second chances for Istanbul's stray cats.",
      transparencyTag: "100% open ledger — every treatment logged by verified vets",
      totalRaised: "Total raised",
      totalSpent: "Spent on care",
      balance: "Available now",
      catsHelped: "Cats helped",
      donors: "donors",
      treatments: "treatments",
      availableForCats: "Ready for the next cat in need",
      uniqueCats: "Unique cats treated",
      makeDonation: "Make a donation",
      everyKurusCounts: "Every kuruş finds a cat.",
      amount: "Amount",
      yourName: "Your name (shown publicly)",
      email: "Email",
      note: "Note",
      donateNow: "Donate",
      securedBy: "Secured by iyzico · We never store your card.",
      iyzicoPending: "Donations are being recorded but card payments are paused while our merchant account is being verified by iyzico. Your details are saved and we will reach out shortly.",
      pendingMerchant: "Donation noted — we'll confirm shortly.",
      thankYou: "Thank you 🐾",
      error: "Could not record donation. Please try again.",
      invalidAmount: "Enter a valid amount",
      treatmentLedger: "Live treatment ledger",
      treatmentLedgerDesc: "Only admin-verified vets can add records. Every cost is itemised next to the cat it helped.",
      noTreatmentsYet: "No treatments recorded yet — be the first to help.",
      spent: "Spent",
      more: "more entries",
      recentDonors: "Recent donations",
      noDonorsYet: "Your donation could be the first.",
      severity: {
        mild: "Mild",
        moderate: "Moderate",
        severe: "Severe",
        critical: "Critical",
      },
      tStatus: {
        planned: "Planned",
        in_progress: "In progress",
        completed: "Completed",
      },
    },
    treatments: {
      title: "Treatment ledger",
      subtitle: "Every visit, every kuruş — added by admin-verified vets only.",
      totalSpent: "Total spent on this cat",
      records: "records",
      noneYet: "No treatments recorded yet.",
      add: "Add treatment",
      added: "Treatment saved",
      condition: "Condition / illness",
      details: "Treatment details (medications, dosages, follow-up…)",
      detailsPlaceholder: "Describe the diagnosis and what was done — be as specific as you can.",
      cost: "Cost",
      date: "Date",
      severityLabel: "Severity",
      statusLabel: "Status",
      clinicName: "Clinic / municipality (optional)",
      save: "Save record",
    },
    apply: {
      overline: "Volunteer Programme",
      title: "Become a volunteer",
      subtitle: "Tell us why you want to care for the street animals in your city. An AI screen runs first to filter spam, then a human admin reviews your application.",
      cardTitle: "Become a volunteer",
      cardSubtitle: "Unlock cat care tools · 2-min application + admin review",
      motivationLabel: "Why do you want to volunteer?",
      motivationPlaceholder: "Describe your experience with stray cats, your neighborhood, and what you'd like to do as a volunteer…",
      minChars: "Min 20 characters",
      aiNotice: "An AI screener will scan your application for spam/troll signals. The final decision is made by a human admin.",
      submitBtn: "Submit application",
      submitted: "Application submitted — admins notified",
      error: "Something went wrong",
      tooShort: "Please write at least 20 characters",
      backToProfile: "Back to profile",
      aiScore: "AI score",
      statusPending: "Your application is under review",
      statusPendingHint: "An admin will review your application soon. You'll get a notification when it's decided.",
      statusRejected: "Application not approved",
    },
    admin: {
      overline: "Admin",
      title: "Volunteer applications",
      subtitle: "AI pre-screens each application. Your job: a final human check before granting volunteer access.",
      forbiddenTitle: "Admin access only",
      forbiddenSubtitle: "You don't have permission to view this page.",
      aiSummary: "AI assessment",
      empty: "No applications in this filter.",
      approveBtn: "Approve",
      rejectBtn: "Reject",
      approvedToast: "Application approved — volunteer access granted.",
      rejectedToast: "Application rejected.",
      bulkRejectTitle: "AI flagged {count} applications as high risk",
      bulkRejectSubtitle: "Reject all of them with one click — applicants will be notified.",
      bulkRejectBtn: "Bulk reject red",
      bulkRejectConfirm: "Reject {count} red-flagged applications? This cannot be auto-undone.",
      bulkRejectedToast: "Bulk-rejected {count} applications.",
      notePlaceholder: "Optional note for the applicant (visible to them)…",
      adminNoteLabel: "Admin note",
      tab: {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
      },
      stats: {
        daysOld: "days old",
        cats: "cats",
        sightings: "sightings",
        feeding: "feeding",
      },
    },
    adminEmerg: {
      overline: "Emergency moderation",
      title: "Emergency review queue",
      subtitle: "AI screens photos and notes. Untrusted reporters route here for a final human check before going public.",
      gotoApplications: "Go to volunteer applications",
      empty: "No emergencies in this filter.",
      approveBtn: "Publish",
      rejectBtn: "Reject",
      approvedToast: "Emergency published.",
      rejectedToast: "Emergency rejected.",
      tab: {
        pending: "Pending",
        approved: "Published",
        rejected: "Rejected",
      },
      photoVerdict: "Photo AI",
      textVerdict: "Text AI",
      notACat: "Not a cat",
      aiGenerated: "AI-generated",
      photoshopped: "Photoshopped",
      photoClean: "Photo looks clean",
      confidence: "Confidence",
      redWarning: "AI flagged this as high risk — review carefully before publishing.",
    },
    photos: {
      archive: "Photo archive",
      galleryTitle: "Photo archive",
      gallerySubtitle: "Every photo of this cat the community has shared. Tap a paw to like.",
      addPhoto: "Add photo",
      uploaded: "Photo uploaded",
      uploadFailed: "Upload failed",
      emptyGallery: "No photos yet — be the first to add one!",
      loginRequired: "Sign in to upload a photo",
      loginToLike: "Sign in to give a paw",
      deleteConfirm: "Delete this photo?",
      deleted: "Photo removed",
      monthlyQuotaUsed: "{used}/{limit} paws used this month",
      unlimitedQuota: "Unlimited paws (Premium)",
      upgradeForUnlimited: "Go Premium for unlimited",
      aiNotice: "Photos are automatically screened for AI-generated, photoshopped, or inappropriate content.",
    },
    photoContest: {
      overline: "Monthly photo contest",
      title: "Photo of the Month",
      subtitle: "Every month the top 3 most-pawed photos win a Photographer of the Month badge. Tap any photo to visit its cat.",
      empty: "No photos with likes this month yet — be the first to put a cat on the podium.",
      by: "by",
      honorableMentions: "Honorable mentions (4-10)",
    },
  },
  id: {
    kediBildir: {
      baslik: "Laporkan kucing",
      altBaslik: "Kami di sini untuk kucing di lingkunganmu. Apa yang ingin kamu lakukan?",
      kaydetBaslik: "Daftarkan kucing",
      kaydetAciklama: "Tambahkan kucing dari lingkunganmu ke sistem",
      gordumBaslik: "Saya melihat kucing",
      gordumAciklama: "Laporkan bahwa kamu melihat kucing yang sudah terdaftar",
      acilBaslik: "Laporkan darurat",
      acilAciklama: "Minta bantuan untuk kucing yang terluka, sakit, atau dalam bahaya",
      mamaSuBaslik: "Tambah titik makan/minum",
      mamaSuAciklama: "Bagikan lokasi titik makanan atau air",
      dipnot: "Setiap laporan sampai ke relawan di lingkunganmu.",
    },
    indir: {
      baslik: "Unduh aplikasi",
      altBaslik: "Pasang StrayPaw di ponselmu — lihat hewan di sekitarmu, laporkan hewan hilang, tanggapi panggilan bantuan.",
      appStore: "Unduh di App Store",
      playStore: "Dapatkan di Google Play",
      yonlendiriliyor: "Mengarahkan ke toko aplikasi…",
      qr: "Pindai dengan kamera ponselmu",
      yakinda: "Aplikasi akan segera hadir di toko aplikasi.",
      androidYakinda: "Versi Android segera hadir.",
      zatenVar: "Aplikasi sudah ada di ponselmu.",
      anaSayfa: "Kembali ke beranda",
    },
    terms: {
      overline: "Legal",
      title: "Ketentuan Penggunaan",
      updated: "Terakhir diperbarui: 18 Februari 2026",
      s1t: "1. Cakupan layanan",
      s1a: "StrayPaw adalah aplikasi komunitas untuk berbagi pengamatan, foto, pesan, dan koordinasi relawan tentang hewan jalanan. Dengan membuat akun, kamu setuju menggunakannya sesuai ketentuan ini.",
      s2t: "2. Konten buatan pengguna",
      s2a: "Kamu sepenuhnya bertanggung jawab atas semua yang kamu unggah — termasuk foto, pesan, komentar, dan laporan. Kamu menyatakan bahwa konten yang kamu unggah sah dan tidak melanggar hak pihak lain.",
      s2zero: "Nol toleransi",
      s2b: ": penghinaan, pelecehan, ujaran kebencian, konten seksual, laporan palsu, penipuan, dan konten melanggar hukum sangat dilarang. ",
      s2c: "Lihat ",
      s2d: " untuk detailnya.",
      s3t: "3. Moderasi, pelaporan, dan pemblokiran",
      s3a: "Setiap tempat yang memuat konten pengguna punya tombol “Laporkan” yang terlihat. Konten yang dilaporkan ditinjau oleh moderator manusia dalam 24 jam; jika melanggar, konten dihapus dan akun bisa ditangguhkan. Kamu bisa memblokir pengguna lain dari profilnya atau dari jendela pesan.",
      s4t: "4. Keamanan akun",
      s4a: "Menjaga kerahasiaan kata sandimu adalah tanggung jawabmu. Kalau kamu melihat orang lain memakai akunmu, segera hubungi dukungan.",
      s5t: "5. Pemrosesan data",
      s5a: "Cara data pribadimu diproses dijelaskan dalam ",
      s5b: ".",
      s6t: "6. Penangguhan dan penutupan akun",
      s6a: "Akun yang melanggar ketentuan ini dapat ditangguhkan atau ditutup permanen tanpa peringatan. Kamu juga bisa menutup akunmu kapan saja.",
      s7t: "7. Penafian",
      s7a: "Aplikasi disediakan “apa adanya”. Jangan menjadikannya pengganti saran dokter hewan profesional. Dalam keadaan darurat, hubungi layanan darurat setempat atau dokter hewan.",
      s8t: "8. Perubahan",
      s8a: "Ketentuan ini dapat diperbarui dari waktu ke waktu. Versi terbaru selalu ditayangkan di halaman ini.",
      linkCommunity: "Aturan Komunitas",
      linkPrivacy: "Kebijakan Privasi",
    },
    communityRules: {
      back: "Masuk",
      overline: "Komunitas",
      title: "Aturan Komunitas",
      intro: "StrayPaw harus menjadi tempat yang aman dan saling menghormati. Mohon ikuti aturan berikut.",
      r1: "Pelecehan, ancaman, ujaran kebencian, dan laporan yang menyesatkan dilarang.",
      r2: "Konten yang tidak pantas bisa dilaporkan.",
      r3: "Pengguna bisa memblokir siapa pun yang berlaku kasar.",
      r4: "Konten yang dilaporkan ditinjau dan dihapus bila perlu.",
      r5: "Dalam keadaan darurat, hubungi pihak berwenang setempat atau dokter hewan.",
      footer: "Pelaporan dan pemblokiran: tersedia dari layar pesan atau dari profil pengguna.",
    },
    aboutPage: {
      overline: "Tentang",
      title: "StrayPaw",
      lede: "Aplikasi yang mempertemukan orang-orang yang mengenali, mengikuti, dan — saat dibutuhkan — menjangkau hewan jalanan di lingkungan mereka.",
      whyT: "Kenapa ada",
      why1: "Kamu memberi makan seekor hewan di jalan. Minggu berikutnya kamu tidak tahu apakah itu hewan yang sama. Kamu melihat hewan terluka dan tidak tahu apakah ada yang pernah melihatnya. Sudah disterilkan? Sudah divaksin? Tidak ada yang tahu.",
      why2: "Orang yang peduli tidak sedikit. Yang kurang adalah kepastian bahwa kita sedang membicarakan hewan yang sama.",
      whatT: "Apa yang kami lakukan",
      what1a: "Saat kamu memotret seekor hewan, aplikasi mencarinya di basis data. Bukan jenis hewannya — melainkan ",
      what1s: "hewan yang mana",
      what1b: ". Takik di telinga, bekas di atas mata, noda di dagu: itulah yang membedakan satu hewan dari yang lain, bukan warnanya.",
      what2a: "Ini masalah yang sulit dan kami tidak selalu berhasil. Kalau kami tidak yakin, kami mengatakannya. Kami menjelaskannya secara terbuka di halaman ",
      what2l: "cara kerjanya",
      what2b: ".",
      whoT: "Siapa yang membuat",
      who: "Tim kecil dan independen. Kami bukan pemerintah daerah, yayasan, atau perusahaan, dan tidak punya kemitraan resmi dengan siapa pun. Aplikasi ini ditulis di Istanbul dan diuji di jalanan Istanbul.",
      moneyT: "Biaya",
      money: "Fungsi inti aplikasi gratis: mendaftarkan hewan, mencari, melaporkan keadaan darurat, berkirim pesan. Premium untuk fitur tambahan seperti potret AI, dan tidak pernah diperlukan untuk menolong seekor hewan.",
      wrongT: "Melihat sesuatu yang keliru?",
      wrong: "Kecocokan yang salah, informasi keliru, konten tidak pantas — laporkan semuanya kepada kami. Mengetahui ada kesalahan lebih baik daripada mengira tidak ada.",
      linkHelp: "Bantuan",
      linkCommunity: "Aturan Komunitas",
      linkPrivacy: "Privasi",
      linkTerms: "Ketentuan Penggunaan",
    },
    howItWorks: {
      overline: "Panduan",
      title: "Cara kerjanya",
      stepLabel: "Langkah",
      lede1: "Kebanyakan aplikasi memberitahumu ",
      ledeEm: "jenis apa",
      lede2: " hewan yang kamu lihat: belang, hitam, muda. Kami berusaha menemukan ",
      ledeStrong: "hewan yang mana",
      lede3: " itu. Bedanya sama seperti mengenali hewan yang kamu beri makan versus mendaftar seribu hewan yang mirip dengannya.",
      s1t: "Ambil fotonya",
      s1a: "Potret hewan itu di tempat kamu menemukannya. Aplikasi memotret sendiri begitu ada hewan yang benar-benar masuk bingkai — ia meminta tiga sudut: depan, samping, atas.",
      s1b: "Tiga sudut bukan iseng: hal-hal yang membedakan seekor hewan — takik di telinga, bekas di atas mata, noda di dagu — sering tidak terlihat dari satu bingkai saja.",
      s2t: "Basis data dicari otomatis",
      s2a1: "Pencarian ",
      s2aStrong: "dimulai sendiri",
      s2a2: " begitu foto pertama diambil. Kamu tidak perlu menekan apa pun, karena kamu memang tidak akan menekannya — dan aplikasi akan penuh dengan enam profil untuk hewan yang sama.",
      s2b1: "Sistem membandingkan ciri tertulis dulu, lalu ",
      s2bStrong: "benar-benar melihat",
      s2b2: " foto kandidat terkuat. Yang memutuskan adalah foto, bukan kata-kata.",
      s3t: "Kalau tidak yakin, kami tidak mencocokkan",
      s3a: "Terlewat hanyalah penundaan: relawan akan melihat lagi besok. Salah mencocokkan adalah kebohongan — ia menggabungkan dua hewan ke satu profil dan membuat orang berhenti mencari hewan yang masih ada di jalan.",
      s3b1: "Karena itu, kalau sistem ragu, ia berkata ",
      s3bStrong: "“saya tidak yakin”",
      s3b2: ". Keputusan akhir ada padamu.",
      s4t: "Hewan masuk ke peta",
      s4a: "Lokasi, catatan pemberian makan, vaksinasi, sterilisasi, kondisi kesehatan — semuanya terkumpul di profil hewan itu. Lama-lama profil itu menjadi kisah seekor hewan: siapa yang memberinya makan, siapa yang membawanya ke dokter hewan, kapan ia hilang.",
      s5t: "Lingkungan sekitar melihatnya",
      s5a: "Saat kamu melaporkan keadaan darurat, laporan itu sampai ke relawan yang mencakup lingkungan tersebut. Orang yang mengenal hewan itu adalah orang yang paling dekat dengannya.",
      boxT: "Kenapa kualitas foto penting",
      boxA: "Bingkai yang buram, terlalu jauh, atau gelap menyembunyikan tanda-tanda yang membedakan seekor hewan. Sistem lalu terlewat atau ragu. Foto yang baik bukan sekadar profil yang bagus — melainkan kecocokan yang benar.",
      ctaScan: "Kenali hewan",
      ctaFaq: "Pertanyaan umum",
    },
    helpPage: {
      overline: "Dukungan",
      title: "Bantuan",
      lede: "Pertanyaan yang sering diajukan. Kalau yang kamu cari tidak ada di sini, tulis kepada kami — dibaca oleh manusia sungguhan.",
      q1: "Kenapa aplikasi kadang tidak mengenali hewan yang sama?",
      a1: "Yang membedakan seekor hewan adalah tandanya: takik di telinga, bekas di atas mata, noda di dagu. Kalau fotonya buram, terlalu jauh, atau gelap, tanda-tanda itu tidak terlihat dan sistem tidak bisa yakin.\n\nKalau tidak yakin, kami memilih untuk tidak mencocokkan. Kecocokan yang salah menggabungkan dua hewan ke satu profil dan membuat orang berhenti mencari hewan yang masih ada di jalan. Terlewat hanyalah penundaan.",
      q2: "Saya tidak sengaja mendaftarkan hewan yang sama dua kali. Bagaimana?",
      a2: "Wajar, tidak apa-apa. Buka profil hewan itu, tandai yang benar, lalu laporkan catatan yang keliru kepada kami. Seiring basis data bertambah, sistem akan menangkap duplikat ini sendiri — setiap koreksi melatihnya.",
      q3: "Saya sudah memotret tapi pencarian tidak dimulai.",
      a3: "Pencarian dimulai sendiri tepat setelah foto pertama; kamu tidak perlu menekan apa pun. Kalau tidak dimulai, kemungkinan besar koneksi terputus. Tutup aplikasi, buka lagi, dan potret ulang.",
      q4: "Apakah lokasi saya dibagikan?",
      a4: "Yang dibagikan adalah lokasi hewan, bukan lokasimu. Tempat hewan itu terlihat tersimpan di peta — hanya itu yang dibutuhkan relawan untuk menemukannya. Lihat Kebijakan Privasi untuk detailnya.",
      q5: "Saya melaporkan keadaan darurat. Siapa yang melihatnya?",
      a5: "Laporan itu sampai ke relawan yang mencakup lingkungan tersebut. Kamu bisa memilih area cakupanmu di Profil → Edit. Orang yang mengenal hewan itu adalah orang yang paling dekat dengannya.",
      q6: "Bagaimana cara menghapus akun saya?",
      a6: "Di bagian paling bawah halaman profil ada “Hapus akun saya”. Setelah permintaanmu, akun dihapus dalam 30 hari; kamu bisa membatalkannya dalam periode itu kalau berubah pikiran.",
      q7: "Saya melihat konten atau pengguna yang tidak pantas.",
      a7: "Setiap profil dan setiap konten punya “Laporkan” dan “Blokir”. Laporan ditinjau dalam 24 jam. Aturan Komunitas memuat hal-hal yang kami beri nol toleransi.",
      q8: "Saya tidak menemukan hewannya; aplikasinya lambat.",
      a8: "Foto berukuran besar lebih lama diunggah. Aplikasi kini memperkecil foto sebelum mengirimnya. Kalau masih lambat, tulis kepada kami — ponsel apa, layar mana, sedang melakukan apa. Tanpa itu kami tidak bisa menemukan masalahnya.",
      contactT: "Hubungi kami",
      contactA: "Laporan bug, saran, keluhan — semuanya masuk ke alamat yang sama.",
      linkHow: "Cara kerjanya",
      linkCommunity: "Aturan Komunitas",
      linkPrivacy: "Privasi",
      linkTerms: "Ketentuan Penggunaan",
    },
    privacyPage: {
      back: "Beranda",
      overline: "Legal",
      title: "Kebijakan Privasi",
      updated: "Terakhir diperbarui: September 2026",
      intro1: "StrayPaw (",
      intro2: ") adalah platform komunitas relawan yang dibuat untuk membantu orang menjaga hewan jalanan di lingkungan mereka. Halaman ini menjelaskan dengan bahasa sederhana data apa yang dikumpulkan saat kamu memakai layanan kami, kenapa diproses, dan hak apa yang kamu miliki.",
      s1t: "1. Data yang kami kumpulkan",
      s1i1L: "Data akun:",
      s1i1: " alamat email, nama, kata sandi (disimpan hanya sebagai hash yang aman).",
      s1i2L: "Data profil:",
      s1i2: " foto profil, preferensi distrik dan wilayah, lencana yang diperoleh, Poin Cakar.",
      s1i3L: "Lokasi:",
      s1i3: " hanya pada saat kamu mengizinkannya — dipakai untuk melaporkan hewan, keadaan darurat, melihat peta, dan tombol “Cari”. Lokasi tidak pernah dilacak di latar belakang.",
      s1i4L: "Unggahan foto:",
      s1i4: " gambar hewan yang kamu tambahkan, lingkunganmu, atau avatar profilmu. Dikompresi di perangkatmu sebelum dikirim.",
      s1i5L: "Isi pesan:",
      s1i5: " percakapanmu dengan relawan dan pengguna lain. Hanya terlihat oleh pihak yang berkomunikasi dan, bila perlu, tim moderasi.",
      s1i6L: "Data penggunaan:",
      s1i6: " informasi sesi, halaman yang kamu buka, catatan galat, dan jenis perangkat (statistik teknis anonim).",
      s1i7L: "Pengukuran iklan:",
      s1i7: " Untuk mengetahui apakah kamu memasang aplikasi ini melalui promosi Meta (Facebook/Instagram), peristiwa pemasangan dan pembukaan aplikasi dikirim ke Meta. Di iOS, jika kamu mengizinkan pelacakan, pengenal iklan perangkatmu ikut disertakan; jika kamu menolak, tidak ada pengenal yang dikirim dan pengukuran hanya dilakukan secara agregat tanpa kaitan denganmu. Kamu dapat menarik izin kapan saja melalui Pengaturan \u2192 Privasi & Keamanan \u2192 Pelacakan.",
      s2t: "2. Untuk apa data ini dipakai",
      s2i1: "Menampilkan hewan jalanan di peta, mengikutinya, dan menyimpan riwayat kesehatannya.",
      s2i2: "Memberi tahu relawan terdekat tentang keadaan darurat (hewan terluka atau sakit).",
      s2i3: "Menyampaikan pengumuman hewan hilang ke pengguna di wilayah terkait.",
      s2i4: "Umpan lingkungan sekitar, komentar, suka, dan komunikasi antar relawan.",
      s2i5: "Keamanan akun, pencegahan penipuan, dan moderasi penyalahgunaan.",
      s2i6: "Pemantauan performa dan galat secara anonim agar aplikasi bekerja lebih baik.",
      s3t: "3. Dengan siapa kami membagikannya",
      s3aA: "Data pribadimu ",
      s3aStrong: "tidak pernah dijual ke pihak ketiga",
      s3aB: " Data terbatas yang dibagikan dengan Meta untuk pengukuran iklan dijelaskan secara eksplisit di bawah ini. Selain itu, pemrosesan hanya dilakukan dalam kondisi berikut:",
      s3i1: "Penyedia infrastruktur yang diperlukan agar layanan berjalan (server, basis data, email) — hanya data minimum yang dibutuhkan.",
      s3i2: "Sebagai tanggapan atas permintaan resmi yang sah dari otoritas berwenang.",
      s3i3: "Kecuali kamu mengizinkannya sendiri lewat pesan, tidak ada pengguna yang bisa melihatmu secara pribadi (nomor telepon, email, dan sejenisnya tidak ditampilkan di profilmu).",
      s3i4: "Pengukuran iklan \u2014 Meta Platforms Ireland Ltd.: hanya peristiwa pemasangan dan pembukaan aplikasi, ditambah pengenal iklan perangkat jika kamu mengizinkan pelacakan. Nama, email, lokasi, foto, dan pesanmu tidak dibagikan dengan Meta.",
      s4t: "4. Tentang foto",
      s4a: "Foto hewan yang kamu unggah menjadi terlihat oleh pengguna lain agar komunitas bisa mengenali hewan itu. Analisis AI (ras, penilaian kesehatan) hanya berjalan saat pengunggahan dan tidak disimpan sebagai data biometrik terpisah. Kamu bisa menghapus foto yang kamu unggah kapan saja.",
      s5t: "5. Privasi pesan",
      s5a: "Pesan pribadimu hanya terlihat olehmu dan orang yang kamu ajak bicara. Isinya disimpan di server kami tetapi tidak dibagikan ke pihak ketiga. Kalau sebuah percakapan dilaporkan, tim moderasi hanya bisa meninjau percakapan itu. Kamu bisa memblokir atau melaporkan pengguna, atau menghapus riwayat pesanmu, kapan saja.",
      s6t: "6. Hak-hakmu",
      s6a: "Berdasarkan KVKK Türkiye dan undang-undang perlindungan data yang sebanding, kamu punya hak berikut:",
      s6i1: "Mengetahui data apa saja yang diproses.",
      s6i2: "Meminta perbaikan informasi yang salah atau tidak lengkap.",
      s6i3L: "Meminta penghapusan akun atau data tertentu.",
      s6i3: " Permintaanmu dijalankan dalam 30 hari.",
      s6i4: "Menolak pemrosesan datamu atau meminta pembatasannya.",
      s6i5: "Menerima datamu dalam format yang dapat dipindahkan (JSON).",
      s6b: "Untuk memakai hak-hak ini, cukup tulis kepada kami di alamat di bawah.",
      s7t: "7. Keamanan data",
      s7a: "Kata sandi di-hash dengan bcrypt, sesi ditandatangani dengan JWT, dan seluruh lalu lintas berjalan lewat HTTPS. Cadangan disimpan terenkripsi. Meski begitu, tidak ada pengiriman data di internet yang 100% aman — jadi kami menyarankan memakai kata sandi yang kuat dan tidak membagikan akunmu kepada siapa pun.",
      s8t: "8. Privasi anak",
      s8a: "Aplikasi kami tidak dirancang untuk anak di bawah 13 tahun dan kami tidak dengan sengaja mengumpulkan data dari kelompok usia itu. Kalau kami mengetahui seorang anak di bawah 13 tahun membuka akun, kami menghapusnya.",
      s9t: "9. Perubahan kebijakan ini",
      s9a: "Kami dapat memperbarui kebijakan ini dari waktu ke waktu. Perubahan penting akan diberitahukan lewat notifikasi di aplikasi atau email. Tanggal “Terakhir diperbarui” di bagian atas halaman ini selalu menunjukkan versi terkini.",
      contactT: "Kontak",
      contactA: "Untuk kebijakan privasi, permintaan penghapusan data, atau pertanyaan perlindungan data apa pun:",
      footer: "StrayPaw · Istanbul, Türkiye",
    },
    // TUR-10: `volunteers` blogu HIC yazilmamisti. Volunteers.jsx alti
    // anahtar istiyordu, t() bulamayinca anahtarin kendisini donduruyordu —
    // yani sayfanin basligi ekranda 'volunteers.title' yaziyordu, uc dilde de.
    volunteers: {
      title: "Relawan",
      subtitle: "Orang-orang yang memberi makan, merawat, dan menyelamatkan hewan di lingkunganmu. Setiap nama di sini adalah orang yang hadir.",
      becomeVolunteer: "Jadi relawan",
      individualsTitle: "Relawan dan dokter hewan",
      individualsSubtitle: "Diurutkan berdasarkan kontribusi — memberi makan, penyelamatan, vaksinasi, dan laporan.",
      noIndividuals: "Belum ada relawan. Jadilah yang pertama di lingkunganmu.",
    },
    dogUi: {
      dbTitleCat: "Basis Data Kucing",
      dbTitleDog: "Basis Data Anjing",
      dbOverlineCat: "BASIS DATA KUCING",
      dbOverlineDog: "BASIS DATA ANJING",
      dbSubCat: "Jelajahi semua kucing yang tercatat di komunitas kita.",
      dbSubDog: "Jelajahi semua anjing yang tercatat di komunitas kita.",
      dbSearchCat: "Cari berdasarkan nama, warna, ras atau lingkungan",
      dbSearchDog: "Cari berdasarkan nama, warna atau lingkungan",
      dbLostCat: "Cari kucingmu yang hilang",
      dbLostDog: "Cari anjingmu yang hilang",
      dbRegisteredCat: "Terdaftar",
      dbRegisteredDog: "Terdaftar",
      dbInjuredCat: "Terluka",
      dbInjuredDog: "Terluka",
      dbEarTagged: "Bertanda telinga",
      dbEarTaggedHint: "Sudah steril — tanda di telinga",
      dbTitleAll: "Basis Data Hewan",
      dbOverlineAll: "BASIS DATA HEWAN",
      dbSubAll: "Jelajahi semua hewan yang tercatat di komunitas kita.",
      dbSearchAll: "Cari berdasarkan nama, warna atau lingkungan",
      dbLostAll: "Cari hewanmu yang hilang",
      dbRegisteredAll: "Terdaftar",
      dbInjuredAll: "Terluka",
      lostConfirmCat: "Buat laporan kehilangan untuk {ad}? Laporan langsung tayang dan tetangga diberi tahu.",
      lostConfirmDog: "Buat laporan kehilangan untuk {ad}? Laporan langsung tayang dan tetangga diberi tahu.",
      lostOpenCat: "Laporan sudah terbuka \u2014 kami arahkan ke sana.",
      lostOpenDog: "Laporan sudah terbuka \u2014 kami arahkan ke sana.",
      deleteCat: "Hapus kucing ini?",
      deleteDog: "Hapus anjing ini?",
      shareCat: "Bagikan kucing ini",
      shareDog: "Bagikan anjing ini",
      editCat: "Edit kucing",
      editDog: "Edit anjing",
      reportCat: "Laporkan profil kucing ini",
      reportDog: "Laporkan profil anjing ini",
      noGpsCat: "GPS tidak tersedia saat kucing ini didaftarkan.",
      noGpsDog: "GPS tidak tersedia saat anjing ini didaftarkan.",
      lostCat: "Kucingku hilang \u2014 buat laporan",
      lostDog: "Anjingku hilang \u2014 buat laporan",
      newCat: "Kucing baru",
      newDog: "Anjing baru",
      saveCat: "Simpan kucing",
      saveDog: "Simpan anjing",
      saving: "Menyimpan\u2026",
      dogOptional: "Tambah detail anjing (opsional)",
      dogOptionalHint: "Tidak wajib. Dua foto, nama dan lingkungan sudah cukup.",
    },
    dog: {
      section: "Detail anjing",
      unknownHint: "Biarkan Tidak jelas jika tidak terlihat jelas",
      matchingSoon: "Pencocokan anjing belum tersedia — profil tetap tersimpan dan bisa dicari saat fitur dibuka.",
      earCarriage: "Posisi telinga",
      ear: { dik: "Tegak", yari_dik: "Setengah tegak", dusuk: "Turun", kesik: "Dipotong", belirsiz: "Tidak jelas" },
      tailType: "Ekor",
      tail: { uzun_duz: "Panjang, lurus", kivrik: "Melingkar", orak: "Sabit", kisa: "Pendek", kesik: "Dipotong", puskullu: "Berumbai", belirsiz: "Tidak jelas" },
      coatLength: "Panjang bulu",
      coatLen: { kisa: "Pendek", orta: "Sedang", uzun: "Panjang", belirsiz: "Tidak jelas" },
      coatTexture: "Tekstur bulu",
      coatTex: { duz: "Lurus", dalgali: "Bergelombang", kivircik: "Keriting", sert_tel: "Kasar", belirsiz: "Tidak jelas" },
      muzzle: "Bentuk moncong",
      muz: { basik: "Pesek", orta: "Sedang", uzun: "Panjang", belirsiz: "Tidak jelas" },
      build: "Postur tubuh",
      bod: { ince: "Ramping", orta: "Sedang", iri: "Besar", belirsiz: "Tidak jelas" },
      earTagNo: "Nomor anting",
      earTagHint: "Hanya jika anjing punya anting sterilisasi",
      registering: "Kamu mendaftarkan anjing",
      registeringCat: "Kamu mendaftarkan kucing",
      switchToCat: "Daftarkan sebagai kucing",
      switchToDog: "Daftarkan sebagai anjing",
      myLostDog: "Ini anjingku yang hilang",
    },
    appName: "StrayPaw",
    tagline: "Hewan jalanan di lingkunganmu",
    // Panodaki baslik slogani. `tagline` bundan AYRI: o sayfa basliklarinda
    // ve paylasim kartlarinda kullaniliyor, bu ise uygulama basliginin ikinci
    // satiri. Ikisini birlestirmek, panoda olmayan bir metni basliga sokardi.
    baslikSlogani: "Lingkungan lebih kuat.",
    arama: {
      rowPlace: "Kelurahan",
      otherNeighborhoods: "Kelurahan lain",
      row_kediler: "kucing",
      row_harita: "di peta",
      row_kayip: "laporan hilang",
      row_besleme: "titik makan",
      rowGo_kediler: "Lihat kucingnya",
      rowGo_harita: "Ada apa di sekitar?",
      rowGo_kayip: "Lihat laporannya",
      rowGo_besleme: "Lihat titiknya",
      unit_kediler: "hasil",
      unit_harita: "",
      unit_kayip: "laporan",
      unit_besleme: "titik",
      tab_ilanlar: "Laporan hilang",
      title: "Cari",
      placeholder: "Cari kucing, kelurahan atau kecamatan…",
      back: "Kembali",
      clear: "Hapus",
      recent: "Pencarian terakhir",
      clearHistory: "Bersihkan",
      mostCats: "Kelurahan dengan kucing terbanyak",
      hint: "Cari dengan nama kucing, kelurahan atau kecamatan untuk menemukan kucing terdaftar, laporan hilang dan semua data di sekitarmu.",
      neighborhoods: "Kelurahan",
      cats: "Kucing",
      catWord: "kucing",
      searching: "Mencari…",
      empty: "Tidak ada hasil. Coba nama kelurahan.",
      searchFor: "Cari",
      tab_hepsi: "Semua",
      tab_kediler: "Kucing",
      tab_mahalleler: "Kelurahan",
    },
    anasayfaV2: {
      odul: "hadiah",
      slogan: "Hidup yang lebih baik, untuk setiap cakar.",
      heroTitle: "Apa yang terjadi di lingkunganmu hari ini?",
      heroSub: "Jalanan yang lebih aman untuk kucing jalanan itu mungkin.",
      heroMark: "Setiap cakar berharga",
      statCats: "Kucing terdaftar",
      statEmergencies: "Kasus darurat",
      statFeeding: "Titik makan",
      statVaccinated: "Divaksin",
      locationPick: "Ubah lokasi",
      search: "Cari kucing, lingkungan atau kecamatan…",
      actionsTitle: "Mau melakukan apa?",
      actionsAside: "Langkah kecil, dampak besar",
      a1t: "Saya lihat kucing",
      a1s: "Laporkan lokasi",
      a2t: "Cari kucing hilang",
      a2s: "Cari laporan",
      aramaVeritabani: "Basis data kucing",
      a3t: "Pusat Darurat",
      a3s: "Menunggu bantuan",
      a4t: "Kenali dengan AI",
      a4s: "Bandingkan lewat foto",
      vtBaslik: "Basis Data Kucing",
      vtAlt: "Jelajahi kucing terdaftar, lihat detailnya.",
      vtAdetSonEk: "kucing",
      lostTitle: "Ada kucing hilang di dekatmu",
      casesTitle: "Kasus penting di dekatmu",
      casesSub: "Kucing yang paling butuh bantuan di lapangan",
      casesAll: "Lihat semua",
      casesEmpty: "Tidak ada kasus terbuka di dekatmu — kabar baik.",
      casesFarAway: "Tidak ada di dekatmu — catatan terbaru ditampilkan.",
      tabLost: "Kucing hilang",
      tabEmergency: "Kasus darurat",
      tabAll: "Semua",
      emergencyBadge: "DARURAT",
      chipVet: "Butuh dokter hewan",
      chipInjured: "Menunggu bantuan",
      chipFood: "Perlu makan/minum",
      chipDanger: "Bahaya dilaporkan",
      chipVolunteer: "Cari relawan",
      chipLost: "Ada yang melihat?",
      lostAll: "Semua",
      lostBadge: "HILANG",
      lostEmpty: "Tidak ada laporan hilang di dekatmu — kabar baik.",
      mapTitle: "Peta lingkungan",
      mapDesc: "Temukan kucing, kasus darurat dan titik makan di dekatmu.",
      mapOpen: "Buka peta",
      aiTitle: "AI · Pengenalan kucing",
      aiDesc: "Foto kucing yang kamu lihat, bandingkan dengan kucing terdaftar.",
      aiPhoto: "Ambil foto",
      aiGallery: "Pilih dari galeri",
      healthTitle: "Kesehatan dan dukungan kucing",
      healthDesc: "Vaksin, sterilisasi, rabies dan catatan kesehatan.",
      communityTitle: "Gabung komunitas",
      communityDesc: "Relawan, acara dan lainnya.",
      communityMark: "Bersama kita lebih kuat",
      navReport: "Lapor kucing",
      navReportShort: "Lapor",
      justNow: "baru saja",
      hoursAgo: "{n} jam lalu",
      hoursAgoLong: "{n} jam yang lalu",
      minutesAgoLong: "{n} menit yang lalu",
      pickPlaceAria: "Pilih wilayahmu",
      pickPlaceCta: "Pilih wilayahmu",
      pickPlaceTitle: "Pilih wilayahmu",
      pickPlaceSub: "Beranda dan Lingkunganku mengikuti pilihan ini.",
      pickPlaceNear: "Berdasarkan lokasimu",
      pickPlaceClose: "Tutup",
      pickPlaceSave: "Simpan",
      pickPlaceSaving: "Menyimpan\u2026",
      yesterday: "kemarin",
      daysAgo: "{n} hari lalu",
    },
    kayipIlan: {
      title: "Kucing hilang",
      lost: "HILANG",
      odul: "hadiah",
      poster: "Buat poster",
      posterTitle: "Poster kucing hilang",
      posterErr: "Poster tidak dapat dibuat. Coba lagi sebentar.",
      share: "Bagikan",
      shareTitle: "Kucing hilang 🔍",
      shareCopied: "Tautan disalin — tempel di mana pun.",
      shareErr: "Tidak dapat dibagikan. Coba lagi sebentar.",
      seen: "Saya melihat kucing ini",
      seenSub: "Laporkan lokasinya di peta",
      aiTitle: "Melihat kucing serupa?",
      aiDesc: "Ambil foto atau pilih dari galeri, AI akan membandingkan.",
      whereTitle: "Di mana kira-kira?",
      whereSub: "Perkiraan area pencarian",
      openMap: "Buka peta",
      teamTitle: "Tim pencari",
      teamCount: "{n} orang mencari",
      teamJoin: "Ikut mencari",
      teamLeave: "Keluar dari pencarian",
      photoCount: "Foto",
      seenCount: "Laporan penampakan",
      owner: "Pemilik iklan",
      call: "Telepon",
      message: "Pesan",
      details: "Detail iklan",
      reunited: "Bertemu — tutup iklan",
      bKisir: "Steril",
      bKisirDegil: "Belum steril",
      bErkek: "Jantan",
      bDisi: "Betina",
      bUrkek: "Penakut",
      bSokulgan: "Ramah",
      bSaldirgan: "Waspada",
      bSakin: "Tenang",
      noPhoto: "Tanpa foto",
      notFound: "Iklan tidak ditemukan.",
      closed: "Iklan ini sudah ditutup.",
    },
    aramaAlani: {
      title: "Area pencarian",
      areaTitle: "Perkiraan area pencarian",
      areaSub: "Dibuat dari lokasi terakhir terlihat.",
      allReports: "Lihat semua",
      directions: "Rute ke area ini",
      legend: "Keterangan peta",
      sonGorulen: "Terakhir terlihat",
      aramaAlaniHalka: "Area pencarian",
      ihbarlar: "Laporan penampakan",
      gonulluler: "Relawan",
      reports: "Laporan terbaru",
      noReports: "Belum ada laporan. Jadilah yang pertama.",
      cFoto: "Ada foto",
      cBenzer: "Mungkin mirip",
      cEminDegil: "Tidak yakin",
      addReport: "Tambah laporan",
      join: "Gabung tim pencarian",
      leave: "Keluar dari tim pencarian",
      team: "{n} orang mencari",
      locHidden: "Masuk untuk melihat lokasi relawan",
      away: "{d} dari sini",
      noCoords: "Iklan ini tidak punya lokasi peta.",
      closed: "Iklan ini sudah ditutup.",
      notFound: "Iklan tidak ditemukan.",
      back: "Kembali",
    },
    mahallem: {
      anOnerilerAcikGorev: "Sudah ada tugas terbuka",
      anHeroBaslik: "Bagaimana kondisi {yer}?",
      anHeroAlt: "Data 30 hari",
      anHeroAltAi: "Data 30 hari & AI",
      anSkorEtiketi: "Skor Kesehatan Lingkungan",
      anSkorBilgiAc: "Apa yang diukur skor ini?",
      anSkorBilgi: "Skor mengukur sumber daya lingkungan (catatan vaksin, titik makan dan air, kasus darurat terbuka, kecepatan respons) — bukan kesehatan kucing.",
      anAzVeri: "data sedikit",
      anBalonYok: "Belum ada ringkasan: data belum cukup.",
      anDurumKayit: "Kucing terus dicatat",
      anDurumKayitAlt: "{n} baru dalam 30 hari",
      anDurumAcilYok: "Tidak ada darurat terbuka",
      anDurumAcilYokAlt: "Semua kasus selesai",
      anDurumSuVar: "Titik air tersedia",
      anDurumSuVarAlt: "{n} titik aktif",
      anDurumMamaVar: "Titik makan tersedia",
      anDurumMamaVarAlt: "{n} titik aktif",
      anDurumBeslenme: "Dukungan makan kurang",
      anDurumBeslenmeAlt: "Perlu titik baru",
      anDurumSu: "Titik air kurang",
      anDurumSuAlt: "Perlu titik baru",
      anDurumGonullu: "Relawan kurang",
      anDurumGonulluAlt: "Perlu lebih banyak relawan",
      anDurumAsi: "Pencatatan vaksin lemah",
      anDurumAsiAlt: "Catatan sedikit",
      anDurumBarinak: "Tidak ada rumah kucing",
      anDurumBarinakAlt: "Perlu tempat berteduh",
      anDurumYarali: "Laporan kucing terluka",
      anDurumYaraliAlt: "Dalam 30 hari",
      anDurumAcil: "{n} darurat menunggu",
      anDurumAcilBir: "1 darurat menunggu",
      anDurumAcilAlt: "Perlu tindakan",
      anKonularBaslik: "{n} isu penting saat ini",
      anTumunuGor: "Lihat semua",
      anKonuAcil: "Darurat menunggu",
      anKonuYarali: "Kucing terluka dilaporkan",
      anKonuBeslenme: "Titik makan kurang",
      anKonuSu: "Titik air kurang",
      anKonuGonullu: "Relawan kurang",
      anKonuAsi: "Vaksin kurang tercatat",
      anKonuBarinak: "Tidak ada rumah kucing",
      anKonuBolgede: "Paling banyak di {yer}.",
      anKonuYok: "Tidak ada kekurangan jelas.",
      anVeriYok: "Data tidak dapat dimuat.",
      anDugmeVakalar: "Lihat kasus",
      anDugmeGonulluler: "Lihat relawan",
      anDugmeKediler: "Lihat kucing",
      anHaritaBaslik: "Peta Analisis Lingkungan",
      anAralikSec: "Pilih periode",
      anAralik7: "7 hari",
      anAralik30: "30 hari",
      anAralik90: "3 bulan",
      anAralik365: "1 tahun",
      anAralikTumu: "Semua",
      anKatmanYogunluk: "Kepadatan Kucing",
      anKatmanIhtiyac: "Kebutuhan",
      anKatmanAcil: "Darurat",
      anKatmanBeslenme: "Makan",
      anKatmanSu: "Titik Air",
      anYogunlukDusuk: "Rendah",
      anYogunlukYuksek: "Tinggi",
      anEtiketKedi: "{n} kucing",
      anEnCokIhtiyac: "Area paling butuh:",
      anHaritaBos: "Belum ada area berbatas.",
      anDegisimBaslik: "Apa yang berubah 30 hari?",
      anDetayliGor: "Detail",
      anSekmeKediler: "Kucing",
      anSekmeAcil: "Darurat",
      anSekmeKayip: "Kucing Hilang",
      anSekmeBeslenme: "Makan",
      anSekmeSu: "Titik Air",
      anSatirKedi: "Catatan kucing",
      anSatirAcil: "Darurat",
      anSatirBeslenme: "Titik makan",
      anSatirSu: "Titik air",
      anRozet30: "30 hari",
      anGrafikNotu: "Menurut tanggal catat: kucing = total di akhir hari; lainnya = ditambahkan hari itu.",
      anGrafikYok: "Belum ada catatan pada periode ini.",
      anOnerilerBaslik: "Saran AI",
      anOnerilerAlt: "Ide untuk lingkungan kita.",
      anTumOneriler: "Semua saran",
      anOneriYok: "Tidak ada saran saat ini.",
      // ÖNCELİK-1 (18 Eylül 2026) — Mahalle Öncelikleri sayfası.
      ocBaslik: "Prioritas Lingkungan",
      ocAlt: "Mari bergerak bersama untuk lingkungan yang lebih baik.",
      ocGeri: "Kembali",
      ocNot: "Prioritas ini ditentukan oleh analisis AI dari data lingkunganmu dalam 30 hari terakhir.",
      ocEtiketAcil: "KASUS DARURAT",
      ocEtiketBeslenme: "PAKAN",
      ocEtiketSu: "TITIK AIR",
      ocEtiketGonullu: "DUKUNGAN RELAWAN",
      ocEtiketDiger: "KEBUTUHAN LAIN",
      ocAcilBaslik: "Kucing menunggu bantuan",
      ocAcilAlt: "{n} kasus darurat terbuka di lingkunganmu.",
      ocAcilYokBaslik: "Tidak ada kasus darurat",
      ocAcilYokAlt: "Tidak ada kasus yang menunggu penanganan.",
      ocAcilBirim: "kasus terbuka",
      ocBeslenmeBaslik: "Perlu dukungan pakan",
      ocBeslenmeAlt: "Titik pakan kurang di {n} area.",
      ocBeslenmeYokBaslik: "Titik pakan mencukupi",
      ocBeslenmeYokAlt: "{n} titik pakan tercatat.",
      ocBeslenmeBirim: "titik baru",
      ocSuBaslik: "Titik air kurang",
      ocSuAlt: "Perlu dukungan air di {n} area.",
      ocSuYokBaslik: "Titik air mencukupi",
      ocSuYokAlt: "{n} titik air tercatat.",
      ocSuBirim: "titik",
      ocGonulluBaslik: "Kami butuh dukungan",
      ocGonulluAlt: "Dicari relawan untuk {n} kucing.",
      ocGonulluYokBaslik: "Dukungan relawan cukup",
      ocGonulluYokAlt: "{g} relawan merawat {n} kucing.",
      ocGonulluBirim: "kucing",
      ocDigerBaslik: "Kehilangan dan tempat tinggal",
      ocDigerAlt: "{k} catatan aktif, {b} kebutuhan tempat tinggal.",
      ocDigerYokBaslik: "Tidak ada masalah kehilangan atau tempat tinggal",
      ocDigerYokAlt: "Tidak ada laporan kehilangan atau kebutuhan tempat tinggal.",
      ocDigerBirim: "catatan",
      ocDugmeGorevler: "Lihat Tugas",
      ocDugmeBolgeler: "Lihat Area",
      ocDugmeDetaylar: "Lihat Detail",
      gvKatBeslenme: "Makan",
      gvKatSu: "Titik air",
      gvKatKayip: "Kucing hilang",
      gvKatSaglik: "Kesehatan",
      gvKatKulube: "Rumah kucing",
      gvDugmeDetay: "Lihat detail",
      gvSloganSade: "Mari bergerak bersama untuk kucing.",
      gvOlusturBaslik: "Buat tugasmu sendiri",
      gvOlusturAlt: "Buat tugas baru untuk lingkunganmu. Bersama kita jangkau lebih banyak kucing.",
      gvOlusturDugme: "Buat tugas",
      gvOneriBaslikKisa: "Saran AI",
      gvGorevlerimBag: "Tugasku · {n}",
      baKatmanKisaAcil: "Darurat",
      baKatmanKisaBeslenme: "Makan",
      baKatmanKisaSu: "Air",
      baKatmanKisaGonullu: "Relawan",
      baKatmanKisaIsi: "Panas",
      gvBaslik: "Tugas",
      gvSlogan: "Hal yang bisa kamu lakukan untuk kucing di lingkunganmu. Langkah kecil, perbedaan besar.",
      gvElYazisi: "Bersama, kucing lebih bahagia.",
      gvSuzTumu: "Semua",
      gvSuzAcil: "Darurat",
      gvSuzBugun: "Hari ini",
      gvSuzYakin: "Terdekat",
      gvFiltrele: "Filter",
      gvFiltreTur: "Jenis tugas",
      gvFiltreDurum: "Status",
      gvTemizle: "Hapus",
      gvTamam: "Selesai",
      gvTur_yarali_kontrol: "Kucing terluka",
      gvTur_veteriner_destegi: "Bantuan dokter hewan",
      gvTur_mama_istasyonu: "Makan",
      gvTur_su_kabi: "Air",
      gvTur_kayip_arama: "Kucing hilang",
      gvTur_kulube: "Rumah kucing",
      gvHata: "Tugas tidak dapat dimuat.",
      gvOncelikliBaslik: "Tugas prioritas",
      gvCipTumu: "Semua",
      gvCipAcil: "Darurat",
      gvCipIhtiyac: "Kebutuhan",
      gvCipDestek: "Dukungan",
      gvListeGeri: "Lingkungan saya",
      gvListeAlt: "Semua tugas di {yer}",
      gvListeSimdi: "Lakukan sekarang",
      gvListeSimdiAlt: "Tugas prioritas di lingkungan",
      gvListeDiger: "Tugas lain",
      gvListeDigerAlt: "Tugas lain di lingkungan",
      gvListeAra: "Cari",
      gvDetayGeri: "Tugas",
      gvDetayAcilRozet: "Tugas darurat",
      gvDetayBenimRozet: "Tugasku",
      gvDetayBittiRozet: "Selesai",
      gvDetayBekliyor: "Menunggu bantuan",
      gvDetayBekliyorAlt: "Tugas ini butuh perhatian segera.",
      gvDetayUstlendi: "Kamu mengambil tugas ini!",
      gvDetayUstlendiAlt: "Terima kasih — kita lebih kuat bersama.",
      gvDetayBittiBanner: "Tugas ini selesai. Terima kasih atas usahamu!",
      gvDetaySekmeDetay: "Detail",
      gvDetaySekmeKonum: "Lokasi",
      gvDetaySekmeYorum: "Komentar",
      gvDetaySonGorulme: "Terakhir terlihat",
      gvDetayKonum: "Lokasi",
      gvDetayDurum: "Status",
      gvDetayBildiren: "Pelapor",
      gvDetayAciklama: "Deskripsi",
      gvDetayHaritada: "Lihat di peta",
      gvDetayUstlen: "Ambil tugas",
      gvDetayPaylas: "Bagikan",
      gvDetayTamamla: "Tandai selesai",
      gvDetayFoto: "Tambah foto",
      gvDetayGelisme: "Foto perkembangan",
      gvDetayBittiTarih: "Tugas ini diselesaikan pada {tarih}",
      gvDetayYorumBos: "Belum ada komentar.",
      gvDetayEkip: "Tim dan langkah",
      gvTumunuGor: "Lihat semua",
      gvRozetAcil: "Darurat",
      gvRozetOncelikli: "Prioritas",
      gvDugmeGor: "Lihat tugas",
      gvDugmeUstlen: "Ambil tugas",
      gvDugmeKontrol: "Periksa",
      gvDugmeDestek: "Bantu",
      gvYakinBaslik: "Tugas di dekatmu",
      gvMahalleBaslik: "Tugas di lingkungan",
      gvOneriBaslik: "Saran AI",
      gvOneriDugme: "Lihat saran",
      gvBenimBaslik: "Tugasku",
      gvAktifSayi: "{n} aktif",
      gvTamamSayi: "{n} selesai",
      gvBenimBos: "Kamu belum ikut tugas.",
      gvBos: "Belum ada tugas.",
      gvKonumGerekli: "Izin lokasi diperlukan.",
      gvKonumReddedildi: "Izin lokasi ditolak.",
      gvDurumAcik: "Terbuka",
      gvDurumDevam: "Berjalan",
      gvDurumTamam: "Selesai",
      gvDurumIptal: "Dibatalkan",
      gvZamanBugun: "Ikut · Hari ini {saat}",
      gvZamanDun: "Ikut · Kemarin {saat}",
      gvZamanTarih: "Ikut · {tarih}",
      gvZamanBugunKisa: "Hari ini {saat}",
      gvZamanDunKisa: "Kemarin {saat}",
      m5Mahallem: "Lingkunganku",
      m5Paylas: "Bagikan",
      m5GonulluOl: "Jadi relawan",
      m5HavaAcik: "Cerah",
      m5HavaParcali: "Berawan sebagian",
      m5HavaSisli: "Berkabut",
      m5HavaYagmurlu: "Hujan",
      m5HavaKarli: "Salju",
      m5HavaFirtinali: "Badai",
      m5HavaBugun: "Hari ini {min}° / {max}°",
      m5PanoKedi: "kucing terdaftar",
      m5EgilimYeni: "baru",
      m5EgilimBaslik: "30 hari terakhir: {son} catatan baru · 30 hari sebelumnya: {once}",
      m5HaritayiAc: "Buka peta",
      baBaslik: "Analisis Wilayah",
      baAltBaslik: "Pahami kebutuhan lingkungan bersama.",
      baAralikTumu: "Semua",
      baAralik7: "7 hari",
      baAralik30: "30 hari",
      baAralik90: "3 bulan",
      baAralik365: "1 tahun",
      baSayacKedi: "Total kucing",
      baSayacAcil: "Darurat terbuka",
      baSayacBeslenme: "Titik makan",
      baSayacSu: "Titik air",
      baSayacGonullu: "Keikutsertaan relawan",
      baSayacKediYeni: "Kucing baru",
      baSayacAcilYeni: "Darurat baru",
      baSayacBeslenmeYeni: "Titik makan baru",
      baSayacSuYeni: "Titik air baru",
      baSayacGonulluYeni: "Keikutsertaan baru",
      baSatirKedi: "{n} kucing",
      baSatirAcil: "{n} darurat",
      baSatirBeslenme: "{n} titik makan",
      baSatirSu: "{n} titik air",
      baSatirGonullu: "{n} keikutsertaan",
      baKatmanlar: "Lapisan",
      baKatmanKedi: "Kucing",
      baKatmanAcil: "Darurat",
      baKatmanBeslenme: "Titik makan",
      baKatmanSu: "Titik air",
      baKatmanGonullu: "Tugas relawan",
      baKatmanIsi: "Peta panas",
      baCercevele: "Sesuaikan peta",
      baIlceNotu: "Sedikit catatan berkelurahan; wilayah ditampilkan per kecamatan.",
      baHata: "Analisis wilayah tidak dapat dimuat.",
      baHaritaBos: "Belum ada wilayah berlokasi",
      baBolgeOzet: "Ringkasan per Wilayah",
      baTumBolgeler: "Semua wilayah",
      baKademeNotu: "Angka = catatan baru pada periode ini; tingkat = sepanjang waktu.",
      baDiger: "Lainnya",
      baDigerAlt: "tanpa kelurahan",
      baBolgeyiIncele: "Lihat wilayah",
      baBolgeYok: "Belum ada wilayah.",
      baAiBaslik: "Analisis Wilayah AI",
      baAiYok: "Ringkasan AI hari ini belum siap.",
      baAiDetay: "Analisis rinci",
      baOncelikli: "Wilayah Prioritas",
      baNedenYok: "Skor terendah",
      baHaritadaGor: "Lihat kucing di peta",
      baHaritadaAlt: "Peta berpusat di wilayah ini.",
      baTumKediler: "Kucing lingkungan",
      baTumKedilerAlt: "Lihat semua kucing sebagai daftar.",
      baKisaKedi: "Total kucing",
      baKisaAcil: "Darurat",
      baKisaBeslenme: "Makan",
      baKisaSu: "Air",
      baKisaGonullu: "Relawan",
      baKisaKediYeni: "Kucing baru",
      baKisaAcilYeni: "Darurat baru",
      baKisaBeslenmeYeni: "Makan baru",
      baKisaSuYeni: "Air baru",
      baKisaGonulluYeni: "Bergabung",
      baSatirKisaKedi: "{n} kucing",
      baSatirKisaAcil: "{n} darurat",
      baSatirKisaBeslenme: "{n} makan",
      baSatirKisaSu: "{n} air",
      baSatirKisaGonullu: "{n} ikut",
      baIncele: "Lihat",
      baAiDetayKisa: "Rincian",
      baKademeNotuKisa: "· catatan baru",
      baIlceNotuKisa: "Tingkat kecamatan",
      gdBaslik: "Detail Harian",
      gdBugun: "Hari ini",
      gdOncekiGun: "Hari sebelumnya",
      gdSonrakiGun: "Hari berikutnya",
      gdToplamBugun: "{n} catatan hari ini.",
      gdToplamGun: "{n} catatan pada hari ini.",
      gdToplamYok: "Tidak ada catatan pada hari ini.",
      gdToplamKarsilastir: "Hari sebelumnya: {once} · hari ini: {simdi}.",
      gdDagilim: "Sebaran Aktivitas",
      gdKisaKedi: "Kucing baru",
      gdKisaAcil: "Darurat",
      gdKisaKayip: "Hilang",
      gdKisaBeslenme: "Makan",
      gdKisaSu: "Air",
      gdKisaGonullu: "Relawan",
      gdBolge: "Per Kelurahan",
      gdKonumsuz: "Tanpa kelurahan: {n}",
      gdKonumYok: "Tidak ada catatan berlokasi",
      gdHaritadaGor: "Lihat di peta",
      gdFotoBugun: "Foto Hari Ini",
      gdFotoGun: "Foto Hari Itu",
      gdFotoYok: "Tidak ada foto",
      gdFazla: "lagi",
      gdKayitBugun: "Catatan Hari Ini",
      gdKayitGun: "Catatan Hari Itu",
      gdFiltreHepsi: "Semua catatan",
      gdTumunuGoster: "Tampilkan semua ({n})",
      gdKayitKedi: "Kucing baru dicatat",
      gdKayitAcil: "Kasus darurat",
      gdKayitKayip: "Laporan kucing hilang",
      gdKayitBeslenme: "Titik makan baru",
      gdKayitSu: "Titik air baru",
      gdKayitGonullu: "Relawan bergabung",
      gdKatilim: "{n} bergabung",
      gdAltBaslik: "Lihat lingkungan Anda di peta.",
      gdAltMetin: "Hidup lebih aman untuk lingkungan Anda, bersama.",
      gdHata: "Detail harian tidak dapat dimuat. Akan muncul setelah server diperbarui.",
      gdPaylasMetni: "{yer} · {tarih}: kucing jalanan, detail harian.",
      gdGunuAc: "Buka detail harian {tarih}",
      s7Baslik: "7 Hari Terakhir",
      s7PaylasMetni: "{yer}: 7 hari terakhir untuk kucing jalanan.",
      s7GrafikBaslik: "Catatan baru per hari",
      s7Yeni: "baru",
      s7Sabit: "tetap",
      s7SayacKedi: "Kucing baru dicatat",
      s7SayacAcil: "Kasus darurat",
      s7SayacKayip: "Laporan kucing hilang",
      s7SayacBeslenme: "Titik makan baru",
      s7SayacSu: "Titik air baru",
      s7SayacGonullu: "Keikutsertaan relawan",
      s7Gunluk: "Detail harian",
      s7KartKotu: "{seri} meningkat dalam 7 hari terakhir.",
      s7KartIyi: "{seri} meningkat dalam 7 hari terakhir.",
      s7KartSabit: "Tidak ada perubahan jelas dibanding 7 hari sebelumnya.",
      s7KartOnceYok: "Perbandingan dengan minggu lalu muncul setelah pembaruan server.",
      s7KartKarsilastir: "7 hari sebelumnya: {once} · minggu ini: {simdi}.",
      s7IstatistikDetay: "Lihat statistik",
      s7SeriAcil: "Kasus darurat",
      s7SeriKayip: "Laporan kucing hilang",
      s7SeriKedi: "Kucing baru",
      s7SeriBeslenme: "Titik makan baru",
      s7SeriSu: "Titik air baru",
      s7SeriGonullu: "Keikutsertaan relawan",
      s7Ac: "Buka 7 hari terakhir",
      zekaGonullu: "Butuh relawan",
      azVeriKisa: "Data sedikit",
      heroYanSlogan: "Lingkungan sama, kucing lebih bahagia.",
      haritaBaslikOzet: "Peta lingkungan (ringkasan)",
      lejantAcil: "Darurat",
      lejantKayip: "Kucing hilang",
      lejantSu: "Titik air",
      lejantBeslenme: "Makan",
      lejantGonullu: "Relawan",
      zekaSu: "Titik air kurang",
      zekaMama: "Titik makan kurang",
      zekaAsi: "Catatan vaksin lemah",
      zekaYarali: "Ada laporan kucing terluka",
      zekaAcil: "Kasus darurat terbuka",
      zekaBarinak: "Tidak ada rumah kucing",
      zekaTamam: "Tidak ada kekurangan jelas",
      yasBilinmiyorKisa: "Tidak diketahui",
      dagilimIpucuBilinmiyor: "{p} kucing belum tercatat usianya.",
      dagilimIpucuCogunluk: "Kelompok terbesar: {grup} ({p}).",
      dagilimYakinda: "Tersedia setelah pembaruan server.",
      ustBaslik: "Lingkunganku",
      paylas: "Bagikan",
      paylasMetni: "{yer}: kucing, kebutuhan, dan relawan di lingkungan.",
      paylasKopyalandi: "Tautan disalin",
      paylasOlmadi: "Gagal membagikan",
      sekmeGenel: "Ringkasan",
      sekmeAnaliz: "Analisis",
      sekmeGorevler: "Tugas",
      sekmeKediler: "Kucing",
      sekmeGonulluler: "Relawan",
      lejantKedi: "Kucing",
      zekaOzet: "Kami menganalisis lingkunganmu. Sorotan:",
      azVeri: "Data masih sedikit — skor bisa cepat berubah.",
      detayliAnaliz: "Lihat analisis lengkap",
      dagilimBaslik: "Kucing menurut usia",
      kediKisa: "kucing",
      yasYavru: "Anak kucing",
      yasYetiskin: "Dewasa",
      yasYasli: "Tua",
      yasBilinmiyor: "Usia tidak diketahui",
      dagilimYok: "Belum ada kucing tercatat.",
      ihtiyacBaslik: "Kebutuhan utama",
      ihtiyacYok: "Tidak ada kebutuhan mendesak saat ini.",
      oncelikKritik: "Kritis",
      oncelikYuksek: "Tinggi",
      oncelikOrta: "Sedang",
      oncelikDusuk: "Rendah",
      son7Baslik: "7 hari terakhir",
      son7Yok: "Tidak ada catatan baru dalam 7 hari terakhir.",
      seriKedi: "Kucing",
      seriAcil: "Darurat",
      seriKayip: "Hilang",
      seriBeslenme: "Makan",
      seriSu: "Air",
      ctaBaslik: "Lebih kuat bersama sebagai lingkungan.",
      ctaAlt: "Bantu ubah hidup kucing di sekitarmu.",
      ctaDugme: "Jadi relawan",
      zekaAlt: "Lihat kebutuhan lingkunganmu dan ambil tindakan.",
      zekaBaslik: "Analisis Lingkungan AI",
      panoBeslenme: "titik makan",
      panoGonullu: "relawan aktif",
      hizliAcilMerkezi: "Pusat Darurat",
      onemliBaslik: "Perhatian di dekatmu",
      rozetKayip: "HILANG",
      rozetAcil: "DARURAT",
      rozetMama: "MAKANAN",
      rozetTehlike: "BAHAYA",
      rozetGonullu: "BANTUAN",
      durumYok: "Tidak ada yang mendesak di dekatmu.",
      sonFotograflar: "Foto terbaru",
      sonAktiviteler: "Aktivitas terbaru",
      tumuSayili: "Semua ({n})",
      paylasimYok: "Belum ada kiriman di lingkunganmu.",
      yakinKediler: "Kucing di dekatmu",
      haritayiAc: "Buka peta",
      cipBesleniyor: "Titik makan",
      cipSu: "Titik air",
      cipAlisik: "Terbiasa dengan orang",
      cipBiliniyor: "Dikenal warga",
      haritaEnYakin: "{k} dari {n} kucing terdekat",
      panoBaslik: "Di Lingkunganmu Hari Ini",
      panoAlt: "Lebih banyak kucing, lingkungan yang lebih baik.",
      panoKedi: "kucing dipantau",
      panoAcil: "kasus darurat",
      panoKayip: "kucing hilang",
      panoSu: "titik air",
      mahallemiGor: "Lihat lingkunganku",
      hizliBaslik: "Aksi cepat",
      hizliKaydet: "Laporkan kucing",
      hizliGordum: "Saya lihat kucing",
      hizliAcil: "Lapor darurat",
      hizliMamaSu: "Makanan / Air",
      title: "Lingkungan saya",
      bolgelerBaslik: "Wilayah yang paling butuh bantuan",
      heroKedi: "{n} kucing terdaftar di lingkunganmu.",
      heroYeniKedi: "{n} kucing baru terdaftar dalam 30 hari terakhir.",
      heroYardimBekleyen: "{n} kucing di lingkunganmu menunggu bantuan.",
      enOnemli: "Kebutuhan terbesar:",
      iSu: "Titik air",
      iMama: "Titik makanan",
      iAsi: "Pelacakan vaksin",
      iYarali: "Kucing terluka",
      iAcil: "Kasus darurat",
      iBarinak: "Tempat berteduh",
      iTamam: "Tidak ada kekurangan jelas",
      haritaAlt: "Jelajahi kucing dan wilayah yang butuh bantuan.",
      tamEkran: "Layar penuh",
      lYuksek: "Kebutuhan tinggi",
      lOrta: "Sedang",
      lYeterli: "Cukup",
      lMama: "Titik makanan",
      lSu: "Titik air",
      tumu: "Semua",
      tumGorevler: "Semua tugas",
      ilerlemeTamam: "{n}/{m} selesai",
      kayipRozet: "HILANG",
      kayipAdsiz: "Kucing hilang",
      kayipYok: "Tidak ada laporan hilang di dekatmu.",
      azOnce: "baru saja",
      saatOnce: "{n} jam lalu",
      gunOnce: "{n} hari lalu",
      son30: "30 hari terakhir",
      sAcil: "Kasus darurat",
      sMama: "Titik makan",
      sSu: "Titik air",
      sGonullu: "Relawan aktif",
      aiBaslik: "Analisis lingkungan AI",
      beta: "BETA",
      aiGor: "Lihat saran AI",
      haritaBaslik: "Peta lingkungan",
      kayipBaslik: "Kucing hilang di dekatmu",
      yardimBaslik: "Bantu lingkunganmu",
      gorevYok: "Belum ada tugas terbuka.",
      gorevKatil: "Gabung",
      ilerleme: "{n}/{m} relawan",
      skorBaslik: "Skor lingkungan",
      skorYaklasik: "perubahan bersifat perkiraan",
      olculmedi: "—",
      yukleniyor: "Memuat…",
      hata: "Laporan lingkungan tidak dapat dimuat.",
      mahalleYok: "Tetapkan lingkungan rumahmu untuk melihat halaman ini.",
      secBaslik: "Pilih lingkunganmu",
      secCumle: "Setelah dipilih, kucing, kebutuhan, dan tugas di sini mengikuti lingkunganmu.",
      secKaydet: "Simpan",
      vazgec: "Batal",
      secHata: "Lingkungan tidak dapat disimpan.",
      tekrarDene: "Coba lagi",
    },
    bolge: {
      title: "Detail wilayah",
      bolgesi: "Wilayah {n}",
      tumunuGor: "Lihat semua",
      detayliAnaliz: "Analisis rinci",
      tumu: "Semua",
      gecenAy: "{n} dari bulan lalu",
      yaklasik: "perkiraan",
      kediAdet: "{n} kucing",
      geri: "Kembali",
      kapat: "Tutup",
      son30: "30 hari terakhir",
      iKritik: "Kebutuhan kritis",
      iYuksek: "Kebutuhan tinggi",
      iOrta: "Kebutuhan sedang",
      iDusuk: "Kebutuhan rendah",
      sKedi: "Kucing terdaftar",
      sMama: "Titik makanan",
      sSu: "Titik air",
      yKuzey: "Utara",
      yGuney: "Selatan",
      yDogu: "Timur",
      yBati: "Barat",
      aiBaslik: "Analisis Wilayah AI",
      beta: "BETA",
      kedilerBaslik: "Kucing di wilayah ini",
      kediYok: "Belum ada kucing terdaftar di wilayah ini.",
      skorBaslik: "Skor lingkungan",
      skorYaklasik: "perubahan bersifat perkiraan",
      dagilimBaslik: "Sebaran per arah",
      dagilimAdet: "{n} kucing",
      bildirimBaslik: "Aktivitas terbaru",
      bildirimYok: "Tidak ada aktivitas dalam 30 hari terakhir.",
      olculmedi: "—",
      olcumYok: "Belum ada pengukuran harian untuk wilayah ini.",
      hata: "Laporan wilayah tidak dapat dimuat.",
    },
    mahalleListesi: {
      title: "Lingkungan",
      ara: "Cari lingkungan",
      mahalle: "lingkungan",
      paylasim: "kiriman",
      kedi: "kucing",
      alinamadi: "Daftar lingkungan tidak dapat dimuat.",
      tekrarDene: "Coba lagi",
      aramaBos: "Tidak ada lingkungan yang cocok dengan pencarian ini.",
      hicYok: "Belum ada lingkungan untuk ditampilkan.",
      kuralNotu: "Daftar ini menampilkan lingkungan yang memiliki kiriman, area cakupan, atau hewan terdaftar.",
    },
    aiAnaliz: {
      sekmeOzet: "Ringkasan",
      sekmeDetay: "Data Rinci",
      sekmeOneriler: "Saran",
      sonNGun: "{n} hari terakhir",
      paylas: "Bagikan",
      paylasMetni: "{yer}: analisis lingkungan untuk kucing jalanan.",
      paylasKopyalandi: "Tautan disalin",
      paylasOlmadi: "Gagal membagikan",
      detayBaslik: "Skor per bidang",
      veriYok: "Belum ada pengukuran.",
      skorEtiketi: "Skor kesehatan lingkungan",
      genelDurum: "Status umum",
      genelSkor: "Sumber daya lingkungan dinilai \"{etiket}\".",
      genelKediArtti: "{n} kucing lagi terdaftar dalam 30 hari.",
      genelKediAzaldi: "{n} kucing lebih sedikit dari 30 hari lalu.",
      genelKediSabit: "Jumlah kucing terdaftar tidak berubah dalam 30 hari.",
      genelBulgu: "{n} kekurangan ditemukan.",
      genelBulguYok: "Tidak ada kekurangan jelas.",
      farkRozeti: "{d} poin dari bulan lalu",
      aiOzeti: "Ringkasan AI",
      ozetSablon: "Ringkasan",
      tumu: "Semua",
      bulgularBaslik: "Temuan penting",
      bulguAcil: "Darurat terbuka",
      bulguYarali: "Laporan kucing terluka",
      bulguMama: "Titik makan kurang",
      bulguSu: "Titik air kurang",
      bulguAsi: "Pelacakan vaksin lemah",
      bulguBarinak: "Tidak ada rumah kucing",
      bulguGonullu: "Butuh dukungan relawan",
      eylemHaritada: "Lihat di peta",
      eylemGorev: "Buat tugas",
      eylemDetaylar: "Lihat detail",
      eylemGonullu: "Panggil relawan",
      eylemVakalar: "Lihat kasus",
      son7AcilEk: "{n} baru dalam 7 hari.",
      bulguYok: "Tidak ada kekurangan jelas.",
      haritadaGorBag: "Lihat di peta",
      bolgeBaslik: "Analisis per wilayah",
      kademeMukemmel: "Sangat baik",
      kademeIyi: "Baik",
      kademeGelistir: "Perlu perbaikan",
      kademeKritik: "Kritis",
      ilceDuzeyiNotu: "Tingkat kecamatan",
      notSu: "Titik air kurang",
      notMama: "Titik makan kurang",
      notAsi: "Pelacakan vaksin",
      notYarali: "Laporan luka",
      notAcil: "Darurat terbuka",
      notBarinak: "Tanpa rumah kucing",
      notGonullu: "Butuh relawan",
      notTamam: "Umumnya baik",
      bolgeYok: "Skor wilayah muncul setelah data lokasi cukup.",
      aralikSec: "Pilih rentang",
      trendBaslik: "Analisis tren",
      seriKedi: "Kucing",
      seriAcil: "Darurat",
      seriKayip: "Hilang",
      seriBeslenme: "Makan",
      seriSu: "Air",
      trendBirikiyor: "Pengukuran harian sedang terkumpul; grafik muncul setelah dua hari.",
      oneriBaslik: "Saran AI",
      bakilanBaslik: "Analisis ini memeriksa",
      bakilanKedi: "Kucing terdaftar",
      bakilanSu: "Titik air",
      bakilanMama: "Titik makan",
      bakilanKulube: "Rumah kucing",
      bakilanAcil: "Kucing dalam kondisi darurat",
      bakilanYarali: "Laporan terluka (30 hari)",
      oneriAlinamadi: "Saran tidak dapat dimuat — server tidak menjawab. Ini bukan berarti tidak ada kekurangan.",
      tekrarDene: "Coba lagi",
      acikGorevNotu: "Saran disembunyikan selama ada tugas terbuka sejenis.",
      sayacKedi: "kucing",
      sayacSu: "titik air",
      sayacMama: "titik makan",
      sayacKulube: "rumah kucing",
      sayacYarali: "laporan terluka (30 hari)",
      sekmeBolge: "Analisis Wilayah",
      sekmeKedi: "Pengenalan Kucing",
      ozetBaslikPano: "Penilaian umum",
      ozetCumle: "Kondisi lingkungan di {yer}: {durum}. Ditemukan {n} kekurangan prioritas.",
      durumBilinmiyor: "belum terukur",
      yerYok: "Lingkungan belum dipilih",
      balon: "Lingkungan yang lebih baik itu mungkin!",
      eksiklerBaslik: "Kekurangan prioritas",
      eksikSu: "Titik air belum cukup",
      eksikMama: "Titik makan belum cukup",
      eksikYarali: "Laporan kucing terluka",
      eksikKulube: "Belum ada rumah kucing",
      eksikVet: "Kucing butuh perawatan darurat",
      bugunBaslik: "3 hal yang bisa kamu lakukan hari ini",
      eylemSu: "Tambah titik air",
      eylemMama: "Buat titik makan",
      eylemYarali: "Periksa kucing terluka",
      eylemKulube: "Bangun rumah kucing",
      eylemVet: "Minta bantuan dokter hewan",
      haritadaGor: "Lihat di peta",
      eylemDetay: "Lihat detail",
      eylemListe: "Lihat daftar",
      yogunBolge: "Area kucing terpadat di lingkungan",
      gorevOlustur: "Buat tugas",
      etkiOnEk: "Dampak yang diharapkan:",
      eksikYok: "Tidak ada kekurangan prioritas saat ini.",
      title: "Analisis AI",
      beta: "BETA",
      sekmeTani: "Kenali lewat foto",
      sekmeSonuc: "Hasil analisis",
      sekmeIstatistik: "Statistik",
      taniBaslik: "Kenali kucing dari foto",
      taniAciklama: "Unggah foto; akan dibandingkan dengan kucing terdaftar dan iklan hilang yang aktif.",
      taniGit: "Ke pengenalan foto",
      sonAnalizler: "Analisis terbaru",
      analizYok: "Kamu belum menjalankan analisis.",
      olcumBaslamadi: "Riwayat analisis baru mulai disimpan; yang sebelumnya tidak tercatat.",
      eslesti: "Cocok",
      eslesmedi: "Tidak cocok",
      adsizIlan: "Nama tidak disebutkan",
      ozetBaslik: "Ringkasan analisis di wilayah ini",
      sAnaliz: "Analisis",
      sEslesme: "Kecocokan",
      sYeniKedi: "Kucing baru",
      oran: "{n}/{m} analisis mencapai ambang batas",
      oranNotu: "Ini bukan tingkat akurasi: berapa banyak kecocokan yang benar belum diukur.",
      son30: "30 hari terakhir",
      yukleniyor: "Memuat…",
      hata: "Analisis tidak dapat dimuat.",
    },
    kayipFormu: {
      baslik: "Tentang kucing",
      adEtiket: "Nama",
      adYerTutucu: "mis. Boncuk",
      adIpucu: "Nama yang akan dipanggil tetangga — ini paling membantu.",
      kisirEtiket: "Steril",
      kEvet: "Ya",
      kHayir: "Tidak",
      cinsiyetEtiket: "Jenis kelamin",
      cErkek: "Jantan",
      cDisi: "Betina",
      cBilinmiyor: "Tidak tahu",
      yasEtiket: "Usia",
      yasIpucu: "mis. 1,5 tahun",
      mizacEtiket: "Sifat",
      mUrkek: "Penakut",
      mSokulgan: "Ramah",
      mSaldirgan: "Defensif",
      mSakin: "Tenang",
      iletisimEtiket: "Kontak (opsional)",
      iletisimIpucu: "Hanya tampil di balik tombol Telepon, untuk yang sudah masuk. Jangan tulis di deskripsi.",
      notNumaraUyarisi: "Sepertinya ada nomor telepon di deskripsi. Taruh di kolom kontak — supaya tidak tampil terbuka.",
    },
    kayipBildir: {
      title: "Laporan kucing hilang",
      subtitle: "Tambahkan foto dan ciri kucingmu. Lokasi menyusul.",
      fotoZorunlu: "Perlu minimal satu foto — hanya itu yang bisa dicocokkan penemu.",
      varsa: "jika tahu",
      renkEtiket: "Warna",
      konumTitle: "Lokasi & waktu terakhir terlihat",
      konumSubtitle: "Tandai titiknya di peta. Tanpa itu, iklanmu tidak sampai ke orang terdekat.",
      konumumuKullan: "Gunakan lokasi saya",
      konumAliniyor: "Mengambil lokasi…",
      konumZorunlu: "Lokasi wajib. Jika GPS tidak jalan, ketuk peta untuk menandai titiknya.",
      konumIsaretlendi: "Lokasi ditandai",
      konumYok: "Belum ada lokasi",
      yaricapEtiket: "Radius pencarian",
      yaricapIpucu: "Kucing rumahan yang hilang biasanya bersembunyi dalam beberapa ratus meter di hari-hari pertama.",
      sonGorulmeEtiket: "Terakhir terlihat",
      ekEtiket: "Informasi tambahan",
      ekYerTutucu: "Apa pun yang membantu: kebiasaan, tempat sembunyi, siapa yang dipercaya…",
      mahalleEtiket: "Lingkungan",
      duzenle: "Ubah",
      ozetAdsiz: "Kucing tanpa nama",
      yayinla: "Terbitkan iklan hilang",
      yayinlaniyor: "Menerbitkan…",
      errSend: "Iklan tidak dapat diterbitkan. Coba lagi.",
      okTitle: "Iklanmu tayang",
      okDesc: "Orang-orang terdekat sudah diberi tahu. Setiap penampakan akan sampai padamu.",
      okView: "Buka iklanku",
      okList: "Semua kucing hilang",
      acikIlanBaslik: "Kamu sudah punya iklan aktif",
      acikIlanMetin: "Iklan kedua memecah pencarian. Apakah ini kucing yang sama?",
      acikIlanGit: "Buka iklan itu",
      acikIlanBaska: "Bukan, ini kucing lain",
      vazgec: "Batal",
      renkYerTutucu: "mis. belang, dada putih",
      ayirtEtiket: "Ciri khas",
      ayirtYerTutucu: "Kalung, bekas luka, telinga sobek, cara berjalan…",
    },
    kayipListe: {
      odul: "hadiah",
      baslik: "Kucing hilang",
      rozet: "HILANG",
      ilanAc: "Buat laporan hilang",
      fotoAra: "Cari dengan foto",
      yukleniyor: "Memuat…",
      yeniden: "Coba lagi",
      hata: "Daftar tidak dapat dimuat.",
      bos: "Belum ada laporan kucing hilang — kabar baik.",
      yakindaYok: "Tidak ada di dekatmu, jadi ditampilkan yang lebih jauh.",
    },
    acilDetay: {
      durumBaslik: "Kondisi kucing",
      aciklamaBaslik: "Keterangan",
      bildirenBaslik: "Dilaporkan oleh",
      bildirdi: "melaporkan {n}",
      konumBaslik: "Lokasi",
      haritadaAc: "Buka di peta",
      paylas: "Bagikan",
      sil: "Hapus laporan",
      silOnayBaslik: "Hapus laporan ini?",
      silOnayMetin: "Laporan akan hilang dari daftar dan peta. Tidak bisa dibatalkan.",
      silEvet: "Ya, hapus",
      vazgec: "Batal",
      siliniyor: "Menghapus…",
      silHata: "Laporan tidak dapat dihapus.",
      silYetki: "Hanya pelapor yang bisa menghapusnya.",
      fotoSayaci: "{a} / {b}",
      yorumlar: "Komentar",
      yorumYok: "Belum ada komentar — warga berkoordinasi di sini.",
      yorumYaz: "Tulis komentar…",
      yorumGonder: "Kirim",
      yorumGidiyor: "Mengirim…",
      yorumHata: "Komentar tidak terkirim.",
      yorumOkunmadi: "Komentar tidak dapat dimuat.",
      yorumGiris: "Masuk untuk berkomentar.",
      yorumSil: "Hapus komentar",
      yorumSilHata: "Komentar tidak dapat dihapus.",
      begen: "Dukung",
      begenHata: "Gagal diproses.",
    },
    anasayfaAlt: {
      baslik: "Tentang Sokak Kedileri",
      hakkinda: "Tentang kami",
      yardim: "Bantuan",
      gizlilik: "Privasi",
      sartlar: "Ketentuan",
      telif: "Dibuat dengan cinta untuk kucing jalanan.",
    },
    ihbarFormu: {
      title: "Tambah laporan",
      sub: "Di mana kamu melihat kucingnya?",
      locTitle: "Lokasi",
      locUse: "Gunakan lokasi saya",
      locPicked: "Lokasi ditandai",
      locHelp: "Ketuk peta untuk memindahkan pin.",
      locNeeded: "Lokasi wajib — laporan tanpa lokasi tidak bisa digambar di peta.",
      noteTitle: "Apa yang kamu lihat?",
      notePh: "Di bawah mobil putih, sangat penakut. Lari ke arah taman.",
      photoTitle: "Foto (opsional)",
      photoAdd: "Tambah foto",
      photoRemove: "Hapus foto",
      photoHint: "Foto mengubah laporanmu jadi bukti — AI bisa membandingkannya.",
      guvenTitle: "Seberapa yakin kamu?",
      gBenzer: "Mungkin mirip",
      gEminDegil: "Tidak yakin",
      gFoto: "Ada foto",
      guvenFotoNote: "Dengan foto, laporanmu ditandai “Ada foto”.",
      send: "Kirim laporan",
      sending: "Mengirim…",
      okTitle: "Laporan terkirim 🧡",
      okBody: "Pemiliknya sudah diberi tahu. Terima kasih.",
      okBack: "Kembali ke area pencarian",
      errSend: "Laporan tidak terkirim. Coba lagi.",
      back: "Kembali",
    },
    aiKars: {
      title: "Perbandingan AI",
      seenSimilar: "Melihat kucing serupa?",
      seenSimilarDesc: "Jika kamu melihat kucing, ambil foto atau pilih dari galeri. AI akan membandingkannya dengan kucing hilang.",
      camera: "Kamera",
      gallery: "Galeri",
      aiNote: "AI hanya membantu. Keputusan akhir ada padamu.",
      resultTitle: "Hasil perbandingan",
      new: "Baru",
      yourPhoto: "Fotomu",
      lostCat: "Kucing hilang",
      levelHigh: "Kemiripan tinggi",
      levelMid: "Ada kemiripan",
      levelLow: "Kemiripan rendah",
      levelHighDesc: "Kucing ini cukup mirip dengan yang hilang.",
      levelMidDesc: "Sebagian ciri cocok, sebagian tidak.",
      levelLowDesc: "Ciri-cirinya sebagian besar tidak cocok.",
      fPattern: "Pola bulu",
      fMarks: "Tanda khas",
      fVisual: "Tampilan umum",
      fAge: "Rentang usia",
      fLocation: "Kedekatan lokasi",
      stateSimilar: "mirip",
      statePartial: "agak mirip",
      stateDifferent: "berbeda",
      stateUnmeasured: "tidak terukur",
      report: "Laporkan kucing ini",
      unsureTitle: "Ragu?",
      unsureDesc: "Laporkan saja. Setiap laporan berharga.",
      tipsTitle: "Tips hasil lebih baik",
      tip1: "Fokuskan wajah kucing",
      tip2: "Jika bisa, ambil dari beberapa sudut",
      tip3: "Ambil di cahaya yang baik",
      tip4: "Tampilkan sekitarnya juga",
      comparing: "Membandingkan…",
      errCompare: "Perbandingan gagal. Coba lagi.",
      notMeasuredNote: "Ciri yang tidak bisa diukur AI ditandai — tidak terukur bukan berarti tidak cocok.",
    },
    acil: {
      merkezHeroNot: "Setiap kucing berharga",
      merkezBaslik: "Pusat Darurat",
      merkezHeroBaslik: "Mari selamatkan nyawa bersama",
      merkezHeroAlt: "Mari bergerak bersama untuk kucing yang terluka, dalam bahaya, atau butuh bantuan.",
      merkezVakalariGor: "Lihat kasus darurat",
      merkezVakalariGorAlt: "Lihat kucing yang sedang menunggu bantuan.",
      merkezVakaBildir: "Laporkan kasus darurat",
      merkezVakaBildirAlt: "Buat kasus darurat baru.",
      merkezYakinVakalar: "Kasus darurat di dekatku",
      merkezKategoriler: "Kategori darurat",
      merkezKatYarali: "Kucing terluka",
      merkezKatTehlike: "Zona bahaya",
      merkezKatKayip: "Kucing hilang",
      merkezKatMamaSu: "Makanan / Air",
      merkezKatGonullu: "Bantuan relawan",
      merkezAltBaslik: "Setiap laporan bisa menyelamatkan nyawa.",
      merkezAltMetin: "Dengan melaporkan situasi yang meragukan pun, kamu membantu kucing hidup di lingkungan yang lebih aman.",
      merkezVakaYok: "Tidak ada kasus darurat terbuka di dekatmu.",
      title: "Lapor darurat",
      subtitle: "Melihat kucing yang butuh bantuan? Laporkan cepat, relawan terdekat akan tahu.",
      safetyNote: "Dalam keadaan darurat, utamakan keselamatanmu sendiri. Jangan membahayakan dirimu.",
      stepInfo: "Detail",
      stepLocation: "Lokasi",
      stepSend: "Kirim",
      catInjured: "Kucing terluka",
      catInjuredDesc: "Kucing terluka, sakit atau kesakitan",
      catLost: "Laporkan kucing hilang",
      catLostDesc: "Kucingmu hilang — buat iklan pencarian",
      catFood: "Butuh makanan / air",
      catFoodDesc: "Tidak ada titik makan atau butuh dukungan",
      catDanger: "Area berbahaya",
      catDangerDesc: "Konstruksi, lalu lintas, risiko keracunan dll.",
      catVolunteer: "Bantuan relawan",
      catVolunteerDesc: "Saat butuh transportasi, penitipan atau bantuan di tempat",
      injuredTitle: "Laporan kucing terluka",
      injuredDesc: "Bagikan kondisi kucing yang kamu lihat. Kami akan segera memberi tahu relawan.",
      injuredPlaceholder: "Kondisi, perilaku, luka kucing…",
      severityLabel: "Tingkat kedaruratan",
      severityHigh: "Darurat",
      severityMid: "Sedang",
      severityLow: "Rendah",
      foodTitle: "Butuh makanan / air",
      foodDesc: "Buat laporan untuk kucing di area ini yang butuh makanan atau air.",
      needLabel: "Yang dibutuhkan",
      needFood: "Makanan",
      needWater: "Air",
      needBoth: "Keduanya",
      foodPlaceholder: "Mis. kucing jalanan di area ini butuh dukungan makanan…",
      dangerTitle: "Area berbahaya",
      dangerDesc: "Ada area yang berisiko bagi kucing? Beri tahu pencinta hewan lain.",
      dangerTypeLabel: "Jenis bahaya",
      multiSelect: "Bisa pilih lebih dari satu",
      dTraffic: "Risiko lalu lintas",
      dConstruction: "Konstruksi / pembongkaran",
      dPoison: "Risiko keracunan",
      dViolence: "Kekerasan / penganiayaan",
      dWell: "Sumur / ruang tertutup",
      dOther: "Lainnya",
      dangerPlaceholder: "Tambahkan detail tentang area ini…",
      volunteerTitle: "Bantuan relawan",
      volunteerDesc: "Kucing ini butuh relawan? Bagikan permintaanmu agar relawan terdekat tahu.",
      supportLabel: "Jenis bantuan",
      sTransport: "Transportasi",
      sFoster: "Penitipan sementara",
      sOnSite: "Bantuan di tempat",
      sVet: "Dukungan dokter hewan",
      sSupplies: "Perlengkapan",
      sOther: "Lainnya",
      volunteerPlaceholder: "Bantuan seperti apa yang dibutuhkan?",
      photoLabel: "Tambah foto",
      optional: "(opsional)",
      photoTake: "Kamera",
      photoPick: "Pilih dari galeri",
      photoMax: "Maksimal {n} foto",
      descLabel: "Keterangan",
      minChars: "Minimal {n} karakter",
      continue: "Lanjut",
      back: "Kembali",
      locTitle: "Pilih lokasi",
      locDesc: "Tandai lokasi kucing di peta atau gunakan lokasimu saat ini.",
      locUseCurrent: "Gunakan lokasiku",
      locAddress: "Alamat (opsional)",
      locNeighborhood: "Lingkungan",
      locNeighborhoodHint: "mis. Moda, Kadıköy",
      locUnsupported: "Peramban ini tidak mendukung lokasi.",
      locDenied: "Izin lokasi ditolak. Ketik nama lingkungan saja.",
      locFailed: "Lokasi tidak bisa diambil. Ketuk peta atau ketik lingkungan.",
      okNotified: "relawan diberi tahu",
      errSend: "Laporan tidak terkirim. Coba lagi.",
      errLogin: "Masuk dulu untuk mengirim. Formulirmu tersimpan.",
      errNetwork: "Tidak ada koneksi. Periksa jaringan lalu coba lagi.",
      errList: "Daftar tidak bisa dimuat.",
      retry: "Coba lagi",
      detayGiris: "Masuk untuk melihat laporan ini.",
      detayYok: "Laporan ini sudah tidak ada.",
      detayKonumsuz: "Tidak ada lokasi yang tersimpan pada laporan ini.",
      detayBildiren: "Dilaporkan oleh {ad}",
      detaySilmeYok: "Menghapus laporan sendiri belum tersedia.",
      locRequired: "Lokasi wajib diisi agar penolong bisa menemukan tempatnya.",
      reviewTitle: "Periksa laporanmu",
      reviewDesc: "Sudah benar semua? Periksa sebelum mengirim.",
      rType: "Jenis",
      rLocation: "Lokasi",
      rPhotos: "Foto ({n})",
      submit: "Kirim laporan",
      okTitle: "Laporan terkirim!",
      okDesc: "Terima kasih! Relawan terdekat sudah diberi tahu. Kamu bisa memantaunya.",
      okMap: "Lihat di peta",
      okList: "Lihat kasus darurat",
      okAgain: "Buat laporan lain",
      okAvgLabel: "Rata-rata waktu tanggap",
      okAvgMinutes: "{n} menit",
      listTitle: "Kasus darurat",
      fAll: "Semua",
      fInjured: "Terluka",
      fFood: "Makanan / air",
      fDanger: "Bahaya",
      fVolunteer: "Relawan",
      badgeHigh: "DARURAT",
      badgeMid: "SEDANG",
      badgeLow: "RENDAH",
      badgeNew: "BARU",
      listEmpty: "Tidak ada kasus darurat terbuka di dekatmu — kabar baik.",
      errNoCategory: "Pilih dulu apa yang kamu laporkan.",
      sending: "Mengirim…",
      kaydet: "Simpan",
      kaydedildi: "Tersimpan",
      kaydettiklerim: "Tersimpan",
      kaydetHata: "Gagal diproses.",
      kaydetBos: "Belum ada yang disimpan. Ketuk hati pada kasus untuk mengikutinya.",
      kaydetGiris: "Masuk untuk menyimpan kasus.",
      yakinFiltre: "Menampilkan kasus dalam radius {n} km.",
      tumunuGoster: "Tampilkan semua",
      yakiniGoster: "Hanya yang dekat",
      tumVakalar: "Semua kasus terbuka ditampilkan.",
    },
    footer: {
      discover: "Jelajahi",
      community: "Komunitas",
      connect: "Terhubung",
      brandDescription:
        "Kami menyatukan relawan, titik perawatan, dan bantuan darurat untuk membantu kucing liar hidup lebih aman, lebih sehat, dan lebih dicintai.",
    },
    assistant: {
      name: "Asisten Paw",
      readyStatus: "Siap · gratis dulu",
      napStatus: "Tidur siang sampai besok 😴",
      placeholder: "Tanya aku soal kucing jalanan…",
      napHint: "Aku lagi tidur siang — sampai jumpa besok!",
      suggestionsTitle: "Coba salah satu ini:",
      disclaimer: "Saran AI · bukan pengganti dokter hewan",
      networkError: "Jaringan lagi rewel nih 🐾 coba lagi sebentar.",
      guestPrompt: "Hai! Aku senang mengobrol, tapi aku perlu mengenalimu dulu. Masuk atau bergabung — gratis dan cuma butuh beberapa detik.",
      guestLoginCta: "Masuk untuk mengobrol denganku",
    },
    nav: {
      home: "Beranda",
      database: "Basis Data Hewan",
      identify: "Identifikasi AI",
      identifyShort: "Pindai AI",
      map: "Peta",
      sightings: "Laporkan darurat",
      emergencies: "Laporkan darurat",
      volunteers: "Relawan",
      feeding: "Titik Pemberian Makan",
      leaderboard: "Papan Peringkat",
      photoContest: "Kontes Foto",
      badges: "Lencana",
      safety: "Keamanan",
      observationHouse: "Rumah Observasi",
      neighborhood: "Lingkunganku",
      messages: "Pesan",
      notifications: "Notifikasi",
      dashboard: "Dasbor",
      profile: "Profil",
      login: "Masuk",
      register: "Bergabung",
      logout: "Keluar",
      addCat: "Tambah kucing",
      admin: "Admin",
      adminEmergencies: "Antrean Darurat",
    },
    landing: {
      overline: "Kesejahteraan Hewan Komunitas",
      heroTitle: "Setiap kucing jalanan punya cerita.",
      heroSubtitle:
        "Kenali, pantau, dan bantu lindungi mereka.",
      liveBadge: "Komunitas langsung",
      ctaPrimary: "Daftarkan kucing",
      ctaSecondary: "Basis Data Kucing",
      statCats: "Kucing terdata",
      statSightings: "Darurat dilaporkan",
      statFeeding: "Titik pemberian makan",
      statVaccinated: "Divaksinasi",
      featuresOverline: "Yang bisa kamu lakukan",
      f1Title: "Identifikasi foto AI",
      f1Desc:
        "Unggah foto dan AI kami akan menjelaskan bulu, ras, usia, dan ciri khasnya — lalu mencocokkannya dengan basis data lokal.",
      f2Title: "Peta lingkungan",
      f2Desc: "Tandai hewan, kondisi darurat, dan titik pemberian makan di seluruh kotamu. Saring berdasarkan distrik, warna, atau status kesehatan.",
      f3Title: "Kesehatan & vaksinasi",
      f3Desc:
        "Pantau rabies, FVRCP, sterilisasi, dan catatan. Dukung relawan lokal dan dokter hewan dengan rekaman nyata.",
      f4Title: "Laporan darurat",
      f4Desc:
        "Melihat anak kucing dalam masalah? Jatuhkan penanda lokasi — atau tambahkan foto dengan lokasi — dalam 30 detik dan beri tahu relawan serta dokter hewan terdekat.",
      finalCta: "Bantu kucing di lingkunganmu hari ini.",
      finalCtaSub: "Cukup satu foto untuk memulai.",
    },
    auth: {
      loginTitle: "Selamat datang kembali",
      registerTitle: "Buat akunmu",
      registerSuccess: "Akun berhasil dibuat!",
      email: "Email",
      password: "Kata sandi",
      name: "Nama lengkap",
      role: "Jenis akun",
      rolePublic: "Publik — Jelajahi dan laporkan kondisi darurat",
      roleVolunteer: "Relawan — Kelola kucing & titik pemberian makan",
      roleAdmin: "Admin — Akses penuh",
      volunteerType: "Spesialisasi relawan",
      volunteerTypeVet: "Relawan dokter hewan — merawat kucing liar yang terluka",
      volunteerTypeIndividual: "Relawan perorangan — memberi makan, mengasuh, menyelamatkan",
      submitLogin: "Masuk",
      submitRegister: "Buat akun",
      switchToRegister: "Belum punya akun? Bergabung",
      switchToLogin: "Sudah punya akun? Masuk",
      forgotPassword: "Lupa kata sandimu?",
      forgotLink: "Lupa kata sandi",
      backToLogin: "Kembali ke masuk",
      forgotTitle: "Setel ulang kata sandimu",
      forgotIntro: "Masukkan emailmu dan kami akan mengirimkan tautan untuk membuat kata sandi baru.",
      forgotSubmit: "Kirim tautan setel ulang",
      forgotSentBody: "Jika kami menemukan akun dengan email itu, tautan setel ulang sedang dikirim. Periksa kotak masukmu (dan folder spam).",
      forgotDevTitle: "Mode dev",
      forgotDevBody: "Email belum dikonfigurasi — gunakan tautan di bawah untuk melanjutkan:",
      newPassword: "Kata sandi baru",
      confirmPassword: "Konfirmasi kata sandi",
      resetTitle: "Buat kata sandi baru",
      resetIntro: "Pilih yang mudah kamu ingat — minimal 8 karakter.",
      resetSubmit: "Perbarui kata sandi",
      resetSuccess: "Kata sandi diperbarui. Kamu bisa masuk sekarang.",
      resetFailed: "Ada yang salah — minta tautan setel ulang baru.",
      resetMinLen: "Kata sandi harus minimal 8 karakter.",
      resetMismatch: "Kata sandi tidak cocok.",
      resetNoToken: "Tautan ini tidak memiliki token. Minta email setel ulang yang baru.",
      invalid: "Kredensial tidak valid",
      emailTaken: "Email sudah terdaftar",
    },
    cats: {
      title: "Basis Data Kucing",
      loadMore: "Muat lebih banyak",
      subtitle: "Jelajahi setiap kucing yang terdata di komunitas kami.",
      search: "Cari berdasarkan nama, warna, ras…",
      filterNeighborhood: "Lingkungan",
      filterHealth: "Status kesehatan",
      add: "Tambah kucing",
      empty: "Belum ada kucing. Jadilah yang pertama menambahkan!",
      details: "Detail",
      visits: "kunjungan",
      aiPortraitShortcut: "Buat potret AI",
      aiPortraitRegenerate: "Buat ulang potret AI",
      aiPortraitShortcutHint: "Hasilkan potret bergaya studio dari foto asli kucing ini",
      aiBadgeHasTooltip: "Potret AI siap",
      aiBadgeMissingTooltip: "Belum ada potret AI — buka dan hasilkan satu",
      health: {
        healthy: "Sehat",
        minor_issues: "Masalah ringan",
        needs_attention: "Perlu perhatian",
        critical: "Kritis",
        injured: "Terluka",
        sick: "Sakit",
        recovering: "Dalam pemulihan",
        unknown: "Tidak diketahui",
      },
    },
    addCat: {
      title: "Tambah kucing",
      subtitle: "Unggah foto — AI kami akan mengisi detailnya untukmu.",
      uploadCta: "Unggah foto",
      analyzing: "Menganalisis foto dengan AI…",
      analyzeAgain: "Analisis ulang",
      name: "Nama",
      color: "Warna",
      pattern: "Pola",
      breed: "Ras",
      age: "Perkiraan usia",
      size: "Ukuran",
      features: "Ciri khas",
      neighborhood: "Lingkungan / Distrik",
      lat: "Lintang",
      lng: "Bujur",
      health: "Status kesehatan",
      spayed: "Sudah disterilkan",
      notes: "Catatan",
      save: "Simpan kucing",
      success: "Kucing tersimpan!",
      error: "Tidak dapat menyimpan kucing",
      aiError: "Analisis AI gagal",
      tooLarge: "Foto terlalu besar (maks 6 MB). Silakan pilih potongan yang lebih kecil.",
      invalidFields: "Bidang wajib kosong atau tidak valid (lokasi / nama).",
      serverDown: "Tidak dapat membuat profil — silakan periksa lokasi dan bidang wajibmu.",
    },
    identify: {
      // TUR-10: bu yedi anahtar ayri bir 'identify' blogundaydi.
      // Ayni nesnede ayni anahtar iki kez yazilinca JS ikincisini
      // alir ve birincisini TAMAMEN atar — birlestirmez. Sonuc:
      // AI Kimlik ekraninda kullanici 'identify.registerCat' gibi
      // ham anahtarlar goruyordu. Uc dilde de boyleydi.
      registerCat: "Daftarkan kucing ini",
      registerDog: "Daftarkan anjing ini",
      noMatch: "Tidak ada kecocokan",
      noMatchCat: "Kucing ini mungkin sudah terdaftar dan kami melewatkannya.",
      noMatchDog: "Anjing ini mungkin sudah terdaftar dan kami melewatkannya.",
      reallyNewCat: "Ini memang kucing baru",
      reallyNewDog: "Ini memang anjing baru",

      title: "Identifikasi AI",
      subtitle:
        "Unggah foto kucing liar. AI kami akan menjelaskannya dan mencari kemungkinan kecocokan di basis data.",
      upload: "Unggah foto kucing",
      analyze: "Analisis & cocokkan",
      analyzing: "AI sedang melihat foto…",
      neighborhood: "Saring berdasarkan lingkungan (opsional)",
      analysis: "Analisis AI",
      matches: "Kemungkinan kecocokan",
      noMatches: "Tidak ada kucing serupa yang ditemukan. Ingin menambahkan ini sebagai kucing baru?",
      addAsNew: "Simpan sebagai kucing baru",
      score: "Kemiripan",
      topMatch: "Kecocokan terbaik",
      otherMatches: "Kemungkinan kecocokan lain",
      matchFound: "Kecocokan ditemukan",
      health: {
        title: "Penilaian kesehatan",
        statusLabel: "Status kesehatan",
        urgencyLabel: "Prioritas",
        issuesLabel: "Tanda yang terlihat",
        adviceLabel: "Apa yang harus dilakukan sekarang",
        bodyLabel: "Kondisi tubuh",
        statuses: {
          healthy: "Sehat",
          minor_issues: "Masalah ringan",
          needs_attention: "Perlu perhatian",
          critical: "Kritis",
          unknown: "Tidak dapat dinilai",
        },
        urgencies: {
          URGENT: "Darurat — ke dokter hewan sekarang",
          MEDIUM: "Sedang — ke dokter hewan dalam 1–3 hari",
          STABLE: "Stabil",
          UNKNOWN: "Tidak jelas",
        },
        body: {
          emaciated: "Sangat kurus",
          thin: "Kurus",
          ideal: "Ideal",
          overweight: "Kelebihan berat",
          unknown: "Tidak diketahui",
        },
        disclaimer: "Hanya penilaian AI — bukan pengganti dokter hewan.",
      },
    },
    liveIdentify: {
      overline: "AI Langsung",
      title: "Arahkan kameramu ke seekor kucing",
      subtitle: "Arahkan ke wajah kucing liar — AI kami akan mengenalinya dari umpan kamera langsungmu dan mencocokkannya dengan basis data.",
      tryLive: "Coba dengan kamera",
      startCamera: "Mulai kamera",
      stopCamera: "Hentikan kamera",
      scanNow: "Pindai sekarang",
      pauseAuto: "Jeda pindai otomatis",
      resumeAuto: "Lanjutkan pindai otomatis",
      autoOn: "Memindai otomatis",
      autoOff: "Mode manual",
      cameraOff: "Kamera mati — ketuk Mulai untuk memulai.",
      aimAtCat: "Arahkan ke wajah kucing dan AI akan mulai menganalisis…",
      startToBegin: "Ketuk Mulai kamera untuk memulai.",
      freeHint: "Paket gratis: ketuk Pindai sekali per percobaan (memakai 1 dari 10 panggilan AI bulananmu). Tingkatkan untuk pindai otomatis tanpa henti.",
      upsell: "Pindai otomatis dengan Premium",
    },
    scanCat: {
      overline: "Pemandu Profil AI",
      title: "Foto paspor kucing",
      subtitle: "Arahkan kamera ke kucing. AI memeriksa basis data terlebih dahulu, lalu memandumu melalui tiga sudut — depan, samping, dan atas — untuk membangun profil yang sempurna.",
      navLabel: "Pindai kucing",
      startCamera: "Mulai kamera",
      startCameraNative: "Ambil foto (3)",
      pickFromGallery: "Pilih 3 foto dari galeri",
      galleryFallbackHint: "Jika kamera tidak terbuka, kamu tetap bisa membuat profil dengan memilih 3 foto dari galerimu.",
      galleryPrimaryHint: "Pilih 3 foto dari galerimu untuk membuat profil kucing. Pencocokan AI, pangkas otomatis, dan potret Premium semuanya bekerja dengan cara yang sama.",
      galleryNeed3: "Silakan pilih tepat 3 foto.",
      tryCameraBeta: "Coba kamera",
      cameraUnavailableFallback: "Kamera tidak dapat dibuka, kamu bisa memilih 3 foto dari galerimu.",
      startUnavailableFallback: "Kamera tidak dapat dimulai, kamu bisa memilih foto dari galerimu.",
      partialFallback: "Hanya {n}/3 foto yang terambil. Melanjutkan dengan pemilih galeri untuk sisanya.",
      cameraCannotOpen: "Kamera tidak dapat dibuka. Silakan periksa aplikasi kameramu atau pilih foto dari galeri.",
      nativeCaptureStep: "Foto terambil",
      nativeAngleStart: "Sekarang mengambil sudut",
      partialCaptures: "{n}/3 foto terambil. Ketuk untuk mengulang sudut yang tersisa.",
      buttonTapped: "Tombol kamera diketuk",
      stopCamera: "Hentikan kamera",
      cameraDenied: "Izin kamera ditolak. Silakan berikan akses kamera untuk situs ini di pengaturan browsermu.",
      cameraDeniedNative: "Izin kamera ditolak. Silakan aktifkan izin kamera di pengaturan ponselmu.",
      cameraDeniedPWA: "Izin kamera tidak bisa diperoleh di aplikasi yang terpasang. Kamu bisa membukanya di Chrome untuk memindai dengan kamera.",
      cameraUnsupported: "Perangkat ini tidak mendukung API kamera.",
      cameraGenericError: "Tidak dapat mengakses kamera.",
      openInChrome: "Buka di Chrome",
      openAppSettings: "Buka pengaturan",
      cameraRetry: "Coba lagi",
      cameraWaiting: "Menunggu izin kamera…",
      matching: "Mencari di basis data…",
      matchFound: "Kecocokan ditemukan!",
      matchPrompt: "Apakah ini kucing yang sama?",
      matchYes: "Ya, buka profil",
      matchNo: "Tidak, profil baru",
      notFound: "Profil tidak ditemukan",
      notFoundSub: "Mari buat yang baru — tiga sudut, dipandu AI.",
      startWizard: "Mulai pindai sekarang",
      stages: {
        front: "Tampak depan",
        side: "Tampak samping",
        top: "Tampak atas",
      },
      stageHint: {
        front: "Arahkan kamera lurus ke wajah kucing. Kedua mata harus terlihat.",
        side: "Bergeser ke samping — profil penuh, hanya satu mata/telinga yang terlihat.",
        top: "Pegang kamera di atas kepala, memandang ke bawah ke arah tempurung kepala.",
      },
      captured: "Berhasil diambil!",
      retake: "Ambil ulang",
      tooFast: "Tahan yang stabil…",
      holdCatOnScreen: "Jaga kucing tetap dalam bingkai",
      timeoutCloser: "Mendekat sedikit",
      timeoutSteady: "Tahan kamera agar stabil",
      timeoutLight: "Cahaya kurang — pindah ke tempat yang lebih terang",
      timeoutShade: "Terlalu silau — pindah ke tempat teduh",
      collectingFrames: "Memilih bingkai terbaik…",
      locationCaptured: "Lokasi berhasil diambil",
      locationNeighborhoodOnly: "Lanjut dengan lingkungan",
      locationMissing: "Tidak ada lokasi — ambil GPS atau ketik nama lingkungan",
      locationRequired: "Silakan ambil GPS atau ketik nama lingkungan.",
      locating: "Mengambil lokasi…",
      gpsRefresh: "Segarkan GPS",
      gpsLocked: "Lokasi berhasil diambil",
      gpsDenied: "Izin lokasi ditolak. Izinkan di pengaturan ponsel atau ketik nama lingkungan.",
      gpsUnavailable: "Tidak bisa mendapatkan lokasi (tidak ada sinyal). Kamu bisa lanjut dengan nama lingkungan.",
      gpsTimeout: "Permintaan lokasi habis waktu. Kamu bisa lanjut dengan nama lingkungan.",
      gpsError: "Tidak bisa mendapatkan lokasi. Kamu bisa lanjut dengan nama lingkungan.",
      gpsUnsupported: "Perangkat ini tidak mendukung layanan lokasi.",
      autoStarting: "Kucing terdeteksi, pemindaian dimulai…",
      autoDetectActive: "Mendeteksi kucing… Jaga wajahnya tetap dalam bingkai. Pemindaian dimulai otomatis.",
      autoProfileTitle: "Foto Profil Otomatis",
      autoFree: "Gratis",
      autoProcessing: "Memproses…",
      premiumOnly: "Premium",
      aiPortraitSectionTitle: "Potret Profil AI",
      aiPortraitOpenBtn: "Buat potret AI",
      aiPortraitFreeHint: "Anggota Premium bisa membuat potret AI yang menghadap depan dari foto-fotomu.",
      aiPortraitTitle: "Buat potret AI",
      aiPortraitOverline: "Fitur premium",
      aiPortraitBody: "AI membuat SATU potret menghadap depan dengan menggunakan 3 foto asli kamu sebagai acuan. Identitas (warna, corak, mata) dipertahankan. Ini memakai 1 kredit potret AI.",
      aiPortraitLoading: "Memuat info kredit…",
      aiPortraitQuota: "Sisa kredit",
      aiPortraitTierLabel: "Tingkatan",
      aiPortraitFreeNotice: "Potret AI hanya untuk anggota Premium / Super Premium. Foto profil otomatis gratis sudah dibuatkan.",
      aiPortraitConfirm: "Buat",
      aiPortraitGenerating: "Membuat…",
      aiPortraitReady: "Potret AI siap!",
      aiPortraitCached: "Dari cache — tidak memakai kredit.",
      aiPortraitFailed: "Tidak bisa membuat potret AI.",
      aiPortraitNotEnoughPhotos: "Foto berkualitas kurang untuk potret AI. Dekatkan kucing dan buat lebih tajam.",
      aiBadge: "AI",
      originalPhotosTitle: "Foto Asli",
      blockedAi: "Gambar buatan AI diblokir.",
      blockedInappropriate: "Gambar tidak pantas terdeteksi.",
      finalize: "Selesaikan profil",
      finalizeTitle: "Satu hal terakhir",
      finalizeSub: "Beri nama untuk kucingnya (dan nama lingkungan jika kamu tahu).",
      submit: "Buat profil",
      submitting: "Menyimpan…",
      done: "Profil dibuat!",
      doneSub: "Ketiga sudut sudah ditambahkan ke galeri.",
      goProfile: "Buka profil",
      manualCapture: "Ambil sekarang",
      angleOf: "Sudut {n} dari 3",
      voiceOn: "Suara aktif",
      voiceOff: "Suara mati",
      directions: {
        left: "Geser ke kiri",
        right: "Geser ke kanan",
        up: "Miringkan ke atas",
        down: "Miringkan ke bawah",
        closer: "Mendekat",
        farther: "Mundur",
        center: "Sempurna, tahan diam",
        unclear: "Arahkan ke wajah kucing",
      },
      overallProgress: "Kemajuan profil",
      bestPhotoChosen: "AI memilih foto depan terbaik sebagai profil",
      detectorLoading: "Memuat AI di perangkat… (unduhan sekali saja)",
      detectorReady: "AI di perangkat siap · pemindaian tanpa biaya",
    },
    thinking: {
      title: "AI sedang berpikir",
      hint: "Biasanya butuh 5–15 detik. Kamu tetap bisa menggunakan aplikasi.",
      stages: {
        compress: "Menyiapkan fotomu…",
        vision: "Mengamati kucing dengan GPT-Vision…",
        matching: "Mencari kucing serupa di basis data…",
        polishing: "Merapikan hasilnya…",
      },
    },
    safety: {
      overline: "Kepedulian Komunitas",
      title: "Panduan Keselamatan Kucing",
      subtitle:
        "Sebelum kamu mendekati, memberi makan, atau menyelamatkan kucing jalanan — baca panduan singkat dan praktis ini. Panduan ini melindungi kamu maupun kucingnya.",
      disclaimerTitle: "Sanggahan medis",
      disclaimerText:
        "Halaman ini adalah edukasi keselamatan umum untuk relawan komunitas, bukan nasihat medis. Untuk gigitan, cakaran dalam, bengkak, atau demam setelah kontak dengan kucing, mohon konsultasikan ke tenaga kesehatan yang berkualitas dalam 24 jam.",
      footerNote:
        "Jaga keselamatanmu di luar sana. Hal terbaik yang bisa kamu lakukan untuk kucing liar adalah menjaga dirimu tetap sehat agar bisa terus membantu.",
      before: {
        eyebrow: "Sebelum mendekat",
        title: "Dekati dengan aman",
        intro:
          "Sebagian besar kucing jalanan di Istanbul toleran terhadap manusia, tetapi setiap kucing punya batas. Membaca bahasa tubuh dan bergerak perlahan mencegah 90% insiden.",
        items: [
          {
            h: "Bergerak perlahan, kedipkan mata lembut, jangan pernah menjangkau di atas kepala",
            p: "Berjongkoklah rendah, ulurkan jari dan biarkan kucing menciumnya lebih dulu. Tangan langsung ke atas tempurung kepala memicu tepukan defensif. Kedipan pelan adalah bahasa tubuh kucing untuk 'Aku bukan ancaman'.",
          },
          {
            h: "Baca ekor dan telinganya",
            p: "Ekor mengibas, telinga rata, tubuh menyamping, geraman rendah = mundur. Ekor tegak dengan sedikit lengkungan di ujung = sapaan ramah.",
          },
          {
            h: "Beri makan dari jarak jauh pada kali pertama",
            p: "Letakkan makanan lalu mundur 1–2 meter. Biarkan kucing makan tanpa kamu membungkuk di atasnya. Memberi makan dengan tangan hanya untuk kucing yang sudah memilih untuk memercayaimu.",
          },
          {
            h: "Jangan pernah menjangkau anak-anak kucing dari induknya",
            p: "Bahkan induk paling ramah pun akan melindungi anak-anaknya. Amati dari jarak minimal 2 meter selama beberapa hari; jika anak kucing sakit atau ditinggalkan, hubungi dokter hewan atau relawan.",
          },
          {
            h: "Kenakan sepatu tertutup dan lengan panjang saat menyelamatkan",
            p: "Kucing yang terluka atau terjebak akan panik. Kain tebal melindungi dari cakaran dalam. Bawa handuk — membungkus kucing mengurangi stres bagi kalian berdua.",
          },
        ],
      },
      scratch: {
        eyebrow: "Setelah dicakar",
        title: "Jika kucing mencakarmu",
        intro:
          "Sebagian besar cakaran dangkal sembuh dengan bersih. Namun cakar kucing membawa bakteri seperti Bartonella henselae (Penyakit Cakaran Kucing). Mencuci dengan cepat dan menyeluruh mencegah sebagian besar infeksi.",
        items: [
          {
            h: "Bilas di bawah air mengalir selama 5+ menit",
            p: "Tahan luka di bawah air keran yang sejuk atau suam-suam kuku dan biarkan berdarah bebas selama satu-dua menit — pendarahan membantu membilas bakteri keluar.",
          },
          {
            h: "Cuci dengan sabun selama minimal 15 menit",
            p: "Gunakan sabun biasa, tidak perlu yang istimewa. Busakan menyeluruh di dalam dan sekitar luka.",
          },
          {
            h: "Hentikan pendarahan dengan kasa bersih",
            p: "Tekan lembut dengan kasa steril atau kain bersih. Jangan gunakan kapas — seratnya menempel di luka.",
          },
          {
            h: "Oleskan antiseptik dan tutup longgar",
            p: "Povidone-iodine (Batticon) atau chlorhexidine bekerja dengan baik. Gunakan pembalut anti lengket dan ganti setiap hari.",
          },
          {
            h: "Temui dokter dalam 24 jam jika ada yang tampak tidak beres",
            p: "Kemerahan menyebar, bengkak, nanah, demam, kelenjar getah bening membengkak — pergilah ke Aile Sağlığı Merkezi atau rumah sakit terdekat. Sebutkan tanggal cakaran dan apakah kucing tampak sehat.",
          },
        ],
        warning:
          "Jika suntikan tetanus terakhirmu lebih dari 10 tahun lalu — atau kamu tidak ingat — minta suntikan baru. Gratis di Aile Sağlığı Merkezi mana pun.",
      },
      bite: {
        eyebrow: "Setelah digigit",
        title: "Jika kucing menggigitmu",
        intro:
          "Gigitan lebih serius daripada cakaran. Gigi kucing menembus dalam dan menyuntikkan bakteri ke jaringan. Di Turki, pencegahan rabies juga hal yang nyata perlu dipertimbangkan. Perlakukan setiap gigitan sebagai peristiwa medis.",
        items: [
          {
            h: "Bilas 15+ menit di bawah air mengalir",
            p: "Ini adalah langkah terpenting. Membilas lama dan kuat sangat menurunkan risiko infeksi dan rabies. Tekan area itu lembut untuk mendorong pendarahan.",
          },
          {
            h: "Cuci dengan sabun dan oleskan antiseptik",
            p: "Sabun + air selama minimal 15 menit, lalu povidone-iodine. Jangan dibalut ketat — luka gigitan butuh udara.",
          },
          {
            h: "Pergi ke rumah sakit atau sağlık ocağı di hari yang sama",
            p: "Dokter akan menilai kedalaman, lokasi, dan risiko infeksi. Antibiotik hampir selalu diresepkan untuk gigitan kucing.",
          },
          {
            h: "Tanyakan tentang pencegahan rabies",
            p: "Jika kucingnya tidak dikenal, berperilaku tidak normal, atau tidak bisa diamati selama 10 hari, vaksinasi rabies pascapaparan mungkin diperlukan. Ini gratis di rumah sakit negeri di Turki.",
          },
          {
            h: "Jika kamu mengenal kucingnya, awasi terus",
            p: "Kucing peliharaan atau kucing komunitas yang sehat yang menggigitmu sebaiknya diamati selama 10 hari. Jika tetap sehat, rabies bisa disingkirkan. Catat lokasi dan foto kucing di aplikasi ini untuk membantu jika diperlukan.",
          },
        ],
        warning:
          "Gigitan di tangan, wajah, atau dekat sendi berisiko lebih tinggi. JANGAN menunda — pergilah ke unit gawat darurat (acil servis) di hari yang sama.",
      },
      contacts: {
        eyebrow: "Darurat",
        title: "Nomor telepon yang perlu diketahui",
        subtitle: "Di Turki, saluran ini gratis, 24/7, dan dijawab dalam bahasa Turki (bahasa Inggris sering tersedia di 112).",
        l112: "Acil Çağrı Merkezi · ambulans, pemadam kebakaran, polisi",
        l184: "Sağlık Bakanlığı · saluran nasihat kesehatan",
        l153: "Belediye · layanan hewan kota",
      },
      ctaTitle: "Apakah kamu dicakar atau digigit?",
      ctaSubtitle: "Catat insidennya agar kamu ingat mengikuti masa pengamatan rabies 10 hari — dan agar komunitas bisa membantu jika diperlukan.",
      ctaScratch: "Aku dicakar",
      ctaBite: "Aku digigit",
    },
    incident: {
      title: "Catat sebuah insiden",
      subtitle: "Mulai masa pengamatan 10 hari. Kamu bisa mengubah statusnya nanti (kucing sehat, muncul gejala, perawatan selesai).",
      types: { scratch: "Cakaran", bite: "Gigitan" },
      severity: "Tingkat keparahan",
      sev: { mild: "Ringan — dangkal", moderate: "Sedang — berdarah", severe: "Parah — dalam / banyak" },
      cat: "Kucing yang mana (opsional)",
      catPlaceholder: "Pilih kucing atau biarkan tidak diketahui",
      catHint: "Menautkan kucingnya membantu komunitas memantau pengamatan 10 hari secara publik.",
      unknownCat: "Tidak diketahui / kucing liar yang tidak kukenal",
      date: "Tanggal insiden",
      location: "Di mana (opsional)",
      locationPlaceholder: "mis. nama lingkungan dan jalan",
      notes: "Catatan",
      notesPlaceholder: "Apa yang terjadi? Apakah kamu mencuci lukanya? Ada gejala?",
      photo: "Foto luka atau kucing (opsional)",
      privacyNote: "Catatan dan fotomu bersifat pribadi hanya untukmu. Hanya kucing yang ditautkan (jika ada) yang mendapat lencana 'sedang diamati' publik dan penghitung — tidak ada data pribadi yang terbuka.",
      useGps: "Gunakan lokasiku (memberi tahu komunitas terdekat)",
      gpsCaptured: "Lokasi berhasil diambil. Kami akan memberi tahu anggota komunitas terdekat.",
      gpsHint: "Relawan di area ini akan melihat ini di notifikasi mereka.",
      submit: "Mulai pengamatan 10 hari",
      submitting: "Menyimpan…",
      created: "Insiden dicatat. Pengamatan 10 hari dimulai.",
      error: "Tidak bisa menyimpan insiden.",
      loginRequired: "Silakan masuk terlebih dahulu.",
      observation: {
        title: "Pengamatan aktif",
        subtitle: "Perhatikan perilaku tidak biasa pada dirimu ATAU kucing selama 10 hari setelah gigitan/cakaran.",
        empty: "Tidak ada pengamatan aktif.",
        daysLeft: "Tersisa {n} hari",
        complete: "Pengamatan selesai",
        scratchOn: "Cakaran",
        biteOn: "Gigitan",
        markHealthy: "Kucing tetap sehat",
        markSymptoms: "Gejala muncul",
        markResolved: "Perawatan selesai",
        statusUpdated: "Status diperbarui.",
        statuses: {
          observing: "Mengamati",
          cat_healthy: "Kucing tetap sehat ✓",
          symptoms_appeared: "Gejala muncul",
          incident_developed: "Muncul komplikasi",
          incident_resolved: "Perawatan selesai ✓",
        },
        bannerObserving: "{n} pengamatan aktif",
        bannerObservingPlural: "{n} pengamatan aktif",
        notified: "tetangga diberi tahu",
        feedbackList: "Masukan komunitas",
      },
      catBadge: {
        observing: "Sedang diamati",
        sub: "{n} anggota komunitas{plural} mengawasi — tersisa {days}h",
      },
      feedbackPage: {
        eyebrow: "Masukan komunitas",
        notFound: "Insiden tidak ditemukan.",
        intro: "Seorang anggota komunitas terdekat melaporkan cakaran/gigitan ini. Jika kamu pernah melihat kucingnya atau tahu sesuatu yang berguna, bagikan pengamatan singkat — ini membantu pelapor menyelesaikan pengamatan 10 hari dengan aman.",
        communityReplies: "balasan komunitas",
        observationLabel: "Apa yang kamu amati?",
        options: {
          cat_seen_healthy: "Aku melihat kucingnya — tampak sehat",
          cat_not_seen: "Aku belum melihat kucingnya",
          cat_seen_concerning: "Aku melihatnya — ada yang tampak tidak beres",
          other: "Pengamatan lain",
        },
        notePlaceholder: "Ada hal lain? Waktu, lokasi, apa yang sedang dilakukan kucing…",
        photo: "Foto kucing (opsional)",
        submit: "Kirim masukan",
        thanks: "Terima kasih sudah membantu komunitas!",
        thanksSub: "Pelapor sudah diberi tahu tentang pengamatanmu.",
        othersTitle: "Pengamatan lainnya",
      },
    },
    observationHouse: {
      overline: "Keamanan Komunitas",
      title: "Rumah Pengamatan",
      subtitle: "Setiap kucing yang sedang dalam masa pengamatan 10 hari setelah laporan cakaran atau gigitan. Bantu tetangga Anda dengan meninggalkan laporan penampakan singkat.",
      allTypes: "Semua",
      neighborhoodPlaceholder: "Saring berdasarkan lingkungan…",
      urgentOnly: "Mendesak saja (≤3 hari)",
      nearbyOn: "Dekat saya",
      nearbyOff: "Terdekat aktif",
      results: "hasil",
      empty: "Tidak ada pengamatan aktif saat ini. 🐾",
      unknownCat: "Kucing tak dikenal",
      locationUnknown: "Lokasi tidak dibagikan",
      loadFailed: "Tidak dapat memuat pengamatan.",
      gpsUnavailable: "Geolokasi tidak didukung.",
      gpsActive: "Menyaring dalam radius 5 km di sekitar Anda.",
      gpsFailed: "Tidak dapat memperoleh lokasi.",
    },
    weekly: {
      eyebrow: "Ringkasan Email",
      title: "Ringkasan Pengamatan Mingguan",
      subtitle: "Setiap Senin pukul 09.00 — ringkasan pribadi berisi insiden dan pengamatan minggu ini di area cakupan Anda.",
      on: "Aktif",
      off: "Nonaktif",
      optedIn: "Ringkasan mingguan diaktifkan.",
      optedOut: "Ringkasan mingguan dinonaktifkan.",
      noScope: "Atur area cakupan di profil Anda terlebih dahulu agar kami bisa menyesuaikan ringkasan dengan lingkungan Anda.",
      statNew: "Insiden baru",
      statActive: "Aktif",
      statUrgent: "Mendesak",
      statFeedbacks: "Umpan balik",
      highlightsTitle: "Kasus aktif teratas minggu ini",
      sendTest: "Kirim uji coba sekarang",
      sending: "Mengirim…",
      scheduleHint: "Dikirim otomatis setiap Senin pukul 09.00 (jika diaktifkan).",
      testSentEmail: "Uji coba terkirim! Periksa kotak masuk Anda.",
      testSentInAppOnly: "Notifikasi dalam aplikasi terkirim. Email dinonaktifkan di server ini.",
      error: "Tidak dapat mengirim ringkasan.",
    },
    map: {
      title: "Peta Hewan",
      subtitle:
        "Setiap penanda adalah kucing yang dikenal, keadaan darurat, atau titik pemberian makan di sekitar kota.",
      layerCats: "Kucing",
      layerSightings: "Keadaan darurat",
      layerFeeding: "Titik pemberian makan",
      layerObservations: "Rumah Pengamatan",
      layerVaccinated: "Divaksinasi",
      layerCommunity: "Komunitas",
      tabMap: "Peta",
      tabNeighborhood: "Lingkungan saya",
      locate: "Cari lokasi",
    },
    sightings: {
      title: "Kasus Darurat",
      subtitle: "Kasus mendesak hewan yang membutuhkan bantuan, dilaporkan oleh komunitas di seluruh kotamu.",
      report: "Laporkan keadaan darurat",
      notes: "Apa yang sedang terjadi?",
      reporter: "Pelapor",
      when: "Kapan",
      submit: "Kirim laporan darurat",
      success: "Terima kasih atas laporannya — relawan di dekat Anda telah diberi tahu.",
      pendingReview: "Terima kasih — laporan darurat Anda sedang ditinjau admin dan akan ditampilkan setelah disetujui.",
      empty: "Belum ada kasus darurat yang dilaporkan.",
      locationOnly: "Atau cukup letakkan penanda lokasi singkat",
      photoAndLocation: "Tambahkan foto untuk membantu petugas mengenali kucing",
    },
    feeding: {
      title: "Titik Pemberian Makan",
      subtitle: "Tempat relawan memberi makan kucing di sekitar kota.",
      add: "Tambah titik pemberian makan",
      name: "Nama titik",
      schedule: "Jadwal (mis. Sen/Rab/Jum 19.00)",
      notes: "Catatan",
      empty: "Belum ada titik pemberian makan.",
      success: "Titik pemberian makan ditambahkan",
      active: "Aktif",
      inactive: "Nonaktif",
      statusLabel: "Status",
      photo: "Foto titiknya",
      findNearest: "Cari terdekat",
      nearestFound: "Titik terdekat adalah",
      nearestBadge: "Terdekat dari Anda",
      noNearest: "Tidak ada titik pemberian makan ditemukan.",
      geoUnsupported: "Geolokasi tidak didukung oleh peramban Anda.",
      geoDenied: "Mohon izinkan akses lokasi untuk menemukan titik terdekat.",
      openDirections: "Buka di peta",
      maintainedBy: "Dikelola oleh",
      requireNameLoc: "Nama dan koordinat GPS wajib diisi",
    },
    dashboard: {
      title: "Dasbor",
      ilkKediBaslik: "Tambahkan kucing pertamamu",
      ilkKediAlt: "Kucing jalanan yang kamu foto membantu orang lain menemukan, memberi makan, dan merawatnya.",
      recentCats: "Baru ditambahkan",
      welcome: "Selamat datang kembali",
      myRole: "Peran Anda",
      quickAdd: "Tambah cepat",
      addCat: "Tambah kucing",
      identify: "Identifikasi AI",
      reportSighting: "Laporkan keadaan darurat",
      addFeeding: "Tambah titik pemberian makan",
    },
    profile: {
      title: "Profil saya",
      settingsTitle: "Pengaturan & bantuan",
      settingsSubtitle: "Kelola akun, notifikasi, bahasa, dan privasi Anda. Semua dalam satu tempat.",
      aiPortraitBannerTitle: "Potret Profil AI",
      aiPortraitBannerRemaining: "Kredit tersisa bulan ini",
      aiPortraitBannerUnlimited: "Tak terbatas",
      aiPortraitBannerCta: "Buat potret",
      aiPortraitBannerUsed: "{used} dari {limit} terpakai",
      aiPortraitBannerExhausted: "Anda telah memakai semua kredit — akan diatur ulang bulan depan.",
      aiCoverageTitle: "Cakupan potret AI komunitas",
      aiCoverageSubtitle: "{with} dari {total} kucing memiliki potret AI",
      aiCoverageEmpty: "Jadilah yang pertama menambahkan potret AI!",
      myAccount: "Akun saya",
      myAccountHint: "Dasbor, kucing saya, kontribusi",
      notificationSettings: "Pengaturan notifikasi",
      notificationSettingsHint: "Apa yang diterima, bagaimana, dan kapan",
      countryTitle: "Negara",
      countryHint: "Komunitas mana yang kamu ikuti",
      languageTitle: "Bahasa",
      languageHint: "Pilih bahasa antarmuka Anda",
      privacyKvkk: "Privasi & perlindungan data",
      privacyKvkkHint: "Hak perlindungan data & cookie",
      terms: "Ketentuan penggunaan",
      termsHint: "Aturan penggunaan aplikasi",
      contact: "Hubungi kami",
      contactHint: "destek@sokakkedileri.tr",
      helpFaq: "Bantuan & FAQ",
      helpFaqHint: "Cara memulai, pertanyaan umum, kontak",
      safetyHint: "Panduan keselamatan kucing jalanan",
      points: "Poin Cakar",
      badgesEarned: "lencana diperoleh",
      seeAllBadges: "Lihat semua lencana",
      nextBadge: "Lencana berikutnya",
      nextLevel: "Level berikutnya",
      nextLevelIn: "Menuju level berikutnya",
      contributions: "kontribusi",
      notificationsTitle: "Notifikasi",
      notificationsHint: "Pembaruan dan balasan terbaru",
      notificationsEmpty: "Belum ada notifikasi",
      seeAllNotifications: "Lihat semua notifikasi",
      settingsGroup: "Pengaturan",
      settingsGroupHint: "Bahasa, tema, izin",
      helpInfoGroup: "Bantuan & info",
      helpInfoHint: "Cara kerja, FAQ, tentang, privasi",
      howItWorks: "Bagaimana cara kerjanya?",
      howItWorksHint: "Temukan fitur dalam 3 langkah",
      aboutUs: "Tentang kami",
      aboutUsHint: "Misi, tim, dan kontak kami",
      themeTitle: "Tema",
      themeHint: "Antarmuka terang atau gelap",
      themeLight: "Terang",
      themeDark: "Gelap",
      locationPermTitle: "Izin lokasi",
      locationPermHint: "Digunakan untuk menyarankan kucing di dekat Anda",
      notifPermTitle: "Izin notifikasi",
      notifPermHint: "Terima peringatan darurat mendesak",
      permRequest: "Izinkan",
      permGranted: "Diizinkan",
      permDenied: "Diblokir",
      pointsShort: "🐾",
      catsDescribed: "Kucing dideskripsikan",
      sightingsReported: "Keadaan darurat dilaporkan",
      feedingSpots: "Titik pemberian makan",
      vaccinations: "Vaksinasi",
      // TUR-10: eskiden bu da `howItWorks` idi ve ayni nesnedeki
      // yardim baglantisinin metnini eziyordu (JS ikinciyi alir).
      // Farkli kavram, farkli anahtar.
      pointsHowItWorks: "Cara penilaian bekerja",
      ruleCat: "Deskripsikan kucing baru: +5 Poin Cakar",
      ruleFeeding: "Tambah titik pemberian makan: +3 Poin Cakar",
      ruleVaccination: "Catat vaksinasi: +10 Poin Cakar",
      ruleSighting: "Laporkan keadaan darurat: +2 Poin Cakar",
      namingNote: "Orang pertama yang mendeskripsikan seekor kucing mendapat hak istimewa untuk menamainya.",
      myCats: "Kucing yang saya deskripsikan",
      noCatsYet: "Anda belum mendeskripsikan kucing apa pun.",
      rank: "Peringkat",
      privilegesTitle: "Hak istimewa yang terbuka di tingkat Anda",
      nextTier: "untuk mencapai",
      keepGoing: "Jadilah yang pertama mengenali seekor kucing — dapatkan 5 Poin Cakar dan buka pita pendeskripsi Anda.",
      photoUpdated: "Foto diperbarui",
      photoError: "Tidak dapat memperbarui foto",
    },
    common: {
      showPassword: "Tampilkan kata sandi",
      hidePassword: "Sembunyikan kata sandi",
      deletePhoto: "Hapus foto",
      cancel: "Batal",
      save: "Simpan",
      delete: "Hapus",
      edit: "Ubah",
      back: "Kembali",
      required: "Wajib",
      loading: "Memuat…",
      locateMe: "Gunakan lokasi saya",
      tryAi: "Coba AI",
      describedBy: "Dideskripsikan oleh",
      optional: "opsional",
    },
    leaderboard: {
      title: "Papan Peringkat Komunitas",
      subtitle: "Jadilah yang pertama mengenali seekor kucing. Naiki peringkat. Buka hak istimewa.",
      contributor: "Kontributor",
      badge: "Tingkat",
      contributions: "Kontribusi",
      points: "Poin Cakar",
      empty: "Belum ada kontributor — jadilah yang pertama!",
      you: "Anda",
      tabVolunteers: "Relawan",
      tabNeighborhoods: "Potret AI lingkungan",
      hoodEmpty: "Belum cukup potret AI — jadilah lingkungan pertama yang memimpin!",
      hoodWithAi: "Potret AI",
      hoodCoverage: "Cakupan",
      hoodTopContributors: "Kontributor teratas minggu ini",
    },
    messenger: {
      title: "Pesan",
      empty: "Belum ada percakapan. Kirim pesan ke relawan dari direktori.",
      selectThread: "Pilih percakapan untuk mulai mengobrol.",
      placeholder: "Ketik pesan…",
      send: "Kirim",
    },
    notifications: {
      title: "Notifikasi",
      subtitle: "Keadaan darurat di dekat Anda, klaim atas laporan Anda, dan pesan baru.",
      empty: "Anda sudah membaca semuanya.",
      markAllRead: "Tandai semua sudah dibaca",
      markRead: "Tandai sudah dibaca",
      open: "Buka",
    },
    severity: {
      minor: "Ringan",
      urgent: "Mendesak",
      critical: "Kritis",
      label: "Tingkat keparahan",
    },
    emergencyActions: {
      claim: "Saya sedang dalam perjalanan",
      claimed: "Diklaim oleh",
      resolve: "Tandai selesai",
      resolved: "Selesai",
      status: "Status",
      open: "Terbuka",
      message: "Pesan",
    },
    emergencyCategories: {
      injured: "Kucing terluka",
      injuredDesc: "Luka terlihat, pincang, atau kesakitan",
      food: "Butuh makanan / air",
      foodDesc: "Ada kucing di sekitar tanpa makanan atau air di dekatnya",
      danger: "Area berbahaya",
      dangerDesc: "Konstruksi, lalu lintas, ancaman cuaca",
      volunteer: "Butuh relawan",
      volunteerDesc: "Butuh bantuan di lapangan (transportasi, penampungan sementara)",
      lost: "Kucing hilang",
      lostDesc: "Cari berdasarkan foto — AI memeriksa setiap kucing yang terdaftar",
    },
    premium: {
      // TUR-10: Profile.jsx `t("premium.active") || "Aktif"` diyordu;
      // anahtar olmayinca Ingilizce ve Endonezce arayuze TURKCE "Aktif" siziyordu.
      active: "Aktif",
      navLink: "Premium",
      title: "Keanggotaan premium",
      subtitle: "AI tak terbatas, tema khusus, lencana verifikasi & titik pemberian makan tersimpan — demi para kucing.",
      priceLine: "Langganan bulanan",
      cta: "Berlangganan Premium",
      comingSoonPlay: "Segera hadir di Google Play",
      comingSoonHint: "Keanggotaan premium akan tersedia begitu aplikasi Android kami hadir di Google Play. Kami akan memberi tahu Anda di sini pada hari peluncuran.",
      ctaActive: "Anda anggota Premium",
      perksHeading: "Apa yang Anda buka",
      perk1: "Identifikasi kucing tak terbatas (tingkat gratis: {limit}/bulan)",
      perk2: "Cakar tak terbatas pada foto kucing",
      perk3: "Pesan langsung antar anggota Premium",
      perk4: "Penempatan prioritas pada laporan darurat",
      perk5: "Lencana Premium — suara Anda lebih diperhitungkan di komunitas",
      perk6: "Daftar untuk program relawan resmi",
      perk7: "Simpan titik pemberian makan favorit",
      activeUntil: "Aktif hingga",
      usedThisMonth: "AI terpakai bulan ini",
      remaining: "tersisa",
      unlimited: "Tak terbatas",
      themeTitle: "Tema",
      themeWarm: "Batu Hangat",
      themeMidnight: "Tengah Malam",
      themeSage: "Sage",
      themeBosphorus: "Biru Laut",
      verificationTitle: "Lencana verifikasi",
      verificationDesc: "Anggota Premium bisa mengajukan permohonan agar organisasinya diverifikasi oleh admin.",
      verificationOrgLabel: "Nama organisasi (pemerintah kota, komunitas, klinik hewan…)",
      verificationRequest: "Ajukan verifikasi",
      verificationPending: "Menunggu tinjauan admin",
      verificationVerified: "Terverifikasi ✓",
      verificationOrgIs: "Terverifikasi sebagai",
      adminApprovals: "Permohonan verifikasi yang menunggu",
      adminApprove: "Setujui",
      adminEmpty: "Tidak ada permohonan verifikasi yang menunggu.",
      paymentSuccess: "Selamat datang di Premium!",
      paymentSuccessSub: "Terima kasih telah mendukung hewan jalanan di kotamu. Semua fitur kamu sudah terbuka.",
      paymentChecking: "Mengonfirmasi pembayaran kamu…",
      paymentExpired: "Sesi pembayaran kamu sudah berakhir. Silakan coba lagi.",
      paymentCancelled: "Pembayaran dibatalkan.",
      paymentCancelSub: "Tidak ada biaya yang dikenakan. Kamu bisa kembali untuk upgrade kapan saja.",
      backHome: "Kembali ke dasbor",
      tryAgain: "Coba lagi",
      checkoutError: "Tidak bisa memulai pembayaran. Silakan coba lagi.",
      quotaReachedTitle: "Batas AI gratis bulanan tercapai",
      quotaReachedSub: "Upgrade ke Premium untuk identifikasi tanpa batas, atau tunggu sampai bulan depan.",
      seePricing: "Lihat Premium",
      favoriteAdd: "Simpan titik",
      favoriteRemove: "Tersimpan",
      favoritePremiumOnly: "Menyimpan titik pemberian makan adalah fitur Premium.",
    },
    lounge: {
      overline: "Lounge Premium",
      title: "Lounge Premium",
      subtitle: "Terhubung dengan sesama pendukung Premium yang menjaga hewan di kotamu tetap aman. Kirim pesan langsung ke siapa saja di sini.",
      memberCount: "anggota premium",
      empty: "Jadilah anggota pertama Lounge Premium!",
      message: "Kirim pesan",
      youAreHere: "Ini kamu",
      vet: "Dokter hewan",
      gatedTitle: "Lounge Premium khusus untuk anggota Premium",
      gatedSub: "Berlangganan Premium untuk bergabung ke lounge dan mengobrol langsung dengan relawan dan dokter hewan terverifikasi.",
    },
    city: {
      generating: "Melukis kotamu…",
      aiGenerated: "Adegan AI",
    },
    coverage: {
      title: "Area cakupan",
      heading: "Tempat kamu merawat",
      subtitle: "Pilih distrik tempat kamu tinggal atau yang kamu rasa jadi tanggung jawabmu. Kami akan memprioritaskan keadaan darurat dan titik pemberian makan di area tersebut.",
      empty: "Kamu belum memilih distrik apa pun. Tambahkan kota di bawah untuk memulai.",
      addCity: "Tambah kota",
      removeCity: "Hapus kota",
      districtsCount: "distrik dipilih",
      save: "Simpan cakupan",
      saved: "Cakupan tersimpan",
    },
    trending: {
      overline: "Aula cakar",
      title: "Kucing minggu ini",
      subtitle: "Kucing liar yang paling banyak dikunjungi saat ini. Setiap 25 kunjungan memberi +5 Poin Cakar kepada orang yang pertama kali mendeskripsikan kucing itu.",
      week: "Minggu",
      month: "Bulan",
      all: "Sepanjang waktu",
      seeAll: "Lihat semua kucing",
    },
    badges: {
      overline: "Koleksi",
      title: "Lencana Cakar",
      subtitle: "Lencana koleksi bertema cakar. Dapatkan dengan membantu kucing. Ketuk lencana mana pun untuk tahu cara membukanya.",
      collected: "Terkumpul",
      earnedOn: "Diperoleh pada",
      filterAll: "Semua",
      filterEarned: "Diperoleh",
      filterLocked: "Terkunci",
      emptyFilter: "Belum ada lencana di filter ini.",
      next: "Berikutnya",
      allEarned: "Semua lencana diperoleh!",
      celebrationTitle: "Lencana baru diperoleh",
      howToTitle: "Cara mendapatkan",
      howToFallback: "Teruslah aktif di komunitas untuk membuka lencana ini.",
      pageOverline: "Koleksi",
      pageTitle: "Lencana Cakar",
      pageSubtitle: "Ketuk lencana mana pun untuk melihat cara membukanya. Dapatkan lencana dengan membantu kucing, memotretnya, dan mengangkat semangat komunitas.",
      shareBtn: "Bagikan",
      shareText: "Aku baru saja mendapatkan lencana cakar {badge} di Pati! 🐾",
      remainingMetric: {
        cats: { one: "kucing lagi", many: "kucing lagi" },
        feeding: { one: "titik lagi", many: "titik lagi" },
        vax: { one: "vaksinasi lagi", many: "vaksinasi lagi" },
        sightings: { one: "keadaan darurat lagi", many: "keadaan darurat lagi" },
        claimed: { one: "klaim lagi", many: "klaim lagi" },
        messages: { one: "pesan lagi", many: "pesan lagi" },
        districts: { one: "distrik lagi", many: "distrik lagi" },
        cities: { one: "kota lagi", many: "kota lagi" },
      },
      category: {
        describer: "Pendeskripsi",
        feeding: "Pemberian makan",
        health: "Kesehatan",
        emergency: "Darurat",
        fame: "Ketenaran",
        coverage: "Cakupan",
        social: "Sosial",
        engagement: "Keterlibatan",
        special: "Spesial",
        paw: "Cakar",
      },
    },
    donate: {
      navLink: "Donasi",
      overline: "Transparansi Langsung",
      title: "Bantu kami menyembuhkan mereka",
      subtitle: "Setiap kuruş bisa dilacak. Lihat persis bagaimana donasi berubah menjadi kunjungan dokter hewan, operasi, dan kesempatan kedua bagi kucing liar Istanbul.",
      transparencyTag: "Buku terbuka 100% — setiap perawatan dicatat oleh dokter hewan terverifikasi",
      totalRaised: "Total terkumpul",
      totalSpent: "Terpakai untuk perawatan",
      balance: "Tersedia sekarang",
      catsHelped: "Kucing terbantu",
      donors: "donatur",
      treatments: "perawatan",
      availableForCats: "Siap untuk kucing berikutnya yang membutuhkan",
      uniqueCats: "Kucing unik yang dirawat",
      makeDonation: "Beri donasi",
      everyKurusCounts: "Setiap kuruş menemukan seekor kucing.",
      amount: "Jumlah",
      yourName: "Namamu (ditampilkan ke publik)",
      email: "Email",
      note: "Catatan",
      donateNow: "Donasi",
      securedBy: "Diamankan oleh iyzico · Kami tidak pernah menyimpan kartumu.",
      iyzicoPending: "Donasi sedang dicatat, tetapi pembayaran kartu dijeda selama akun merchant kami diverifikasi oleh iyzico. Data kamu tersimpan dan kami akan segera menghubungimu.",
      pendingMerchant: "Donasi tercatat — kami akan segera mengonfirmasi.",
      thankYou: "Terima kasih 🐾",
      error: "Tidak bisa mencatat donasi. Silakan coba lagi.",
      invalidAmount: "Masukkan jumlah yang valid",
      treatmentLedger: "Buku perawatan langsung",
      treatmentLedgerDesc: "Hanya dokter hewan terverifikasi admin yang bisa menambahkan catatan. Setiap biaya dirinci di samping kucing yang dibantunya.",
      noTreatmentsYet: "Belum ada perawatan yang dicatat — jadilah yang pertama membantu.",
      spent: "Terpakai",
      more: "entri lagi",
      recentDonors: "Donasi terbaru",
      noDonorsYet: "Donasimu bisa jadi yang pertama.",
      severity: {
        mild: "Ringan",
        moderate: "Sedang",
        severe: "Parah",
        critical: "Kritis",
      },
      tStatus: {
        planned: "Direncanakan",
        in_progress: "Sedang berlangsung",
        completed: "Selesai",
      },
    },
    treatments: {
      title: "Buku perawatan",
      subtitle: "Setiap kunjungan, setiap kuruş — ditambahkan hanya oleh dokter hewan terverifikasi admin.",
      totalSpent: "Total terpakai untuk kucing ini",
      records: "catatan",
      noneYet: "Belum ada perawatan yang dicatat.",
      add: "Tambah perawatan",
      added: "Perawatan tersimpan",
      condition: "Kondisi / penyakit",
      details: "Detail perawatan (obat, dosis, tindak lanjut…)",
      detailsPlaceholder: "Deskripsikan diagnosis dan apa yang dilakukan — sedetail mungkin.",
      cost: "Biaya",
      date: "Tanggal",
      severityLabel: "Tingkat keparahan",
      statusLabel: "Status",
      clinicName: "Klinik / pemerintah kota (opsional)",
      save: "Simpan catatan",
    },
    apply: {
      overline: "Program Relawan",
      title: "Jadi relawan",
      subtitle: "Ceritakan kepada kami kenapa kamu ingin merawat hewan jalanan di kotamu. Penyaringan AI berjalan lebih dulu untuk menyaring spam, lalu admin manusia meninjau lamaranmu.",
      cardTitle: "Jadi relawan",
      cardSubtitle: "Buka alat perawatan kucing · Lamaran 2 menit + tinjauan admin",
      motivationLabel: "Kenapa kamu ingin jadi relawan?",
      motivationPlaceholder: "Deskripsikan pengalamanmu dengan kucing liar, lingkunganmu, dan apa yang ingin kamu lakukan sebagai relawan…",
      minChars: "Min 20 karakter",
      aiNotice: "Penyaring AI akan memindai lamaranmu untuk mencari tanda spam/troll. Keputusan akhir dibuat oleh admin manusia.",
      submitBtn: "Kirim lamaran",
      submitted: "Lamaran terkirim — admin telah diberi tahu",
      error: "Ada yang tidak beres",
      tooShort: "Tulis minimal 20 karakter",
      backToProfile: "Kembali ke profil",
      aiScore: "Skor AI",
      statusPending: "Lamaranmu sedang ditinjau",
      statusPendingHint: "Admin akan segera meninjau lamaranmu. Kamu akan mendapat notifikasi saat sudah diputuskan.",
      statusRejected: "Lamaran tidak disetujui",
    },
    admin: {
      overline: "Admin",
      title: "Lamaran relawan",
      subtitle: "AI menyaring setiap lamaran lebih dulu. Tugasmu: pemeriksaan akhir oleh manusia sebelum memberikan akses relawan.",
      forbiddenTitle: "Khusus akses admin",
      forbiddenSubtitle: "Kamu tidak punya izin untuk melihat halaman ini.",
      aiSummary: "Penilaian AI",
      empty: "Tidak ada lamaran di filter ini.",
      approveBtn: "Setujui",
      rejectBtn: "Tolak",
      approvedToast: "Lamaran disetujui — akses relawan diberikan.",
      rejectedToast: "Lamaran ditolak.",
      bulkRejectTitle: "AI menandai {count} lamaran sebagai berisiko tinggi",
      bulkRejectSubtitle: "Tolak semuanya dengan satu klik — pelamar akan diberi tahu.",
      bulkRejectBtn: "Tolak massal yang merah",
      bulkRejectConfirm: "Tolak {count} lamaran bertanda merah? Ini tidak bisa dibatalkan otomatis.",
      bulkRejectedToast: "Menolak massal {count} lamaran.",
      notePlaceholder: "Catatan opsional untuk pelamar (bisa dilihat olehnya)…",
      adminNoteLabel: "Catatan admin",
      tab: {
        pending: "Menunggu",
        approved: "Disetujui",
        rejected: "Ditolak",
      },
      stats: {
        daysOld: "hari yang lalu",
        cats: "kucing",
        sightings: "laporan penampakan",
        feeding: "pemberian makan",
      },
    },
    adminEmerg: {
      overline: "Moderasi darurat",
      title: "Antrean tinjauan darurat",
      subtitle: "AI menyaring foto dan catatan. Pelapor yang belum tepercaya diarahkan ke sini untuk pemeriksaan akhir oleh manusia sebelum dipublikasikan.",
      gotoApplications: "Buka lamaran relawan",
      empty: "Tidak ada keadaan darurat di filter ini.",
      approveBtn: "Publikasikan",
      rejectBtn: "Tolak",
      approvedToast: "Keadaan darurat dipublikasikan.",
      rejectedToast: "Keadaan darurat ditolak.",
      tab: {
        pending: "Menunggu",
        approved: "Dipublikasikan",
        rejected: "Ditolak",
      },
      photoVerdict: "AI Foto",
      textVerdict: "AI Teks",
      notACat: "Bukan kucing",
      aiGenerated: "Dibuat AI",
      photoshopped: "Hasil photoshop",
      photoClean: "Foto tampak bersih",
      confidence: "Tingkat keyakinan",
      redWarning: "AI menandai ini sebagai berisiko tinggi — tinjau dengan cermat sebelum dipublikasikan.",
    },
    photos: {
      archive: "Arsip foto",
      galleryTitle: "Arsip foto",
      gallerySubtitle: "Setiap foto kucing ini yang dibagikan komunitas. Ketuk cakar untuk menyukai.",
      addPhoto: "Tambah foto",
      uploaded: "Foto diunggah",
      uploadFailed: "Unggahan gagal",
      emptyGallery: "Belum ada foto — jadilah yang pertama menambahkan!",
      loginRequired: "Masuk untuk mengunggah foto",
      loginToLike: "Masuk untuk memberi cakar",
      deleteConfirm: "Hapus foto ini?",
      deleted: "Foto dihapus",
      monthlyQuotaUsed: "{used}/{limit} cakar terpakai bulan ini",
      unlimitedQuota: "Cakar tanpa batas (Premium)",
      upgradeForUnlimited: "Jadi Premium untuk tanpa batas",
      aiNotice: "Foto otomatis disaring untuk konten buatan AI, hasil photoshop, atau tidak pantas.",
    },
    photoContest: {
      overline: "Kontes foto bulanan",
      title: "Foto Bulan Ini",
      subtitle: "Setiap bulan, 3 foto dengan cakar terbanyak memenangkan lencana Fotografer Bulan Ini. Ketuk foto mana pun untuk mengunjungi kucingnya.",
      empty: "Belum ada foto dengan suka bulan ini — jadilah yang pertama menaikkan kucing ke podium.",
      by: "oleh",
      honorableMentions: "Penghargaan khusus (4-10)",
    },
  },
  // CEVIRI-1 (30 Agu 2026): Portekizce (Brezilya) ve Ispanyolca.
  // 964 anahtar; `safety.*` (57) BILEREK yok — o sayfa GUVENLIK-1 ile Turkiye
  // disinda gizleniyor (icinde TR acil numaralari ve TR saglik sistemi var).
  // Eksik anahtar t() tarafindan Ingilizce'ye duser, Turkce'ye ASLA.
  "pt-br": {
    kediBildir: {
      baslik: "Relatar um gato",
      altBaslik: "Estamos aqui pelos gatos do seu bairro. O que você gostaria de fazer?",
      kaydetBaslik: "Cadastrar um gato",
      kaydetAciklama: "Adicione ao sistema um gato do seu bairro",
      gordumBaslik: "Vi um gato",
      gordumAciklama: "Informe que você viu um gato já cadastrado",
      acilBaslik: "Relatar emergência",
      acilAciklama: "Peça ajuda para um gato ferido, doente ou em perigo",
      mamaSuBaslik: "Adicionar ponto de comida/água",
      mamaSuAciklama: "Compartilhe a localização de um ponto de comida ou água",
      dipnot: "Cada relato chega aos voluntários do seu bairro.",
    },
    volunteers: {
      title: "Voluntários",
      subtitle: "As pessoas que alimentam, tratam e resgatam os animais do seu bairro. Cada nome aqui é alguém que apareceu.",
      becomeVolunteer: "Seja voluntário",
      individualsTitle: "Voluntários e veterinários",
      individualsSubtitle: "Classificados por contribuição — alimentação, resgates, vacinas e denúncias.",
      noIndividuals: "Ainda não há voluntários. Seja o primeiro do seu bairro.",
    },
    dogUi: {
      dbTitleCat: "Banco de dados de gatos",
      dbTitleDog: "Banco de dados de cães",
      dbOverlineCat: "BANCO DE DADOS DE GATOS",
      dbOverlineDog: "BANCO DE DADOS DE CÃES",
      dbSubCat: "Veja todos os gatos catalogados na nossa comunidade.",
      dbSubDog: "Veja todos os cães catalogados na nossa comunidade.",
      dbSearchCat: "Busque por nome, cor, raça ou bairro",
      dbSearchDog: "Busque por nome, cor ou bairro",
      dbLostCat: "Encontre seu gato perdido",
      dbLostDog: "Encontre seu cão perdido",
      dbRegisteredCat: "Cadastrados",
      dbRegisteredDog: "Cadastrados",
      dbInjuredCat: "Feridos",
      dbInjuredDog: "Feridos",
      dbEarTagged: "Com brinco",
      dbEarTaggedHint: "Castrado — corte ou brinco na orelha",
      dbTitleAll: "Banco de dados de animais",
      dbOverlineAll: "BANCO DE DADOS DE ANIMAIS",
      dbSubAll: "Veja todos os animais catalogados na nossa comunidade.",
      dbSearchAll: "Busque por nome, cor ou bairro",
      dbLostAll: "Encontre seu animal perdido",
      dbRegisteredAll: "Cadastrados",
      dbInjuredAll: "Feridos",
      lostConfirmCat: "Criar um anúncio de desaparecimento para {ad}? Ele entra no ar na hora e os vizinhos são avisados.",
      lostConfirmDog: "Criar um anúncio de desaparecimento para {ad}? Ele entra no ar na hora e os vizinhos são avisados.",
      lostOpenCat: "Já existe um anúncio aberto — levando você até ele.",
      lostOpenDog: "Já existe um anúncio aberto — levando você até ele.",
      deleteCat: "Excluir este gato?",
      deleteDog: "Excluir este cão?",
      shareCat: "Compartilhar este gato",
      shareDog: "Compartilhar este cão",
      editCat: "Editar gato",
      editDog: "Editar cão",
      reportCat: "Denunciar este perfil de gato",
      reportDog: "Denunciar este perfil de cão",
      noGpsCat: "O GPS não estava disponível quando este gato foi cadastrado.",
      noGpsDog: "O GPS não estava disponível quando este cão foi cadastrado.",
      lostCat: "Meu gato sumiu — criar anúncio",
      lostDog: "Meu cão sumiu — criar anúncio",
      newCat: "Novo gato",
      newDog: "Novo cão",
      saveCat: "Salvar gato",
      saveDog: "Salvar cão",
      saving: "Salvando…",
      dogOptional: "Adicionar detalhes do cão (opcional)",
      dogOptionalHint: "Não é obrigatório. Duas fotos, um nome e um bairro já bastam.",
    },
    dog: {
      section: "Detalhes do cão",
      unknownHint: "Deixe como Não sei se não conseguir ver com clareza",
      matchingSoon: "A busca por semelhança de cães ainda não está disponível — o perfil foi salvo e poderá ser encontrado quando ela abrir.",
      earCarriage: "Orelhas",
      ear: {
        dik: "Em pé",
        yari_dik: "Semi-eretas",
        dusuk: "Caídas",
        kesik: "Cortadas",
        belirsiz: "Não sei",
      },
      tailType: "Cauda",
      tail: {
        uzun_duz: "Longa e reta",
        kivrik: "Enrolada",
        orak: "Em foice",
        kisa: "Curta",
        kesik: "Cortada",
        puskullu: "Em pluma",
        belirsiz: "Não sei",
      },
      coatLength: "Comprimento do pelo",
      coatLen: {
        kisa: "Curto",
        orta: "Médio",
        uzun: "Longo",
        belirsiz: "Não sei",
      },
      coatTexture: "Textura do pelo",
      coatTex: {
        duz: "Liso",
        dalgali: "Ondulado",
        kivircik: "Cacheado",
        sert_tel: "Duro",
        belirsiz: "Não sei",
      },
      muzzle: "Focinho",
      muz: {
        basik: "Curto",
        orta: "Médio",
        uzun: "Longo",
        belirsiz: "Não sei",
      },
      build: "Porte",
      bod: {
        ince: "Magro",
        orta: "Médio",
        iri: "Robusto",
        belirsiz: "Não sei",
      },
      earTagNo: "Número do brinco",
      earTagHint: "Somente se o cão tiver brinco de castração",
      registering: "Você está cadastrando um cão",
      registeringCat: "Você está cadastrando um gato",
      switchToCat: "Cadastrar como gato",
      switchToDog: "Cadastrar como cão",
      myLostDog: "Este é o meu cão perdido",
    },
    appName: "StrayPaw",
    tagline: "Os animais de rua do seu bairro",
    // Panodaki baslik slogani. `tagline` bundan AYRI: o sayfa basliklarinda
    // ve paylasim kartlarinda kullaniliyor, bu ise uygulama basliginin ikinci
    // satiri. Ikisini birlestirmek, panoda olmayan bir metni basliga sokardi.
    baslikSlogani: "Um bairro mais forte.",
    arama: {
      rowPlace: "Bairro",
      otherNeighborhoods: "Outros bairros",
      row_kediler: "gatos",
      row_harita: "no mapa",
      row_kayip: "anúncios de perdidos",
      row_besleme: "pontos de alimentação",
      rowGo_kediler: "Ver os gatos",
      rowGo_harita: "O que há na região?",
      rowGo_kayip: "Ver os anúncios",
      rowGo_besleme: "Ver os pontos",
      unit_kediler: "resultados",
      unit_harita: "",
      unit_kayip: "anúncios",
      unit_besleme: "pontos",
      tab_ilanlar: "Perdidos",
      title: "Buscar",
      placeholder: "Busque um gato, bairro ou distrito…",
      back: "Voltar",
      clear: "Limpar",
      recent: "Buscas recentes",
      clearHistory: "Limpar",
      mostCats: "Bairros com mais gatos",
      hint: "Busque pelo nome do gato, bairro ou distrito para chegar aos gatos cadastrados, anúncios de perdidos e tudo da sua região.",
      neighborhoods: "Bairros",
      cats: "Gatos",
      catWord: "gatos",
      searching: "Buscando…",
      empty: "Nada encontrado. Tente um nome de bairro.",
      searchFor: "Buscar por",
      tab_hepsi: "Tudo",
      tab_kediler: "Gatos",
      tab_mahalleler: "Bairros",
    },
    anasayfaV2: {
      odul: "recompensa",
      slogan: "Uma vida melhor, para cada patinha.",
      heroTitle: "O que esta acontecendo no seu bairro hoje?",
      heroSub: "Ruas mais seguras para os gatos de rua sao possiveis.",
      heroMark: "Cada patinha importa",
      statCats: "Gatos registrados",
      statEmergencies: "Emergencias",
      statFeeding: "Pontos de alimentacao",
      statVaccinated: "Vacinados",
      locationPick: "Mudar local",
      search: "Busque um gato, bairro ou distrito…",
      actionsTitle: "O que você quer fazer?",
      actionsAside: "Um pequeno passo, uma grande diferença",
      a1t: "Vi um gato",
      a1s: "Informar local",
      a2t: "Procurar gato perdido",
      a2s: "Buscar nos anúncios",
      aramaVeritabani: "Banco de dados de gatos",
      a3t: "Central de Emergência",
      a3s: "Gatos aguardando ajuda",
      a4t: "Identificar com IA",
      a4s: "Comparar por foto",
      vtBaslik: "Banco de Dados de Gatos",
      vtAlt: "Explore os gatos cadastrados e veja os detalhes.",
      vtAdetSonEk: "gatos",
      lostTitle: "Um gato desaparecido perto de você",
      casesTitle: "Casos importantes perto de você",
      casesSub: "Gatos que mais precisam de ajuda no campo",
      casesAll: "Ver todos",
      casesEmpty: "Nenhum caso aberto perto de você — boa notícia.",
      casesFarAway: "Nenhum por perto — mostrando os registros mais recentes.",
      tabLost: "Gatos perdidos",
      tabEmergency: "Casos urgentes",
      tabAll: "Todos",
      emergencyBadge: "URGENTE",
      chipVet: "Precisa de veterinário",
      chipInjured: "Aguardando ajuda",
      chipFood: "Precisa de comida/água",
      chipDanger: "Perigo relatado",
      chipVolunteer: "Procura-se voluntário",
      chipLost: "Alguém viu?",
      lostAll: "Ver todos",
      lostBadge: "PERDIDO",
      lostEmpty: "Nenhum anúncio de perdido perto de você — boa notícia.",
      mapTitle: "Mapa do bairro",
      mapDesc: "Descubra gatos, emergências e pontos de alimentação perto de você.",
      mapOpen: "Abrir o mapa",
      aiTitle: "IA · Reconhecimento de gatos",
      aiDesc: "Fotografe o gato que você viu e compare com os gatos registrados.",
      aiPhoto: "Tirar foto",
      aiGallery: "Escolher da galeria",
      healthTitle: "Saúde e apoio ao gato",
      healthDesc: "Vacinas, castração, raiva e notas de saúde.",
      communityTitle: "Junte-se à comunidade",
      communityDesc: "Voluntários, eventos e muito mais.",
      communityMark: "Juntos somos mais fortes",
      navReport: "Relatar gato",
      navReportShort: "Relatar",
      justNow: "agora mesmo",
      hoursAgo: "há {n}h",
      hoursAgoLong: "há {n} horas",
      minutesAgoLong: "há {n} minutos",
      pickPlaceAria: "Escolha sua região",
      pickPlaceCta: "Escolha sua região",
      pickPlaceTitle: "Escolha sua região",
      pickPlaceSub: "As telas Início e Meu Bairro seguem esta escolha.",
      pickPlaceNear: "Com base na sua localização",
      pickPlaceClose: "Fechar",
      pickPlaceSave: "Salvar",
      pickPlaceSaving: "Salvando…",
      yesterday: "ontem",
      daysAgo: "há {n}d",
    },
    kayipIlan: {
      title: "Gato perdido",
      lost: "PERDIDO",
      seen: "Eu vi este gato",
      odul: "recompensa",
      poster: "Criar cartaz",
      posterTitle: "Cartaz de gato perdido",
      posterErr: "Não foi possível criar o cartaz. Tente novamente.",
      share: "Partilhar",
      shareTitle: "Gato perdido 🔍",
      shareCopied: "Link copiado — cole onde quiser.",
      shareErr: "Não foi possível partilhar. Tente novamente.",
      seenSub: "Marque o local no mapa",
      aiTitle: "Viu um gato parecido?",
      aiDesc: "Tire uma foto ou escolha da galeria, a IA compara.",
      whereTitle: "Onde pode estar?",
      whereSub: "Área de busca estimada",
      openMap: "Abrir mapa",
      teamTitle: "Equipe de busca",
      teamCount: "{n} pessoas procurando",
      teamJoin: "Entrar na busca",
      teamLeave: "Sair da busca",
      photoCount: "Fotos",
      seenCount: "Avistamentos relatados",
      owner: "Dono do anúncio",
      call: "Ligar",
      message: "Mensagem",
      details: "Detalhes do anúncio",
      reunited: "Reencontrado — fechar anúncio",
      bKisir: "Castrado",
      bKisirDegil: "Não castrado",
      bErkek: "Macho",
      bDisi: "Fêmea",
      bUrkek: "Tímido",
      bSokulgan: "Sociável",
      bSaldirgan: "Defensivo",
      bSakin: "Calmo",
      noPhoto: "Sem foto",
      notFound: "Anúncio não encontrado.",
      closed: "Este anúncio está fechado.",
    },
    aramaAlani: {
      title: "Área de busca",
      areaTitle: "Área estimada de busca",
      areaSub: "Criado a partir do último local visto.",
      allReports: "Ver tudo",
      directions: "Como chegar a esta área",
      legend: "Legenda do mapa",
      sonGorulen: "Visto por último",
      aramaAlaniHalka: "Área de busca",
      ihbarlar: "Avistamentos relatados",
      gonulluler: "Voluntários",
      reports: "Relatos recentes",
      noReports: "Ainda sem relatos. Seja o primeiro.",
      cFoto: "Com foto",
      cBenzer: "Pode ser parecido",
      cEminDegil: "Não tenho certeza",
      addReport: "Adicionar relato",
      join: "Entrar na equipe de busca",
      leave: "Sair da equipe de busca",
      team: "{n} pessoas procurando",
      locHidden: "Entre para ver onde estão os voluntários",
      away: "a {d}",
      noCoords: "Este anúncio não tem local no mapa.",
      closed: "Este anúncio está fechado.",
      notFound: "Anúncio não encontrado.",
      back: "Voltar",
    },
    mahallem: {
      anOnerilerAcikGorev: "Já há tarefas abertas",
      anHeroBaslik: "Como está {yer}?",
      anHeroAlt: "Dados de 30 dias",
      anHeroAltAi: "30 dias e análise IA",
      anSkorEtiketi: "Índice de Saúde do Bairro",
      anSkorBilgiAc: "O que o índice mede?",
      anSkorBilgi: "O índice mede os recursos do bairro (vacinas registradas, pontos de alimentação e água, emergências abertas, rapidez) — não a saúde dos gatos.",
      anAzVeri: "poucos dados",
      anBalonYok: "Ainda sem resumo: poucos dados nesta área.",
      anDurumKayit: "Gatos sendo registrados",
      anDurumKayitAlt: "{n} novos em 30 dias",
      anDurumAcilYok: "Sem emergências abertas",
      anDurumAcilYokAlt: "Todos os casos resolvidos",
      anDurumSuVar: "Pontos de água ativos",
      anDurumSuVarAlt: "{n} pontos ativos",
      anDurumMamaVar: "Pontos de alimentação ativos",
      anDurumMamaVarAlt: "{n} pontos ativos",
      anDurumBeslenme: "Pouca alimentação",
      anDurumBeslenmeAlt: "Faltam pontos",
      anDurumSu: "Poucos pontos de água",
      anDurumSuAlt: "Faltam pontos",
      anDurumGonullu: "Poucos voluntários",
      anDurumGonulluAlt: "Faltam voluntários",
      anDurumAsi: "Vacinas pouco registradas",
      anDurumAsiAlt: "Poucos registros",
      anDurumBarinak: "Sem casinhas",
      anDurumBarinakAlt: "Falta abrigo",
      anDurumYarali: "Gatos feridos relatados",
      anDurumYaraliAlt: "Nos últimos 30 dias",
      anDurumAcil: "{n} emergências abertas",
      anDurumAcilBir: "1 emergência aberta",
      anDurumAcilAlt: "Precisa de ação",
      anKonularBaslik: "{n} temas importantes agora",
      anTumunuGor: "Ver tudo",
      anKonuAcil: "Emergência aberta",
      anKonuYarali: "Gatos feridos",
      anKonuBeslenme: "Falta ponto de ração",
      anKonuSu: "Falta ponto de água",
      anKonuGonullu: "Poucos voluntários",
      anKonuAsi: "Vacinas pouco registradas",
      anKonuBarinak: "Sem casinha",
      anKonuBolgede: "Mais em {yer}.",
      anKonuYok: "Nenhuma falta clara nas áreas medidas.",
      anVeriYok: "Não foi possível carregar os dados.",
      anDugmeVakalar: "Ver casos",
      anDugmeGonulluler: "Ver voluntários",
      anDugmeKediler: "Ver gatos",
      anHaritaBaslik: "Mapa de Análise do Bairro",
      anAralikSec: "Escolher período",
      anAralik7: "Últimos 7 dias",
      anAralik30: "Últimos 30 dias",
      anAralik90: "Últimos 3 meses",
      anAralik365: "Último ano",
      anAralikTumu: "Tudo",
      anKatmanYogunluk: "Densidade",
      anKatmanIhtiyac: "Necessidades",
      anKatmanAcil: "Emergências",
      anKatmanBeslenme: "Alimentação",
      anKatmanSu: "Água",
      anYogunlukDusuk: "Baixa",
      anYogunlukYuksek: "Alta",
      anEtiketKedi: "{n} gatos",
      anEnCokIhtiyac: "Área com mais necessidade:",
      anHaritaBos: "Ainda não há área com limite.",
      anDegisimBaslik: "O que mudou em 30 dias?",
      anDetayliGor: "Detalhes",
      anSekmeKediler: "Gatos",
      anSekmeAcil: "Emergências",
      anSekmeKayip: "Perdidos",
      anSekmeBeslenme: "Alimentação",
      anSekmeSu: "Água",
      anSatirKedi: "Registros",
      anSatirAcil: "Emergências",
      anSatirBeslenme: "Ração",
      anSatirSu: "Água",
      anRozet30: "em 30 dias",
      anGrafikNotu: "Pela data de registro: gatos = total no fim do dia; demais = adicionados no dia.",
      anGrafikYok: "Ainda sem registros no período.",
      anOnerilerBaslik: "Sugestões de IA",
      anOnerilerAlt: "Ideias para melhorar o bairro.",
      anTumOneriler: "Todas",
      anOneriYok: "Sem sugestões agora.",
      // ÖNCELİK-1 (18 Eylül 2026) — Mahalle Öncelikleri sayfası.
      ocBaslik: "Prioridades do Bairro",
      ocAlt: "Vamos agir juntos por um bairro melhor.",
      ocGeri: "Voltar",
      ocNot: "Estas prioridades foram definidas por análise de IA com os dados do seu bairro nos últimos 30 dias.",
      ocEtiketAcil: "EMERGÊNCIAS",
      ocEtiketBeslenme: "ALIMENTAÇÃO",
      ocEtiketSu: "PONTOS DE ÁGUA",
      ocEtiketGonullu: "APOIO DE VOLUNTÁRIOS",
      ocEtiketDiger: "OUTRAS NECESSIDADES",
      ocAcilBaslik: "Gatos esperando ajuda",
      ocAcilAlt: "{n} caso de emergência aberto no seu bairro.",
      ocAcilYokBaslik: "Nenhuma emergência aberta",
      ocAcilYokAlt: "Nenhum caso aguardando atendimento agora.",
      ocAcilBirim: "caso aberto",
      ocBeslenmeBaslik: "Apoio de alimentação necessário",
      ocBeslenmeAlt: "Pontos de alimentação insuficientes em {n} área(s).",
      ocBeslenmeYokBaslik: "Pontos de alimentação suficientes",
      ocBeslenmeYokAlt: "{n} ponto(s) de alimentação registrado(s).",
      ocBeslenmeBirim: "novo ponto",
      ocSuBaslik: "Faltam pontos de água",
      ocSuAlt: "Apoio de água necessário em {n} área(s).",
      ocSuYokBaslik: "Pontos de água suficientes",
      ocSuYokAlt: "{n} ponto(s) de água registrado(s).",
      ocSuBirim: "ponto",
      ocGonulluBaslik: "Precisamos de apoio",
      ocGonulluAlt: "Procuram-se voluntários para {n} gatos.",
      ocGonulluYokBaslik: "Apoio de voluntários suficiente",
      ocGonulluYokAlt: "{g} voluntário(s) cuidando de {n} gatos.",
      ocGonulluBirim: "gatos",
      ocDigerBaslik: "Perdidos e abrigo",
      ocDigerAlt: "{k} registro(s) ativo(s), {b} necessidade de abrigo.",
      ocDigerYokBaslik: "Sem questões de perdidos ou abrigo",
      ocDigerYokAlt: "Sem anúncio de perdido nem necessidade de abrigo.",
      ocDigerBirim: "registros",
      ocDugmeGorevler: "Ver Tarefas",
      ocDugmeBolgeler: "Ver Áreas",
      ocDugmeDetaylar: "Ver Detalhes",
      gvKatBeslenme: "Alimentação",
      gvKatSu: "Ponto de água",
      gvKatKayip: "Gato perdido",
      gvKatSaglik: "Saúde",
      gvKatKulube: "Abrigo",
      gvDugmeDetay: "Ver detalhes",
      gvSloganSade: "Vamos agir juntos pelos gatos.",
      gvOlusturBaslik: "Crie sua tarefa",
      gvOlusturAlt: "Crie uma nova tarefa para o seu bairro. Juntos chegamos a mais gatos.",
      gvOlusturDugme: "Criar tarefa",
      gvOneriBaslikKisa: "Sugestão da IA",
      gvGorevlerimBag: "Minhas tarefas · {n}",
      baKatmanKisaAcil: "Urgências",
      baKatmanKisaBeslenme: "Comida",
      baKatmanKisaSu: "Água",
      baKatmanKisaGonullu: "Voluntários",
      baKatmanKisaIsi: "Calor",
      gvBaslik: "Tarefas",
      gvSlogan: "O que podes fazer pelos gatos do bairro. Um pequeno passo, uma grande diferença.",
      gvElYazisi: "Juntos, gatos mais felizes.",
      gvSuzTumu: "Todos",
      gvSuzAcil: "Urgente",
      gvSuzBugun: "Hoje",
      gvSuzYakin: "Perto",
      gvFiltrele: "Filtrar",
      gvFiltreTur: "Tipo",
      gvFiltreDurum: "Estado",
      gvTemizle: "Limpar",
      gvTamam: "OK",
      gvTur_yarali_kontrol: "Gato ferido",
      gvTur_veteriner_destegi: "Veterinário",
      gvTur_mama_istasyonu: "Comida",
      gvTur_su_kabi: "Água",
      gvTur_kayip_arama: "Gato perdido",
      gvTur_kulube: "Abrigo",
      gvHata: "Não foi possível carregar as tarefas.",
      gvOncelikliBaslik: "Tarefas prioritárias",
      gvCipTumu: "Tudo",
      gvCipAcil: "Urgente",
      gvCipIhtiyac: "Necessidade",
      gvCipDestek: "Apoio",
      gvListeGeri: "Meu bairro",
      gvListeAlt: "Todas as tarefas em {yer}",
      gvListeSimdi: "Fazer agora",
      gvListeSimdiAlt: "Tarefas prioritárias no bairro",
      gvListeDiger: "Outras tarefas",
      gvListeDigerAlt: "Outras tarefas no bairro",
      gvListeAra: "Buscar",
      gvDetayGeri: "Tarefas",
      gvDetayAcilRozet: "Tarefa urgente",
      gvDetayBenimRozet: "Minha tarefa",
      gvDetayBittiRozet: "Concluída",
      gvDetayBekliyor: "Aguardando ajuda",
      gvDetayBekliyorAlt: "Esta tarefa precisa de atenção urgente.",
      gvDetayUstlendi: "Você assumiu esta tarefa!",
      gvDetayUstlendiAlt: "Obrigado — juntos somos mais fortes.",
      gvDetayBittiBanner: "Esta tarefa foi concluída. Obrigado pelo seu trabalho!",
      gvDetaySekmeDetay: "Detalhes",
      gvDetaySekmeKonum: "Local",
      gvDetaySekmeYorum: "Comentários",
      gvDetaySonGorulme: "Visto pela última vez",
      gvDetayKonum: "Local",
      gvDetayDurum: "Estado",
      gvDetayBildiren: "Quem avisou",
      gvDetayAciklama: "Descrição",
      gvDetayHaritada: "Ver no mapa",
      gvDetayUstlen: "Assumir tarefa",
      gvDetayPaylas: "Partilhar",
      gvDetayTamamla: "Marcar como concluída",
      gvDetayFoto: "Adicionar foto",
      gvDetayGelisme: "Fotos do progresso",
      gvDetayBittiTarih: "Esta tarefa foi concluída em {tarih}",
      gvDetayYorumBos: "Ainda sem comentários.",
      gvDetayEkip: "Equipa e passos",
      gvTumunuGor: "Ver tudo",
      gvRozetAcil: "Urgente",
      gvRozetOncelikli: "Prioritário",
      gvDugmeGor: "Ver tarefa",
      gvDugmeUstlen: "Assumir",
      gvDugmeKontrol: "Verificar",
      gvDugmeDestek: "Ajudar",
      gvYakinBaslik: "Tarefas perto de ti",
      gvMahalleBaslik: "Tarefas no bairro",
      gvOneriBaslik: "Sugestão da IA",
      gvOneriDugme: "Ver sugestões",
      gvBenimBaslik: "As minhas tarefas",
      gvAktifSayi: "{n} ativas",
      gvTamamSayi: "{n} concluídas",
      gvBenimBos: "Ainda não participaste em tarefas.",
      gvBos: "Nenhuma tarefa por agora.",
      gvKonumGerekli: "É preciso permissão de localização.",
      gvKonumReddedildi: "A permissão de localização foi negada.",
      gvDurumAcik: "Aberta",
      gvDurumDevam: "Em curso",
      gvDurumTamam: "Concluída",
      gvDurumIptal: "Cancelada",
      gvZamanBugun: "Entraste · Hoje {saat}",
      gvZamanDun: "Entraste · Ontem {saat}",
      gvZamanTarih: "Entraste · {tarih}",
      gvZamanBugunKisa: "Hoje {saat}",
      gvZamanDunKisa: "Ontem {saat}",
      m5Mahallem: "Meu bairro",
      m5Paylas: "Partilhar",
      m5GonulluOl: "Voluntariar",
      m5HavaAcik: "Limpo",
      m5HavaParcali: "Parcialmente nublado",
      m5HavaSisli: "Nevoeiro",
      m5HavaYagmurlu: "Chuva",
      m5HavaKarli: "Neve",
      m5HavaFirtinali: "Tempestade",
      m5HavaBugun: "Hoje {min}° / {max}°",
      m5PanoKedi: "gatos registados",
      m5EgilimYeni: "novo",
      m5EgilimBaslik: "Últimos 30 dias: {son} novos registos · 30 dias anteriores: {once}",
      m5HaritayiAc: "Abrir mapa",
      baBaslik: "Análise por área",
      baAltBaslik: "Entenda juntos as necessidades do bairro.",
      baAralikTumu: "Tudo",
      baAralik7: "7 dias",
      baAralik30: "30 dias",
      baAralik90: "3 meses",
      baAralik365: "1 ano",
      baSayacKedi: "Total de gatos",
      baSayacAcil: "Emergências abertas",
      baSayacBeslenme: "Pontos de alimentação",
      baSayacSu: "Pontos de água",
      baSayacGonullu: "Participações",
      baSayacKediYeni: "Gatos novos",
      baSayacAcilYeni: "Novas emergências",
      baSayacBeslenmeYeni: "Novos pontos",
      baSayacSuYeni: "Novos pontos de água",
      baSayacGonulluYeni: "Novas participações",
      baSatirKedi: "{n} gatos",
      baSatirAcil: "{n} emergências",
      baSatirBeslenme: "{n} pontos de alimentação",
      baSatirSu: "{n} pontos de água",
      baSatirGonullu: "{n} participações",
      baKatmanlar: "Camadas",
      baKatmanKedi: "Gatos",
      baKatmanAcil: "Emergências",
      baKatmanBeslenme: "Pontos de alimentação",
      baKatmanSu: "Pontos de água",
      baKatmanGonullu: "Tarefas voluntárias",
      baKatmanIsi: "Mapa de calor",
      baCercevele: "Ajustar mapa",
      baIlceNotu: "Poucos registros têm bairro; as áreas aparecem por distrito.",
      baHata: "Não foi possível carregar a análise.",
      baHaritaBos: "Nenhuma área com localização",
      baBolgeOzet: "Resumo por área",
      baTumBolgeler: "Todas as áreas",
      baKademeNotu: "Números = novos registros no período; níveis = todo o período.",
      baDiger: "Outros",
      baDigerAlt: "sem bairro",
      baBolgeyiIncele: "Ver área",
      baBolgeYok: "Ainda não há áreas.",
      baAiBaslik: "Análise por IA",
      baAiYok: "O resumo de hoje ainda não está pronto.",
      baAiDetay: "Análise detalhada",
      baOncelikli: "Áreas prioritárias",
      baNedenYok: "Menor pontuação",
      baHaritadaGor: "Ver gatos no mapa",
      baHaritadaAlt: "Mapa centrado nestas áreas.",
      baTumKediler: "Gatos do bairro",
      baTumKedilerAlt: "Veja todos em lista.",
      baKisaKedi: "Gatos",
      baKisaAcil: "Urgências",
      baKisaBeslenme: "Alimentação",
      baKisaSu: "Água",
      baKisaGonullu: "Voluntários",
      baKisaKediYeni: "Gatos novos",
      baKisaAcilYeni: "Novas emerg.",
      baKisaBeslenmeYeni: "Nova alim.",
      baKisaSuYeni: "Nova água",
      baKisaGonulluYeni: "Adesões",
      baSatirKisaKedi: "{n} gatos",
      baSatirKisaAcil: "{n} emerg.",
      baSatirKisaBeslenme: "{n} alim.",
      baSatirKisaSu: "{n} água",
      baSatirKisaGonullu: "{n} adesões",
      baIncele: "Ver",
      baAiDetayKisa: "Detalhes",
      baKademeNotuKisa: "· novos registros",
      baIlceNotuKisa: "Nível distrito",
      gdBaslik: "Detalhe do dia",
      gdBugun: "Hoje",
      gdOncekiGun: "Dia anterior",
      gdSonrakiGun: "Próximo dia",
      gdToplamBugun: "{n} registros hoje.",
      gdToplamGun: "{n} registros neste dia.",
      gdToplamYok: "Nenhum registro neste dia.",
      gdToplamKarsilastir: "Dia anterior: {once} · este dia: {simdi}.",
      gdDagilim: "Distribuição da atividade",
      gdKisaKedi: "Gatos novos",
      gdKisaAcil: "Emergência",
      gdKisaKayip: "Perdidos",
      gdKisaBeslenme: "Alimentação",
      gdKisaSu: "Água",
      gdKisaGonullu: "Voluntários",
      gdBolge: "Por bairro",
      gdKonumsuz: "Sem bairro: {n}",
      gdKonumYok: "Nenhum registro com local",
      gdHaritadaGor: "Ver no mapa",
      gdFotoBugun: "Fotos de hoje",
      gdFotoGun: "Fotos do dia",
      gdFotoYok: "Sem fotos",
      gdFazla: "mais",
      gdKayitBugun: "Registros de hoje",
      gdKayitGun: "Registros do dia",
      gdFiltreHepsi: "Todos os registros",
      gdTumunuGoster: "Mostrar todos ({n})",
      gdKayitKedi: "Novo gato registrado",
      gdKayitAcil: "Emergência",
      gdKayitKayip: "Anúncio de gato perdido",
      gdKayitBeslenme: "Novo ponto de alimentação",
      gdKayitSu: "Novo ponto de água",
      gdKayitGonullu: "Voluntários participaram",
      gdKatilim: "{n} participações",
      gdAltBaslik: "Veja seu bairro no mapa.",
      gdAltMetin: "Uma vida mais segura para o seu bairro, juntos.",
      gdHata: "Não foi possível carregar o detalhe do dia. Aparecerá quando o servidor for atualizado.",
      gdPaylasMetni: "{yer} · {tarih}: gatos de rua, detalhe do dia.",
      gdGunuAc: "Abrir detalhe de {tarih}",
      s7Baslik: "Últimos 7 dias",
      s7PaylasMetni: "{yer}: os últimos 7 dias dos gatos de rua.",
      s7GrafikBaslik: "Novos registros por dia",
      s7Yeni: "novo",
      s7Sabit: "sem mudança",
      s7SayacKedi: "Novos gatos registrados",
      s7SayacAcil: "Emergências",
      s7SayacKayip: "Gatos perdidos",
      s7SayacBeslenme: "Novos pontos de alimentação",
      s7SayacSu: "Novos pontos de água",
      s7SayacGonullu: "Participações voluntárias",
      s7Gunluk: "Detalhes diários",
      s7KartKotu: "{seri} aumentaram nos últimos 7 dias.",
      s7KartIyi: "{seri} aumentaram nos últimos 7 dias.",
      s7KartSabit: "Nenhuma mudança clara em relação aos 7 dias anteriores.",
      s7KartOnceYok: "A comparação com a semana anterior aparece após a atualização do servidor.",
      s7KartKarsilastir: "7 dias anteriores: {once} · esta semana: {simdi}.",
      s7IstatistikDetay: "Ver estatísticas",
      s7SeriAcil: "Emergências",
      s7SeriKayip: "Gatos perdidos",
      s7SeriKedi: "Novos gatos",
      s7SeriBeslenme: "Novos pontos de alimentação",
      s7SeriSu: "Novos pontos de água",
      s7SeriGonullu: "Participações voluntárias",
      s7Ac: "Abrir últimos 7 dias",
      zekaGonullu: "Faltam voluntários",
      azVeriKisa: "Poucos dados",
      heroYanSlogan: "Mesmo bairro, gatos mais felizes.",
      haritaBaslikOzet: "Mapa do bairro (resumo)",
      lejantAcil: "Emergências",
      lejantKayip: "Gatos perdidos",
      lejantSu: "Pontos de água",
      lejantBeslenme: "Alimentação",
      lejantGonullu: "Voluntários",
      zekaSu: "Poucos pontos de água",
      zekaMama: "Pouca alimentação",
      zekaAsi: "Registro de vacinas fraco",
      zekaYarali: "Gatos feridos relatados",
      zekaAcil: "Emergências em aberto",
      zekaBarinak: "Nenhum abrigo registrado",
      zekaTamam: "Nenhuma lacuna clara",
      yasBilinmiyorKisa: "Desconhecida",
      dagilimIpucuBilinmiyor: "{p} dos gatos sem idade registrada.",
      dagilimIpucuCogunluk: "Maior grupo: {grup} ({p}).",
      dagilimYakinda: "Disponível após a atualização do servidor.",
      ustBaslik: "Meu bairro",
      paylas: "Compartilhar",
      paylasMetni: "{yer}: gatos, necessidades e voluntários do bairro.",
      paylasKopyalandi: "Link copiado",
      paylasOlmadi: "Não foi possível compartilhar",
      sekmeGenel: "Visão geral",
      sekmeAnaliz: "Análise",
      sekmeGorevler: "Tarefas",
      sekmeKediler: "Gatos",
      sekmeGonulluler: "Voluntários",
      lejantKedi: "Gatos",
      zekaOzet: "Analisamos seu bairro. Destaques:",
      azVeri: "Poucos registros ainda — a pontuação pode mudar rápido.",
      detayliAnaliz: "Ver análise detalhada",
      dagilimBaslik: "Gatos por idade",
      kediKisa: "gatos",
      yasYavru: "Filhote",
      yasYetiskin: "Adulto",
      yasYasli: "Idoso",
      yasBilinmiyor: "Idade desconhecida",
      dagilimYok: "Nenhum gato registrado ainda.",
      ihtiyacBaslik: "Principais necessidades",
      ihtiyacYok: "Nenhuma necessidade clara agora.",
      oncelikKritik: "Crítica",
      oncelikYuksek: "Alta",
      oncelikOrta: "Média",
      oncelikDusuk: "Baixa",
      son7Baslik: "Últimos 7 dias",
      son7Yok: "Nenhum registro novo nos últimos 7 dias.",
      seriKedi: "Gato",
      seriAcil: "Emergência",
      seriKayip: "Perdido",
      seriBeslenme: "Alimentação",
      seriSu: "Água",
      ctaBaslik: "Juntos, um bairro mais forte.",
      ctaAlt: "Ajude a mudar a vida dos gatos do seu bairro.",
      ctaDugme: "Seja voluntário",
      zekaAlt: "Veja o que seu bairro precisa e aja.",
      zekaBaslik: "Análise de Bairro com IA",
      panoBeslenme: "pontos de comida",
      panoGonullu: "voluntários ativos",
      hizliAcilMerkezi: "Central de Emergência",
      onemliBaslik: "Alertas por perto",
      rozetKayip: "PERDIDO",
      rozetAcil: "URGENTE",
      rozetMama: "COMIDA",
      rozetTehlike: "PERIGO",
      rozetGonullu: "AJUDA",
      durumYok: "Nada urgente por perto agora.",
      sonFotograflar: "Fotos recentes",
      sonAktiviteler: "Atividade recente",
      tumuSayili: "Todas ({n})",
      paylasimYok: "Ainda não há publicações no seu bairro.",
      yakinKediler: "Gatos perto de você",
      haritayiAc: "Abrir mapa",
      cipBesleniyor: "Pontos de comida",
      cipSu: "Pontos de água",
      cipAlisik: "Acostumado a pessoas",
      cipBiliniyor: "Conhecido no bairro",
      haritaEnYakin: "{k} de {n} gatos mais próximos",
      panoBaslik: "Hoje no Seu Bairro",
      panoAlt: "Mais gatos, um bairro melhor.",
      panoKedi: "gatos monitorados",
      panoAcil: "emergências",
      panoKayip: "gatos perdidos",
      panoSu: "pontos de água",
      mahallemiGor: "Ver meu bairro",
      hizliBaslik: "Ações rápidas",
      hizliKaydet: "Registrar gato",
      hizliGordum: "Eu vi um gato",
      hizliAcil: "Emergência",
      hizliMamaSu: "Comida / Água",
      title: "Meu bairro",
      bolgelerBaslik: "Áreas que mais precisam de ajuda",
      heroKedi: "{n} gatos registados no teu bairro.",
      heroYeniKedi: "{n} novos gatos registados nos últimos 30 dias.",
      heroYardimBekleyen: "{n} gatos no teu bairro estão à espera de ajuda.",
      enOnemli: "Maior necessidade:",
      iSu: "Pontos de água",
      iMama: "Pontos de comida",
      iAsi: "Registo de vacinas",
      iYarali: "Gatos feridos",
      iAcil: "Casos urgentes",
      iBarinak: "Abrigo",
      iTamam: "Sem lacunas claras",
      haritaAlt: "Explora gatos e zonas que precisam de ajuda.",
      tamEkran: "Ecrã inteiro",
      lYuksek: "Necessidade alta",
      lOrta: "Média",
      lYeterli: "Suficiente",
      lMama: "Ponto de comida",
      lSu: "Ponto de água",
      tumu: "Todos",
      tumGorevler: "Todas as tarefas",
      ilerlemeTamam: "{n}/{m} concluído",
      kayipRozet: "PERDIDO",
      kayipAdsiz: "Gato perdido",
      kayipYok: "Sem anúncios de desaparecimento perto de ti.",
      azOnce: "agora mesmo",
      saatOnce: "há {n}h",
      gunOnce: "há {n}d",
      son30: "Últimos 30 dias",
      sAcil: "Emergências",
      sMama: "Pontos de comida",
      sSu: "Pontos de água",
      sGonullu: "Voluntários ativos",
      aiBaslik: "Análise de bairro por IA",
      beta: "BETA",
      aiGor: "Ver sugestões da IA",
      haritaBaslik: "Mapa do bairro",
      kayipBaslik: "Gatos perdidos perto de você",
      yardimBaslik: "Ajude seu bairro",
      gorevYok: "Nenhuma tarefa aberta no momento.",
      gorevKatil: "Participar",
      ilerleme: "{n}/{m} voluntários",
      skorBaslik: "Nota do bairro",
      skorYaklasik: "variação aproximada",
      olculmedi: "—",
      yukleniyor: "Carregando…",
      hata: "Não foi possível carregar o relatório do bairro.",
      mahalleYok: "Defina seu bairro para ver esta página.",
      secBaslik: "Escolha seu bairro",
      secCumle: "Depois de escolher, os gatos, as necessidades e as tarefas aqui seguem o seu bairro.",
      secKaydet: "Salvar",
      vazgec: "Cancelar",
      secHata: "Não foi possível salvar seu bairro.",
      tekrarDene: "Tentar de novo",
    },
    bolge: {
      title: "Detalhe da área",
      bolgesi: "Área de {n}",
      tumunuGor: "Ver todos",
      detayliAnaliz: "Análise detalhada",
      tumu: "Todos",
      gecenAy: "{n} face ao mês passado",
      yaklasik: "aprox.",
      kediAdet: "{n} gatos",
      geri: "Voltar",
      kapat: "Fechar",
      son30: "Últimos 30 dias",
      iKritik: "Necessidade crítica",
      iYuksek: "Necessidade alta",
      iOrta: "Necessidade média",
      iDusuk: "Necessidade baixa",
      sKedi: "Gatos registados",
      sMama: "Pontos de comida",
      sSu: "Pontos de água",
      yKuzey: "Norte",
      yGuney: "Sul",
      yDogu: "Leste",
      yBati: "Oeste",
      aiBaslik: "Análise de Área por IA",
      beta: "BETA",
      kedilerBaslik: "Gatos nesta área",
      kediYok: "Nenhum gato registado nesta área.",
      skorBaslik: "Pontuação do bairro",
      skorYaklasik: "variação aproximada",
      dagilimBaslik: "Distribuição por direção",
      dagilimAdet: "{n} gatos",
      bildirimBaslik: "Atividade recente",
      bildirimYok: "Sem atividade nos últimos 30 dias.",
      olculmedi: "—",
      olcumYok: "Ainda não há medições diárias para esta área.",
      hata: "Não foi possível carregar o relatório da área.",
    },
    mahalleListesi: {
      title: "Bairros",
      ara: "Procurar bairro",
      mahalle: "bairros",
      paylasim: "publicações",
      kedi: "gatos",
      alinamadi: "Não foi possível carregar a lista de bairros.",
      tekrarDene: "Tentar novamente",
      aramaBos: "Nenhum bairro corresponde a esta procura.",
      hicYok: "Ainda não há bairros para mostrar.",
      kuralNotu: "Esta lista mostra bairros com publicações, áreas de cobertura ou animais registados.",
    },
    aiAnaliz: {
      sekmeOzet: "Resumo",
      sekmeDetay: "Dados detalhados",
      sekmeOneriler: "Sugestões",
      sonNGun: "Últimos {n} dias",
      paylas: "Compartilhar",
      paylasMetni: "{yer}: análise do bairro para gatos de rua.",
      paylasKopyalandi: "Link copiado",
      paylasOlmadi: "Não foi possível compartilhar",
      detayBaslik: "Pontuação por área",
      veriYok: "Ainda sem medição.",
      skorEtiketi: "Saúde do bairro",
      genelDurum: "Situação geral",
      genelSkor: "Os recursos do bairro estão \"{etiket}\".",
      genelKediArtti: "Mais {n} gatos registrados em 30 dias.",
      genelKediAzaldi: "{n} gatos a menos que há 30 dias.",
      genelKediSabit: "O número de gatos registrados não mudou em 30 dias.",
      genelBulgu: "{n} lacunas encontradas.",
      genelBulguYok: "Nenhuma lacuna clara nas áreas medidas.",
      farkRozeti: "{d} pontos vs mês passado",
      aiOzeti: "Resumo IA",
      ozetSablon: "Resumo",
      tumu: "Todos",
      bulgularBaslik: "Principais achados",
      bulguAcil: "Emergências abertas",
      bulguYarali: "Relatos de gatos feridos",
      bulguMama: "Poucos pontos de alimentação",
      bulguSu: "Poucos pontos de água",
      bulguAsi: "Controle de vacinas fraco",
      bulguBarinak: "Sem casinha de gato",
      bulguGonullu: "Precisa de voluntários",
      eylemHaritada: "Ver no mapa",
      eylemGorev: "Criar tarefa",
      eylemDetaylar: "Ver detalhes",
      eylemGonullu: "Chamar voluntários",
      eylemVakalar: "Ver casos",
      son7AcilEk: "{n} novos nos últimos 7 dias.",
      bulguYok: "Nenhuma lacuna clara.",
      haritadaGorBag: "Ver no mapa",
      bolgeBaslik: "Análise por região",
      kademeMukemmel: "Excelente",
      kademeIyi: "Bom",
      kademeGelistir: "Melhorar",
      kademeKritik: "Crítico",
      ilceDuzeyiNotu: "Nível distrito",
      notSu: "Pouca água",
      notMama: "Falta comida",
      notAsi: "Vacinas",
      notYarali: "Feridos",
      notAcil: "Emergência",
      notBarinak: "Sem casinha",
      notGonullu: "Voluntários",
      notTamam: "Bom no geral",
      bolgeYok: "As notas por região aparecem com dados de local suficientes.",
      aralikSec: "Escolher período",
      trendBaslik: "Análise de tendência",
      seriKedi: "Gatos",
      seriAcil: "Emergência",
      seriKayip: "Perdidos",
      seriBeslenme: "Alimentação",
      seriSu: "Água",
      trendBirikiyor: "As medições diárias estão se acumulando; o gráfico aparece após dois dias.",
      oneriBaslik: "Sugestão IA",
      bakilanBaslik: "O que esta análise verificou",
      bakilanKedi: "Gatos registados",
      bakilanSu: "Pontos de água",
      bakilanMama: "Pontos de comida",
      bakilanKulube: "Abrigos",
      bakilanAcil: "Gatos em estado urgente",
      bakilanYarali: "Relatos de feridos (30 dias)",
      oneriAlinamadi: "Não foi possível carregar as sugestões — o servidor não respondeu. Isso não significa que não há lacunas.",
      tekrarDene: "Tentar de novo",
      acikGorevNotu: "Uma sugestão fica oculta enquanto houver uma tarefa aberta do mesmo tipo.",
      sayacKedi: "gatos",
      sayacSu: "pontos de água",
      sayacMama: "pontos de comida",
      sayacKulube: "abrigos",
      sayacYarali: "relatos de feridos (30 dias)",
      sekmeBolge: "Análise da Região",
      sekmeKedi: "Reconhecimento de Gato",
      ozetBaslikPano: "Avaliação geral",
      ozetCumle: "A situação do bairro em {yer} é {durum}. {n} lacuna(s) prioritária(s) encontrada(s).",
      durumBilinmiyor: "não medida",
      yerYok: "Bairro não selecionado",
      balon: "Um bairro melhor é possível!",
      eksiklerBaslik: "Lacunas prioritárias",
      eksikSu: "Pontos de água insuficientes",
      eksikMama: "Pontos de comida insuficientes",
      eksikYarali: "Relatos de gatos feridos",
      eksikKulube: "Sem abrigo registrado",
      eksikVet: "Gatos que precisam de cuidado urgente",
      bugunBaslik: "3 coisas que você pode fazer hoje",
      eylemSu: "Adicionar ponto de água",
      eylemMama: "Criar ponto de comida",
      eylemYarali: "Verificar gato ferido",
      eylemKulube: "Construir um abrigo",
      eylemVet: "Buscar apoio veterinário",
      haritadaGor: "Ver no mapa",
      eylemDetay: "Ver detalhes",
      eylemListe: "Ver lista",
      yogunBolge: "Área com mais gatos no bairro",
      gorevOlustur: "Criar tarefa",
      etkiOnEk: "Impacto esperado:",
      eksikYok: "Nenhuma lacuna prioritária no momento.",
      title: "Análise por IA",
      beta: "BETA",
      sekmeTani: "Identificar por foto",
      sekmeSonuc: "Resultados da análise",
      sekmeIstatistik: "Estatísticas",
      taniBaslik: "Identificar um gato por foto",
      taniAciklama: "Envie uma foto; ela é comparada com gatos registados e anúncios de desaparecimento abertos.",
      taniGit: "Ir para identificação por foto",
      sonAnalizler: "Análises recentes",
      analizYok: "Ainda não fez nenhuma análise.",
      olcumBaslamadi: "O histórico de análises começou a ser guardado agora; as anteriores não foram registadas.",
      eslesti: "Correspondência",
      eslesmedi: "Sem correspondência",
      adsizIlan: "Nome não indicado",
      ozetBaslik: "Resumo de análises nesta área",
      sAnaliz: "Análises",
      sEslesme: "Correspondências",
      sYeniKedi: "Gatos novos",
      oran: "{n}/{m} análises atingiram o limiar",
      oranNotu: "Isto não é uma taxa de precisão: quantas correspondências estavam certas não foi medido.",
      son30: "Últimos 30 dias",
      yukleniyor: "A carregar…",
      hata: "Não foi possível carregar as análises.",
    },
    kayipFormu: {
      baslik: "Sobre o gato",
      adEtiket: "Nome",
      adYerTutucu: "ex. Boncuk",
      adIpucu: "O nome que os vizinhos vão chamar — ajuda mais que tudo.",
      kisirEtiket: "Castrado",
      kEvet: "Sim",
      kHayir: "Não",
      cinsiyetEtiket: "Sexo",
      cErkek: "Macho",
      cDisi: "Fêmea",
      cBilinmiyor: "Não sei",
      yasEtiket: "Idade",
      yasIpucu: "ex. 1,5 ano",
      mizacEtiket: "Temperamento",
      mUrkek: "Arisco",
      mSokulgan: "Sociável",
      mSaldirgan: "Defensivo",
      mSakin: "Calmo",
      iletisimEtiket: "Contato (opcional)",
      iletisimIpucu: "Só aparece atrás do botão Ligar, para quem está logado. Não coloque na descrição.",
      notNumaraUyarisi: "Parece haver um telefone na descrição. Coloque no campo de contato — assim ele não fica exposto.",
    },
    kayipBildir: {
      title: "Aviso de gato perdido",
      subtitle: "Adicione fotos e como é o seu gato. A localização vem depois.",
      fotoZorunlu: "É preciso ao menos uma foto — é o único ponto de comparação de quem encontrar.",
      varsa: "se souber",
      renkEtiket: "Cor",
      konumTitle: "Onde e quando foi visto",
      konumSubtitle: "Marque o ponto no mapa. Sem isso, seu anúncio não alcança quem está por perto.",
      konumumuKullan: "Usar minha localização atual",
      konumAliniyor: "Obtendo localização…",
      konumZorunlu: "A localização é obrigatória. Se o GPS não abrir, toque no mapa para marcar.",
      konumIsaretlendi: "Localização marcada",
      konumYok: "Sem localização ainda",
      yaricapEtiket: "Raio de busca",
      yaricapIpucu: "Um gato doméstico perdido costuma se esconder a algumas centenas de metros nos primeiros dias.",
      sonGorulmeEtiket: "Visto pela última vez",
      ekEtiket: "Informação adicional",
      ekYerTutucu: "Qualquer coisa que ajude: um hábito, onde costuma se esconder, em quem confia…",
      mahalleEtiket: "Bairro",
      duzenle: "Editar",
      ozetAdsiz: "Gato sem nome",
      yayinla: "Publicar anúncio de perdido",
      yayinlaniyor: "Publicando…",
      errSend: "Não foi possível publicar o anúncio. Tente de novo.",
      okTitle: "Seu anúncio está no ar",
      okDesc: "Quem está por perto foi avisado. Cada avistamento chegará até você.",
      okView: "Abrir meu anúncio",
      okList: "Todos os gatos perdidos",
      acikIlanBaslik: "Você já tem um anúncio aberto",
      acikIlanMetin: "Um segundo anúncio divide a busca. É o mesmo gato?",
      acikIlanGit: "Abrir aquele anúncio",
      acikIlanBaska: "Não, este é outro gato",
      vazgec: "Cancelar",
      renkYerTutucu: "ex. rajado, peito branco",
      ayirtEtiket: "Sinais particulares",
      ayirtYerTutucu: "Coleira, cicatriz, orelha marcada, o jeito de andar…",
    },
    kayipListe: {
      odul: "recompensa",
      baslik: "Gatos perdidos",
      rozet: "PERDIDO",
      ilanAc: "Publicar anúncio de perdido",
      fotoAra: "Buscar por foto",
      yukleniyor: "Carregando…",
      yeniden: "Tentar de novo",
      hata: "Não foi possível carregar os anúncios.",
      bos: "Nenhum anúncio de perdido aberto — boa notícia.",
      yakindaYok: "Nada por perto, então mostramos anúncios mais distantes.",
    },
    acilDetay: {
      durumBaslik: "Estado do gato",
      aciklamaBaslik: "Descrição",
      bildirenBaslik: "Relatado por",
      bildirdi: "relatou {n}",
      konumBaslik: "Localização",
      haritadaAc: "Abrir no mapa",
      paylas: "Compartilhar",
      sil: "Excluir ocorrência",
      silOnayBaslik: "Excluir esta ocorrência?",
      silOnayMetin: "Ela some da lista e do mapa. Não dá para desfazer.",
      silEvet: "Sim, excluir",
      vazgec: "Cancelar",
      siliniyor: "Excluindo…",
      silHata: "Não foi possível excluir a ocorrência.",
      silYetki: "Só quem relatou pode excluir.",
      fotoSayaci: "{a} / {b}",
      yorumlar: "Comentários",
      yorumYok: "Ainda sem comentários — os vizinhos se organizam aqui.",
      yorumYaz: "Escreva um comentário…",
      yorumGonder: "Enviar",
      yorumGidiyor: "Enviando…",
      yorumHata: "Não foi possível enviar o comentário.",
      yorumOkunmadi: "Não foi possível carregar os comentários.",
      yorumGiris: "Entre para comentar.",
      yorumSil: "Excluir comentário",
      yorumSilHata: "Não foi possível excluir o comentário.",
      begen: "Apoiar",
      begenHata: "Não deu certo.",
    },
    anasayfaAlt: {
      baslik: "Sobre o Sokak Kedileri",
      hakkinda: "Sobre nós",
      yardim: "Ajuda",
      gizlilik: "Privacidade",
      sartlar: "Termos de uso",
      telif: "Feito com amor pelos gatos de rua.",
    },
    ihbarFormu: {
      title: "Adicionar relato",
      sub: "Onde você viu o gato?",
      locTitle: "Local",
      locUse: "Usar minha localização",
      locPicked: "Local marcado",
      locHelp: "Toque no mapa para mover o pino.",
      locNeeded: "O local é obrigatório — um relato sem ele não aparece no mapa.",
      noteTitle: "O que você viu?",
      notePh: "Embaixo de um carro branco, muito arisco. Correu para o parque.",
      photoTitle: "Foto (opcional)",
      photoAdd: "Adicionar foto",
      photoRemove: "Remover foto",
      photoHint: "Uma foto transforma seu relato em prova — a IA pode compará-la.",
      guvenTitle: "Qual sua certeza?",
      gBenzer: "Pode ser parecido",
      gEminDegil: "Não tenho certeza",
      gFoto: "Com foto",
      guvenFotoNote: "Com foto, seu relato é marcado como “Com foto”.",
      send: "Enviar relato",
      sending: "Enviando…",
      okTitle: "Relato enviado 🧡",
      okBody: "O dono foi avisado. Obrigado.",
      okBack: "Voltar à área de busca",
      errSend: "Não foi possível enviar o relato. Tente de novo.",
      back: "Voltar",
    },
    aiKars: {
      title: "Comparação por IA",
      seenSimilar: "Viu um gato parecido?",
      seenSimilarDesc: "Se viu um gato, tire uma foto ou escolha da galeria. A IA vai comparar com o gato perdido.",
      camera: "Câmera",
      gallery: "Galeria",
      aiNote: "A IA é uma ajudante. A decisão final é sua.",
      resultTitle: "Resultado da comparação",
      new: "Novo",
      yourPhoto: "Sua foto",
      lostCat: "Gato perdido",
      levelHigh: "Semelhança alta",
      levelMid: "Alguma semelhança",
      levelLow: "Semelhança baixa",
      levelHighDesc: "Este gato se parece bastante com o perdido.",
      levelMidDesc: "Algumas características batem, outras não.",
      levelLowDesc: "As características em geral não batem.",
      fPattern: "Padrão da pelagem",
      fMarks: "Marcas distintivas",
      fVisual: "Aparência geral",
      fAge: "Faixa etária",
      fLocation: "Proximidade do local",
      stateSimilar: "parecido",
      statePartial: "parcialmente parecido",
      stateDifferent: "diferente",
      stateUnmeasured: "não medido",
      report: "Relatar este gato",
      unsureTitle: "Está em dúvida?",
      unsureDesc: "Relate mesmo assim. Cada relato ajuda.",
      tipsTitle: "Dicas para um resultado melhor",
      tip1: "Deixe o rosto bem nítido",
      tip2: "Se possível, fotografe de vários ângulos",
      tip3: "Fotografe com boa luz",
      tip4: "Mostre também o entorno",
      comparing: "Comparando…",
      errCompare: "A comparação falhou. Tente de novo.",
      notMeasuredNote: "Características que a IA não conseguiu medir aparecem assim — não medido não é o mesmo que diferente.",
    },
    acil: {
      merkezHeroNot: "Cada gato importa",
      merkezBaslik: "Central de Emergência",
      merkezHeroBaslik: "Vamos salvar vidas juntos",
      merkezHeroAlt: "Vamos agir juntos pelos gatos feridos, em perigo ou necessitados.",
      merkezVakalariGor: "Ver emergências",
      merkezVakalariGorAlt: "Veja os gatos que esperam ajuda agora.",
      merkezVakaBildir: "Relatar emergência",
      merkezVakaBildirAlt: "Crie uma nova emergência.",
      merkezYakinVakalar: "Emergências perto de mim",
      merkezKategoriler: "Categorias de emergência",
      merkezKatYarali: "Gatos feridos",
      merkezKatTehlike: "Zonas de perigo",
      merkezKatKayip: "Gatos perdidos",
      merkezKatMamaSu: "Comida / Água",
      merkezKatGonullu: "Ajuda voluntária",
      merkezAltBaslik: "Cada relato pode salvar uma vida.",
      merkezAltMetin: "Ao relatar até situações duvidosas, você ajuda os gatos a viverem em bairros mais seguros.",
      merkezVakaYok: "Nenhuma emergência aberta perto de você agora.",
      title: "Relatar emergência",
      subtitle: "Viu um gato que precisa de ajuda? Relate rápido e alcance os voluntários mais próximos.",
      safetyNote: "Em uma emergência, priorize sua própria segurança. Não se coloque em risco.",
      stepInfo: "Detalhes",
      stepLocation: "Local",
      stepSend: "Enviar",
      catInjured: "Gato ferido",
      catInjuredDesc: "Gato ferido, doente ou sofrendo",
      catLost: "Avisar gato perdido",
      catLostDesc: "Seu gato sumiu — publique um anúncio de busca",
      catFood: "Necessidade de comida / água",
      catFoodDesc: "Sem ponto de alimentação ou precisando de apoio",
      catDanger: "Área perigosa",
      catDangerDesc: "Obra, trânsito, risco de envenenamento etc.",
      catVolunteer: "Ajuda de voluntário",
      catVolunteerDesc: "Quando é preciso transporte, lar temporário ou ajuda no local",
      injuredTitle: "Relato de gato ferido",
      injuredDesc: "Compartilhe a condição do gato que você viu. Avisaremos os voluntários imediatamente.",
      injuredPlaceholder: "Condição, comportamento, ferimento do gato…",
      severityLabel: "Nível de urgência",
      severityHigh: "Urgente",
      severityMid: "Média",
      severityLow: "Baixa",
      foodTitle: "Necessidade de comida / água",
      foodDesc: "Crie um relato para gatos desta área que precisam de comida ou água.",
      needLabel: "O que é preciso",
      needFood: "Comida",
      needWater: "Água",
      needBoth: "Ambos",
      foodPlaceholder: "Ex. os gatos de rua desta área precisam de apoio com comida…",
      dangerTitle: "Área perigosa",
      dangerDesc: "Existe uma área que coloca gatos em risco? Avise outros protetores.",
      dangerTypeLabel: "Tipo de perigo",
      multiSelect: "Você pode escolher mais de um",
      dTraffic: "Risco de trânsito",
      dConstruction: "Obra / demolição",
      dPoison: "Risco de envenenamento",
      dViolence: "Violência / maus-tratos",
      dWell: "Poço / espaço fechado",
      dOther: "Outro",
      dangerPlaceholder: "Adicione detalhes sobre a área…",
      volunteerTitle: "Ajuda de voluntário",
      volunteerDesc: "Este gato precisa de um voluntário? Compartilhe seu pedido para que voluntários próximos saibam.",
      supportLabel: "Tipo de ajuda",
      sTransport: "Transporte",
      sFoster: "Lar temporário",
      sOnSite: "Ajuda no local",
      sVet: "Apoio veterinário",
      sSupplies: "Suprimentos",
      sOther: "Outro",
      volunteerPlaceholder: "Que tipo de ajuda é necessária?",
      photoLabel: "Adicionar fotos",
      optional: "(opcional)",
      photoTake: "Câmera",
      photoPick: "Escolher da galeria",
      photoMax: "Até {n} fotos",
      descLabel: "Descrição",
      minChars: "Pelo menos {n} caracteres",
      continue: "Continuar",
      back: "Voltar",
      locTitle: "Escolher local",
      locDesc: "Marque o local do gato no mapa ou use sua localização atual.",
      locUseCurrent: "Usar minha localização",
      locAddress: "Endereço (opcional)",
      locNeighborhood: "Bairro",
      locNeighborhoodHint: "ex. Moda, Kadıköy",
      locUnsupported: "Este navegador não suporta localização.",
      locDenied: "Permissão de localização negada. Digite o bairro.",
      locFailed: "Não foi possível obter a localização. Toque no mapa ou digite o bairro.",
      okNotified: "voluntários avisados",
      errSend: "Não foi possível enviar. Tente novamente.",
      errLogin: "Entre na conta para enviar. Seu formulário foi mantido.",
      errNetwork: "Sem conexão. Verifique a rede e tente de novo.",
      errList: "Não foi possível carregar a lista.",
      retry: "Tentar de novo",
      detayGiris: "Entre para ver esta ocorrência.",
      detayYok: "Esta ocorrência não está mais disponível.",
      detayKonumsuz: "Nenhuma localização foi salva nesta ocorrência.",
      detayBildiren: "Relatado por {ad}",
      detaySilmeYok: "Excluir a própria ocorrência ainda não está disponível.",
      locRequired: "O local é obrigatório para que quem ajuda encontre o ponto.",
      reviewTitle: "Revise seu relato",
      reviewDesc: "Está tudo certo? Confira antes de enviar.",
      rType: "Tipo",
      rLocation: "Local",
      rPhotos: "Fotos ({n})",
      submit: "Enviar relato",
      okTitle: "Relato enviado!",
      okDesc: "Obrigado! Os voluntários mais próximos foram avisados. Você pode acompanhar.",
      okMap: "Ver no mapa",
      okList: "Ver emergências",
      okAgain: "Fazer outro relato",
      okAvgLabel: "Tempo médio de resposta",
      okAvgMinutes: "{n} minutos",
      listTitle: "Emergências",
      fAll: "Todos",
      fInjured: "Feridos",
      fFood: "Comida / água",
      fDanger: "Perigo",
      fVolunteer: "Voluntário",
      badgeHigh: "URGENTE",
      badgeMid: "MÉDIA",
      badgeLow: "BAIXA",
      badgeNew: "NOVO",
      listEmpty: "Nenhuma emergência aberta perto de você — boa notícia.",
      errNoCategory: "Primeiro escolha o que você está relatando.",
      sending: "Enviando…",
      kaydet: "Salvar",
      kaydedildi: "Salvo",
      kaydettiklerim: "Casos salvos",
      kaydetHata: "Não deu certo.",
      kaydetBos: "Nada salvo ainda. Toque no coração de um caso para acompanhá-lo.",
      kaydetGiris: "Entre para salvar casos.",
      yakinFiltre: "Mostrando casos num raio de {n} km.",
      tumunuGoster: "Mostrar todos",
      yakiniGoster: "Só os próximos",
      tumVakalar: "Todos os casos abertos.",
    },
    footer: {
      discover: "Descobrir",
      community: "Comunidade",
      connect: "Conectar",
      brandDescription: "Reunimos voluntários, pontos de cuidado e apoio urgente para que os animais de rua vivam com mais segurança, saúde e carinho.",
    },
    assistant: {
      name: "Assistente Patinha",
      readyStatus: "Pronto · custo zero primeiro",
      napStatus: "Tirando uma soneca até amanhã 😴",
      placeholder: "Pergunte sobre animais de rua…",
      napHint: "Estou tirando uma soneca — até amanhã!",
      suggestionsTitle: "Experimente uma destas:",
      disclaimer: "Conselho de IA · não substitui um veterinário",
      networkError: "A rede está de mau humor agora 🐾 tente de novo em instantes.",
      guestPrompt: "Oi! Adoraria conversar, mas preciso te reconhecer primeiro. Entre ou cadastre-se — é grátis e leva poucos segundos.",
      guestLoginCta: "Entre para conversar comigo",
    },
    nav: {
      home: "Início",
      database: "Banco de dados",
      identify: "Identificar com IA",
      identifyShort: "Scan IA",
      map: "Mapa",
      sightings: "Relatar emergência",
      emergencies: "Relatar emergência",
      volunteers: "Voluntários",
      feeding: "Pontos de alimentação",
      leaderboard: "Ranking",
      photoContest: "Concurso de fotos",
      badges: "Conquistas",
      safety: "Segurança",
      observationHouse: "Casa de observação",
      neighborhood: "Meu bairro",
      messages: "Mensagens",
      notifications: "Notificações",
      dashboard: "Painel",
      profile: "Perfil",
      login: "Entrar",
      register: "Cadastrar-se",
      logout: "Sair",
      addCat: "Adicionar um animal",
      admin: "Administração",
      adminEmergencies: "Fila de emergências",
    },
    landing: {
      overline: "Bem-estar animal comunitário",
      heroTitle: "Todo animal de rua tem uma história.",
      heroSubtitle: "Identifique, acompanhe e ajude a protegê-los.",
      liveBadge: "Comunidade ao vivo",
      ctaPrimary: "Cadastrar um animal",
      ctaSecondary: "Banco de dados",
      statCats: "Gatos catalogados",
      statSightings: "Emergências relatadas",
      statFeeding: "Pontos de alimentação",
      statVaccinated: "Vacinados",
      featuresOverline: "O que você pode fazer",
      f1Title: "Identificação por foto com IA",
      f1Desc: "Envie uma foto e nossa IA descreve pelagem, raça, idade e marcas distintivas — depois compara com o banco de dados local.",
      f2Title: "Mapa do bairro",
      f2Desc: "Marque animais, emergências e pontos de alimentação na sua cidade. Filtre por bairro, cor ou estado de saúde.",
      f3Title: "Saúde e vacinação",
      f3Desc: "Acompanhe raiva, vacinas múltiplas, castração e observações. Dê a voluntários e veterinários locais registros de verdade.",
      f4Title: "Avisos de emergência",
      f4Desc: "Viu um filhote em apuros? Marque o local — ou envie uma foto com a localização — em 30 segundos e avise voluntários e veterinários por perto.",
      finalCta: "Ajude os animais do seu bairro hoje.",
      finalCtaSub: "Basta uma foto para começar.",
    },
    auth: {
      loginTitle: "Bem-vindo de volta",
      registerTitle: "Crie sua conta",
      registerSuccess: "Conta criada!",
      email: "E-mail",
      password: "Senha",
      name: "Nome completo",
      role: "Tipo de conta",
      rolePublic: "Público — navegar e relatar emergências",
      roleVolunteer: "Voluntário — gerenciar animais e pontos de alimentação",
      roleAdmin: "Administrador — acesso total",
      volunteerType: "Especialidade do voluntário",
      volunteerTypeVet: "Veterinário voluntário — tratar animais de rua feridos",
      volunteerTypeIndividual: "Voluntário individual — alimentar, acolher, resgatar",
      submitLogin: "Entrar",
      submitRegister: "Criar conta",
      switchToRegister: "Ainda não tem conta? Cadastre-se",
      switchToLogin: "Já tem conta? Entrar",
      forgotPassword: "Esqueceu sua senha?",
      forgotLink: "Esqueci a senha",
      backToLogin: "Voltar para entrar",
      forgotTitle: "Redefina sua senha",
      forgotIntro: "Informe seu e-mail e enviaremos um link para criar uma nova senha.",
      forgotSubmit: "Enviar link de redefinição",
      forgotSentBody: "Se encontrarmos uma conta com esse e-mail, o link de redefinição está a caminho. Confira sua caixa de entrada (e o spam).",
      forgotDevTitle: "Modo de desenvolvimento",
      forgotDevBody: "O e-mail ainda não está configurado — use o link abaixo para continuar:",
      newPassword: "Nova senha",
      confirmPassword: "Confirmar senha",
      resetTitle: "Defina uma nova senha",
      resetIntro: "Escolha algo de que você se lembre — pelo menos 8 caracteres.",
      resetSubmit: "Atualizar senha",
      resetSuccess: "Senha atualizada. Você já pode entrar.",
      resetFailed: "Algo deu errado — peça um novo link de redefinição.",
      resetMinLen: "A senha deve ter pelo menos 8 caracteres.",
      resetMismatch: "As senhas não coincidem.",
      resetNoToken: "Falta o token neste link. Peça um novo e-mail de redefinição.",
      invalid: "Credenciais inválidas",
      emailTaken: "E-mail já cadastrado",
    },
    cats: {
      title: "Banco de dados de gatos",
      loadMore: "Carregar mais",
      subtitle: "Veja todos os gatos catalogados na nossa comunidade.",
      search: "Busque por nome, cor, raça…",
      filterNeighborhood: "Bairro",
      filterHealth: "Estado de saúde",
      add: "Adicionar um gato",
      empty: "Nenhum gato ainda. Seja o primeiro a adicionar!",
      details: "Detalhes",
      visits: "visitas",
      aiPortraitShortcut: "Criar retrato com IA",
      aiPortraitRegenerate: "Gerar outro retrato com IA",
      aiPortraitShortcutHint: "Gere um retrato em estilo de estúdio a partir das fotos reais deste gato",
      aiBadgeHasTooltip: "Retrato com IA pronto",
      aiBadgeMissingTooltip: "Ainda sem retrato com IA — abra e gere um",
      health: {
        healthy: "Saudável",
        minor_issues: "Problemas leves",
        needs_attention: "Precisa de atenção",
        critical: "Crítico",
        injured: "Ferido",
        sick: "Doente",
        recovering: "Em recuperação",
        unknown: "Desconhecido",
      },
    },
    addCat: {
      title: "Adicionar um gato",
      subtitle: "Envie uma foto — nossa IA preenche os detalhes para você.",
      uploadCta: "Enviar foto",
      analyzing: "Analisando a foto com IA…",
      analyzeAgain: "Analisar de novo",
      name: "Nome",
      color: "Cor",
      pattern: "Padrão",
      breed: "Raça",
      age: "Idade estimada",
      size: "Porte",
      features: "Marcas distintivas",
      neighborhood: "Bairro / Região",
      lat: "Latitude",
      lng: "Longitude",
      health: "Estado de saúde",
      spayed: "Castrado",
      notes: "Observações",
      save: "Salvar gato",
      success: "Gato salvo!",
      error: "Não foi possível salvar o gato",
      aiError: "A análise da IA falhou",
      tooLarge: "A foto é grande demais (máx. 6 MB). Escolha um recorte menor.",
      invalidFields: "Campos obrigatórios ausentes ou inválidos (localização / nome).",
      serverDown: "Não foi possível criar o perfil — verifique a localização e os campos obrigatórios.",
    },
    identify: {
      registerCat: "Cadastrar este gato",
      registerDog: "Cadastrar este cão",
      noMatch: "Nenhuma correspondência encontrada",
      noMatchCat: "Este gato pode já estar cadastrado e talvez não o tenhamos encontrado.",
      noMatchDog: "Este cão pode já estar cadastrado e talvez não o tenhamos encontrado.",
      reallyNewCat: "Este é mesmo um gato novo",
      reallyNewDog: "Este é mesmo um cão novo",
      title: "Identificar com IA",
      subtitle: "Envie a foto de um animal de rua. Nossa IA vai descrevê-lo e procurar possíveis correspondências no banco de dados.",
      upload: "Envie uma foto",
      analyze: "Analisar e comparar",
      analyzing: "A IA está olhando a foto…",
      neighborhood: "Filtrar por bairro (opcional)",
      analysis: "Análise da IA",
      matches: "Possíveis correspondências",
      noMatches: "Nenhum animal parecido encontrado. Quer cadastrar como novo?",
      addAsNew: "Salvar como novo",
      score: "Semelhança",
      topMatch: "Melhor correspondência",
      otherMatches: "Outras correspondências possíveis",
      matchFound: "Correspondência encontrada",
      health: {
        title: "Avaliação de saúde",
        statusLabel: "Estado de saúde",
        urgencyLabel: "Prioridade",
        issuesLabel: "Sinais visíveis",
        adviceLabel: "O que fazer agora",
        bodyLabel: "Condição corporal",
        statuses: {
          healthy: "Saudável",
          minor_issues: "Problemas leves",
          needs_attention: "Precisa de atenção",
          critical: "Crítico",
          unknown: "Não foi possível avaliar",
        },
        urgencies: {
          URGENT: "Urgente — veterinário agora",
          MEDIUM: "Média — veterinário em 1 a 3 dias",
          STABLE: "Estável",
          UNKNOWN: "Incerto",
        },
        body: {
          emaciated: "Muito magro",
          thin: "Magro",
          ideal: "Ideal",
          overweight: "Acima do peso",
          unknown: "Desconhecido",
        },
        disclaimer: "Apenas uma avaliação de IA — não substitui um veterinário.",
      },
    },
    liveIdentify: {
      overline: "IA ao vivo",
      title: "Aponte a câmera para um gato",
      subtitle: "Mire no rosto de um gato de rua — nossa IA vai reconhecê-lo pela câmera ao vivo e comparar com o banco de dados.",
      tryLive: "Testar com a câmera",
      startCamera: "Ligar câmera",
      stopCamera: "Desligar câmera",
      scanNow: "Escanear agora",
      pauseAuto: "Pausar escaneamento automático",
      resumeAuto: "Retomar escaneamento automático",
      autoOn: "Escaneando automaticamente",
      autoOff: "Modo manual",
      cameraOff: "A câmera está desligada — toque em Ligar para começar.",
      aimAtCat: "Mire no rosto de um gato e a IA começará a analisar…",
      startToBegin: "Toque em Ligar câmera para começar.",
      freeHint: "Plano gratuito: toque em Escanear uma vez por tentativa (usa 1 das suas 10 chamadas de IA mensais). Assine para escaneamento automático sem parar.",
      upsell: "Escaneamento automático com o Premium",
    },
    scanCat: {
      overline: "Assistente de perfil com IA",
      title: "Foto de passaporte do gato",
      subtitle: "Aponte a câmera para o gato. A IA consulta o banco de dados primeiro e depois guia você por três ângulos — frente, lado e topo — para montar um perfil perfeito.",
      navLabel: "Escanear um gato",
      startCamera: "Ligar câmera",
      startCameraNative: "Tirar fotos (3)",
      pickFromGallery: "Escolher 3 fotos da galeria",
      galleryFallbackHint: "Se a câmera não abrir, você ainda pode criar um perfil escolhendo 3 fotos da galeria.",
      galleryPrimaryHint: "Escolha 3 fotos da galeria para criar um perfil de gato. A busca por IA, o recorte automático e os retratos Premium funcionam do mesmo jeito.",
      galleryNeed3: "Escolha exatamente 3 fotos.",
      tryCameraBeta: "Testar a câmera",
      cameraUnavailableFallback: "A câmera não abriu; você pode escolher 3 fotos da galeria.",
      startUnavailableFallback: "A câmera não iniciou; você pode escolher fotos da galeria.",
      partialFallback: "Só {n}/3 fotos capturadas. Continuando com a galeria para as demais.",
      cameraCannotOpen: "Não foi possível abrir a câmera. Verifique o app da câmera ou escolha fotos da galeria.",
      nativeCaptureStep: "Foto capturada",
      nativeAngleStart: "Capturando o ângulo",
      partialCaptures: "{n}/3 fotos capturadas. Toque para repetir os ângulos que faltam.",
      buttonTapped: "Botão da câmera pressionado",
      stopCamera: "Desligar câmera",
      cameraDenied: "Permissão de câmera negada. Autorize o acesso à câmera para este site nas configurações do navegador.",
      cameraDeniedNative: "Permissão de câmera negada. Ative a permissão da câmera nas configurações do celular.",
      cameraDeniedPWA: "Não foi possível obter a permissão de câmera no app instalado. Você pode abri-lo no Chrome para escanear com a câmera.",
      cameraUnsupported: "Este aparelho não é compatível com a API da câmera.",
      cameraGenericError: "Não foi possível acessar a câmera.",
      openInChrome: "Abrir no Chrome",
      openAppSettings: "Abrir configurações",
      cameraRetry: "Tentar de novo",
      cameraWaiting: "Aguardando a permissão da câmera…",
      matching: "Consultando o banco de dados…",
      matchFound: "Encontramos uma correspondência!",
      matchPrompt: "É o mesmo gato?",
      matchYes: "Sim, abrir perfil",
      matchNo: "Não, criar perfil novo",
      notFound: "Perfil não encontrado",
      notFoundSub: "Vamos criar um novo — três ângulos, guiados por IA.",
      startWizard: "Começar a escanear",
      stages: {
        front: "Vista frontal",
        side: "Vista lateral",
        top: "Vista de cima",
      },
      stageHint: {
        front: "Aponte a câmera direto para o rosto do gato. Os dois olhos visíveis.",
        side: "Vá para o lado — perfil inteiro, só um olho e uma orelha visíveis.",
        top: "Segure a câmera acima da cabeça, olhando para o alto do crânio.",
      },
      captured: "Capturada!",
      retake: "Tirar de novo",
      tooFast: "Segure firme…",
      holdCatOnScreen: "Mantenha o gato no enquadramento",
      timeoutCloser: "Chegue um pouco mais perto",
      timeoutSteady: "Segure a câmera firme",
      timeoutLight: "Luz insuficiente — vá para um lugar mais claro",
      timeoutShade: "Muito reflexo — vá para a sombra",
      collectingFrames: "Escolhendo o melhor quadro…",
      locationCaptured: "Localização capturada",
      locationNeighborhoodOnly: "Seguindo apenas com o bairro",
      locationMissing: "Sem localização — ative o GPS ou digite um bairro",
      locationRequired: "Capture o GPS ou digite um bairro.",
      locating: "Obtendo a localização…",
      gpsRefresh: "Atualizar GPS",
      gpsLocked: "Localização capturada",
      gpsDenied: "Permissão de localização negada. Autorize nas configurações do celular ou digite um bairro.",
      gpsUnavailable: "Não foi possível obter a localização (sem sinal). Você pode continuar com um bairro.",
      gpsTimeout: "A busca pela localização expirou. Você pode continuar com um bairro.",
      gpsError: "Não foi possível obter a localização. Você pode continuar com um bairro.",
      gpsUnsupported: "Este aparelho não é compatível com serviços de localização.",
      autoStarting: "Gato detectado, iniciando o escaneamento…",
      autoDetectActive: "Detectando o gato… Mantenha o rosto no enquadramento. O escaneamento começa sozinho.",
      autoProfileTitle: "Foto de perfil automática",
      autoFree: "Grátis",
      autoProcessing: "Processando…",
      premiumOnly: "Premium",
      aiPortraitSectionTitle: "Retrato de perfil com IA",
      aiPortraitOpenBtn: "Gerar retrato com IA",
      aiPortraitFreeHint: "Assinantes Premium podem gerar um retrato frontal com IA a partir das suas fotos.",
      aiPortraitTitle: "Criar retrato com IA",
      aiPortraitOverline: "Recurso Premium",
      aiPortraitBody: "A IA gera UM retrato frontal usando suas 3 fotos reais como referência. A identidade (cor, padrão, olhos) é preservada. Isso consome 1 crédito de retrato.",
      aiPortraitLoading: "Carregando os créditos…",
      aiPortraitQuota: "Créditos restantes",
      aiPortraitTierLabel: "Plano",
      aiPortraitFreeNotice: "Os retratos com IA são para assinantes Premium / Super Premium. A foto de perfil automática gratuita já foi gerada.",
      aiPortraitConfirm: "Gerar",
      aiPortraitGenerating: "Gerando…",
      aiPortraitReady: "Retrato com IA pronto!",
      aiPortraitCached: "Do cache — nenhum crédito usado.",
      aiPortraitFailed: "Não foi possível gerar o retrato com IA.",
      aiPortraitNotEnoughPhotos: "Não há fotos de qualidade suficiente para o retrato com IA. Chegue mais perto e deixe a imagem mais nítida.",
      aiBadge: "IA",
      originalPhotosTitle: "Fotos reais",
      blockedAi: "Imagens geradas por IA não são aceitas.",
      blockedInappropriate: "Imagem inadequada detectada.",
      finalize: "Finalizar perfil",
      finalizeTitle: "Só mais uma coisa",
      finalizeSub: "Dê um nome ao gato (e o bairro, se você souber).",
      submit: "Criar perfil",
      submitting: "Salvando…",
      done: "Perfil criado!",
      doneSub: "Os três ângulos foram adicionados à galeria.",
      goProfile: "Ir para o perfil",
      manualCapture: "Capturar agora",
      angleOf: "Ângulo {n} de 3",
      voiceOn: "Voz ligada",
      voiceOff: "Voz desligada",
      directions: {
        left: "Vá para a esquerda",
        right: "Vá para a direita",
        up: "Incline para cima",
        down: "Incline para baixo",
        closer: "Chegue mais perto",
        farther: "Afaste-se um pouco",
        center: "Perfeito, não se mexa",
        unclear: "Aponte para o rosto do gato",
      },
      overallProgress: "Progresso do perfil",
      bestPhotoChosen: "A IA escolheu a melhor foto de frente para o perfil",
      detectorLoading: "Carregando a IA no aparelho… (download único)",
      detectorReady: "IA no aparelho pronta · escaneamento sem custo",
    },
    thinking: {
      title: "A IA está pensando",
      hint: "Isso costuma levar de 5 a 15 segundos. Você pode continuar usando o app.",
      stages: {
        compress: "Preparando sua foto…",
        vision: "Olhando o gato com o GPT-Vision…",
        matching: "Procurando gatos parecidos no banco de dados…",
        polishing: "Ajustando os resultados…",
      },
    },
    incident: {
      title: "Registrar um incidente",
      subtitle: "Comece o período de observação de 10 dias. Você pode mudar o status depois (gato saudável, sintomas apareceram, tratamento concluído).",
      types: {
        scratch: "Arranhão",
        bite: "Mordida",
      },
      severity: "Gravidade",
      sev: {
        mild: "Leve — superficial",
        moderate: "Moderada — com sangramento",
        severe: "Grave — profunda / múltipla",
      },
      cat: "Qual gato (opcional)",
      catPlaceholder: "Escolha um gato ou deixe como desconhecido",
      catHint: "Vincular o gato ajuda a comunidade a acompanhar publicamente os 10 dias de observação.",
      unknownCat: "Desconhecido / um gato de rua que eu não conheço",
      date: "Data do incidente",
      location: "Onde (opcional)",
      locationPlaceholder: "ex.: Vila Madalena, Rua Harmonia",
      notes: "Observações",
      notesPlaceholder: "O que aconteceu? Você lavou o ferimento? Algum sintoma?",
      photo: "Foto do ferimento ou do gato (opcional)",
      privacyNote: "Suas anotações e sua foto são privadas. Só o gato vinculado (se houver) recebe um selo público de 'em observação' e um contador — nenhum dado pessoal é exposto.",
      useGps: "Usar minha localização (avisa a comunidade por perto)",
      gpsCaptured: "Localização capturada. Vamos avisar quem está por perto.",
      gpsHint: "Os voluntários desta região verão isso nas notificações.",
      submit: "Iniciar observação de 10 dias",
      submitting: "Salvando…",
      created: "Incidente registrado. Observação de 10 dias iniciada.",
      error: "Não foi possível salvar o incidente.",
      loginRequired: "Entre na sua conta primeiro.",
      observation: {
        title: "Observações ativas",
        subtitle: "Fique atento a qualquer comportamento estranho em você OU no gato durante 10 dias após uma mordida ou arranhão.",
        empty: "Nenhuma observação ativa.",
        daysLeft: "faltam {n} dias",
        complete: "Observação concluída",
        scratchOn: "Arranhão",
        biteOn: "Mordida",
        markHealthy: "O gato continuou saudável",
        markSymptoms: "Sintomas apareceram",
        markResolved: "Tratamento concluído",
        statusUpdated: "Status atualizado.",
        statuses: {
          observing: "Em observação",
          cat_healthy: "O gato continuou saudável ✓",
          symptoms_appeared: "Sintomas apareceram",
          incident_developed: "Surgiu uma complicação",
          incident_resolved: "Tratamento concluído ✓",
        },
        bannerObserving: "{n} observação ativa",
        bannerObservingPlural: "{n} observações ativas",
        notified: "vizinhos avisados",
        feedbackList: "Retorno da comunidade",
      },
      catBadge: {
        observing: "Em observação",
        sub: "{n} pessoa{plural} acompanhando — faltam {days}d",
      },
      feedbackPage: {
        eyebrow: "Retorno da comunidade",
        notFound: "Incidente não encontrado.",
        intro: "Alguém da comunidade por perto relatou este arranhão/mordida. Se você viu o gato ou sabe de algo útil, deixe uma observação rápida — isso ajuda quem relatou a completar os 10 dias com segurança.",
        communityReplies: "respostas da comunidade",
        observationLabel: "O que você observou?",
        options: {
          cat_seen_healthy: "Vi o gato — parece saudável",
          cat_not_seen: "Não vi o gato",
          cat_seen_concerning: "Vi — algo parecia estranho",
          other: "Outra observação",
        },
        notePlaceholder: "Mais alguma coisa? Horário, local, o que o gato estava fazendo…",
        photo: "Foto do gato (opcional)",
        submit: "Enviar retorno",
        thanks: "Obrigado por ajudar a comunidade!",
        thanksSub: "Quem relatou já foi avisado da sua observação.",
        othersTitle: "Outras observações",
      },
    },
    observationHouse: {
      overline: "Segurança da comunidade",
      title: "Casa de observação",
      subtitle: "Todos os gatos que estão em observação de 10 dias após um arranhão ou mordida relatado. Ajude seus vizinhos deixando um avistamento rápido.",
      allTypes: "Todos",
      neighborhoodPlaceholder: "Filtrar por bairro…",
      urgentOnly: "Só urgentes (≤3 dias)",
      nearbyOn: "Perto de mim",
      nearbyOff: "Filtro de proximidade ligado",
      results: "resultados",
      empty: "Nenhuma observação ativa no momento. 🐾",
      unknownCat: "Gato desconhecido",
      locationUnknown: "Localização não compartilhada",
      loadFailed: "Não foi possível carregar as observações.",
      gpsUnavailable: "Geolocalização não suportada.",
      gpsActive: "Filtrando num raio de 5 km de você.",
      gpsFailed: "Não foi possível obter a localização.",
    },
    weekly: {
      eyebrow: "Resumo por e-mail",
      title: "Resumo semanal de observações",
      subtitle: "Toda segunda-feira às 09:00 — um resumo pessoal dos incidentes e observações da sua área nesta semana.",
      on: "Ligado",
      off: "Desligado",
      optedIn: "Resumo semanal ativado.",
      optedOut: "Resumo semanal desativado.",
      noScope: "Defina uma área de atuação no seu perfil para que possamos ajustar o resumo ao seu bairro.",
      statNew: "Novos incidentes",
      statActive: "Ativos",
      statUrgent: "Urgentes",
      statFeedbacks: "Retornos",
      highlightsTitle: "Casos mais ativos da semana",
      sendTest: "Enviar teste agora",
      sending: "Enviando…",
      scheduleHint: "Enviado automaticamente toda segunda-feira às 09:00 (quando ativado).",
      testSentEmail: "Teste enviado! Confira sua caixa de entrada.",
      testSentInAppOnly: "Notificação enviada no app. O e-mail está desativado neste servidor.",
      error: "Não foi possível enviar o resumo.",
    },
    map: {
      title: "Mapa dos animais",
      subtitle: "Cada marcador é um animal conhecido, uma emergência ou um ponto de alimentação na sua cidade.",
      layerCats: "Gatos",
      layerSightings: "Emergências",
      layerFeeding: "Pontos de alimentação",
      layerObservations: "Casa de observação",
      layerVaccinated: "Vacinados",
      layerCommunity: "Comunidade",
      tabMap: "Mapa",
      tabNeighborhood: "Meu bairro",
      locate: "Localizar",
    },
    sightings: {
      title: "Casos de emergência",
      subtitle: "Casos urgentes de animais em necessidade, relatados pela comunidade da sua cidade.",
      report: "Relatar uma emergência",
      notes: "O que está acontecendo?",
      reporter: "Quem relatou",
      when: "Quando",
      submit: "Enviar aviso de emergência",
      success: "Obrigado por avisar — os voluntários perto de você foram notificados.",
      pendingReview: "Obrigado — sua emergência está em análise e ficará pública após a aprovação.",
      empty: "Nenhum caso de emergência relatado ainda.",
      locationOnly: "Ou apenas marque rapidamente a localização",
      photoAndLocation: "Adicione uma foto para ajudar quem for atender a identificar o animal",
    },
    feeding: {
      title: "Pontos de alimentação",
      subtitle: "Onde os voluntários alimentam os animais pela cidade.",
      add: "Adicionar ponto de alimentação",
      name: "Nome do ponto",
      schedule: "Horários (ex.: seg/qua/sex 19:00)",
      notes: "Observações",
      empty: "Ainda não há pontos de alimentação.",
      success: "Ponto de alimentação adicionado",
      active: "Ativo",
      inactive: "Inativo",
      statusLabel: "Situação",
      photo: "Foto do ponto",
      findNearest: "Encontrar o mais próximo",
      nearestFound: "O ponto mais próximo é",
      nearestBadge: "O mais perto de você",
      noNearest: "Nenhum ponto de alimentação encontrado.",
      geoUnsupported: "Seu navegador não é compatível com geolocalização.",
      geoDenied: "Permita a localização para encontrar o ponto mais próximo.",
      openDirections: "Abrir no mapa",
      maintainedBy: "Mantido por",
      requireNameLoc: "O nome e as coordenadas de GPS são obrigatórios",
    },
    dashboard: {
      title: "Painel",
      ilkKediBaslik: "Adicione seu primeiro gato",
      ilkKediAlt: "Os gatos de rua que você fotografa ajudam outras pessoas a encontrá-los, alimentá-los e cuidar deles.",
      recentCats: "Adicionados recentemente",
      welcome: "Bem-vindo de volta",
      myRole: "Sua função",
      quickAdd: "Adicionar rápido",
      addCat: "Adicionar um gato",
      identify: "Identificar com IA",
      reportSighting: "Relatar emergência",
      addFeeding: "Adicionar ponto de alimentação",
    },
    profile: {
      title: "Meu perfil",
      settingsTitle: "Configurações e ajuda",
      settingsSubtitle: "Gerencie sua conta, notificações, idioma e privacidade. Tudo num só lugar.",
      aiPortraitBannerTitle: "Retratos de perfil com IA",
      aiPortraitBannerRemaining: "Créditos restantes neste mês",
      aiPortraitBannerUnlimited: "Ilimitado",
      aiPortraitBannerCta: "Gerar um retrato",
      aiPortraitBannerUsed: "{used} de {limit} usados",
      aiPortraitBannerExhausted: "Você usou todos os créditos — eles voltam no mês que vem.",
      aiCoverageTitle: "Cobertura de retratos com IA na comunidade",
      aiCoverageSubtitle: "{with} de {total} gatos têm retrato com IA",
      aiCoverageEmpty: "Seja o primeiro a adicionar um retrato com IA!",
      myAccount: "Minha conta",
      myAccountHint: "Painel, meus animais, contribuições",
      notificationSettings: "Configurações de notificação",
      notificationSettingsHint: "O que receber, como e quando",
      countryTitle: "País",
      countryHint: "A qual comunidade você pertence",
      languageTitle: "Idioma",
      languageHint: "Escolha o idioma da interface",
      privacyKvkk: "Privacidade e proteção de dados",
      privacyKvkkHint: "Seus direitos sobre os dados e cookies",
      terms: "Termos de uso",
      termsHint: "Regras de uso do aplicativo",
      contact: "Fale conosco",
      contactHint: "destek@sokakkedileri.tr",
      helpFaq: "Ajuda e perguntas frequentes",
      helpFaqHint: "Primeiros passos, dúvidas comuns, contato",
      safetyHint: "Guias de segurança com animais de rua",
      points: "Pontos Patinha",
      badgesEarned: "conquistas obtidas",
      seeAllBadges: "Ver todas as conquistas",
      nextBadge: "Próxima conquista",
      nextLevel: "Próximo nível",
      nextLevelIn: "Para o próximo nível",
      contributions: "contribuições",
      notificationsTitle: "Notificações",
      notificationsHint: "Novidades e respostas recentes",
      notificationsEmpty: "Nenhuma notificação ainda",
      seeAllNotifications: "Ver todas as notificações",
      settingsGroup: "Configurações",
      settingsGroupHint: "Idioma, tema, permissões",
      helpInfoGroup: "Ajuda e informações",
      helpInfoHint: "Como funciona, perguntas frequentes, sobre, privacidade",
      howItWorks: "Como funciona?",
      howItWorksHint: "Conheça os recursos em 3 passos",
      aboutUs: "Sobre nós",
      aboutUsHint: "Nossa missão, equipe e contato",
      themeTitle: "Tema",
      themeHint: "Interface clara ou escura",
      themeLight: "Claro",
      themeDark: "Escuro",
      locationPermTitle: "Permissão de localização",
      locationPermHint: "Usada para sugerir animais perto de você",
      notifPermTitle: "Permissão de notificação",
      notifPermHint: "Receber alertas urgentes de emergência",
      permRequest: "Permitir",
      permGranted: "Permitido",
      permDenied: "Bloqueado",
      pointsShort: "🐾",
      catsDescribed: "Gatos descritos",
      sightingsReported: "Emergências relatadas",
      feedingSpots: "Pontos de alimentação",
      vaccinations: "Vacinas",
      pointsHowItWorks: "Como funciona a pontuação",
      ruleCat: "Descrever um animal novo: +5 Pontos Patinha",
      ruleFeeding: "Adicionar um ponto de alimentação: +3 Pontos Patinha",
      ruleVaccination: "Registrar uma vacina: +10 Pontos Patinha",
      ruleSighting: "Relatar uma emergência: +2 Pontos Patinha",
      namingNote: "Quem descreve um animal primeiro ganha o direito de dar nome a ele.",
      myCats: "Animais que descrevi",
      noCatsYet: "Você ainda não descreveu nenhum animal.",
      rank: "Posição",
      privilegesTitle: "Vantagens do seu nível",
      nextTier: "para chegar a",
      keepGoing: "Seja o primeiro a reconhecer um animal — ganhe 5 Pontos Patinha e desbloqueie sua faixa de descritor.",
      photoUpdated: "Foto atualizada",
      photoError: "Não foi possível atualizar a foto",
    },
    common: {
      showPassword: "Mostrar senha",
      hidePassword: "Ocultar senha",
      deletePhoto: "Excluir foto",
      cancel: "Cancelar",
      save: "Salvar",
      delete: "Excluir",
      edit: "Editar",
      back: "Voltar",
      required: "Obrigatório",
      loading: "Carregando…",
      locateMe: "Usar minha localização",
      tryAi: "Testar a IA",
      describedBy: "Descrito por",
      optional: "opcional",
    },
    leaderboard: {
      title: "Ranking da comunidade",
      subtitle: "Seja o primeiro a reconhecer um animal. Suba no ranking. Desbloqueie vantagens.",
      contributor: "Colaborador",
      badge: "Nível",
      contributions: "Contribuições",
      points: "Pontos Patinha",
      empty: "Ainda não há colaboradores — seja o primeiro!",
      you: "Você",
      tabVolunteers: "Voluntários",
      tabNeighborhoods: "Retratos com IA por bairro",
      hoodEmpty: "Ainda não há retratos com IA suficientes — seja o primeiro bairro a liderar!",
      hoodWithAi: "Retratos com IA",
      hoodCoverage: "Cobertura",
      hoodTopContributors: "Quem mais contribuiu esta semana",
    },
    messenger: {
      title: "Mensagens",
      empty: "Ainda não há conversas. Mande uma mensagem para um voluntário na lista.",
      selectThread: "Escolha uma conversa para começar.",
      placeholder: "Escreva uma mensagem…",
      send: "Enviar",
    },
    notifications: {
      title: "Notificações",
      subtitle: "Emergências perto de você, quem assumiu seus avisos e mensagens novas.",
      empty: "Você está em dia.",
      markAllRead: "Marcar todas como lidas",
      markRead: "Marcar como lida",
      open: "Abrir",
    },
    severity: {
      minor: "Leve",
      urgent: "Urgente",
      critical: "Crítico",
      label: "Gravidade",
    },
    emergencyActions: {
      claim: "Estou a caminho",
      claimed: "Assumido por",
      resolve: "Marcar como resolvido",
      resolved: "Resolvido",
      status: "Situação",
      open: "Aberto",
      message: "Mensagem",
    },
    emergencyCategories: {
      injured: "Animal ferido",
      injuredDesc: "Ferimento visível, mancando ou com dor",
      food: "Precisa de comida / água",
      foodDesc: "Animais na região sem comida nem água por perto",
      danger: "Área perigosa",
      dangerDesc: "Obra, trânsito, risco climático",
      volunteer: "Preciso de um voluntário",
      volunteerDesc: "Preciso de ajuda no local (transporte, lar temporário)",
      lost: "Animal perdido",
      lostDesc: "Busca por foto — a IA confere todos os animais cadastrados",
    },
    premium: {
      active: "Ativo",
      navLink: "Premium",
      title: "Assinatura Premium",
      subtitle: "IA ilimitada, temas exclusivos, selo de verificação e pontos de alimentação salvos — pelos animais.",
      priceLine: "Assinatura mensal",
      cta: "Assinar o Premium",
      comingSoonPlay: "Em breve na Google Play",
      comingSoonHint: "A assinatura Premium estará disponível assim que nosso app Android entrar no ar na Google Play. Avisamos você aqui no dia do lançamento.",
      ctaActive: "Você é assinante Premium",
      perksHeading: "O que você desbloqueia",
      perk1: "Identificações ilimitadas (plano gratuito: {limit}/mês)",
      perk2: "Patinhas ilimitadas nas fotos",
      perk3: "Mensagens diretas entre assinantes Premium",
      perk4: "Prioridade nos avisos de emergência",
      perk5: "Selo Premium — sua voz pesa mais na comunidade",
      perk6: "Inscrição no programa oficial de voluntariado",
      perk7: "Salvar pontos de alimentação favoritos",
      activeUntil: "Ativo até",
      usedThisMonth: "IA usada neste mês",
      remaining: "restantes",
      unlimited: "Ilimitado",
      themeTitle: "Tema",
      themeWarm: "Pedra Quente",
      themeMidnight: "Meia-noite",
      themeSage: "Sálvia",
      themeBosphorus: "Azul Profundo",
      verificationTitle: "Selo de verificação",
      verificationDesc: "Assinantes Premium podem pedir a verificação da sua organização pelos administradores.",
      verificationOrgLabel: "Nome da organização (prefeitura, associação, clínica veterinária…)",
      verificationRequest: "Pedir verificação",
      verificationPending: "Aguardando análise",
      verificationVerified: "Verificado ✓",
      verificationOrgIs: "Verificado como",
      adminApprovals: "Pedidos de verificação pendentes",
      adminApprove: "Aprovar",
      adminEmpty: "Nenhum pedido de verificação pendente.",
      paymentSuccess: "Bem-vindo ao Premium!",
      paymentSuccessSub: "Obrigado por apoiar os animais de rua da sua cidade. Suas vantagens estão liberadas.",
      paymentChecking: "Confirmando seu pagamento…",
      paymentExpired: "Sua sessão de pagamento expirou. Tente de novo.",
      paymentCancelled: "Pagamento cancelado.",
      paymentCancelSub: "Nada foi cobrado. Você pode voltar e assinar quando quiser.",
      backHome: "Voltar ao painel",
      tryAgain: "Tentar de novo",
      checkoutError: "Não foi possível iniciar o pagamento. Tente de novo.",
      quotaReachedTitle: "Limite mensal gratuito de IA atingido",
      quotaReachedSub: "Assine o Premium para identificações ilimitadas, ou espere até o mês que vem.",
      seePricing: "Ver o Premium",
      favoriteAdd: "Salvar ponto",
      favoriteRemove: "Salvo",
      favoritePremiumOnly: "Salvar pontos de alimentação é uma vantagem Premium.",
    },
    lounge: {
      overline: "Sala Premium",
      title: "Sala Premium",
      subtitle: "Converse com os outros assinantes que ajudam a manter os animais da cidade em segurança. Mande uma mensagem direta para qualquer pessoa daqui.",
      memberCount: "assinantes Premium",
      empty: "Seja o primeiro membro da Sala Premium!",
      message: "Mensagem",
      youAreHere: "Este é você",
      vet: "Veterinário",
      gatedTitle: "A Sala Premium é para assinantes Premium",
      gatedSub: "Assine o Premium para entrar na sala e conversar diretamente com voluntários e veterinários verificados.",
    },
    city: {
      generating: "Pintando sua cidade…",
      aiGenerated: "Cena gerada por IA",
    },
    coverage: {
      title: "Áreas de atuação",
      heading: "Onde você cuida",
      subtitle: "Escolha as regiões onde você mora ou pelas quais se sente responsável. Vamos priorizar emergências e pontos de alimentação nessas áreas.",
      empty: "Você ainda não escolheu nenhuma região. Adicione uma cidade abaixo para começar.",
      addCity: "Adicionar uma cidade",
      removeCity: "Remover cidade",
      districtsCount: "regiões selecionadas",
      save: "Salvar área de atuação",
      saved: "Área de atuação salva",
    },
    trending: {
      overline: "Galeria das patinhas",
      title: "Animais da semana",
      subtitle: "Os animais de rua mais visitados no momento. A cada 25 visitas, quem descreveu o animal primeiro ganha +5 Pontos Patinha.",
      week: "Semana",
      month: "Mês",
      all: "Sempre",
      seeAll: "Ver todos os animais",
    },
    badges: {
      overline: "Coleção",
      title: "Conquistas Patinha",
      subtitle: "Conquistas colecionáveis com tema de patinhas. Ganhe-as ajudando os animais. Toque em qualquer uma para ver como desbloqueá-la.",
      collected: "Coletadas",
      earnedOn: "Conquistada em",
      filterAll: "Todas",
      filterEarned: "Conquistadas",
      filterLocked: "Bloqueadas",
      emptyFilter: "Nenhuma conquista neste filtro ainda.",
      next: "Próxima",
      allEarned: "Todas as conquistas obtidas!",
      celebrationTitle: "Nova conquista",
      howToTitle: "Como conquistar",
      howToFallback: "Continue ativo na comunidade para desbloquear esta conquista.",
      pageOverline: "Coleção",
      pageTitle: "Conquistas Patinha",
      pageSubtitle: "Toque em qualquer conquista para ver como desbloqueá-la. Ganhe conquistas ajudando os animais, fotografando-os e fortalecendo a comunidade.",
      shareBtn: "Compartilhar",
      shareText: "Acabei de ganhar a conquista {badge} no StrayPaw! 🐾",
      remainingMetric: {
        cats: {
          one: "animal a mais",
          many: "animais a mais",
        },
        feeding: {
          one: "ponto a mais",
          many: "pontos a mais",
        },
        vax: {
          one: "vacina a mais",
          many: "vacinas a mais",
        },
        sightings: {
          one: "emergência a mais",
          many: "emergências a mais",
        },
        claimed: {
          one: "atendimento a mais",
          many: "atendimentos a mais",
        },
        messages: {
          one: "mensagem a mais",
          many: "mensagens a mais",
        },
        districts: {
          one: "região a mais",
          many: "regiões a mais",
        },
        cities: {
          one: "cidade a mais",
          many: "cidades a mais",
        },
      },
      category: {
        describer: "Descritor",
        feeding: "Alimentação",
        health: "Saúde",
        emergency: "Emergência",
        fame: "Fama",
        coverage: "Cobertura",
        social: "Social",
        engagement: "Participação",
        special: "Especial",
        paw: "Patinha",
      },
    },
    donate: {
      navLink: "Doar",
      overline: "Transparência ao vivo",
      title: "Ajude a curá-los",
      subtitle: "Cada centavo é rastreável. Veja exatamente como as doações viram consultas, cirurgias e segundas chances para os animais de rua.",
      transparencyTag: "Contabilidade 100% aberta — cada tratamento registrado por veterinários verificados",
      totalRaised: "Total arrecadado",
      totalSpent: "Gasto em cuidados",
      balance: "Disponível agora",
      catsHelped: "Animais ajudados",
      donors: "doadores",
      treatments: "tratamentos",
      availableForCats: "Pronto para o próximo animal que precisar",
      uniqueCats: "Animais diferentes tratados",
      makeDonation: "Fazer uma doação",
      everyKurusCounts: "Cada centavo chega a um animal.",
      amount: "Valor",
      yourName: "Seu nome (aparece publicamente)",
      email: "E-mail",
      note: "Recado",
      donateNow: "Doar",
      securedBy: "Pagamento seguro · Nunca guardamos os dados do seu cartão.",
      iyzicoPending: "As doações estão sendo registradas, mas os pagamentos com cartão estão pausados enquanto nossa conta é verificada. Seus dados foram salvos e entraremos em contato em breve.",
      pendingMerchant: "Doação registrada — confirmamos em breve.",
      thankYou: "Obrigado 🐾",
      error: "Não foi possível registrar a doação. Tente de novo.",
      invalidAmount: "Informe um valor válido",
      treatmentLedger: "Registro de tratamentos ao vivo",
      treatmentLedgerDesc: "Só veterinários verificados pelos administradores podem adicionar registros. Cada custo aparece detalhado ao lado do animal que ele ajudou.",
      noTreatmentsYet: "Nenhum tratamento registrado ainda — seja o primeiro a ajudar.",
      spent: "Gasto",
      more: "outros registros",
      recentDonors: "Doações recentes",
      noDonorsYet: "Sua doação pode ser a primeira.",
      severity: {
        mild: "Leve",
        moderate: "Moderada",
        severe: "Grave",
        critical: "Crítica",
      },
      tStatus: {
        planned: "Planejado",
        in_progress: "Em andamento",
        completed: "Concluído",
      },
    },
    treatments: {
      title: "Registro de tratamentos",
      subtitle: "Cada consulta, cada centavo — adicionados só por veterinários verificados pelos administradores.",
      totalSpent: "Total gasto com este animal",
      records: "registros",
      noneYet: "Nenhum tratamento registrado ainda.",
      add: "Adicionar tratamento",
      added: "Tratamento salvo",
      condition: "Condição / doença",
      details: "Detalhes do tratamento (medicamentos, doses, retorno…)",
      detailsPlaceholder: "Descreva o diagnóstico e o que foi feito — seja o mais específico possível.",
      cost: "Custo",
      date: "Data",
      severityLabel: "Gravidade",
      statusLabel: "Situação",
      clinicName: "Clínica / prefeitura (opcional)",
      save: "Salvar registro",
    },
    apply: {
      overline: "Programa de voluntariado",
      title: "Seja voluntário",
      subtitle: "Conte por que você quer cuidar dos animais de rua da sua cidade. Primeiro passa por uma triagem de IA contra spam e depois um administrador humano analisa sua inscrição.",
      cardTitle: "Seja voluntário",
      cardSubtitle: "Libere as ferramentas de cuidado · inscrição de 2 min + análise da administração",
      motivationLabel: "Por que você quer ser voluntário?",
      motivationPlaceholder: "Descreva sua experiência com animais de rua, seu bairro e o que você gostaria de fazer como voluntário…",
      minChars: "Mínimo de 20 caracteres",
      aiNotice: "Uma triagem por IA vai procurar sinais de spam ou provocação na sua inscrição. A decisão final é de um administrador humano.",
      submitBtn: "Enviar inscrição",
      submitted: "Inscrição enviada — os administradores foram avisados",
      error: "Algo deu errado",
      tooShort: "Escreva pelo menos 20 caracteres",
      backToProfile: "Voltar ao perfil",
      aiScore: "Nota da IA",
      statusPending: "Sua inscrição está em análise",
      statusPendingHint: "Um administrador vai analisar sua inscrição em breve. Você receberá uma notificação com a decisão.",
      statusRejected: "Inscrição não aprovada",
    },
    admin: {
      overline: "Administração",
      title: "Inscrições de voluntários",
      subtitle: "A IA faz a triagem prévia de cada inscrição. Seu papel: a checagem humana final antes de liberar o acesso de voluntário.",
      forbiddenTitle: "Somente administradores",
      forbiddenSubtitle: "Você não tem permissão para ver esta página.",
      aiSummary: "Avaliação da IA",
      empty: "Nenhuma inscrição neste filtro.",
      approveBtn: "Aprovar",
      rejectBtn: "Recusar",
      approvedToast: "Inscrição aprovada — acesso de voluntário liberado.",
      rejectedToast: "Inscrição recusada.",
      bulkRejectTitle: "A IA marcou {count} inscrições como de alto risco",
      bulkRejectSubtitle: "Recuse todas com um clique — os candidatos serão avisados.",
      bulkRejectBtn: "Recusar todas as vermelhas",
      bulkRejectConfirm: "Recusar {count} inscrições marcadas em vermelho? Isso não pode ser desfeito automaticamente.",
      bulkRejectedToast: "{count} inscrições recusadas de uma vez.",
      notePlaceholder: "Recado opcional para o candidato (ele consegue ver)…",
      adminNoteLabel: "Recado da administração",
      tab: {
        pending: "Pendentes",
        approved: "Aprovadas",
        rejected: "Recusadas",
      },
      stats: {
        daysOld: "dias",
        cats: "animais",
        sightings: "avistamentos",
        feeding: "alimentação",
      },
    },
    adminEmerg: {
      overline: "Moderação de emergências",
      title: "Fila de análise de emergências",
      subtitle: "A IA analisa fotos e textos. Avisos de quem ainda não tem histórico vêm para cá, para uma checagem humana antes de ficar público.",
      gotoApplications: "Ir para as inscrições de voluntários",
      empty: "Nenhuma emergência neste filtro.",
      approveBtn: "Publicar",
      rejectBtn: "Recusar",
      approvedToast: "Emergência publicada.",
      rejectedToast: "Emergência recusada.",
      tab: {
        pending: "Pendentes",
        approved: "Publicadas",
        rejected: "Recusadas",
      },
      photoVerdict: "IA da foto",
      textVerdict: "IA do texto",
      notACat: "Não é um animal",
      aiGenerated: "Gerada por IA",
      photoshopped: "Manipulada",
      photoClean: "A foto parece legítima",
      confidence: "Confiança",
      redWarning: "A IA marcou isto como alto risco — analise com atenção antes de publicar.",
    },
    photos: {
      archive: "Arquivo de fotos",
      galleryTitle: "Arquivo de fotos",
      gallerySubtitle: "Todas as fotos deste animal que a comunidade compartilhou. Toque numa patinha para curtir.",
      addPhoto: "Adicionar foto",
      uploaded: "Foto enviada",
      uploadFailed: "Falha no envio",
      emptyGallery: "Nenhuma foto ainda — seja o primeiro a adicionar!",
      loginRequired: "Entre na sua conta para enviar uma foto",
      loginToLike: "Entre na sua conta para dar uma patinha",
      deleteConfirm: "Excluir esta foto?",
      deleted: "Foto removida",
      monthlyQuotaUsed: "{used}/{limit} patinhas usadas neste mês",
      unlimitedQuota: "Patinhas ilimitadas (Premium)",
      upgradeForUnlimited: "Assine o Premium para ter ilimitadas",
      aiNotice: "As fotos passam por uma verificação automática contra conteúdo gerado por IA, manipulado ou inadequado.",
    },
    photoContest: {
      overline: "Concurso mensal de fotos",
      title: "Foto do mês",
      subtitle: "Todo mês as 3 fotos com mais patinhas ganham a conquista de Fotógrafo do Mês. Toque em qualquer foto para visitar o animal.",
      empty: "Ainda não há fotos curtidas neste mês — seja o primeiro a colocar um animal no pódio.",
      by: "por",
      honorableMentions: "Menções honrosas (4-10)",
    },
  },
  "es": {
    kediBildir: {
      baslik: "Informar de un gato",
      altBaslik: "Estamos aquí por los gatos de tu barrio. ¿Qué quieres hacer?",
      kaydetBaslik: "Registrar un gato",
      kaydetAciklama: "Añade al sistema un gato de tu barrio",
      gordumBaslik: "He visto un gato",
      gordumAciklama: "Informa de que has visto un gato ya registrado",
      acilBaslik: "Informar de una emergencia",
      acilAciklama: "Pide ayuda para un gato herido, enfermo o en peligro",
      mamaSuBaslik: "Añadir punto de comida/agua",
      mamaSuAciklama: "Comparte la ubicación de un punto de comida o agua",
      dipnot: "Cada aviso llega a los voluntarios de tu barrio.",
    },
    volunteers: {
      title: "Voluntarios",
      subtitle: "Las personas que alimentan, curan y rescatan a los animales de tu barrio. Cada nombre de aquí es alguien que dio la cara.",
      becomeVolunteer: "Hazte voluntario",
      individualsTitle: "Voluntarios y veterinarios",
      individualsSubtitle: "Ordenados por contribución: alimentación, rescates, vacunas y avisos.",
      noIndividuals: "Todavía no hay voluntarios. Sé el primero de tu barrio.",
    },
    dogUi: {
      dbTitleCat: "Base de datos de gatos",
      dbTitleDog: "Base de datos de perros",
      dbOverlineCat: "BASE DE DATOS DE GATOS",
      dbOverlineDog: "BASE DE DATOS DE PERROS",
      dbSubCat: "Consulta todos los gatos catalogados de nuestra comunidad.",
      dbSubDog: "Consulta todos los perros catalogados de nuestra comunidad.",
      dbSearchCat: "Busca por nombre, color, raza o barrio",
      dbSearchDog: "Busca por nombre, color o barrio",
      dbLostCat: "Encuentra a tu gato perdido",
      dbLostDog: "Encuentra a tu perro perdido",
      dbRegisteredCat: "Registrados",
      dbRegisteredDog: "Registrados",
      dbInjuredCat: "Heridos",
      dbInjuredDog: "Heridos",
      dbEarTagged: "Con crotal",
      dbEarTaggedHint: "Esterilizado: muesca o crotal en la oreja",
      dbTitleAll: "Base de datos de animales",
      dbOverlineAll: "BASE DE DATOS DE ANIMALES",
      dbSubAll: "Consulta todos los animales catalogados de nuestra comunidad.",
      dbSearchAll: "Busca por nombre, color o barrio",
      dbLostAll: "Encuentra a tu animal perdido",
      dbRegisteredAll: "Registrados",
      dbInjuredAll: "Heridos",
      lostConfirmCat: "¿Crear un aviso de desaparición para {ad}? Se publica al instante y se avisa a los vecinos.",
      lostConfirmDog: "¿Crear un aviso de desaparición para {ad}? Se publica al instante y se avisa a los vecinos.",
      lostOpenCat: "Ya hay un aviso abierto: te llevamos a él.",
      lostOpenDog: "Ya hay un aviso abierto: te llevamos a él.",
      deleteCat: "¿Eliminar este gato?",
      deleteDog: "¿Eliminar este perro?",
      shareCat: "Compartir este gato",
      shareDog: "Compartir este perro",
      editCat: "Editar gato",
      editDog: "Editar perro",
      reportCat: "Denunciar este perfil de gato",
      reportDog: "Denunciar este perfil de perro",
      noGpsCat: "El GPS no estaba disponible cuando se registró este gato.",
      noGpsDog: "El GPS no estaba disponible cuando se registró este perro.",
      lostCat: "Mi gato se ha perdido: crear aviso",
      lostDog: "Mi perro se ha perdido: crear aviso",
      newCat: "Nuevo gato",
      newDog: "Nuevo perro",
      saveCat: "Guardar gato",
      saveDog: "Guardar perro",
      saving: "Guardando…",
      dogOptional: "Añadir detalles del perro (opcional)",
      dogOptionalHint: "No es obligatorio. Con dos fotos, un nombre y un barrio basta.",
    },
    dog: {
      section: "Detalles del perro",
      unknownHint: "Déjalo en No lo sé si no lo ves con claridad",
      matchingSoon: "La búsqueda por parecido de perros aún no está disponible: el perfil se ha guardado y podrá encontrarse cuando se active.",
      earCarriage: "Orejas",
      ear: {
        dik: "Erguidas",
        yari_dik: "Semierguidas",
        dusuk: "Caídas",
        kesik: "Cortadas",
        belirsiz: "No lo sé",
      },
      tailType: "Cola",
      tail: {
        uzun_duz: "Larga y recta",
        kivrik: "Enroscada",
        orak: "En hoz",
        kisa: "Corta",
        kesik: "Amputada",
        puskullu: "En pluma",
        belirsiz: "No lo sé",
      },
      coatLength: "Longitud del pelo",
      coatLen: {
        kisa: "Corto",
        orta: "Medio",
        uzun: "Largo",
        belirsiz: "No lo sé",
      },
      coatTexture: "Textura del pelo",
      coatTex: {
        duz: "Liso",
        dalgali: "Ondulado",
        kivircik: "Rizado",
        sert_tel: "Duro",
        belirsiz: "No lo sé",
      },
      muzzle: "Hocico",
      muz: {
        basik: "Corto",
        orta: "Medio",
        uzun: "Largo",
        belirsiz: "No lo sé",
      },
      build: "Complexión",
      bod: {
        ince: "Delgado",
        orta: "Media",
        iri: "Robusto",
        belirsiz: "No lo sé",
      },
      earTagNo: "Número de crotal",
      earTagHint: "Solo si el perro lleva crotal de esterilización",
      registering: "Estás registrando un perro",
      registeringCat: "Estás registrando un gato",
      switchToCat: "Registrar como gato",
      switchToDog: "Registrar como perro",
      myLostDog: "Este es mi perro perdido",
    },
    appName: "StrayPaw",
    tagline: "Los animales callejeros de tu barrio",
    // Panodaki baslik slogani. `tagline` bundan AYRI: o sayfa basliklarinda
    // ve paylasim kartlarinda kullaniliyor, bu ise uygulama basliginin ikinci
    // satiri. Ikisini birlestirmek, panoda olmayan bir metni basliga sokardi.
    baslikSlogani: "Un barrio más fuerte.",
    arama: {
      rowPlace: "Barrio",
      otherNeighborhoods: "Otros barrios",
      row_kediler: "gatos",
      row_harita: "en el mapa",
      row_kayip: "anuncios de perdidos",
      row_besleme: "puntos de comida",
      rowGo_kediler: "Ver los gatos",
      rowGo_harita: "¿Qué hay en la zona?",
      rowGo_kayip: "Ver los anuncios",
      rowGo_besleme: "Ver los puntos",
      unit_kediler: "resultados",
      unit_harita: "",
      unit_kayip: "anuncios",
      unit_besleme: "puntos",
      tab_ilanlar: "Perdidos",
      title: "Buscar",
      placeholder: "Busca un gato, barrio o distrito…",
      back: "Atrás",
      clear: "Borrar",
      recent: "Búsquedas recientes",
      clearHistory: "Borrar",
      mostCats: "Barrios con más gatos",
      hint: "Busca por nombre de gato, barrio o distrito para llegar a los gatos registrados, anuncios de perdidos y todo lo de tu zona.",
      neighborhoods: "Barrios",
      cats: "Gatos",
      catWord: "gatos",
      searching: "Buscando…",
      empty: "Sin resultados. Prueba con un barrio.",
      searchFor: "Buscar",
      tab_hepsi: "Todo",
      tab_kediler: "Gatos",
      tab_mahalleler: "Barrios",
    },
    anasayfaV2: {
      odul: "recompensa",
      slogan: "Una vida mejor, para cada patita.",
      heroTitle: "Que esta pasando hoy en tu barrio?",
      heroSub: "Calles mas seguras para los gatos callejeros son posibles.",
      heroMark: "Cada patita importa",
      statCats: "Gatos registrados",
      statEmergencies: "Emergencias",
      statFeeding: "Puntos de alimentacion",
      statVaccinated: "Vacunados",
      locationPick: "Cambiar ubicacion",
      search: "Busca un gato, barrio o distrito…",
      actionsTitle: "¿Qué quieres hacer?",
      actionsAside: "Un pequeño paso, una gran diferencia",
      a1t: "Vi un gato",
      a1s: "Reportar ubicación",
      a2t: "Buscar gato perdido",
      a2s: "Buscar en los anuncios",
      aramaVeritabani: "Base de datos de gatos",
      a3t: "Centro de Emergencias",
      a3s: "Gatos esperando ayuda",
      a4t: "Identificar con IA",
      a4s: "Comparar por foto",
      vtBaslik: "Base de Datos de Gatos",
      vtAlt: "Explora los gatos registrados y mira los detalles.",
      vtAdetSonEk: "gatos",
      lostTitle: "Un gato perdido cerca de ti",
      casesTitle: "Casos importantes cerca de ti",
      casesSub: "Gatos que más necesitan ayuda sobre el terreno",
      casesAll: "Ver todos",
      casesEmpty: "No hay casos abiertos cerca de ti — buenas noticias.",
      casesFarAway: "Ninguno cerca — mostrando los registros más recientes.",
      tabLost: "Gatos perdidos",
      tabEmergency: "Casos urgentes",
      tabAll: "Todos",
      emergencyBadge: "URGENTE",
      chipVet: "Necesita veterinario",
      chipInjured: "Esperando ayuda",
      chipFood: "Necesita comida/agua",
      chipDanger: "Peligro notificado",
      chipVolunteer: "Se busca voluntario",
      chipLost: "¿Alguien lo ha visto?",
      lostAll: "Ver todos",
      lostBadge: "PERDIDO",
      lostEmpty: "No hay anuncios de perdidos cerca de ti — buenas noticias.",
      mapTitle: "Mapa del barrio",
      mapDesc: "Descubre gatos, emergencias y puntos de alimentación cerca de ti.",
      mapOpen: "Abrir el mapa",
      aiTitle: "IA · Reconocimiento de gatos",
      aiDesc: "Fotografía el gato que viste y compáralo con los gatos registrados.",
      aiPhoto: "Tomar foto",
      aiGallery: "Elegir de la galería",
      healthTitle: "Salud y apoyo felino",
      healthDesc: "Vacunas, esterilización, rabia y notas de salud.",
      communityTitle: "Únete a la comunidad",
      communityDesc: "Voluntarios, eventos y más.",
      communityMark: "Juntos somos más fuertes",
      navReport: "Reportar gato",
      navReportShort: "Reportar",
      justNow: "ahora mismo",
      hoursAgo: "hace {n}h",
      hoursAgoLong: "hace {n} horas",
      minutesAgoLong: "hace {n} minutos",
      pickPlaceAria: "Elige tu zona",
      pickPlaceCta: "Elige tu zona",
      pickPlaceTitle: "Elige tu zona",
      pickPlaceSub: "Las pantallas Inicio y Mi Barrio siguen esta elección.",
      pickPlaceNear: "Según tu ubicación",
      pickPlaceClose: "Cerrar",
      pickPlaceSave: "Guardar",
      pickPlaceSaving: "Guardando…",
      yesterday: "ayer",
      daysAgo: "hace {n}d",
    },
    kayipIlan: {
      title: "Gato perdido",
      lost: "PERDIDO",
      seen: "Vi a este gato",
      odul: "recompensa",
      poster: "Crear cartel",
      posterTitle: "Cartel de gato perdido",
      posterErr: "No se pudo crear el cartel. Inténtalo de nuevo.",
      share: "Compartir",
      shareTitle: "Gato perdido 🔍",
      shareCopied: "Enlace copiado — pégalo donde quieras.",
      shareErr: "No se pudo compartir. Inténtalo de nuevo.",
      seenSub: "Marca el lugar en el mapa",
      aiTitle: "¿Viste un gato parecido?",
      aiDesc: "Haz una foto o elige de la galería, la IA compara.",
      whereTitle: "¿Dónde puede estar?",
      whereSub: "Zona de búsqueda estimada",
      openMap: "Abrir mapa",
      teamTitle: "Equipo de búsqueda",
      teamCount: "{n} personas buscando",
      teamJoin: "Unirme a la búsqueda",
      teamLeave: "Salir de la búsqueda",
      photoCount: "Fotos",
      seenCount: "Avistamientos reportados",
      owner: "Autor del anuncio",
      call: "Llamar",
      message: "Mensaje",
      details: "Detalles del anuncio",
      reunited: "Reencontrado — cerrar anuncio",
      bKisir: "Esterilizado",
      bKisirDegil: "Sin esterilizar",
      bErkek: "Macho",
      bDisi: "Hembra",
      bUrkek: "Tímido",
      bSokulgan: "Sociable",
      bSaldirgan: "Defensivo",
      bSakin: "Tranquilo",
      noPhoto: "Sin foto",
      notFound: "Anuncio no encontrado.",
      closed: "Este anuncio está cerrado.",
    },
    aramaAlani: {
      title: "Zona de búsqueda",
      areaTitle: "Zona estimada de búsqueda",
      areaSub: "Creado a partir de la última ubicación.",
      allReports: "Ver todo",
      directions: "Cómo llegar a esta zona",
      legend: "Leyenda del mapa",
      sonGorulen: "Visto por última vez",
      aramaAlaniHalka: "Zona de búsqueda",
      ihbarlar: "Avistamientos reportados",
      gonulluler: "Voluntarios",
      reports: "Reportes recientes",
      noReports: "Aún no hay reportes. Sé el primero.",
      cFoto: "Con foto",
      cBenzer: "Podría parecerse",
      cEminDegil: "No estoy seguro",
      addReport: "Añadir reporte",
      join: "Unirse al equipo de búsqueda",
      leave: "Salir del equipo de búsqueda",
      team: "{n} personas buscando",
      locHidden: "Inicia sesión para ver dónde están los voluntarios",
      away: "a {d}",
      noCoords: "Este anuncio no tiene ubicación en el mapa.",
      closed: "Este anuncio está cerrado.",
      notFound: "Anuncio no encontrado.",
      back: "Atrás",
    },
    mahallem: {
      anOnerilerAcikGorev: "Ya hay tareas abiertas",
      anHeroBaslik: "¿Cómo está {yer}?",
      anHeroAlt: "Datos de 30 días",
      anHeroAltAi: "30 días y análisis IA",
      anSkorEtiketi: "Índice de Salud del Barrio",
      anSkorBilgiAc: "¿Qué mide el índice?",
      anSkorBilgi: "El índice mide los recursos del barrio (vacunas registradas, puntos de comida y agua, emergencias abiertas, rapidez) — no la salud de los gatos.",
      anAzVeri: "pocos datos",
      anBalonYok: "Aún sin resumen: pocos datos en esta zona.",
      anDurumKayit: "Se registran gatos",
      anDurumKayitAlt: "{n} nuevos en 30 días",
      anDurumAcilYok: "Sin emergencias abiertas",
      anDurumAcilYokAlt: "Todos los casos resueltos",
      anDurumSuVar: "Puntos de agua activos",
      anDurumSuVarAlt: "{n} puntos activos",
      anDurumMamaVar: "Puntos de comida activos",
      anDurumMamaVarAlt: "{n} puntos activos",
      anDurumBeslenme: "Poca alimentación",
      anDurumBeslenmeAlt: "Faltan puntos",
      anDurumSu: "Pocos puntos de agua",
      anDurumSuAlt: "Faltan puntos",
      anDurumGonullu: "Pocos voluntarios",
      anDurumGonulluAlt: "Faltan voluntarios",
      anDurumAsi: "Vacunas poco registradas",
      anDurumAsiAlt: "Pocos registros",
      anDurumBarinak: "Sin casitas",
      anDurumBarinakAlt: "Falta refugio",
      anDurumYarali: "Gatos heridos reportados",
      anDurumYaraliAlt: "En 30 días",
      anDurumAcil: "{n} emergencias abiertas",
      anDurumAcilBir: "1 emergencia abierta",
      anDurumAcilAlt: "Requiere acción",
      anKonularBaslik: "{n} temas importantes ahora",
      anTumunuGor: "Ver todo",
      anKonuAcil: "Emergencia abierta",
      anKonuYarali: "Gatos heridos",
      anKonuBeslenme: "Falta punto de comida",
      anKonuSu: "Falta punto de agua",
      anKonuGonullu: "Pocos voluntarios",
      anKonuAsi: "Vacunas poco registradas",
      anKonuBarinak: "Sin casita",
      anKonuBolgede: "Sobre todo en {yer}.",
      anKonuYok: "Sin carencias claras en lo medido.",
      anVeriYok: "No se pudieron cargar los datos.",
      anDugmeVakalar: "Ver casos",
      anDugmeGonulluler: "Ver voluntarios",
      anDugmeKediler: "Ver gatos",
      anHaritaBaslik: "Mapa de Análisis del Barrio",
      anAralikSec: "Elegir periodo",
      anAralik7: "Últimos 7 días",
      anAralik30: "Últimos 30 días",
      anAralik90: "Últimos 3 meses",
      anAralik365: "Último año",
      anAralikTumu: "Todo",
      anKatmanYogunluk: "Densidad",
      anKatmanIhtiyac: "Necesidades",
      anKatmanAcil: "Emergencias",
      anKatmanBeslenme: "Comida",
      anKatmanSu: "Agua",
      anYogunlukDusuk: "Baja",
      anYogunlukYuksek: "Alta",
      anEtiketKedi: "{n} gatos",
      anEnCokIhtiyac: "Zona con más necesidad:",
      anHaritaBos: "Aún no hay zonas con límite.",
      anDegisimBaslik: "¿Qué cambió en 30 días?",
      anDetayliGor: "Detalles",
      anSekmeKediler: "Gatos",
      anSekmeAcil: "Emergencias",
      anSekmeKayip: "Perdidos",
      anSekmeBeslenme: "Comida",
      anSekmeSu: "Agua",
      anSatirKedi: "Registros",
      anSatirAcil: "Emergencias",
      anSatirBeslenme: "Comida",
      anSatirSu: "Agua",
      anRozet30: "en 30 días",
      anGrafikNotu: "Por fecha de registro: gatos = total al final del día; resto = añadidos ese día.",
      anGrafikYok: "Aún sin registros en el periodo.",
      anOnerilerBaslik: "Sugerencias IA",
      anOnerilerAlt: "Ideas para mejorar el barrio.",
      anTumOneriler: "Todas",
      anOneriYok: "Sin sugerencias ahora.",
      // ÖNCELİK-1 (18 Eylül 2026) — Mahalle Öncelikleri sayfası.
      ocBaslik: "Prioridades del Barrio",
      ocAlt: "Actuemos juntos por un barrio mejor.",
      ocGeri: "Atrás",
      ocNot: "Estas prioridades se han definido con análisis de IA a partir de los datos de tu barrio en los últimos 30 días.",
      ocEtiketAcil: "EMERGENCIAS",
      ocEtiketBeslenme: "ALIMENTACIÓN",
      ocEtiketSu: "PUNTOS DE AGUA",
      ocEtiketGonullu: "APOYO DE VOLUNTARIOS",
      ocEtiketDiger: "OTRAS NECESIDADES",
      ocAcilBaslik: "Gatos esperando ayuda",
      ocAcilAlt: "{n} caso de emergencia abierto en tu barrio.",
      ocAcilYokBaslik: "Sin emergencias abiertas",
      ocAcilYokAlt: "Ningún caso espera atención ahora mismo.",
      ocAcilBirim: "caso abierto",
      ocBeslenmeBaslik: "Hace falta apoyo de comida",
      ocBeslenmeAlt: "Los puntos de comida no bastan en {n} zona(s).",
      ocBeslenmeYokBaslik: "Los puntos de comida bastan",
      ocBeslenmeYokAlt: "{n} punto(s) de comida registrado(s).",
      ocBeslenmeBirim: "punto nuevo",
      ocSuBaslik: "Faltan puntos de agua",
      ocSuAlt: "Hace falta apoyo de agua en {n} zona(s).",
      ocSuYokBaslik: "Los puntos de agua bastan",
      ocSuYokAlt: "{n} punto(s) de agua registrado(s).",
      ocSuBirim: "punto",
      ocGonulluBaslik: "Necesitamos apoyo",
      ocGonulluAlt: "Se buscan voluntarios para {n} gatos.",
      ocGonulluYokBaslik: "El apoyo de voluntarios basta",
      ocGonulluYokAlt: "{g} voluntario(s) cuidan de {n} gatos.",
      ocGonulluBirim: "gatos",
      ocDigerBaslik: "Perdidos y refugio",
      ocDigerAlt: "{k} registro(s) activo(s), {b} necesidad de refugio.",
      ocDigerYokBaslik: "Sin problemas de perdidos ni refugio",
      ocDigerYokAlt: "Sin anuncio de perdido ni necesidad de refugio.",
      ocDigerBirim: "registros",
      ocDugmeGorevler: "Ver Tareas",
      ocDugmeBolgeler: "Ver Zonas",
      ocDugmeDetaylar: "Ver Detalles",
      gvKatBeslenme: "Comida",
      gvKatSu: "Punto de agua",
      gvKatKayip: "Gato perdido",
      gvKatSaglik: "Salud",
      gvKatKulube: "Refugio",
      gvDugmeDetay: "Ver detalles",
      gvSloganSade: "Actuemos juntos por los gatos.",
      gvOlusturBaslik: "Crea tu tarea",
      gvOlusturAlt: "Crea una nueva tarea para tu barrio. Juntos llegamos a más gatos.",
      gvOlusturDugme: "Crear tarea",
      gvOneriBaslikKisa: "Sugerencia IA",
      gvGorevlerimBag: "Mis tareas · {n}",
      baKatmanKisaAcil: "Urgencias",
      baKatmanKisaBeslenme: "Comida",
      baKatmanKisaSu: "Agua",
      baKatmanKisaGonullu: "Voluntarios",
      baKatmanKisaIsi: "Calor",
      gvBaslik: "Tareas",
      gvSlogan: "Lo que puedes hacer por los gatos de tu barrio. Un pequeño paso, una gran diferencia.",
      gvElYazisi: "Juntos, gatos más felices.",
      gvSuzTumu: "Todos",
      gvSuzAcil: "Urgente",
      gvSuzBugun: "Hoy",
      gvSuzYakin: "Cerca",
      gvFiltrele: "Filtrar",
      gvFiltreTur: "Tipo",
      gvFiltreDurum: "Estado",
      gvTemizle: "Limpiar",
      gvTamam: "Listo",
      gvTur_yarali_kontrol: "Gato herido",
      gvTur_veteriner_destegi: "Veterinario",
      gvTur_mama_istasyonu: "Comida",
      gvTur_su_kabi: "Agua",
      gvTur_kayip_arama: "Gato perdido",
      gvTur_kulube: "Refugio",
      gvHata: "No se pudieron cargar las tareas.",
      gvOncelikliBaslik: "Tareas prioritarias",
      gvCipTumu: "Todo",
      gvCipAcil: "Urgente",
      gvCipIhtiyac: "Necesidad",
      gvCipDestek: "Apoyo",
      gvListeGeri: "Mi barrio",
      gvListeAlt: "Todas las tareas en {yer}",
      gvListeSimdi: "Hacer ahora",
      gvListeSimdiAlt: "Tareas prioritarias del barrio",
      gvListeDiger: "Otras tareas",
      gvListeDigerAlt: "Otras tareas del barrio",
      gvListeAra: "Buscar",
      gvDetayGeri: "Tareas",
      gvDetayAcilRozet: "Tarea urgente",
      gvDetayBenimRozet: "Mi tarea",
      gvDetayBittiRozet: "Hecha",
      gvDetayBekliyor: "Esperando ayuda",
      gvDetayBekliyorAlt: "Esta tarea necesita atención urgente.",
      gvDetayUstlendi: "¡Asumiste esta tarea!",
      gvDetayUstlendiAlt: "Gracias — juntos somos más fuertes.",
      gvDetayBittiBanner: "Esta tarea está hecha. ¡Gracias por tu trabajo!",
      gvDetaySekmeDetay: "Detalles",
      gvDetaySekmeKonum: "Ubicación",
      gvDetaySekmeYorum: "Comentarios",
      gvDetaySonGorulme: "Última vez visto",
      gvDetayKonum: "Ubicación",
      gvDetayDurum: "Estado",
      gvDetayBildiren: "Quién avisó",
      gvDetayAciklama: "Descripción",
      gvDetayHaritada: "Ver en el mapa",
      gvDetayUstlen: "Asumir tarea",
      gvDetayPaylas: "Compartir",
      gvDetayTamamla: "Marcar como hecha",
      gvDetayFoto: "Añadir foto",
      gvDetayGelisme: "Fotos del avance",
      gvDetayBittiTarih: "Esta tarea se completó el {tarih}",
      gvDetayYorumBos: "Aún no hay comentarios.",
      gvDetayEkip: "Equipo y pasos",
      gvTumunuGor: "Ver todo",
      gvRozetAcil: "Urgente",
      gvRozetOncelikli: "Prioritario",
      gvDugmeGor: "Ver tarea",
      gvDugmeUstlen: "Asumir",
      gvDugmeKontrol: "Revisar",
      gvDugmeDestek: "Ayudar",
      gvYakinBaslik: "Tareas cerca de ti",
      gvMahalleBaslik: "Tareas en el barrio",
      gvOneriBaslik: "Sugerencia de IA",
      gvOneriDugme: "Ver sugerencias",
      gvBenimBaslik: "Mis tareas",
      gvAktifSayi: "{n} activas",
      gvTamamSayi: "{n} completadas",
      gvBenimBos: "Aún no te has unido a ninguna tarea.",
      gvBos: "No hay tareas por ahora.",
      gvKonumGerekli: "Se necesita permiso de ubicación.",
      gvKonumReddedildi: "Se denegó el permiso de ubicación.",
      gvDurumAcik: "Abierta",
      gvDurumDevam: "En curso",
      gvDurumTamam: "Completada",
      gvDurumIptal: "Cancelada",
      gvZamanBugun: "Te uniste · Hoy {saat}",
      gvZamanDun: "Te uniste · Ayer {saat}",
      gvZamanTarih: "Te uniste · {tarih}",
      gvZamanBugunKisa: "Hoy {saat}",
      gvZamanDunKisa: "Ayer {saat}",
      m5Mahallem: "Mi barrio",
      m5Paylas: "Compartir",
      m5GonulluOl: "Ser voluntario",
      m5HavaAcik: "Despejado",
      m5HavaParcali: "Parcialmente nublado",
      m5HavaSisli: "Niebla",
      m5HavaYagmurlu: "Lluvia",
      m5HavaKarli: "Nieve",
      m5HavaFirtinali: "Tormenta",
      m5HavaBugun: "Hoy {min}° / {max}°",
      m5PanoKedi: "gatos registrados",
      m5EgilimYeni: "nuevo",
      m5EgilimBaslik: "Últimos 30 días: {son} registros nuevos · 30 días anteriores: {once}",
      m5HaritayiAc: "Abrir mapa",
      baBaslik: "Análisis por zona",
      baAltBaslik: "Entiende juntos las necesidades del barrio.",
      baAralikTumu: "Todo",
      baAralik7: "7 días",
      baAralik30: "30 días",
      baAralik90: "3 meses",
      baAralik365: "1 año",
      baSayacKedi: "Total de gatos",
      baSayacAcil: "Emergencias abiertas",
      baSayacBeslenme: "Puntos de comida",
      baSayacSu: "Puntos de agua",
      baSayacGonullu: "Participaciones",
      baSayacKediYeni: "Gatos nuevos",
      baSayacAcilYeni: "Nuevas emergencias",
      baSayacBeslenmeYeni: "Nuevos puntos",
      baSayacSuYeni: "Nuevos puntos de agua",
      baSayacGonulluYeni: "Nuevas participaciones",
      baSatirKedi: "{n} gatos",
      baSatirAcil: "{n} emergencias",
      baSatirBeslenme: "{n} puntos de comida",
      baSatirSu: "{n} puntos de agua",
      baSatirGonullu: "{n} participaciones",
      baKatmanlar: "Capas",
      baKatmanKedi: "Gatos",
      baKatmanAcil: "Emergencias",
      baKatmanBeslenme: "Puntos de comida",
      baKatmanSu: "Puntos de agua",
      baKatmanGonullu: "Tareas voluntarias",
      baKatmanIsi: "Mapa de calor",
      baCercevele: "Ajustar mapa",
      baIlceNotu: "Pocos registros tienen barrio; las zonas se muestran por distrito.",
      baHata: "No se pudo cargar el análisis.",
      baHaritaBos: "Ninguna zona con ubicación",
      baBolgeOzet: "Resumen por zona",
      baTumBolgeler: "Todas las zonas",
      baKademeNotu: "Números = nuevos registros del período; niveles = todo el tiempo.",
      baDiger: "Otros",
      baDigerAlt: "sin barrio",
      baBolgeyiIncele: "Ver zona",
      baBolgeYok: "Aún no hay zonas.",
      baAiBaslik: "Análisis con IA",
      baAiYok: "El resumen de hoy aún no está listo.",
      baAiDetay: "Análisis detallado",
      baOncelikli: "Zonas prioritarias",
      baNedenYok: "Menor puntuación",
      baHaritadaGor: "Ver gatos en el mapa",
      baHaritadaAlt: "Mapa centrado en estas zonas.",
      baTumKediler: "Gatos del barrio",
      baTumKedilerAlt: "Míralos en lista.",
      baKisaKedi: "Gatos",
      baKisaAcil: "Urgencias",
      baKisaBeslenme: "Comida",
      baKisaSu: "Agua",
      baKisaGonullu: "Voluntarios",
      baKisaKediYeni: "Gatos nuevos",
      baKisaAcilYeni: "Nuevas emerg.",
      baKisaBeslenmeYeni: "Nueva comida",
      baKisaSuYeni: "Nueva agua",
      baKisaGonulluYeni: "Uniones",
      baSatirKisaKedi: "{n} gatos",
      baSatirKisaAcil: "{n} emerg.",
      baSatirKisaBeslenme: "{n} comida",
      baSatirKisaSu: "{n} agua",
      baSatirKisaGonullu: "{n} uniones",
      baIncele: "Ver",
      baAiDetayKisa: "Detalles",
      baKademeNotuKisa: "· nuevos registros",
      baIlceNotuKisa: "Nivel distrito",
      gdBaslik: "Detalle del día",
      gdBugun: "Hoy",
      gdOncekiGun: "Día anterior",
      gdSonrakiGun: "Día siguiente",
      gdToplamBugun: "{n} registros hoy.",
      gdToplamGun: "{n} registros este día.",
      gdToplamYok: "No hay registros este día.",
      gdToplamKarsilastir: "Día anterior: {once} · este día: {simdi}.",
      gdDagilim: "Distribución de actividad",
      gdKisaKedi: "Gatos nuevos",
      gdKisaAcil: "Emergencia",
      gdKisaKayip: "Perdidos",
      gdKisaBeslenme: "Comida",
      gdKisaSu: "Agua",
      gdKisaGonullu: "Voluntarios",
      gdBolge: "Por barrio",
      gdKonumsuz: "Sin barrio: {n}",
      gdKonumYok: "No hay registros con ubicación",
      gdHaritadaGor: "Ver en el mapa",
      gdFotoBugun: "Fotos de hoy",
      gdFotoGun: "Fotos del día",
      gdFotoYok: "Sin fotos",
      gdFazla: "más",
      gdKayitBugun: "Registros de hoy",
      gdKayitGun: "Registros del día",
      gdFiltreHepsi: "Todos los registros",
      gdTumunuGoster: "Mostrar todos ({n})",
      gdKayitKedi: "Nuevo gato registrado",
      gdKayitAcil: "Emergencia",
      gdKayitKayip: "Anuncio de gato perdido",
      gdKayitBeslenme: "Nuevo punto de comida",
      gdKayitSu: "Nuevo punto de agua",
      gdKayitGonullu: "Voluntarios se unieron",
      gdKatilim: "{n} participaciones",
      gdAltBaslik: "Ve tu barrio en el mapa.",
      gdAltMetin: "Una vida más segura para tu barrio, juntos.",
      gdHata: "No se pudo cargar el detalle del día. Aparecerá cuando se actualice el servidor.",
      gdPaylasMetni: "{yer} · {tarih}: gatos callejeros, detalle del día.",
      gdGunuAc: "Abrir detalle de {tarih}",
      s7Baslik: "Últimos 7 días",
      s7PaylasMetni: "{yer}: los últimos 7 días de los gatos callejeros.",
      s7GrafikBaslik: "Nuevos registros por día",
      s7Yeni: "nuevo",
      s7Sabit: "sin cambio",
      s7SayacKedi: "Nuevos gatos registrados",
      s7SayacAcil: "Emergencias",
      s7SayacKayip: "Gatos perdidos",
      s7SayacBeslenme: "Nuevos puntos de comida",
      s7SayacSu: "Nuevos puntos de agua",
      s7SayacGonullu: "Participaciones voluntarias",
      s7Gunluk: "Detalles diarios",
      s7KartKotu: "{seri} aumentaron en los últimos 7 días.",
      s7KartIyi: "{seri} aumentaron en los últimos 7 días.",
      s7KartSabit: "Sin cambios claros frente a los 7 días anteriores.",
      s7KartOnceYok: "La comparación con la semana anterior aparece tras actualizar el servidor.",
      s7KartKarsilastir: "7 días anteriores: {once} · esta semana: {simdi}.",
      s7IstatistikDetay: "Ver estadísticas",
      s7SeriAcil: "Emergencias",
      s7SeriKayip: "Gatos perdidos",
      s7SeriKedi: "Nuevos gatos",
      s7SeriBeslenme: "Nuevos puntos de comida",
      s7SeriSu: "Nuevos puntos de agua",
      s7SeriGonullu: "Participaciones voluntarias",
      s7Ac: "Abrir últimos 7 días",
      zekaGonullu: "Faltan voluntarios",
      azVeriKisa: "Pocos datos",
      heroYanSlogan: "Mismo barrio, gatos más felices.",
      haritaBaslikOzet: "Mapa del barrio (resumen)",
      lejantAcil: "Emergencias",
      lejantKayip: "Gatos perdidos",
      lejantSu: "Puntos de agua",
      lejantBeslenme: "Alimentación",
      lejantGonullu: "Voluntarios",
      zekaSu: "Pocos puntos de agua",
      zekaMama: "Poca alimentación",
      zekaAsi: "Registro de vacunas débil",
      zekaYarali: "Gatos heridos reportados",
      zekaAcil: "Emergencias abiertas",
      zekaBarinak: "Sin refugio registrado",
      zekaTamam: "Sin carencias claras",
      yasBilinmiyorKisa: "Desconocida",
      dagilimIpucuBilinmiyor: "{p} de los gatos sin edad registrada.",
      dagilimIpucuCogunluk: "Grupo mayor: {grup} ({p}).",
      dagilimYakinda: "Disponible tras la actualización del servidor.",
      ustBaslik: "Mi barrio",
      paylas: "Compartir",
      paylasMetni: "{yer}: gatos, necesidades y voluntarios del barrio.",
      paylasKopyalandi: "Enlace copiado",
      paylasOlmadi: "No se pudo compartir",
      sekmeGenel: "Resumen",
      sekmeAnaliz: "Análisis",
      sekmeGorevler: "Tareas",
      sekmeKediler: "Gatos",
      sekmeGonulluler: "Voluntarios",
      lejantKedi: "Gatos",
      zekaOzet: "Analizamos tu barrio. Lo más destacado:",
      azVeri: "Aún hay pocos registros — la puntuación puede cambiar rápido.",
      detayliAnaliz: "Ver análisis detallado",
      dagilimBaslik: "Gatos por edad",
      kediKisa: "gatos",
      yasYavru: "Gatito",
      yasYetiskin: "Adulto",
      yasYasli: "Mayor",
      yasBilinmiyor: "Edad desconocida",
      dagilimYok: "Aún no hay gatos registrados.",
      ihtiyacBaslik: "Necesidades principales",
      ihtiyacYok: "Ninguna necesidad clara ahora.",
      oncelikKritik: "Crítica",
      oncelikYuksek: "Alta",
      oncelikOrta: "Media",
      oncelikDusuk: "Baja",
      son7Baslik: "Últimos 7 días",
      son7Yok: "Sin registros nuevos en los últimos 7 días.",
      seriKedi: "Gato",
      seriAcil: "Emergencia",
      seriKayip: "Perdido",
      seriBeslenme: "Alimentación",
      seriSu: "Agua",
      ctaBaslik: "Juntos, un barrio más fuerte.",
      ctaAlt: "Ayuda a cambiar la vida de los gatos de tu barrio.",
      ctaDugme: "Hazte voluntario",
      zekaAlt: "Mira lo que tu barrio necesita y actúa.",
      zekaBaslik: "Análisis de Barrio con IA",
      panoBeslenme: "puntos de comida",
      panoGonullu: "voluntarios activos",
      hizliAcilMerkezi: "Centro de Emergencias",
      onemliBaslik: "Alertas cerca",
      rozetKayip: "PERDIDO",
      rozetAcil: "URGENTE",
      rozetMama: "COMIDA",
      rozetTehlike: "PELIGRO",
      rozetGonullu: "AYUDA",
      durumYok: "Nada urgente cerca ahora.",
      sonFotograflar: "Fotos recientes",
      sonAktiviteler: "Actividad reciente",
      tumuSayili: "Todas ({n})",
      paylasimYok: "Aún no hay publicaciones en tu barrio.",
      yakinKediler: "Gatos cerca de ti",
      haritayiAc: "Abrir mapa",
      cipBesleniyor: "Puntos de comida",
      cipSu: "Puntos de agua",
      cipAlisik: "Acostumbrado a la gente",
      cipBiliniyor: "Conocido en el barrio",
      haritaEnYakin: "{k} de {n} gatos más cercanos",
      panoBaslik: "Hoy en Tu Barrio",
      panoAlt: "Más gatos, un barrio mejor.",
      panoKedi: "gatos en seguimiento",
      panoAcil: "emergencias",
      panoKayip: "gatos perdidos",
      panoSu: "puntos de agua",
      mahallemiGor: "Ver mi barrio",
      hizliBaslik: "Acciones rápidas",
      hizliKaydet: "Registrar gato",
      hizliGordum: "He visto un gato",
      hizliAcil: "Emergencia",
      hizliMamaSu: "Comida / Agua",
      title: "Mi barrio",
      bolgelerBaslik: "Zonas que más ayuda necesitan",
      heroKedi: "{n} gatos registrados en tu barrio.",
      heroYeniKedi: "{n} gatos nuevos registrados en los últimos 30 días.",
      heroYardimBekleyen: "{n} gatos de tu barrio están esperando ayuda.",
      enOnemli: "Mayor necesidad:",
      iSu: "Puntos de agua",
      iMama: "Puntos de comida",
      iAsi: "Seguimiento de vacunas",
      iYarali: "Gatos heridos",
      iAcil: "Casos urgentes",
      iBarinak: "Refugio",
      iTamam: "Sin carencias claras",
      haritaAlt: "Explora gatos y zonas que necesitan ayuda.",
      tamEkran: "Pantalla completa",
      lYuksek: "Necesidad alta",
      lOrta: "Media",
      lYeterli: "Suficiente",
      lMama: "Punto de comida",
      lSu: "Punto de agua",
      tumu: "Todos",
      tumGorevler: "Todas las tareas",
      ilerlemeTamam: "{n}/{m} completado",
      kayipRozet: "PERDIDO",
      kayipAdsiz: "Gato perdido",
      kayipYok: "No hay anuncios de pérdida cerca de ti.",
      azOnce: "ahora mismo",
      saatOnce: "hace {n}h",
      gunOnce: "hace {n}d",
      son30: "Últimos 30 días",
      sAcil: "Emergencias",
      sMama: "Puntos de comida",
      sSu: "Puntos de agua",
      sGonullu: "Voluntarios activos",
      aiBaslik: "Análisis del barrio con IA",
      beta: "BETA",
      aiGor: "Ver sugerencias de IA",
      haritaBaslik: "Mapa del barrio",
      kayipBaslik: "Gatos perdidos cerca de ti",
      yardimBaslik: "Ayuda a tu barrio",
      gorevYok: "No hay tareas abiertas ahora.",
      gorevKatil: "Unirme",
      ilerleme: "{n}/{m} voluntarios",
      skorBaslik: "Puntuación del barrio",
      skorYaklasik: "el cambio es aproximado",
      olculmedi: "—",
      yukleniyor: "Cargando…",
      hata: "No se pudo cargar el informe del barrio.",
      mahalleYok: "Elige tu barrio para ver esta página.",
      secBaslik: "Elige tu barrio",
      secCumle: "Al elegirlo, los gatos, las necesidades y las tareas de aquí siguen tu barrio.",
      secKaydet: "Guardar",
      vazgec: "Cancelar",
      secHata: "No se pudo guardar tu barrio.",
      tekrarDene: "Reintentar",
    },
    bolge: {
      title: "Detalle de la zona",
      bolgesi: "Zona de {n}",
      tumunuGor: "Ver todos",
      detayliAnaliz: "Análisis detallado",
      tumu: "Todos",
      gecenAy: "{n} frente al mes pasado",
      yaklasik: "aprox.",
      kediAdet: "{n} gatos",
      geri: "Atrás",
      kapat: "Cerrar",
      son30: "Últimos 30 días",
      iKritik: "Necesidad crítica",
      iYuksek: "Necesidad alta",
      iOrta: "Necesidad media",
      iDusuk: "Necesidad baja",
      sKedi: "Gatos registrados",
      sMama: "Puntos de comida",
      sSu: "Puntos de agua",
      yKuzey: "Norte",
      yGuney: "Sur",
      yDogu: "Este",
      yBati: "Oeste",
      aiBaslik: "Análisis de Zona con IA",
      beta: "BETA",
      kedilerBaslik: "Gatos en esta zona",
      kediYok: "No hay gatos registrados en esta zona.",
      skorBaslik: "Puntuación del barrio",
      skorYaklasik: "cambio aproximado",
      dagilimBaslik: "Distribución por dirección",
      dagilimAdet: "{n} gatos",
      bildirimBaslik: "Actividad reciente",
      bildirimYok: "Sin actividad en los últimos 30 días.",
      olculmedi: "—",
      olcumYok: "Aún no hay mediciones diarias para esta zona.",
      hata: "No se pudo cargar el informe de la zona.",
    },
    mahalleListesi: {
      title: "Barrios",
      ara: "Buscar barrio",
      mahalle: "barrios",
      paylasim: "publicaciones",
      kedi: "gatos",
      alinamadi: "No se pudo cargar la lista de barrios.",
      tekrarDene: "Reintentar",
      aramaBos: "Ningún barrio coincide con esta búsqueda.",
      hicYok: "Aún no hay barrios que mostrar.",
      kuralNotu: "Esta lista muestra barrios con publicaciones, áreas de cobertura o animales registrados.",
    },
    aiAnaliz: {
      sekmeOzet: "Resumen",
      sekmeDetay: "Datos detallados",
      sekmeOneriler: "Sugerencias",
      sonNGun: "Últimos {n} días",
      paylas: "Compartir",
      paylasMetni: "{yer}: análisis del barrio para gatos callejeros.",
      paylasKopyalandi: "Enlace copiado",
      paylasOlmadi: "No se pudo compartir",
      detayBaslik: "Puntuación por área",
      veriYok: "Aún no hay medición.",
      skorEtiketi: "Salud del barrio",
      genelDurum: "Situación general",
      genelSkor: "Los recursos del barrio están \"{etiket}\".",
      genelKediArtti: "{n} gatos más registrados en 30 días.",
      genelKediAzaldi: "{n} gatos menos que hace 30 días.",
      genelKediSabit: "El número de gatos registrados no cambió en 30 días.",
      genelBulgu: "Se encontraron {n} carencias.",
      genelBulguYok: "Ninguna carencia clara en las áreas medidas.",
      farkRozeti: "{d} puntos vs mes pasado",
      aiOzeti: "Resumen IA",
      ozetSablon: "Resumen",
      tumu: "Todos",
      bulgularBaslik: "Hallazgos clave",
      bulguAcil: "Emergencias abiertas",
      bulguYarali: "Reportes de gatos heridos",
      bulguMama: "Faltan puntos de comida",
      bulguSu: "Faltan puntos de agua",
      bulguAsi: "Control de vacunas débil",
      bulguBarinak: "Sin casita para gatos",
      bulguGonullu: "Se necesitan voluntarios",
      eylemHaritada: "Ver en el mapa",
      eylemGorev: "Crear tarea",
      eylemDetaylar: "Ver detalles",
      eylemGonullu: "Llamar voluntarios",
      eylemVakalar: "Ver casos",
      son7AcilEk: "{n} nuevos en 7 días.",
      bulguYok: "Ninguna carencia clara.",
      haritadaGorBag: "Ver en el mapa",
      bolgeBaslik: "Análisis por zona",
      kademeMukemmel: "Excelente",
      kademeIyi: "Bueno",
      kademeGelistir: "Mejorable",
      kademeKritik: "Crítico",
      ilceDuzeyiNotu: "Nivel distrito",
      notSu: "Poca agua",
      notMama: "Falta comida",
      notAsi: "Vacunas",
      notYarali: "Heridos",
      notAcil: "Emergencia",
      notBarinak: "Sin casita",
      notGonullu: "Voluntarios",
      notTamam: "Bien en general",
      bolgeYok: "Las puntuaciones por zona aparecen con datos suficientes.",
      aralikSec: "Elegir periodo",
      trendBaslik: "Análisis de tendencia",
      seriKedi: "Gatos",
      seriAcil: "Emergencia",
      seriKayip: "Perdidos",
      seriBeslenme: "Comida",
      seriSu: "Agua",
      trendBirikiyor: "Las mediciones diarias se acumulan; el gráfico aparece tras dos días.",
      oneriBaslik: "Sugerencia IA",
      bakilanBaslik: "Qué revisó este análisis",
      bakilanKedi: "Gatos registrados",
      bakilanSu: "Puntos de agua",
      bakilanMama: "Puntos de comida",
      bakilanKulube: "Refugios",
      bakilanAcil: "Gatos en estado urgente",
      bakilanYarali: "Avisos de heridos (30 días)",
      oneriAlinamadi: "No se pudieron cargar las sugerencias — el servidor no respondió. Esto no significa que no haya carencias.",
      tekrarDene: "Reintentar",
      acikGorevNotu: "Una sugerencia se oculta mientras exista una tarea abierta del mismo tipo.",
      sayacKedi: "gatos",
      sayacSu: "puntos de agua",
      sayacMama: "puntos de comida",
      sayacKulube: "refugios",
      sayacYarali: "avisos de heridos (30 días)",
      sekmeBolge: "Análisis de Zona",
      sekmeKedi: "Reconocimiento de Gato",
      ozetBaslikPano: "Evaluación general",
      ozetCumle: "La situación del barrio en {yer} es {durum}. Se encontraron {n} carencia(s) prioritaria(s).",
      durumBilinmiyor: "no medida",
      yerYok: "Barrio no seleccionado",
      balon: "¡Un barrio mejor es posible!",
      eksiklerBaslik: "Carencias prioritarias",
      eksikSu: "Puntos de agua insuficientes",
      eksikMama: "Puntos de comida insuficientes",
      eksikYarali: "Avisos de gatos heridos",
      eksikKulube: "Sin refugio registrado",
      eksikVet: "Gatos que necesitan atención urgente",
      bugunBaslik: "3 cosas que puedes hacer hoy",
      eylemSu: "Añadir punto de agua",
      eylemMama: "Crear punto de comida",
      eylemYarali: "Revisar gato herido",
      eylemKulube: "Construir un refugio",
      eylemVet: "Conseguir apoyo veterinario",
      haritadaGor: "Ver en el mapa",
      eylemDetay: "Ver detalles",
      eylemListe: "Ver lista",
      yogunBolge: "Zona con más gatos del barrio",
      gorevOlustur: "Crear tarea",
      etkiOnEk: "Impacto esperado:",
      eksikYok: "No hay carencias prioritarias ahora.",
      title: "Análisis con IA",
      beta: "BETA",
      sekmeTani: "Identificar por foto",
      sekmeSonuc: "Resultados del análisis",
      sekmeIstatistik: "Estadísticas",
      taniBaslik: "Identificar un gato por foto",
      taniAciklama: "Sube una foto; se compara con gatos registrados y anuncios de pérdida abiertos.",
      taniGit: "Ir a identificación por foto",
      sonAnalizler: "Análisis recientes",
      analizYok: "Aún no has hecho ningún análisis.",
      olcumBaslamadi: "El historial de análisis se acaba de empezar a guardar; los anteriores no se registraron.",
      eslesti: "Coincidencia",
      eslesmedi: "Sin coincidencia",
      adsizIlan: "Nombre no indicado",
      ozetBaslik: "Resumen de análisis en esta zona",
      sAnaliz: "Análisis",
      sEslesme: "Coincidencias",
      sYeniKedi: "Gatos nuevos",
      oran: "{n}/{m} análisis alcanzaron el umbral",
      oranNotu: "Esto no es una tasa de precisión: cuántas coincidencias eran correctas no se ha medido.",
      son30: "Últimos 30 días",
      yukleniyor: "Cargando…",
      hata: "No se pudieron cargar los análisis.",
    },
    kayipFormu: {
      baslik: "Sobre el gato",
      adEtiket: "Nombre",
      adYerTutucu: "p. ej. Boncuk",
      adIpucu: "El nombre que llamarán los vecinos — ayuda más que nada.",
      kisirEtiket: "Esterilizado",
      kEvet: "Sí",
      kHayir: "No",
      cinsiyetEtiket: "Sexo",
      cErkek: "Macho",
      cDisi: "Hembra",
      cBilinmiyor: "No lo sé",
      yasEtiket: "Edad",
      yasIpucu: "p. ej. 1,5 años",
      mizacEtiket: "Carácter",
      mUrkek: "Asustadizo",
      mSokulgan: "Sociable",
      mSaldirgan: "Defensivo",
      mSakin: "Tranquilo",
      iletisimEtiket: "Contacto (opcional)",
      iletisimIpucu: "Solo se muestra tras el botón Llamar, a personas registradas. No lo pongas en la descripción.",
      notNumaraUyarisi: "Parece que hay un teléfono en la descripción. Ponlo en el campo de contacto — así no queda a la vista.",
    },
    kayipBildir: {
      title: "Aviso de gato perdido",
      subtitle: "Añade fotos y cómo es tu gato. La ubicación viene después.",
      fotoZorunlu: "Hace falta al menos una foto — es lo único que puede comparar quien lo encuentre.",
      varsa: "si lo sabes",
      renkEtiket: "Color",
      konumTitle: "Dónde y cuándo se le vio",
      konumSubtitle: "Marca el punto en el mapa. Sin eso, tu aviso no llega a quienes están cerca.",
      konumumuKullan: "Usar mi ubicación actual",
      konumAliniyor: "Obteniendo ubicación…",
      konumZorunlu: "La ubicación es obligatoria. Si el GPS no abre, toca el mapa para marcarlo.",
      konumIsaretlendi: "Ubicación marcada",
      konumYok: "Sin ubicación aún",
      yaricapEtiket: "Radio de búsqueda",
      yaricapIpucu: "Un gato doméstico perdido suele esconderse a unos cientos de metros los primeros días.",
      sonGorulmeEtiket: "Visto por última vez",
      ekEtiket: "Información adicional",
      ekYerTutucu: "Cualquier cosa que ayude: una costumbre, dónde suele esconderse, en quién confía…",
      mahalleEtiket: "Barrio",
      duzenle: "Editar",
      ozetAdsiz: "Gato sin nombre",
      yayinla: "Publicar aviso de perdido",
      yayinlaniyor: "Publicando…",
      errSend: "No se pudo publicar el aviso. Inténtalo de nuevo.",
      okTitle: "Tu aviso está publicado",
      okDesc: "Se avisó a quienes están cerca. Cada avistamiento te llegará.",
      okView: "Abrir mi aviso",
      okList: "Todos los gatos perdidos",
      acikIlanBaslik: "Ya tienes un aviso abierto",
      acikIlanMetin: "Un segundo aviso divide la búsqueda. ¿Es el mismo gato?",
      acikIlanGit: "Abrir ese aviso",
      acikIlanBaska: "No, este es otro gato",
      vazgec: "Cancelar",
      renkYerTutucu: "p. ej. atigrado, pecho blanco",
      ayirtEtiket: "Señas particulares",
      ayirtYerTutucu: "Collar, cicatriz, oreja marcada, su forma de andar…",
    },
    kayipListe: {
      odul: "recompensa",
      baslik: "Gatos perdidos",
      rozet: "PERDIDO",
      ilanAc: "Publicar aviso de perdido",
      fotoAra: "Buscar por foto",
      yukleniyor: "Cargando…",
      yeniden: "Reintentar",
      hata: "No se pudieron cargar los avisos.",
      bos: "No hay avisos de perdidos abiertos — buena noticia.",
      yakindaYok: "Nada cerca, así que se muestran avisos más lejanos.",
    },
    acilDetay: {
      durumBaslik: "Estado del gato",
      aciklamaBaslik: "Descripción",
      bildirenBaslik: "Reportado por",
      bildirdi: "reportó {n}",
      konumBaslik: "Ubicación",
      haritadaAc: "Abrir en el mapa",
      paylas: "Compartir",
      sil: "Eliminar reporte",
      silOnayBaslik: "¿Eliminar este reporte?",
      silOnayMetin: "Desaparece de la lista y del mapa. No se puede deshacer.",
      silEvet: "Sí, eliminar",
      vazgec: "Cancelar",
      siliniyor: "Eliminando…",
      silHata: "No se pudo eliminar el reporte.",
      silYetki: "Solo quien lo reportó puede eliminarlo.",
      fotoSayaci: "{a} / {b}",
      yorumlar: "Comentarios",
      yorumYok: "Aún no hay comentarios — aquí se coordinan los vecinos.",
      yorumYaz: "Escribe un comentario…",
      yorumGonder: "Enviar",
      yorumGidiyor: "Enviando…",
      yorumHata: "No se pudo enviar el comentario.",
      yorumOkunmadi: "No se pudieron cargar los comentarios.",
      yorumGiris: "Inicia sesión para comentar.",
      yorumSil: "Eliminar comentario",
      yorumSilHata: "No se pudo eliminar el comentario.",
      begen: "Apoyar",
      begenHata: "No se pudo completar.",
    },
    anasayfaAlt: {
      baslik: "Sobre Sokak Kedileri",
      hakkinda: "Sobre nosotros",
      yardim: "Ayuda",
      gizlilik: "Privacidad",
      sartlar: "Términos de uso",
      telif: "Hecho con amor por los gatos callejeros.",
    },
    ihbarFormu: {
      title: "Añadir reporte",
      sub: "¿Dónde viste al gato?",
      locTitle: "Ubicación",
      locUse: "Usar mi ubicación",
      locPicked: "Ubicación marcada",
      locHelp: "Toca el mapa para mover el pin.",
      locNeeded: "La ubicación es obligatoria — un reporte sin ella no se dibuja en el mapa.",
      noteTitle: "¿Qué viste?",
      notePh: "Debajo de un coche blanco, muy asustadizo. Corrió hacia el parque.",
      photoTitle: "Foto (opcional)",
      photoAdd: "Añadir foto",
      photoRemove: "Quitar foto",
      photoHint: "Una foto convierte tu reporte en prueba — la IA puede compararla.",
      guvenTitle: "¿Qué tan seguro estás?",
      gBenzer: "Podría parecerse",
      gEminDegil: "No estoy seguro",
      gFoto: "Con foto",
      guvenFotoNote: "Con foto, tu reporte se marca como “Con foto”.",
      send: "Enviar reporte",
      sending: "Enviando…",
      okTitle: "Reporte enviado 🧡",
      okBody: "Se avisó a la persona dueña. Gracias.",
      okBack: "Volver a la zona de búsqueda",
      errSend: "No se pudo enviar el reporte. Inténtalo de nuevo.",
      back: "Atrás",
    },
    aiKars: {
      title: "Comparación con IA",
      seenSimilar: "¿Viste un gato parecido?",
      seenSimilarDesc: "Si viste un gato, haz una foto o elige una de la galería. La IA la comparará con el gato perdido.",
      camera: "Cámara",
      gallery: "Galería",
      aiNote: "La IA es una ayuda. La última palabra es tuya.",
      resultTitle: "Resultado de la comparación",
      new: "Nuevo",
      yourPhoto: "Tu foto",
      lostCat: "Gato perdido",
      levelHigh: "Parecido alto",
      levelMid: "Algo de parecido",
      levelLow: "Parecido bajo",
      levelHighDesc: "Este gato se parece bastante al perdido.",
      levelMidDesc: "Algunos rasgos coinciden, otros no.",
      levelLowDesc: "Los rasgos en general no coinciden.",
      fPattern: "Patrón del pelaje",
      fMarks: "Marcas distintivas",
      fVisual: "Aspecto general",
      fAge: "Rango de edad",
      fLocation: "Cercanía del lugar",
      stateSimilar: "parecido",
      statePartial: "algo parecido",
      stateDifferent: "distinto",
      stateUnmeasured: "no medido",
      report: "Reportar este gato",
      unsureTitle: "¿No estás seguro?",
      unsureDesc: "Repórtalo igual. Cada aviso suma.",
      tipsTitle: "Consejos para un mejor resultado",
      tip1: "Enfoca bien la cara",
      tip2: "Si puedes, fotografía desde varios ángulos",
      tip3: "Haz la foto con buena luz",
      tip4: "Muestra también el entorno",
      comparing: "Comparando…",
      errCompare: "No se pudo comparar. Inténtalo de nuevo.",
      notMeasuredNote: "Los rasgos que la IA no pudo medir se marcan así — no medido no es lo mismo que distinto.",
    },
    acil: {
      merkezHeroNot: "Cada gato importa",
      merkezBaslik: "Centro de Emergencias",
      merkezHeroBaslik: "Salvemos vidas juntos",
      merkezHeroAlt: "Actuemos juntos por los gatos heridos, en peligro o necesitados.",
      merkezVakalariGor: "Ver emergencias",
      merkezVakalariGorAlt: "Mira los gatos que esperan ayuda ahora.",
      merkezVakaBildir: "Reportar emergencia",
      merkezVakaBildirAlt: "Crea una nueva emergencia.",
      merkezYakinVakalar: "Emergencias cerca de mí",
      merkezKategoriler: "Categorías de emergencia",
      merkezKatYarali: "Gatos heridos",
      merkezKatTehlike: "Zonas de peligro",
      merkezKatKayip: "Gatos perdidos",
      merkezKatMamaSu: "Comida / Agua",
      merkezKatGonullu: "Ayuda voluntaria",
      merkezAltBaslik: "Cada informe puede salvar una vida.",
      merkezAltMetin: "Al informar incluso situaciones dudosas, ayudas a que los gatos vivan en barrios más seguros.",
      merkezVakaYok: "No hay emergencias abiertas cerca de ti ahora.",
      title: "Reportar emergencia",
      subtitle: "¿Viste un gato que necesita ayuda? Repórtalo rápido y llega a los voluntarios más cercanos.",
      safetyNote: "En una emergencia, prioriza tu propia seguridad. No te pongas en riesgo.",
      stepInfo: "Detalles",
      stepLocation: "Ubicación",
      stepSend: "Enviar",
      catInjured: "Gato herido",
      catInjuredDesc: "Gato herido, enfermo o que sufre",
      catLost: "Avisar de un gato perdido",
      catLostDesc: "Tu gato se perdió — publica un anuncio de búsqueda",
      catFood: "Necesidad de comida / agua",
      catFoodDesc: "Sin punto de alimentación o necesita apoyo",
      catDanger: "Zona peligrosa",
      catDangerDesc: "Obra, tráfico, riesgo de envenenamiento, etc.",
      catVolunteer: "Ayuda de voluntario",
      catVolunteerDesc: "Cuando hace falta transporte, acogida o ayuda en el lugar",
      injuredTitle: "Reporte de gato herido",
      injuredDesc: "Comparte el estado del gato que viste. Avisaremos a los voluntarios enseguida.",
      injuredPlaceholder: "Estado, comportamiento, herida del gato…",
      severityLabel: "Nivel de urgencia",
      severityHigh: "Urgente",
      severityMid: "Media",
      severityLow: "Baja",
      foodTitle: "Necesidad de comida / agua",
      foodDesc: "Crea un reporte para los gatos de esta zona que necesitan comida o agua.",
      needLabel: "Qué hace falta",
      needFood: "Comida",
      needWater: "Agua",
      needBoth: "Ambos",
      foodPlaceholder: "Ej. los gatos callejeros de esta zona necesitan apoyo con comida…",
      dangerTitle: "Zona peligrosa",
      dangerDesc: "¿Hay una zona que pone en riesgo a los gatos? Que lo sepan otros protectores.",
      dangerTypeLabel: "Tipo de peligro",
      multiSelect: "Puedes elegir más de uno",
      dTraffic: "Riesgo de tráfico",
      dConstruction: "Obra / demolición",
      dPoison: "Riesgo de envenenamiento",
      dViolence: "Violencia / maltrato",
      dWell: "Pozo / espacio cerrado",
      dOther: "Otro",
      dangerPlaceholder: "Añade detalles sobre la zona…",
      volunteerTitle: "Ayuda de voluntario",
      volunteerDesc: "¿Este gato necesita un voluntario? Comparte tu petición para que lo sepan los voluntarios cercanos.",
      supportLabel: "Tipo de ayuda",
      sTransport: "Transporte",
      sFoster: "Acogida temporal",
      sOnSite: "Ayuda en el lugar",
      sVet: "Apoyo veterinario",
      sSupplies: "Suministros",
      sOther: "Otro",
      volunteerPlaceholder: "¿Qué tipo de ayuda hace falta?",
      photoLabel: "Añadir fotos",
      optional: "(opcional)",
      photoTake: "Cámara",
      photoPick: "Elegir de la galería",
      photoMax: "Hasta {n} fotos",
      descLabel: "Descripción",
      minChars: "Al menos {n} caracteres",
      continue: "Continuar",
      back: "Atrás",
      locTitle: "Elegir ubicación",
      locDesc: "Marca la ubicación del gato en el mapa o usa tu ubicación actual.",
      locUseCurrent: "Usar mi ubicación",
      locAddress: "Dirección (opcional)",
      locNeighborhood: "Barrio",
      locNeighborhoodHint: "ej. Moda, Kadıköy",
      locUnsupported: "Este navegador no admite ubicación.",
      locDenied: "Permiso de ubicación denegado. Escribe el barrio.",
      locFailed: "No se pudo obtener la ubicación. Toca el mapa o escribe el barrio.",
      okNotified: "voluntarios avisados",
      errSend: "No se pudo enviar. Inténtalo de nuevo.",
      errLogin: "Inicia sesión para enviar. Tu formulario se conservó.",
      errNetwork: "Sin conexión. Revisa la red e inténtalo otra vez.",
      errList: "No se pudo cargar la lista.",
      retry: "Reintentar",
      detayGiris: "Inicia sesión para ver este reporte.",
      detayYok: "Este reporte ya no está disponible.",
      detayKonumsuz: "No se guardó ninguna ubicación en este reporte.",
      detayBildiren: "Reportado por {ad}",
      detaySilmeYok: "Eliminar tu propio reporte todavía no está disponible.",
      locRequired: "La ubicación es obligatoria para que quien ayude encuentre el sitio.",
      reviewTitle: "Revisa tu reporte",
      reviewDesc: "¿Está todo bien? Compruébalo antes de enviar.",
      rType: "Tipo",
      rLocation: "Ubicación",
      rPhotos: "Fotos ({n})",
      submit: "Enviar reporte",
      okTitle: "¡Reporte enviado!",
      okDesc: "¡Gracias! Se ha avisado a los voluntarios más cercanos. Puedes seguirlo.",
      okMap: "Ver en el mapa",
      okList: "Ver emergencias",
      okAgain: "Hacer otro reporte",
      okAvgLabel: "Tiempo medio de respuesta",
      okAvgMinutes: "{n} minutos",
      listTitle: "Emergencias",
      fAll: "Todos",
      fInjured: "Heridos",
      fFood: "Comida / agua",
      fDanger: "Peligro",
      fVolunteer: "Voluntario",
      badgeHigh: "URGENTE",
      badgeMid: "MEDIA",
      badgeLow: "BAJA",
      badgeNew: "NUEVO",
      listEmpty: "No hay emergencias abiertas cerca de ti — buenas noticias.",
      errNoCategory: "Primero elige qué estás reportando.",
      sending: "Enviando…",
      kaydet: "Guardar",
      kaydedildi: "Guardado",
      kaydettiklerim: "Casos guardados",
      kaydetHata: "No se pudo completar.",
      kaydetBos: "Nada guardado aún. Toca el corazón de un caso para seguirlo.",
      kaydetGiris: "Inicia sesión para guardar casos.",
      yakinFiltre: "Mostrando casos a {n} km a la redonda.",
      tumunuGoster: "Mostrar todos",
      yakiniGoster: "Solo cercanos",
      tumVakalar: "Se muestran todos los casos.",
    },
    footer: {
      discover: "Descubrir",
      community: "Comunidad",
      connect: "Conectar",
      brandDescription: "Reunimos a voluntarios, puntos de cuidado y ayuda urgente para que los animales callejeros vivan más seguros, más sanos y más queridos.",
    },
    assistant: {
      name: "Asistente Huella",
      readyStatus: "Listo · primero sin coste",
      napStatus: "Durmiendo hasta mañana 😴",
      placeholder: "Pregúntame sobre animales callejeros…",
      napHint: "Estoy durmiendo: ¡hasta mañana!",
      suggestionsTitle: "Prueba con una de estas:",
      disclaimer: "Consejo de IA · no sustituye a un veterinario",
      networkError: "La red está de mal humor ahora mismo 🐾 inténtalo de nuevo en un momento.",
      guestPrompt: "¡Hola! Me encantaría charlar, pero antes necesito reconocerte. Inicia sesión o únete: es gratis y tarda unos segundos.",
      guestLoginCta: "Inicia sesión para hablar conmigo",
    },
    nav: {
      home: "Inicio",
      database: "Base de datos",
      identify: "Identificar con IA",
      identifyShort: "Scan IA",
      map: "Mapa",
      sightings: "Avisar de una emergencia",
      emergencies: "Avisar de una emergencia",
      volunteers: "Voluntarios",
      feeding: "Puntos de alimentación",
      leaderboard: "Clasificación",
      photoContest: "Concurso de fotos",
      badges: "Insignias",
      safety: "Seguridad",
      observationHouse: "Casa de observación",
      neighborhood: "Mi barrio",
      messages: "Mensajes",
      notifications: "Notificaciones",
      dashboard: "Panel",
      profile: "Perfil",
      login: "Iniciar sesión",
      register: "Únete",
      logout: "Cerrar sesión",
      addCat: "Añadir un animal",
      admin: "Administración",
      adminEmergencies: "Cola de emergencias",
    },
    landing: {
      overline: "Bienestar animal comunitario",
      heroTitle: "Cada animal callejero tiene una historia.",
      heroSubtitle: "Identifícalos, haz seguimiento y ayuda a protegerlos.",
      liveBadge: "Comunidad en directo",
      ctaPrimary: "Registrar un animal",
      ctaSecondary: "Base de datos",
      statCats: "Gatos catalogados",
      statSightings: "Emergencias avisadas",
      statFeeding: "Puntos de alimentación",
      statVaccinated: "Vacunados",
      featuresOverline: "Lo que puedes hacer",
      f1Title: "Identificación por foto con IA",
      f1Desc: "Sube una foto y nuestra IA describe el pelaje, la raza, la edad y las marcas distintivas; después la compara con la base de datos local.",
      f2Title: "Mapa del barrio",
      f2Desc: "Marca animales, emergencias y puntos de alimentación de tu ciudad. Filtra por barrio, color o estado de salud.",
      f3Title: "Salud y vacunas",
      f3Desc: "Lleva el registro de la rabia, las vacunas múltiples, la esterilización y las notas. Da a los voluntarios y veterinarios locales datos reales.",
      f4Title: "Avisos de emergencia",
      f4Desc: "¿Has visto a un cachorro en apuros? Marca la ubicación —o sube una foto con la ubicación— en 30 segundos y avisa a los voluntarios y veterinarios cercanos.",
      finalCta: "Ayuda hoy a los animales de tu barrio.",
      finalCtaSub: "Basta con una foto para empezar.",
    },
    auth: {
      loginTitle: "Bienvenido de nuevo",
      registerTitle: "Crea tu cuenta",
      registerSuccess: "¡Cuenta creada!",
      email: "Correo electrónico",
      password: "Contraseña",
      name: "Nombre completo",
      role: "Tipo de cuenta",
      rolePublic: "Público: consultar y avisar de emergencias",
      roleVolunteer: "Voluntario: gestionar animales y puntos de alimentación",
      roleAdmin: "Administrador: acceso total",
      volunteerType: "Especialidad del voluntario",
      volunteerTypeVet: "Veterinario voluntario: atender animales callejeros heridos",
      volunteerTypeIndividual: "Voluntario particular: alimentar, acoger, rescatar",
      submitLogin: "Iniciar sesión",
      submitRegister: "Crear cuenta",
      switchToRegister: "¿Aún no tienes cuenta? Únete",
      switchToLogin: "¿Ya tienes cuenta? Inicia sesión",
      forgotPassword: "¿Has olvidado la contraseña?",
      forgotLink: "Olvidé mi contraseña",
      backToLogin: "Volver a iniciar sesión",
      forgotTitle: "Restablece tu contraseña",
      forgotIntro: "Escribe tu correo y te enviaremos un enlace para crear una contraseña nueva.",
      forgotSubmit: "Enviar enlace",
      forgotSentBody: "Si encontramos una cuenta con ese correo, el enlace ya va de camino. Revisa tu bandeja de entrada (y el spam).",
      forgotDevTitle: "Modo de desarrollo",
      forgotDevBody: "El correo aún no está configurado; usa el enlace de abajo para continuar:",
      newPassword: "Nueva contraseña",
      confirmPassword: "Confirmar contraseña",
      resetTitle: "Elige una contraseña nueva",
      resetIntro: "Elige algo que puedas recordar: al menos 8 caracteres.",
      resetSubmit: "Actualizar contraseña",
      resetSuccess: "Contraseña actualizada. Ya puedes iniciar sesión.",
      resetFailed: "Algo ha salido mal: pide un enlace nuevo.",
      resetMinLen: "La contraseña debe tener al menos 8 caracteres.",
      resetMismatch: "Las contraseñas no coinciden.",
      resetNoToken: "A este enlace le falta el token. Pide un correo de restablecimiento nuevo.",
      invalid: "Credenciales incorrectas",
      emailTaken: "Ese correo ya está registrado",
    },
    cats: {
      title: "Base de datos de gatos",
      loadMore: "Cargar más",
      subtitle: "Consulta todos los gatos catalogados de nuestra comunidad.",
      search: "Busca por nombre, color, raza…",
      filterNeighborhood: "Barrio",
      filterHealth: "Estado de salud",
      add: "Añadir un gato",
      empty: "Todavía no hay gatos. ¡Sé el primero en añadir uno!",
      details: "Detalles",
      visits: "visitas",
      aiPortraitShortcut: "Crear retrato con IA",
      aiPortraitRegenerate: "Generar otro retrato con IA",
      aiPortraitShortcutHint: "Genera un retrato de estudio a partir de las fotos reales de este gato",
      aiBadgeHasTooltip: "Retrato con IA listo",
      aiBadgeMissingTooltip: "Todavía sin retrato con IA: ábrelo y genera uno",
      health: {
        healthy: "Sano",
        minor_issues: "Problemas leves",
        needs_attention: "Necesita atención",
        critical: "Crítico",
        injured: "Herido",
        sick: "Enfermo",
        recovering: "En recuperación",
        unknown: "Desconocido",
      },
    },
    addCat: {
      title: "Añadir un gato",
      subtitle: "Sube una foto: nuestra IA rellenará los datos por ti.",
      uploadCta: "Subir foto",
      analyzing: "Analizando la foto con IA…",
      analyzeAgain: "Analizar de nuevo",
      name: "Nombre",
      color: "Color",
      pattern: "Patrón",
      breed: "Raza",
      age: "Edad estimada",
      size: "Tamaño",
      features: "Marcas distintivas",
      neighborhood: "Barrio / Distrito",
      lat: "Latitud",
      lng: "Longitud",
      health: "Estado de salud",
      spayed: "Esterilizado",
      notes: "Notas",
      save: "Guardar gato",
      success: "¡Gato guardado!",
      error: "No se ha podido guardar el gato",
      aiError: "El análisis de la IA ha fallado",
      tooLarge: "La foto es demasiado grande (máx. 6 MB). Elige un recorte más pequeño.",
      invalidFields: "Faltan campos obligatorios o no son válidos (ubicación / nombre).",
      serverDown: "No se ha podido crear el perfil: revisa la ubicación y los campos obligatorios.",
    },
    identify: {
      registerCat: "Registrar este gato",
      registerDog: "Registrar este perro",
      noMatch: "No se ha encontrado ninguna coincidencia",
      noMatchCat: "Puede que este gato ya esté registrado y no lo hayamos encontrado.",
      noMatchDog: "Puede que este perro ya esté registrado y no lo hayamos encontrado.",
      reallyNewCat: "Es realmente un gato nuevo",
      reallyNewDog: "Es realmente un perro nuevo",
      title: "Identificar con IA",
      subtitle: "Sube la foto de un animal callejero. Nuestra IA lo describirá y buscará posibles coincidencias en la base de datos.",
      upload: "Sube una foto",
      analyze: "Analizar y comparar",
      analyzing: "La IA está mirando la foto…",
      neighborhood: "Filtrar por barrio (opcional)",
      analysis: "Análisis de la IA",
      matches: "Posibles coincidencias",
      noMatches: "No se ha encontrado ningún animal parecido. ¿Quieres registrarlo como nuevo?",
      addAsNew: "Guardar como nuevo",
      score: "Parecido",
      topMatch: "Mejor coincidencia",
      otherMatches: "Otras coincidencias posibles",
      matchFound: "Coincidencia encontrada",
      health: {
        title: "Valoración de salud",
        statusLabel: "Estado de salud",
        urgencyLabel: "Prioridad",
        issuesLabel: "Signos visibles",
        adviceLabel: "Qué hacer ahora",
        bodyLabel: "Condición corporal",
        statuses: {
          healthy: "Sano",
          minor_issues: "Problemas leves",
          needs_attention: "Necesita atención",
          critical: "Crítico",
          unknown: "No se ha podido valorar",
        },
        urgencies: {
          URGENT: "Urgente: al veterinario ya",
          MEDIUM: "Media: al veterinario en 1-3 días",
          STABLE: "Estable",
          UNKNOWN: "Poco claro",
        },
        body: {
          emaciated: "Muy delgado",
          thin: "Delgado",
          ideal: "Ideal",
          overweight: "Con sobrepeso",
          unknown: "Desconocida",
        },
        disclaimer: "Solo una valoración de IA: no sustituye a un veterinario.",
      },
    },
    liveIdentify: {
      overline: "IA en directo",
      title: "Apunta la cámara a un gato",
      subtitle: "Enfoca la cara de un gato callejero: nuestra IA lo reconocerá desde la cámara en directo y lo comparará con la base de datos.",
      tryLive: "Probar con la cámara",
      startCamera: "Encender la cámara",
      stopCamera: "Apagar la cámara",
      scanNow: "Escanear ahora",
      pauseAuto: "Pausar el escaneo automático",
      resumeAuto: "Reanudar el escaneo automático",
      autoOn: "Escaneando automáticamente",
      autoOff: "Modo manual",
      cameraOff: "La cámara está apagada: toca Encender para empezar.",
      aimAtCat: "Enfoca la cara de un gato y la IA empezará a analizar…",
      startToBegin: "Toca Encender la cámara para empezar.",
      freeHint: "Plan gratuito: toca Escanear una vez por intento (consume 1 de tus 10 llamadas de IA al mes). Hazte Premium para el escaneo automático continuo.",
      upsell: "Escaneo automático con Premium",
    },
    scanCat: {
      overline: "Asistente de perfil con IA",
      title: "Foto de carné del gato",
      subtitle: "Apunta la cámara al gato. La IA consulta primero la base de datos y después te guía por tres ángulos —frente, perfil y desde arriba— para crear un perfil perfecto.",
      navLabel: "Escanear un gato",
      startCamera: "Encender la cámara",
      startCameraNative: "Hacer fotos (3)",
      pickFromGallery: "Elegir 3 fotos de la galería",
      galleryFallbackHint: "Si la cámara no se abre, aún puedes crear un perfil eligiendo 3 fotos de la galería.",
      galleryPrimaryHint: "Elige 3 fotos de tu galería para crear un perfil de gato. La búsqueda con IA, el recorte automático y los retratos Premium funcionan igual.",
      galleryNeed3: "Elige exactamente 3 fotos.",
      tryCameraBeta: "Probar la cámara",
      cameraUnavailableFallback: "La cámara no se ha abierto; puedes elegir 3 fotos de la galería.",
      startUnavailableFallback: "La cámara no se ha iniciado; puedes elegir fotos de la galería.",
      partialFallback: "Solo {n}/3 fotos capturadas. Seguimos con la galería para el resto.",
      cameraCannotOpen: "No se ha podido abrir la cámara. Revisa la app de cámara o elige fotos de la galería.",
      nativeCaptureStep: "Foto capturada",
      nativeAngleStart: "Capturando el ángulo",
      partialCaptures: "{n}/3 fotos capturadas. Toca para repetir los ángulos que faltan.",
      buttonTapped: "Botón de cámara pulsado",
      stopCamera: "Apagar la cámara",
      cameraDenied: "Permiso de cámara denegado. Concede el acceso a la cámara para este sitio en los ajustes del navegador.",
      cameraDeniedNative: "Permiso de cámara denegado. Activa el permiso de cámara en los ajustes del teléfono.",
      cameraDeniedPWA: "No se ha podido obtener el permiso de cámara en la app instalada. Puedes abrirla en Chrome para escanear con la cámara.",
      cameraUnsupported: "Este dispositivo no admite la API de cámara.",
      cameraGenericError: "No se ha podido acceder a la cámara.",
      openInChrome: "Abrir en Chrome",
      openAppSettings: "Abrir ajustes",
      cameraRetry: "Intentar de nuevo",
      cameraWaiting: "Esperando el permiso de cámara…",
      matching: "Buscando en la base de datos…",
      matchFound: "¡Hemos encontrado una coincidencia!",
      matchPrompt: "¿Es el mismo gato?",
      matchYes: "Sí, abrir el perfil",
      matchNo: "No, crear un perfil nuevo",
      notFound: "Perfil no encontrado",
      notFoundSub: "Vamos a crear uno nuevo: tres ángulos, guiados por IA.",
      startWizard: "Empezar a escanear",
      stages: {
        front: "Vista frontal",
        side: "Vista de perfil",
        top: "Vista desde arriba",
      },
      stageHint: {
        front: "Apunta la cámara de frente a la cara del gato. Los dos ojos visibles.",
        side: "Ponte de lado: perfil completo, solo un ojo y una oreja visibles.",
        top: "Sujeta la cámara por encima de la cabeza, mirando hacia el cráneo.",
      },
      captured: "¡Capturada!",
      retake: "Repetir",
      tooFast: "Mantén el pulso…",
      holdCatOnScreen: "Mantén al gato en el encuadre",
      timeoutCloser: "Acércate un poco más",
      timeoutSteady: "Sujeta la cámara sin moverla",
      timeoutLight: "No hay luz suficiente: ve a un sitio más iluminado",
      timeoutShade: "Demasiado reflejo: ponte a la sombra",
      collectingFrames: "Eligiendo el mejor fotograma…",
      locationCaptured: "Ubicación capturada",
      locationNeighborhoodOnly: "Seguimos solo con el barrio",
      locationMissing: "Sin ubicación: activa el GPS o escribe un barrio",
      locationRequired: "Captura el GPS o escribe un barrio.",
      locating: "Obteniendo la ubicación…",
      gpsRefresh: "Actualizar GPS",
      gpsLocked: "Ubicación capturada",
      gpsDenied: "Permiso de ubicación denegado. Concédelo en los ajustes del teléfono o escribe un barrio.",
      gpsUnavailable: "No se ha podido obtener la ubicación (sin señal). Puedes continuar con un barrio.",
      gpsTimeout: "La búsqueda de ubicación ha caducado. Puedes continuar con un barrio.",
      gpsError: "No se ha podido obtener la ubicación. Puedes continuar con un barrio.",
      gpsUnsupported: "Este dispositivo no admite los servicios de ubicación.",
      autoStarting: "Gato detectado, empezando el escaneo…",
      autoDetectActive: "Detectando al gato… Mantén su cara en el encuadre. El escaneo empieza solo.",
      autoProfileTitle: "Foto de perfil automática",
      autoFree: "Gratis",
      autoProcessing: "Procesando…",
      premiumOnly: "Premium",
      aiPortraitSectionTitle: "Retrato de perfil con IA",
      aiPortraitOpenBtn: "Generar retrato con IA",
      aiPortraitFreeHint: "Los miembros Premium pueden generar un retrato frontal con IA a partir de tus fotos.",
      aiPortraitTitle: "Crear retrato con IA",
      aiPortraitOverline: "Función Premium",
      aiPortraitBody: "La IA genera UN retrato frontal usando tus 3 fotos reales como referencia. La identidad (color, patrón, ojos) se mantiene. Esto consume 1 crédito de retrato.",
      aiPortraitLoading: "Cargando los créditos…",
      aiPortraitQuota: "Créditos restantes",
      aiPortraitTierLabel: "Plan",
      aiPortraitFreeNotice: "Los retratos con IA son para miembros Premium / Súper Premium. La foto de perfil automática gratuita ya se ha generado.",
      aiPortraitConfirm: "Generar",
      aiPortraitGenerating: "Generando…",
      aiPortraitReady: "¡Retrato con IA listo!",
      aiPortraitCached: "De la caché: no se ha usado ningún crédito.",
      aiPortraitFailed: "No se ha podido generar el retrato con IA.",
      aiPortraitNotEnoughPhotos: "No hay fotos de calidad suficiente para el retrato con IA. Acércate más y busca más nitidez.",
      aiBadge: "IA",
      originalPhotosTitle: "Fotos reales",
      blockedAi: "No se aceptan imágenes generadas por IA.",
      blockedInappropriate: "Se ha detectado una imagen inadecuada.",
      finalize: "Finalizar el perfil",
      finalizeTitle: "Una última cosa",
      finalizeSub: "Ponle nombre al gato (y el barrio, si lo sabes).",
      submit: "Crear perfil",
      submitting: "Guardando…",
      done: "¡Perfil creado!",
      doneSub: "Los tres ángulos se han añadido a la galería.",
      goProfile: "Ir al perfil",
      manualCapture: "Capturar ahora",
      angleOf: "Ángulo {n} de 3",
      voiceOn: "Voz activada",
      voiceOff: "Voz desactivada",
      directions: {
        left: "Muévete a la izquierda",
        right: "Muévete a la derecha",
        up: "Inclina hacia arriba",
        down: "Inclina hacia abajo",
        closer: "Acércate más",
        farther: "Aléjate un poco",
        center: "Perfecto, no te muevas",
        unclear: "Apunta a la cara del gato",
      },
      overallProgress: "Progreso del perfil",
      bestPhotoChosen: "La IA ha elegido la mejor foto frontal para el perfil",
      detectorLoading: "Cargando la IA del dispositivo… (descarga única)",
      detectorReady: "IA del dispositivo lista · escaneo sin coste",
    },
    thinking: {
      title: "La IA está pensando",
      hint: "Esto suele tardar entre 5 y 15 segundos. Puedes seguir usando la app.",
      stages: {
        compress: "Preparando tu foto…",
        vision: "Mirando al gato con GPT-Vision…",
        matching: "Buscando gatos parecidos en la base de datos…",
        polishing: "Puliendo los resultados…",
      },
    },
    incident: {
      title: "Registrar un incidente",
      subtitle: "Empieza el periodo de observación de 10 días. Podrás cambiar el estado más tarde (gato sano, aparecieron síntomas, tratamiento terminado).",
      types: {
        scratch: "Arañazo",
        bite: "Mordedura",
      },
      severity: "Gravedad",
      sev: {
        mild: "Leve: superficial",
        moderate: "Moderada: con sangrado",
        severe: "Grave: profunda / múltiple",
      },
      cat: "Qué gato (opcional)",
      catPlaceholder: "Elige un gato o déjalo como desconocido",
      catHint: "Vincular el gato ayuda a la comunidad a seguir públicamente los 10 días de observación.",
      unknownCat: "Desconocido / un gato callejero que no conozco",
      date: "Fecha del incidente",
      location: "Dónde (opcional)",
      locationPlaceholder: "p. ej. Malasaña, calle del Pez",
      notes: "Notas",
      notesPlaceholder: "¿Qué pasó? ¿Lavaste la herida? ¿Algún síntoma?",
      photo: "Foto de la herida o del gato (opcional)",
      privacyNote: "Tus notas y tu foto son privadas. Solo el gato vinculado (si lo hay) recibe una etiqueta pública de 'en observación' y un contador; no se expone ningún dato personal.",
      useGps: "Usar mi ubicación (avisa a la comunidad cercana)",
      gpsCaptured: "Ubicación capturada. Avisaremos a quienes estén cerca.",
      gpsHint: "Los voluntarios de esta zona lo verán en sus notificaciones.",
      submit: "Iniciar la observación de 10 días",
      submitting: "Guardando…",
      created: "Incidente registrado. Observación de 10 días iniciada.",
      error: "No se ha podido guardar el incidente.",
      loginRequired: "Inicia sesión primero.",
      observation: {
        title: "Observaciones activas",
        subtitle: "Vigila cualquier comportamiento raro en ti O en el gato durante los 10 días siguientes a una mordedura o arañazo.",
        empty: "No hay observaciones activas.",
        daysLeft: "quedan {n} días",
        complete: "Observación terminada",
        scratchOn: "Arañazo",
        biteOn: "Mordedura",
        markHealthy: "El gato siguió sano",
        markSymptoms: "Aparecieron síntomas",
        markResolved: "Tratamiento terminado",
        statusUpdated: "Estado actualizado.",
        statuses: {
          observing: "En observación",
          cat_healthy: "El gato siguió sano ✓",
          symptoms_appeared: "Aparecieron síntomas",
          incident_developed: "Surgió una complicación",
          incident_resolved: "Tratamiento terminado ✓",
        },
        bannerObserving: "{n} observación activa",
        bannerObservingPlural: "{n} observaciones activas",
        notified: "vecinos avisados",
        feedbackList: "Aportaciones de la comunidad",
      },
      catBadge: {
        observing: "En observación",
        sub: "{n} persona{plural} pendiente — quedan {days}d",
      },
      feedbackPage: {
        eyebrow: "Aportaciones de la comunidad",
        notFound: "Incidente no encontrado.",
        intro: "Alguien de la comunidad cercana ha avisado de este arañazo o mordedura. Si has visto al gato o sabes algo útil, deja una observación rápida: ayuda a quien lo avisó a completar los 10 días con seguridad.",
        communityReplies: "respuestas de la comunidad",
        observationLabel: "¿Qué has observado?",
        options: {
          cat_seen_healthy: "He visto al gato: parece sano",
          cat_not_seen: "No he visto al gato",
          cat_seen_concerning: "Lo he visto: algo no iba bien",
          other: "Otra observación",
        },
        notePlaceholder: "¿Algo más? Hora, lugar, qué estaba haciendo el gato…",
        photo: "Foto del gato (opcional)",
        submit: "Enviar",
        thanks: "¡Gracias por ayudar a la comunidad!",
        thanksSub: "Ya se ha avisado de tu observación a quien lo comunicó.",
        othersTitle: "Otras observaciones",
      },
    },
    observationHouse: {
      overline: "Seguridad de la comunidad",
      title: "Casa de observación",
      subtitle: "Todos los gatos que están en observación de 10 días tras un arañazo o mordedura comunicado. Ayuda a tus vecinos dejando un avistamiento rápido.",
      allTypes: "Todos",
      neighborhoodPlaceholder: "Filtrar por barrio…",
      urgentOnly: "Solo urgentes (≤3 días)",
      nearbyOn: "Cerca de mí",
      nearbyOff: "Filtro de cercanía activado",
      results: "resultados",
      empty: "Ahora mismo no hay observaciones activas. 🐾",
      unknownCat: "Gato desconocido",
      locationUnknown: "Ubicación no compartida",
      loadFailed: "No se han podido cargar las observaciones.",
      gpsUnavailable: "La geolocalización no es compatible.",
      gpsActive: "Filtrando en 5 km a tu alrededor.",
      gpsFailed: "No se ha podido obtener la ubicación.",
    },
    weekly: {
      eyebrow: "Resumen por correo",
      title: "Resumen semanal de observaciones",
      subtitle: "Cada lunes a las 09:00: un resumen personal de los incidentes y observaciones de tu zona esta semana.",
      on: "Activado",
      off: "Desactivado",
      optedIn: "Resumen semanal activado.",
      optedOut: "Resumen semanal desactivado.",
      noScope: "Define primero una zona de actuación en tu perfil para que podamos adaptar el resumen a tu barrio.",
      statNew: "Incidentes nuevos",
      statActive: "Activos",
      statUrgent: "Urgentes",
      statFeedbacks: "Aportaciones",
      highlightsTitle: "Casos más activos de la semana",
      sendTest: "Enviar una prueba ahora",
      sending: "Enviando…",
      scheduleHint: "Se envía automáticamente cada lunes a las 09:00 (cuando está activado).",
      testSentEmail: "¡Prueba enviada! Revisa tu bandeja de entrada.",
      testSentInAppOnly: "Notificación enviada en la app. El correo está desactivado en este servidor.",
      error: "No se ha podido enviar el resumen.",
    },
    map: {
      title: "Mapa de animales",
      subtitle: "Cada marcador es un animal conocido, una emergencia o un punto de alimentación de tu ciudad.",
      layerCats: "Gatos",
      layerSightings: "Emergencias",
      layerFeeding: "Puntos de alimentación",
      layerObservations: "Casa de observación",
      layerVaccinated: "Vacunados",
      layerCommunity: "Comunidad",
      tabMap: "Mapa",
      tabNeighborhood: "Mi barrio",
      locate: "Localizar",
    },
    sightings: {
      title: "Casos de emergencia",
      subtitle: "Casos urgentes de animales que necesitan ayuda, avisados por la comunidad de tu ciudad.",
      report: "Avisar de una emergencia",
      notes: "¿Qué está pasando?",
      reporter: "Quién ha avisado",
      when: "Cuándo",
      submit: "Enviar el aviso de emergencia",
      success: "Gracias por avisar: se ha notificado a los voluntarios que están cerca de ti.",
      pendingReview: "Gracias: tu emergencia está en revisión y se hará pública tras la aprobación.",
      empty: "Todavía no se ha avisado de ningún caso de emergencia.",
      locationOnly: "O simplemente marca la ubicación",
      photoAndLocation: "Añade una foto para que quien acuda pueda identificar al animal",
    },
    feeding: {
      title: "Puntos de alimentación",
      subtitle: "Dónde alimentan los voluntarios a los animales de la ciudad.",
      add: "Añadir punto de alimentación",
      name: "Nombre del punto",
      schedule: "Horario (p. ej. lun/mié/vie 19:00)",
      notes: "Notas",
      empty: "Todavía no hay puntos de alimentación.",
      success: "Punto de alimentación añadido",
      active: "Activo",
      inactive: "Inactivo",
      statusLabel: "Estado",
      photo: "Foto del punto",
      findNearest: "Buscar el más cercano",
      nearestFound: "El punto más cercano es",
      nearestBadge: "El más cercano a ti",
      noNearest: "No se ha encontrado ningún punto de alimentación.",
      geoUnsupported: "Tu navegador no admite la geolocalización.",
      geoDenied: "Permite la ubicación para encontrar el punto más cercano.",
      openDirections: "Abrir en el mapa",
      maintainedBy: "Mantenido por",
      requireNameLoc: "El nombre y las coordenadas GPS son obligatorios",
    },
    dashboard: {
      title: "Panel",
      ilkKediBaslik: "Añade tu primer gato",
      ilkKediAlt: "Los gatos callejeros que fotografías ayudan a otras personas a encontrarlos, alimentarlos y cuidarlos.",
      recentCats: "Añadidos recientemente",
      welcome: "Bienvenido de nuevo",
      myRole: "Tu papel",
      quickAdd: "Añadir rápido",
      addCat: "Añadir un gato",
      identify: "Identificar con IA",
      reportSighting: "Avisar de una emergencia",
      addFeeding: "Añadir punto de alimentación",
    },
    profile: {
      title: "Mi perfil",
      settingsTitle: "Ajustes y ayuda",
      settingsSubtitle: "Gestiona tu cuenta, las notificaciones, el idioma y la privacidad. Todo en un mismo sitio.",
      aiPortraitBannerTitle: "Retratos de perfil con IA",
      aiPortraitBannerRemaining: "Créditos que te quedan este mes",
      aiPortraitBannerUnlimited: "Ilimitado",
      aiPortraitBannerCta: "Generar un retrato",
      aiPortraitBannerUsed: "{used} de {limit} usados",
      aiPortraitBannerExhausted: "Has usado todos los créditos: se renuevan el mes que viene.",
      aiCoverageTitle: "Cobertura de retratos con IA en la comunidad",
      aiCoverageSubtitle: "{with} de {total} gatos tienen retrato con IA",
      aiCoverageEmpty: "¡Sé el primero en añadir un retrato con IA!",
      myAccount: "Mi cuenta",
      myAccountHint: "Panel, mis animales, contribuciones",
      notificationSettings: "Ajustes de notificaciones",
      notificationSettingsHint: "Qué recibir, cómo y cuándo",
      countryTitle: "País",
      countryHint: "A qué comunidad perteneces",
      languageTitle: "Idioma",
      languageHint: "Elige el idioma de la interfaz",
      privacyKvkk: "Privacidad y protección de datos",
      privacyKvkkHint: "Tus derechos sobre los datos y las cookies",
      terms: "Términos de uso",
      termsHint: "Reglas de uso de la aplicación",
      contact: "Contáctanos",
      contactHint: "destek@sokakkedileri.tr",
      helpFaq: "Ayuda y preguntas frecuentes",
      helpFaqHint: "Primeros pasos, dudas habituales, contacto",
      safetyHint: "Guías de seguridad con animales callejeros",
      points: "Puntos Huella",
      badgesEarned: "insignias conseguidas",
      seeAllBadges: "Ver todas las insignias",
      nextBadge: "Próxima insignia",
      nextLevel: "Siguiente nivel",
      nextLevelIn: "Para el siguiente nivel",
      contributions: "contribuciones",
      notificationsTitle: "Notificaciones",
      notificationsHint: "Novedades y respuestas recientes",
      notificationsEmpty: "Todavía no hay notificaciones",
      seeAllNotifications: "Ver todas las notificaciones",
      settingsGroup: "Ajustes",
      settingsGroupHint: "Idioma, tema, permisos",
      helpInfoGroup: "Ayuda e información",
      helpInfoHint: "Cómo funciona, preguntas frecuentes, quiénes somos, privacidad",
      howItWorks: "¿Cómo funciona?",
      howItWorksHint: "Descubre las funciones en 3 pasos",
      aboutUs: "Quiénes somos",
      aboutUsHint: "Nuestra misión, el equipo y el contacto",
      themeTitle: "Tema",
      themeHint: "Interfaz clara u oscura",
      themeLight: "Claro",
      themeDark: "Oscuro",
      locationPermTitle: "Permiso de ubicación",
      locationPermHint: "Se usa para sugerirte animales cercanos",
      notifPermTitle: "Permiso de notificaciones",
      notifPermHint: "Recibir avisos urgentes de emergencia",
      permRequest: "Permitir",
      permGranted: "Permitido",
      permDenied: "Bloqueado",
      pointsShort: "🐾",
      catsDescribed: "Gatos descritos",
      sightingsReported: "Emergencias avisadas",
      feedingSpots: "Puntos de alimentación",
      vaccinations: "Vacunas",
      pointsHowItWorks: "Cómo funciona la puntuación",
      ruleCat: "Describir un animal nuevo: +5 Puntos Huella",
      ruleFeeding: "Añadir un punto de alimentación: +3 Puntos Huella",
      ruleVaccination: "Registrar una vacuna: +10 Puntos Huella",
      ruleSighting: "Avisar de una emergencia: +2 Puntos Huella",
      namingNote: "Quien describe un animal primero se gana el derecho a ponerle nombre.",
      myCats: "Animales que he descrito",
      noCatsYet: "Todavía no has descrito ningún animal.",
      rank: "Puesto",
      privilegesTitle: "Ventajas de tu nivel",
      nextTier: "para llegar a",
      keepGoing: "Sé el primero en reconocer un animal: consigue 5 Puntos Huella y desbloquea tu distintivo de descriptor.",
      photoUpdated: "Foto actualizada",
      photoError: "No se ha podido actualizar la foto",
    },
    common: {
      showPassword: "Mostrar contraseña",
      hidePassword: "Ocultar contraseña",
      deletePhoto: "Eliminar foto",
      cancel: "Cancelar",
      save: "Guardar",
      delete: "Eliminar",
      edit: "Editar",
      back: "Volver",
      required: "Obligatorio",
      loading: "Cargando…",
      locateMe: "Usar mi ubicación",
      tryAi: "Probar la IA",
      describedBy: "Descrito por",
      optional: "opcional",
    },
    leaderboard: {
      title: "Clasificación de la comunidad",
      subtitle: "Sé el primero en reconocer un animal. Sube puestos. Desbloquea ventajas.",
      contributor: "Colaborador",
      badge: "Nivel",
      contributions: "Contribuciones",
      points: "Puntos Huella",
      empty: "Todavía no hay colaboradores. ¡Sé el primero!",
      you: "Tú",
      tabVolunteers: "Voluntarios",
      tabNeighborhoods: "Retratos con IA por barrio",
      hoodEmpty: "Todavía no hay suficientes retratos con IA. ¡Sé el primer barrio en encabezar la lista!",
      hoodWithAi: "Retratos con IA",
      hoodCoverage: "Cobertura",
      hoodTopContributors: "Quienes más han contribuido esta semana",
    },
    messenger: {
      title: "Mensajes",
      empty: "Todavía no hay conversaciones. Escribe a un voluntario desde el directorio.",
      selectThread: "Elige una conversación para empezar.",
      placeholder: "Escribe un mensaje…",
      send: "Enviar",
    },
    notifications: {
      title: "Notificaciones",
      subtitle: "Emergencias cerca de ti, quién ha asumido tus avisos y mensajes nuevos.",
      empty: "Estás al día.",
      markAllRead: "Marcar todas como leídas",
      markRead: "Marcar como leída",
      open: "Abrir",
    },
    severity: {
      minor: "Leve",
      urgent: "Urgente",
      critical: "Crítica",
      label: "Gravedad",
    },
    emergencyActions: {
      claim: "Voy de camino",
      claimed: "Asumido por",
      resolve: "Marcar como resuelto",
      resolved: "Resuelto",
      status: "Estado",
      open: "Abierto",
      message: "Mensaje",
    },
    emergencyCategories: {
      injured: "Animal herido",
      injuredDesc: "Herida visible, cojera o dolor",
      food: "Necesita comida / agua",
      foodDesc: "Animales en la zona sin comida ni agua cerca",
      danger: "Zona peligrosa",
      dangerDesc: "Obras, tráfico, riesgo meteorológico",
      volunteer: "Hace falta un voluntario",
      volunteerDesc: "Se necesita ayuda sobre el terreno (transporte, acogida)",
      lost: "Animal perdido",
      lostDesc: "Búsqueda por foto: la IA revisa todos los animales registrados",
    },
    premium: {
      active: "Activa",
      navLink: "Premium",
      title: "Suscripción Premium",
      subtitle: "IA ilimitada, temas exclusivos, distintivo de verificación y puntos de alimentación guardados: por los animales.",
      priceLine: "Suscripción mensual",
      cta: "Hazte Premium",
      comingSoonPlay: "Muy pronto en Google Play",
      comingSoonHint: "La suscripción Premium estará disponible en cuanto nuestra app de Android esté publicada en Google Play. Te avisaremos aquí el día del lanzamiento.",
      ctaActive: "Ya eres miembro Premium",
      perksHeading: "Lo que desbloqueas",
      perk1: "Identificaciones ilimitadas (plan gratuito: {limit}/mes)",
      perk2: "Huellas ilimitadas en las fotos",
      perk3: "Mensajes directos entre miembros Premium",
      perk4: "Prioridad en los avisos de emergencia",
      perk5: "Distintivo Premium: tu voz cuenta más en la comunidad",
      perk6: "Solicitar plaza en el programa oficial de voluntariado",
      perk7: "Guardar tus puntos de alimentación favoritos",
      activeUntil: "Activa hasta",
      usedThisMonth: "IA usada este mes",
      remaining: "restantes",
      unlimited: "Ilimitado",
      themeTitle: "Tema",
      themeWarm: "Piedra Cálida",
      themeMidnight: "Medianoche",
      themeSage: "Salvia",
      themeBosphorus: "Azul Profundo",
      verificationTitle: "Distintivo de verificación",
      verificationDesc: "Los miembros Premium pueden pedir que los administradores verifiquen su organización.",
      verificationOrgLabel: "Nombre de la organización (ayuntamiento, asociación, clínica veterinaria…)",
      verificationRequest: "Pedir la verificación",
      verificationPending: "Pendiente de revisión",
      verificationVerified: "Verificada ✓",
      verificationOrgIs: "Verificada como",
      adminApprovals: "Solicitudes de verificación pendientes",
      adminApprove: "Aprobar",
      adminEmpty: "No hay solicitudes de verificación pendientes.",
      paymentSuccess: "¡Bienvenido a Premium!",
      paymentSuccessSub: "Gracias por apoyar a los animales callejeros de tu ciudad. Ya tienes tus ventajas.",
      paymentChecking: "Confirmando tu pago…",
      paymentExpired: "Tu sesión de pago ha caducado. Inténtalo de nuevo.",
      paymentCancelled: "Pago cancelado.",
      paymentCancelSub: "No se ha cobrado nada. Puedes volver a suscribirte cuando quieras.",
      backHome: "Volver al panel",
      tryAgain: "Intentar de nuevo",
      checkoutError: "No se ha podido iniciar el pago. Inténtalo de nuevo.",
      quotaReachedTitle: "Has alcanzado el límite mensual gratuito de IA",
      quotaReachedSub: "Hazte Premium para tener identificaciones ilimitadas, o espera al mes que viene.",
      seePricing: "Ver Premium",
      favoriteAdd: "Guardar punto",
      favoriteRemove: "Guardado",
      favoritePremiumOnly: "Guardar puntos de alimentación es una ventaja Premium.",
    },
    lounge: {
      overline: "Sala Premium",
      title: "Sala Premium",
      subtitle: "Habla con el resto de miembros Premium que ayudan a mantener a salvo a los animales de la ciudad. Escribe un mensaje directo a cualquiera de aquí.",
      memberCount: "miembros Premium",
      empty: "¡Sé el primer miembro de la Sala Premium!",
      message: "Mensaje",
      youAreHere: "Este eres tú",
      vet: "Veterinario",
      gatedTitle: "La Sala Premium es para miembros Premium",
      gatedSub: "Hazte Premium para entrar en la sala y hablar directamente con voluntarios y veterinarios verificados.",
    },
    city: {
      generating: "Pintando tu ciudad…",
      aiGenerated: "Escena generada con IA",
    },
    coverage: {
      title: "Zonas de actuación",
      heading: "Dónde te ocupas",
      subtitle: "Elige los distritos donde vives o de los que te sientes responsable. Daremos prioridad a las emergencias y a los puntos de alimentación de esas zonas.",
      empty: "Todavía no has elegido ningún distrito. Añade una ciudad abajo para empezar.",
      addCity: "Añadir una ciudad",
      removeCity: "Quitar ciudad",
      districtsCount: "distritos seleccionados",
      save: "Guardar zona de actuación",
      saved: "Zona de actuación guardada",
    },
    trending: {
      overline: "Galería de huellas",
      title: "Animales de la semana",
      subtitle: "Los animales callejeros más visitados ahora mismo. Cada 25 visitas dan +5 Puntos Huella a quien describió al animal primero.",
      week: "Semana",
      month: "Mes",
      all: "Siempre",
      seeAll: "Ver todos los animales",
    },
    badges: {
      overline: "Colección",
      title: "Insignias Huella",
      subtitle: "Insignias coleccionables con tema de huellas. Consíguelas ayudando a los animales. Toca cualquiera para ver cómo desbloquearla.",
      collected: "Conseguidas",
      earnedOn: "Conseguida el",
      filterAll: "Todas",
      filterEarned: "Conseguidas",
      filterLocked: "Bloqueadas",
      emptyFilter: "Todavía no hay insignias en este filtro.",
      next: "Siguiente",
      allEarned: "¡Has conseguido todas las insignias!",
      celebrationTitle: "Nueva insignia",
      howToTitle: "Cómo conseguirla",
      howToFallback: "Sigue participando en la comunidad para desbloquear esta insignia.",
      pageOverline: "Colección",
      pageTitle: "Insignias Huella",
      pageSubtitle: "Toca cualquier insignia para ver cómo desbloquearla. Consigue insignias ayudando a los animales, fotografiándolos y cuidando de la comunidad.",
      shareBtn: "Compartir",
      shareText: "¡Acabo de conseguir la insignia {badge} en StrayPaw! 🐾",
      remainingMetric: {
        cats: {
          one: "animal más",
          many: "animales más",
        },
        feeding: {
          one: "punto más",
          many: "puntos más",
        },
        vax: {
          one: "vacuna más",
          many: "vacunas más",
        },
        sightings: {
          one: "emergencia más",
          many: "emergencias más",
        },
        claimed: {
          one: "atención más",
          many: "atenciones más",
        },
        messages: {
          one: "mensaje más",
          many: "mensajes más",
        },
        districts: {
          one: "distrito más",
          many: "distritos más",
        },
        cities: {
          one: "ciudad más",
          many: "ciudades más",
        },
      },
      category: {
        describer: "Descriptor",
        feeding: "Alimentación",
        health: "Salud",
        emergency: "Emergencia",
        fame: "Fama",
        coverage: "Cobertura",
        social: "Social",
        engagement: "Participación",
        special: "Especial",
        paw: "Huella",
      },
    },
    donate: {
      navLink: "Donar",
      overline: "Transparencia en directo",
      title: "Ayúdanos a curarlos",
      subtitle: "Cada céntimo se puede rastrear. Mira exactamente cómo las donaciones se convierten en consultas, cirugías y segundas oportunidades para los animales callejeros.",
      transparencyTag: "Contabilidad 100% abierta: cada tratamiento lo registra un veterinario verificado",
      totalRaised: "Total recaudado",
      totalSpent: "Gastado en cuidados",
      balance: "Disponible ahora",
      catsHelped: "Animales ayudados",
      donors: "donantes",
      treatments: "tratamientos",
      availableForCats: "Listo para el próximo animal que lo necesite",
      uniqueCats: "Animales distintos atendidos",
      makeDonation: "Hacer una donación",
      everyKurusCounts: "Cada céntimo llega a un animal.",
      amount: "Importe",
      yourName: "Tu nombre (se muestra públicamente)",
      email: "Correo electrónico",
      note: "Mensaje",
      donateNow: "Donar",
      securedBy: "Pago seguro · Nunca guardamos los datos de tu tarjeta.",
      iyzicoPending: "Las donaciones se están registrando, pero los pagos con tarjeta están pausados mientras se verifica nuestra cuenta. Tus datos se han guardado y te escribiremos en breve.",
      pendingMerchant: "Donación registrada: te lo confirmamos en breve.",
      thankYou: "Gracias 🐾",
      error: "No se ha podido registrar la donación. Inténtalo de nuevo.",
      invalidAmount: "Introduce un importe válido",
      treatmentLedger: "Registro de tratamientos en directo",
      treatmentLedgerDesc: "Solo los veterinarios verificados por los administradores pueden añadir registros. Cada gasto aparece detallado junto al animal al que ayudó.",
      noTreatmentsYet: "Todavía no hay tratamientos registrados: sé el primero en ayudar.",
      spent: "Gastado",
      more: "registros más",
      recentDonors: "Donaciones recientes",
      noDonorsYet: "Tu donación puede ser la primera.",
      severity: {
        mild: "Leve",
        moderate: "Moderada",
        severe: "Grave",
        critical: "Crítica",
      },
      tStatus: {
        planned: "Planificado",
        in_progress: "En curso",
        completed: "Terminado",
      },
    },
    treatments: {
      title: "Registro de tratamientos",
      subtitle: "Cada consulta, cada céntimo: los añaden solo veterinarios verificados por los administradores.",
      totalSpent: "Total gastado en este animal",
      records: "registros",
      noneYet: "Todavía no hay tratamientos registrados.",
      add: "Añadir tratamiento",
      added: "Tratamiento guardado",
      condition: "Afección / enfermedad",
      details: "Detalles del tratamiento (medicación, dosis, revisión…)",
      detailsPlaceholder: "Describe el diagnóstico y lo que se hizo; sé lo más concreto posible.",
      cost: "Coste",
      date: "Fecha",
      severityLabel: "Gravedad",
      statusLabel: "Estado",
      clinicName: "Clínica / ayuntamiento (opcional)",
      save: "Guardar registro",
    },
    apply: {
      overline: "Programa de voluntariado",
      title: "Hazte voluntario",
      subtitle: "Cuéntanos por qué quieres cuidar de los animales callejeros de tu ciudad. Primero pasa un filtro de IA contra el spam y luego un administrador revisa tu solicitud.",
      cardTitle: "Hazte voluntario",
      cardSubtitle: "Desbloquea las herramientas de cuidado · solicitud de 2 min + revisión de administración",
      motivationLabel: "¿Por qué quieres ser voluntario?",
      motivationPlaceholder: "Describe tu experiencia con animales callejeros, tu barrio y qué te gustaría hacer como voluntario…",
      minChars: "Mínimo 20 caracteres",
      aiNotice: "Un filtro de IA buscará señales de spam o provocación en tu solicitud. La decisión final la toma un administrador.",
      submitBtn: "Enviar solicitud",
      submitted: "Solicitud enviada: se ha avisado a los administradores",
      error: "Algo ha salido mal",
      tooShort: "Escribe al menos 20 caracteres",
      backToProfile: "Volver al perfil",
      aiScore: "Puntuación de la IA",
      statusPending: "Tu solicitud está en revisión",
      statusPendingHint: "Un administrador la revisará pronto. Recibirás una notificación con la decisión.",
      statusRejected: "Solicitud no aprobada",
    },
    admin: {
      overline: "Administración",
      title: "Solicitudes de voluntariado",
      subtitle: "La IA hace un filtrado previo de cada solicitud. Tu tarea: la revisión humana final antes de dar acceso de voluntario.",
      forbiddenTitle: "Solo administradores",
      forbiddenSubtitle: "No tienes permiso para ver esta página.",
      aiSummary: "Valoración de la IA",
      empty: "No hay solicitudes en este filtro.",
      approveBtn: "Aprobar",
      rejectBtn: "Rechazar",
      approvedToast: "Solicitud aprobada: acceso de voluntario concedido.",
      rejectedToast: "Solicitud rechazada.",
      bulkRejectTitle: "La IA ha marcado {count} solicitudes como de alto riesgo",
      bulkRejectSubtitle: "Recházalas todas con un clic; se avisará a quienes las enviaron.",
      bulkRejectBtn: "Rechazar todas las rojas",
      bulkRejectConfirm: "¿Rechazar {count} solicitudes marcadas en rojo? Esto no se puede deshacer automáticamente.",
      bulkRejectedToast: "{count} solicitudes rechazadas de una vez.",
      notePlaceholder: "Nota opcional para quien solicita (podrá verla)…",
      adminNoteLabel: "Nota de administración",
      tab: {
        pending: "Pendientes",
        approved: "Aprobadas",
        rejected: "Rechazadas",
      },
      stats: {
        daysOld: "días",
        cats: "animales",
        sightings: "avistamientos",
        feeding: "alimentación",
      },
    },
    adminEmerg: {
      overline: "Moderación de emergencias",
      title: "Cola de revisión de emergencias",
      subtitle: "La IA analiza fotos y textos. Los avisos de quien todavía no tiene historial pasan por aquí para una revisión humana antes de hacerse públicos.",
      gotoApplications: "Ir a las solicitudes de voluntariado",
      empty: "No hay emergencias en este filtro.",
      approveBtn: "Publicar",
      rejectBtn: "Rechazar",
      approvedToast: "Emergencia publicada.",
      rejectedToast: "Emergencia rechazada.",
      tab: {
        pending: "Pendientes",
        approved: "Publicadas",
        rejected: "Rechazadas",
      },
      photoVerdict: "IA de la foto",
      textVerdict: "IA del texto",
      notACat: "No es un animal",
      aiGenerated: "Generada con IA",
      photoshopped: "Manipulada",
      photoClean: "La foto parece legítima",
      confidence: "Confianza",
      redWarning: "La IA lo ha marcado como de alto riesgo: revísalo con atención antes de publicar.",
    },
    photos: {
      archive: "Archivo de fotos",
      galleryTitle: "Archivo de fotos",
      gallerySubtitle: "Todas las fotos de este animal que ha compartido la comunidad. Toca una huella para dar me gusta.",
      addPhoto: "Añadir foto",
      uploaded: "Foto subida",
      uploadFailed: "No se ha podido subir",
      emptyGallery: "Todavía no hay fotos. ¡Sé el primero en añadir una!",
      loginRequired: "Inicia sesión para subir una foto",
      loginToLike: "Inicia sesión para dar una huella",
      deleteConfirm: "¿Eliminar esta foto?",
      deleted: "Foto eliminada",
      monthlyQuotaUsed: "{used}/{limit} huellas usadas este mes",
      unlimitedQuota: "Huellas ilimitadas (Premium)",
      upgradeForUnlimited: "Hazte Premium para tenerlas ilimitadas",
      aiNotice: "Las fotos se revisan automáticamente en busca de contenido generado con IA, manipulado o inadecuado.",
    },
    photoContest: {
      overline: "Concurso mensual de fotos",
      title: "Foto del mes",
      subtitle: "Cada mes, las 3 fotos con más huellas ganan la insignia de Fotógrafo del Mes. Toca cualquier foto para visitar a su animal.",
      empty: "Todavía no hay fotos con me gusta este mes: sé el primero en poner a un animal en el podio.",
      by: "por",
      honorableMentions: "Menciones especiales (4-10)",
    },
  },
  tr: {
    kediBildir: {
      baslik: "Kedi Bildir",
      altBaslik: "Mahallenizdeki kediler için buradayız. Ne yapmak istersin?",
      kaydetBaslik: "Kedi Kaydet",
      kaydetAciklama: "Mahallendeki bir kediyi sisteme ekle",
      gordumBaslik: "Kedi Gördüm",
      gordumAciklama: "Daha önce kayıtlı bir kediyi gördüğünü bildir",
      acilBaslik: "Acil Bildir",
      acilAciklama: "Yaralı, hasta veya acil durumdaki bir kedi için yardım çağır",
      mamaSuBaslik: "Mama/Su Noktası Ekle",
      mamaSuAciklama: "Mama veya su noktası konumunu paylaş",
      dipnot: "Her bildirim mahallendeki gönüllülere ulaşır.",
    },
    indir: {
      baslik: "Uygulamayı indir",
      altBaslik: "Sokak Kedileri'ni telefonuna kur — mahallendeki kedileri gör, kayıp ilanı ver, yardım çağrısına yetiş.",
      appStore: "App Store'dan indir",
      playStore: "Google Play'den indir",
      yonlendiriliyor: "Mağazaya yönlendiriliyorsun…",
      qr: "Telefonunun kamerasıyla okut",
      yakinda: "Uygulama yakında mağazalarda.",
      androidYakinda: "Android sürümü yakında.",
      zatenVar: "Uygulama zaten telefonunda.",
      anaSayfa: "Ana sayfaya dön",
    },
    terms: {
      overline: "Yasal",
      title: "Kullanım Koşulları",
      updated: "Son güncelleme: 18 Şubat 2026",
      s1t: "1. Hizmetin Kapsamı",
      s1a: "Sokak Kedileri, sokak hayvanlarına dair gözlem, fotoğraf, mesaj ve gönüllü koordinasyonu paylaşmaya yarayan bir topluluk uygulamasıdır. Hesap açarak bu uygulamayı kullanmayı kabul edersin.",
      s2t: "2. Kullanıcı Tarafından Üretilen İçerik (UGC)",
      s2a: "Yüklediğin fotoğraflar, mesajlar, yorumlar, ihbarlar dahil tüm içerikten yalnızca sen sorumlusun. Yüklediğin içeriğin yasal olduğunu ve üçüncü kişilerin haklarını ihlal etmediğini taahhüt edersin.",
      s2zero: "Sıfır tolerans",
      s2b: ": Hakaret, taciz, nefret söylemi, cinsel içerik, sahte ihbar, dolandırıcılık ve yasadışı içerik kesinlikle yasaktır. ",
      s2c: "Detaylar için ",
      s2d: "'na bak.",
      s3t: "3. Moderasyon, Şikayet ve Engelleme",
      s3a: "Her UGC yüzeyinde görünür bir “Şikayet et” aksiyonu vardır. Şikayet edilen içerik 24 saat içinde insan moderatör ekibimiz tarafından gözden geçirilir; ihlal halinde içerik kaldırılır ve hesap askıya alınabilir. Diğer kullanıcıları kullanıcı profilinden veya mesaj penceresinden engelleyebilirsin.",
      s4t: "4. Hesap Güvenliği",
      s4a: "Şifreni gizli tutmak senin sorumluluğundur. Hesabının başkası tarafından kullanıldığını fark edersen derhal destek ile iletişime geç.",
      s5t: "5. Verilerin İşlenmesi",
      s5a: "Kişisel verilerin işlenmesine ilişkin detaylar ",
      s5b: "'nda açıklanmıştır.",
      s6t: "6. Hesap Kapatma ve Askıya Alma",
      s6a: "Bu Koşulları ihlal eden hesaplar uyarı yapılmaksızın askıya alınabilir veya kalıcı olarak kapatılabilir. Sen de istediğin zaman hesabını kapatabilirsin.",
      s7t: "7. Sorumluluk Reddi",
      s7a: "Uygulama “olduğu gibi” sunulur. Sokak hayvanlarına müdahaleyi profesyonel veterinerlik tavsiyesi yerine koyma. Acil durumlarda 112 / 184 / 153 ile iletişime geç.",
      s8t: "8. Değişiklikler",
      s8a: "Bu Koşullar zaman zaman güncellenebilir. Güncel sürüm bu sayfada yayınlanır.",
      linkCommunity: "Topluluk Kuralları",
      linkPrivacy: "Gizlilik Politikası",
    },
    communityRules: {
      back: "Giriş",
      overline: "Topluluk",
      title: "Topluluk Kuralları",
      intro: "Sokak Kedileri güvenli ve saygılı bir ortam olmalı. Lütfen aşağıdaki kurallara uyun.",
      r1: "Taciz, tehdit, nefret söylemi ve yanıltıcı bildirim yasaktır.",
      r2: "Uygunsuz içerikler şikayet edilebilir.",
      r3: "Kullanıcılar kötüye kullanım yapan kişileri engelleyebilir.",
      r4: "Şikayet edilen içerikler incelenir ve gerekirse kaldırılır.",
      r5: "Acil durumlarda yerel yetkililer/veterinerlerle iletişime geçilmelidir.",
      footer: "Şikayet ve engelleme: Mesajlaşma ekranından veya kullanıcı profilinden ulaşabilirsiniz.",
    },
    aboutPage: {
      overline: "Hakkımızda",
      title: "Sokak Kedileri",
      lede: "İstanbul'da sokak kedilerini tanıyan, takip eden ve gerektiğinde onlara ulaşan insanları bir araya getiren bir uygulama.",
      whyT: "Neden var",
      why1: "Sokakta bir kediyi beslersin. Ertesi hafta aynı kedi mi, bilemezsin. Yaralı bir kedi görürsün, birileri onu daha önce görmüş müdür, bilemezsin. Kısırlaştırıldı mı, aşısı yapıldı mı — kimse bilmez.",
      why2: "Kedilerle ilgilenen insan sayısı az değil. Eksik olan şey, aynı kediden bahsettiğimizi bilmek.",
      whatT: "Ne yapıyoruz",
      what1a: "Bir kedinin fotoğrafını çektiğinde, uygulama onu veritabanında arar. Ne tür bir kedi olduğunu değil — ",
      what1s: "hangi kedi",
      what1b: " olduğunu. Kulaktaki çentik, gözün üstündeki iz, çenedeki leke: bir hayvanı başkasından ayıran şeyler bunlardır, rengi değil.",
      what2a: "Bu zor bir problem ve biz de her seferinde başaramıyoruz. Emin olmadığımızda öyle söylüyoruz. Bunu ",
      what2l: "nasıl çalıştığı",
      what2b: " sayfasında açık açık anlattık.",
      whoT: "Kim yapıyor",
      who: "Bağımsız, küçük bir ekip. Belediye, dernek ya da şirket değiliz; kimseyle resmi bir ortaklığımız yok. Uygulama İstanbul'da yazıldı ve İstanbul sokaklarında test ediliyor.",
      moneyT: "Para",
      money: "Uygulamanın temel işlevleri ücretsiz: kedi kaydetmek, aramak, acil vaka bildirmek, mesajlaşmak. Premium, yapay zekâ vesikalığı gibi ek özellikler içindir ve hiçbir kediyi kurtarmak için gerekli değildir.",
      wrongT: "Yanlış bir şey mi gördün",
      wrong: "Yanlış eşleşme, hatalı bilgi, uygunsuz içerik — hepsini bize bildir. Bir hatayı bilmek, olmadığını sanmaktan iyidir.",
      linkHelp: "Yardım",
      linkCommunity: "Topluluk Kuralları",
      linkPrivacy: "Gizlilik",
      linkTerms: "Kullanım Koşulları",
    },
    howItWorks: {
      overline: "Rehber",
      title: "Nasıl çalışır",
      stepLabel: "Adım",
      lede1: "Çoğu uygulama bir kedinin ",
      ledeEm: "ne tür",
      lede2: " bir kedi olduğunu söyler: tekir, siyah, genç. Biz ",
      ledeStrong: "hangi kedi",
      lede3: " olduğunu bulmaya çalışıyoruz. Aradaki fark, sokakta beslediğin kediyi tanımakla, ona benzeyen bin kediyi listelemek arasındaki farktır.",
      s1t: "Fotoğrafı çek",
      s1a: "Kediyi gördüğün yerde fotoğrafını çek. Uygulama kadraja gerçekten bir kedi girdiğinde kendi kendine yakalar — üç açı ister: önden, yandan, yukarıdan.",
      s1b: "Üç açı bir kaprisin sonucu değil: bir kediyi ayırt eden şeyler — kulaktaki çentik, gözün üstündeki iz, çenedeki leke — tek bir kareden çoğu zaman görünmez.",
      s2t: "Veritabanı kendiliğinden aranır",
      s2a1: "İlk fotoğraf çekilir çekilmez arama ",
      s2aStrong: "otomatik başlar",
      s2a2: ". Bir düğmeye basman gerekmez, çünkü basmayacaktın — ve o zaman uygulama aynı kedinin altı profiliyle dolardı.",
      s2b1: "Sistem önce yazılı özellikleri karşılaştırır, sonra en güçlü adayların fotoğraflarına ",
      s2bStrong: "gerçekten bakar",
      s2b2: ". Kararı fotoğraflar verir, kelimeler değil.",
      s3t: "Emin değilsek eşleştirmeyiz",
      s3a: "Kaçırmak bir gecikmedir: gönüllü yarın tekrar bakar. Yanlış eşleştirmek ise bir yalandır — iki hayvanı tek profilde birleştirir ve hâlâ sokakta olan bir kediyi arayan kişiyi aramaktan vazgeçirir.",
      s3b1: "Bu yüzden sistem kararsızsa sana ",
      s3bStrong: "“emin değilim”",
      s3b2: " der. Son sözü sen söylersin.",
      s4t: "Kedi haritaya işlenir",
      s4a: "Konum, beslenme kaydı, aşı, kısırlaştırma, sağlık durumu — hepsi o kedinin profilinde birikir. Zamanla o profil bir hayvanın hikâyesi olur: kim beslemiş, kim vetere götürmüş, ne zaman kaybolmuş.",
      s5t: "Mahalle görür",
      s5a: "Acil bir durum bildirdiğinde, o mahalleyi kapsama alanına almış gönüllülere düşer. Kediyi tanıyan insan, kediye en yakın insandır.",
      boxT: "Neden fotoğraf kalitesi önemli",
      boxA: "Bulanık, uzaktan ya da karanlıkta çekilmiş bir kare, kediyi ayırt eden işaretleri gizler. Sistem o zaman ya kaçırır ya da tereddüt eder. İyi bir fotoğraf, yalnızca güzel bir profil değil — doğru bir eşleşmedir.",
      ctaScan: "Kedi tanıt",
      ctaFaq: "Sık sorulanlar",
    },
    helpPage: {
      overline: "Destek",
      title: "Yardım",
      lede: "Sık sorulanlar. Aradığın burada yoksa yaz — gerçek bir insan okuyor.",
      q1: "Uygulama aynı kediyi neden bazen tanımıyor?",
      a1: "Bir kediyi ayırt eden şeyler işaretlerdir: kulaktaki çentik, gözün üstündeki iz, çenedeki leke. Fotoğraf bulanıksa, uzaktansa ya da karanlıktaysa bu işaretler görünmez ve sistem emin olamaz.\n\nEmin olmadığında eşleştirmemeyi tercih ediyoruz. Yanlış eşleştirmek, iki hayvanı tek profilde birleştirir ve hâlâ sokakta olan bir kediyi arayan kişiyi aramaktan vazgeçirir. Kaçırmak yalnızca bir gecikmedir.",
      q2: "Aynı kediyi yanlışlıkla iki kez kaydettim, ne yapmalıyım?",
      a2: "Olur, sorun değil. Kedinin profiline gir ve doğru olanı işaretle; yanlış kaydı bize bildir. Zamanla veritabanı büyüdükçe sistem bu tekrarları kendisi yakalayacak — çünkü her düzeltme onu eğitiyor.",
      q3: "Fotoğrafı çektim ama arama başlamadı.",
      a3: "Arama, ilk fotoğraftan hemen sonra kendiliğinden başlar; bir düğmeye basman gerekmez. Başlamadıysa büyük ihtimalle bağlantı koptu. Uygulamayı kapatıp aç, fotoğrafı tekrar çek.",
      q4: "Konumum paylaşılıyor mu?",
      a4: "Kedinin konumu paylaşılır, senin konumun değil. Kedinin nerede görüldüğü haritada durur — onu bulacak gönüllünün ihtiyacı olan tek şey budur. Detaylar için Gizlilik Politikası'na bak.",
      q5: "Acil bir vaka bildirdim, kim görüyor?",
      a5: "O mahalleyi kapsama alanına almış gönüllülere düşer. Kapsama alanını Profil → Düzenle bölümünden seçebilirsin. Kediyi tanıyan insan, kediye en yakın insandır.",
      q6: "Hesabımı nasıl silerim?",
      a6: "Profil sayfasının en altında “Hesabımı sil” var. Talebinden sonra hesabın 30 gün içinde silinir; bu süre içinde vazgeçersen iptal edebilirsin.",
      q7: "Uygunsuz bir içerik ya da kullanıcı gördüm.",
      a7: "Her profilde ve her içerikte “Şikayet et” ve “Engelle” seçenekleri var. Şikayetler 24 saat içinde incelenir. Topluluk Kuralları'nda sıfır tolerans gösterdiğimiz şeyler yazılı.",
      q8: "Kedi bulamıyorum, uygulama yavaş.",
      a8: "Fotoğraflar büyükse yükleme uzar. Uygulama artık fotoğrafları göndermeden önce küçültüyor. Yine de yavaşsa bize yaz — hangi telefon, hangi ekran, ne yaparken. Bu bilgi olmadan hatayı bulamayız.",
      contactT: "Bize ulaş",
      contactA: "Hata bildirimi, öneri, şikayet — hepsi aynı adrese gelir.",
      linkHow: "Nasıl çalışır",
      linkCommunity: "Topluluk Kuralları",
      linkPrivacy: "Gizlilik",
      linkTerms: "Kullanım Koşulları",
    },
    privacyPage: {
      back: "Ana sayfa",
      overline: "Yasal",
      title: "Gizlilik Politikası",
      updated: "Son güncelleme: Eylül 2026",
      intro1: "Sokak Kedileri (",
      intro2: ") uygulaması ve web sitesi, İstanbul başta olmak üzere Türkiye'deki sokak kedilerini koruma altına almak için tasarlanmış bir gönüllü topluluk platformudur. Bu sayfa, hizmetimizi kullandığında hangi verilerin toplandığını, neden işlendiğini ve hangi haklara sahip olduğunu açıkça anlatır.",
      s1t: "1. Topladığımız Veriler",
      s1i1L: "Hesap bilgileri:",
      s1i1: " e-posta adresi, ad-soyad, şifre (yalnızca güvenli hash formatında saklanır).",
      s1i2L: "Profil bilgileri:",
      s1i2: " profil fotoğrafı, ilçe ve bölge tercihleri, kazanılan rozetler, Pati Puanları.",
      s1i3L: "Konum bilgisi:",
      s1i3: " yalnızca sen izin verdiğin anda — kedi bildirimi, acil vaka, harita görüntüleme ve “Bul” butonu gibi özelliklerde kullanılır. Arka planda konum takip edilmez.",
      s1i4L: "Fotoğraf yüklemeleri:",
      s1i4: " eklediğin kedilere, mahallene veya profil avatarına ait görseller. Cihazında otomatik sıkıştırılarak gönderilir.",
      s1i5L: "Mesajlaşma içeriği:",
      s1i5: " diğer gönüllüler ve kullanıcılarla yaptığın yazışmalar. Yalnızca iletişimin taraflarına ve gerektiğinde moderasyon ekibine açıktır.",
      s1i6L: "Uygulama kullanım verileri:",
      s1i6: " oturum bilgisi, hangi sayfalara girdiğin, hata kayıtları ve cihaz türü (anonim teknik istatistikler).",
      s1i7L: "Reklam ölçümü:",
      s1i7: " Uygulamayı bir Meta (Facebook/Instagram) tanıtımı üzerinden yükleyip yüklemediğini anlamak için uygulamanın kurulum ve açılış bilgisi Meta'ya iletilir. iOS'ta izleme izni verirsen buna cihazının reklam kimliği de eklenir; vermezsen kimlik gönderilmez ve ölçüm yalnızca toplu, kişiye bağlanamayan biçimde yapılır. İzni istediğin zaman Ayarlar \u2192 Gizlilik ve Güvenlik \u2192 İzleme bölümünden geri alabilirsin.",
      s2t: "2. Verileri Hangi Amaçla Kullanıyoruz",
      s2i1: "Sokak kedilerinin haritada görüntülenmesi, takibi ve sağlık geçmişinin tutulması.",
      s2i2: "Acil durum bildirimleri (yaralı/hasta kedi) için en yakın gönüllülere haber verilmesi.",
      s2i3: "Kayıp kedi ilanlarının ilgili bölgedeki kullanıcılara ulaştırılması.",
      s2i4: "Mahallem (yerel topluluk) akışı, yorumlar, beğeniler ve gönüllü iletişimi.",
      s2i5: "Hesap güvenliği, dolandırıcılık önleme ve kötüye kullanım moderasyonu.",
      s2i6: "Uygulamanın daha iyi çalışması için anonim performans ve hata izleme.",
      s3t: "3. Verileri Kimlerle Paylaşıyoruz",
      s3aA: "Kişisel verilerin ",
      s3aStrong: "üçüncü taraflara satılmaz",
      s3aB: " Reklam ölçümü için Meta ile paylaşılan sınırlı veri aşağıda açıkça belirtilmiştir. Bunun dışında yalnızca şu durumlarda işlem yapılır:",
      s3i1: "Hizmetin çalışması için zorunlu altyapı sağlayıcıları (sunucu, veritabanı, e-posta servisleri) — yalnızca işleme için gereken minimum veri.",
      s3i2: "Yasal yükümlülük durumunda, yetkili merciilerin resmi talebine yanıt olarak.",
      s3i3: "Mesajlarına özel olarak izin vermediğin sürece hiçbir kullanıcı seni kişisel olarak göremez (telefon, e-posta vb. profilinde gösterilmez).",
      s3i4: "Reklam ölçümü \u2014 Meta Platforms Ireland Ltd.: yalnızca uygulama kurulum ve açılış olayları ile, iznin varsa cihaz reklam kimliği. Adın, e-postan, konumun, fotoğrafların ve mesajların Meta ile paylaşılmaz.",
      s4t: "4. Fotoğraflar Hakkında",
      s4a: "Yüklediğin kedi fotoğrafları topluluğun bu kediyi tanıyabilmesi için diğer kullanıcılara açık olur. Yapay zekâ analizi (kedi cinsi, sağlık değerlendirmesi) yalnızca yükleme anında çalıştırılır ve ayrı bir biyometrik veri olarak saklanmaz. Yüklediğin bir fotoğrafı dilediğin zaman kaldırabilirsin.",
      s5t: "5. Mesajlaşma Gizliliği",
      s5a: "Birebir mesajların yalnızca sen ve yazıştığın kişi tarafından görünür. İçerikler sunucumuzda saklanır ama üçüncü taraflarla paylaşılmaz. Şikâyet/rapor edildiğinde moderasyon ekibi yalnızca o konuşmayı inceleyebilir. İstediğin zaman bir kullanıcıyı engelleyebilir, rapor edebilir veya geçmiş yazışmalarını silebilirsin.",
      s6t: "6. Haklarına Sahip Ol",
      s6a: "KVKK ve uluslararası gizlilik standartları kapsamında aşağıdaki haklara sahipsin:",
      s6i1: "Hangi verilerinin işlendiğini öğrenme.",
      s6i2: "Yanlış veya eksik bilgilerin düzeltilmesini isteme.",
      s6i3L: "Hesabını veya tek tek verilerini silme talebinde bulunma.",
      s6i3: " Talebin 30 gün içinde uygulanır.",
      s6i4: "Verilerinin işlenmesine itiraz etme veya işlemenin kısıtlanmasını isteme.",
      s6i5: "Verilerinin taşınabilir bir formatta (JSON) sana iletilmesini isteme.",
      s6b: "Bu hakları kullanmak için aşağıdaki iletişim adresinden bize yazman yeterli.",
      s7t: "7. Veri Güvenliği",
      s7a: "Şifreler bcrypt ile hash'lenir, oturumlar JWT ile imzalanır, tüm trafik HTTPS üzerinden iletilir. Yedeklemeler şifreli ortamda saklanır. Yine de internet üzerinde hiçbir veri aktarımı %100 güvenli değildir — bu nedenle güçlü bir şifre kullanmanı ve hesabını başka biriyle paylaşmamanı öneririz.",
      s8t: "8. Çocukların Gizliliği",
      s8a: "Uygulamamız 13 yaş altı çocuklara yönelik tasarlanmamıştır ve bu yaş grubundan bilerek veri toplamayız. 13 yaş altı bir çocuğun hesap açtığını fark edersek hesabı kaldırırız.",
      s9t: "9. Bu Politikadaki Değişiklikler",
      s9a: "Bu politikayı zaman zaman güncelleyebiliriz. Önemli değişikliklerde uygulama içinde bildirim veya e-posta ile haberdar edileceksin. Bu sayfanın üst kısmındaki “Son güncelleme” tarihi her zaman en güncel sürümü gösterir.",
      contactT: "İletişim",
      contactA: "Gizlilik politikası, veri silme talepleri veya KVKK ile ilgili her soru için:",
      footer: "Sokak Kedileri · İstanbul, Türkiye",
    },
    // TUR-10: `thinking` blogu Turkce'de HIC yoktu (en ve id'de vardi).
    // t() eksik anahtarda Ingilizce'ye dustugu icin Turk kullaniciya
    // "AI is thinking" yaziyordu — sessiz, cunku hata degil, yedek.
    thinking: {
      title: "Yapay zeka düşünüyor",
      hint: "Genelde 5–15 saniye sürer. Bu sırada uygulamayı kullanmaya devam edebilirsin.",
      stages: {
        compress: "Fotoğrafın hazırlanıyor…",
        vision: "Kediye GPT-Vision ile bakılıyor…",
        matching: "Veritabanında benzer kediler aranıyor…",
        polishing: "Sonuçlar düzenleniyor…",
      },
    },
    dogUi: {
      dbTitleCat: "Kedi Veritabanı",
      dbTitleDog: "Köpek Veritabanı",
      dbOverlineCat: "KEDİ VERİTABANI",
      dbOverlineDog: "KÖPEK VERİTABANI",
      dbSubCat: "Topluluğumuzda kayıtlı her kediye göz at.",
      dbSubDog: "Topluluğumuzda kayıtlı her köpeğe göz at.",
      dbSearchCat: "Ada, renge, cinse veya mahalleye göre ara",
      dbSearchDog: "Ada, renge veya mahalleye göre ara",
      dbLostCat: "Kayıp Kedini Bul",
      dbLostDog: "Kayıp Köpeğini Bul",
      dbRegisteredCat: "Kayıtlı Kedi",
      dbRegisteredDog: "Kayıtlı Köpek",
      dbInjuredCat: "Yaralı Kedi",
      dbInjuredDog: "Yaralı Köpek",
      dbEarTagged: "Küpeli",
      dbEarTaggedHint: "Kısırlaştırılmış — kulak küpesi / çentiği",
      dbTitleAll: "Hayvan Veritabanı",
      dbOverlineAll: "HAYVAN VERİTABANI",
      dbSubAll: "Topluluğumuzda kayıtlı her hayvana göz at.",
      dbSearchAll: "Ada, renge veya mahalleye göre ara",
      dbLostAll: "Kayıp Hayvanını Bul",
      dbRegisteredAll: "Kayıtlı Hayvan",
      dbInjuredAll: "Yaralı Hayvan",
      lostConfirmCat: "{ad} için kayıp ilanı oluşturulsun mu?\n\nFotoğrafı, konumu ve adıyla ilan hemen yayına girer; mahalledeki kullanıcılara ve gönüllülere haber gider.",
      lostConfirmDog: "{ad} için kayıp ilanı oluşturulsun mu?\n\nFotoğrafı, konumu ve adıyla ilan hemen yayına girer; mahalledeki kullanıcılara ve gönüllülere haber gider.",
      lostOpenCat: "Bu kedi için zaten açık bir ilan var — oraya götürüyorum.",
      lostOpenDog: "Bu köpek için zaten açık bir ilan var — oraya götürüyorum.",
      deleteCat: "Bu kediyi silmek istediğine emin misin?",
      deleteDog: "Bu köpeği silmek istediğine emin misin?",
      shareCat: "Bu kediyi paylaş",
      shareDog: "Bu köpeği paylaş",
      editCat: "Kediyi düzenle",
      editDog: "Köpeği düzenle",
      reportCat: "Bu kedi profilini şikayet et",
      reportDog: "Bu köpek profilini şikayet et",
      noGpsCat: "Bu kedi kaydedilirken GPS alınamamış.",
      noGpsDog: "Bu köpek kaydedilirken GPS alınamamış.",
      lostCat: "Kedim kayboldu \u2014 ilan oluştur",
      lostDog: "Köpeğim kayboldu \u2014 ilan oluştur",
      newCat: "Yeni kedi",
      newDog: "Yeni köpek",
      saveCat: "Kediyi kaydet",
      saveDog: "Köpeği kaydet",
      saving: "Kaydediliyor\u2026",
      dogOptional: "Köpek bilgisi ekle (isteğe bağlı)",
      dogOptionalHint: "Zorunlu değil. İki fotoğraf, ad ve mahalle yeterli.",
    },
    dog: {
      section: "Köpek bilgileri",
      unknownHint: "Net göremiyorsan Belirsiz bırak",
      matchingSoon: "Köpek eşleştirmesi henüz açık değil — profil kaydedilir, eşleştirme açıldığında aranabilir olur.",
      earCarriage: "Kulak duruşu",
      ear: { dik: "Dik", yari_dik: "Yarı dik", dusuk: "Düşük", kesik: "Kesik", belirsiz: "Belirsiz" },
      tailType: "Kuyruk",
      tail: { uzun_duz: "Uzun, düz", kivrik: "Kıvrık", orak: "Orak", kisa: "Kısa", kesik: "Kesik", puskullu: "Püsküllü", belirsiz: "Belirsiz" },
      coatLength: "Tüy uzunluğu",
      coatLen: { kisa: "Kısa", orta: "Orta", uzun: "Uzun", belirsiz: "Belirsiz" },
      coatTexture: "Tüy dokusu",
      coatTex: { duz: "Düz", dalgali: "Dalgalı", kivircik: "Kıvırcık", sert_tel: "Sert tel", belirsiz: "Belirsiz" },
      muzzle: "Burun yapısı",
      muz: { basik: "Basık", orta: "Orta", uzun: "Uzun", belirsiz: "Belirsiz" },
      build: "Vücut yapısı",
      bod: { ince: "İnce", orta: "Orta", iri: "İri", belirsiz: "Belirsiz" },
      earTagNo: "Küpe numarası",
      earTagHint: "Yalnız kısırlaştırma küpesi varsa",
      registering: "Köpek kaydediyorsun",
      registeringCat: "Kedi kaydediyorsun",
      switchToCat: "Kedi olarak kaydet",
      switchToDog: "Köpek olarak kaydet",
      myLostDog: "Bu benim kayıp köpeğim",
    },
    appName: "Sokak Kedileri",
    tagline: "İstanbul Sokak Kedileri",
    // Panodaki baslik slogani. `tagline` bundan AYRI: o sayfa basliklarinda
    // ve paylasim kartlarinda kullaniliyor, bu ise uygulama basliginin ikinci
    // satiri. Ikisini birlestirmek, panoda olmayan bir metni basliga sokardi.
    baslikSlogani: "Daha güçlü mahalle.",
    arama: {
      rowPlace: "Mahalle",
      otherNeighborhoods: "Diğer mahalleler",
      row_kediler: "kedileri",
      row_harita: "haritada göster",
      row_kayip: "kayıp ilanları",
      row_besleme: "beslenme noktaları",
      rowGo_kediler: "Kedileri gör",
      rowGo_harita: "Bölgede neler var?",
      rowGo_kayip: "İlanları gör",
      rowGo_besleme: "Noktaları gör",
      unit_kediler: "sonuç",
      unit_harita: "",
      unit_kayip: "ilan",
      unit_besleme: "nokta",
      tab_ilanlar: "Kayıp ilanları",
      title: "Ara",
      placeholder: "Kedi, mahalle veya ilçe ara…",
      back: "Geri",
      clear: "Temizle",
      recent: "Son aramalar",
      clearHistory: "Temizle",
      mostCats: "En çok kedi kayıtlı mahalleler",
      hint: "Kedi adı, mahalle veya ilçe yazarak kayıtlı kedilere, kayıp ilanlarına ve bölgendeki her şeye ulaşabilirsin.",
      neighborhoods: "Mahalleler",
      cats: "Kediler",
      catWord: "kedi",
      searching: "Aranıyor…",
      empty: "Sonuç yok. Bir mahalle adı deneyebilirsin.",
      searchFor: "Şunu ara:",
      tab_hepsi: "Tümü",
      tab_kediler: "Kediler",
      tab_mahalleler: "Mahalleler",
    },
    anasayfaV2: {
      odul: "ödül",
      slogan: "Daha iyi bir yaşam, her pati için.",
      // Tasarımda başlık ELLE bölünmüş: "Mahallende" tek başına bir satır.
      // Doğal sarma bunu üretemez — 1. satır için gereken genişlik 2.
      // satırdan dar olmalıydı, ki değil. `pre-line` ile çiziliyor.
      heroTitle: "Mahallende\nbugün neler oluyor?",
      heroSub: "Kediler için daha güvenli sokaklar mümkün.",
      heroMark: "Her pati değerli",
      statCats: "Kayıtlı kedi",
      statEmergencies: "Acil vaka",
      statFeeding: "Besleme noktası",
      statVaccinated: "Aşılı",
      locationPick: "Konumu değiştir",
      search: "Kedi, mahalle veya ilçe ara…",
      actionsTitle: "Ne yapmak istiyorsun?",
      actionsAside: "Küçük bir adım, büyük bir fark",
      a1t: "Kedi gördüm",
      a1s: "Konum bildir",
      a2t: "Kayıp kedi ara",
      a2s: "İlanları ara",
      aramaVeritabani: "Kedi veritabanı",
      a3t: "Acil Merkezi",
      a3s: "Yardım bekleyenler",
      a4t: "AI ile tanı",
      a4s: "Fotoğrafla karşılaştır",
      vtBaslik: "Kedi Veritabanı",
      vtAlt: "Kayıtlı kedileri keşfet, detayları incele.",
      vtAdetSonEk: "kedi",
      lostTitle: "Yakınında kayıp kedi aranıyor",
      casesTitle: "Yakınındaki önemli vakalar",
      casesSub: "Sahada yardıma en çok ihtiyaç duyan kediler",
      casesAll: "Tümü gör",
      casesEmpty: "Yakınında açık vaka yok — iyi haber.",
      casesFarAway: "Yakınında yok — en yeni kayıtlar gösteriliyor.",
      tabLost: "Kayıp kediler",
      tabEmergency: "Acil vakalar",
      tabAll: "Tümü",
      emergencyBadge: "ACİL",
      chipVet: "Veteriner ihtiyacı var",
      chipInjured: "Yardım bekliyor",
      chipFood: "Mama/su gerekli",
      chipDanger: "Tehlike bildirildi",
      chipVolunteer: "Gönüllü aranıyor",
      chipLost: "Gören var mı?",
      lostAll: "Tümü",
      lostBadge: "KAYIP",
      lostEmpty: "Yakınında kayıp ilanı yok — iyi haber.",
      mapTitle: "Mahalle haritası",
      mapDesc: "Yakınındaki kedileri, acil vakaları ve besleme noktalarını keşfet.",
      mapOpen: "Haritayı aç",
      aiTitle: "AI · Kedi tanıma",
      aiDesc: "Gördüğün kediyi fotoğrafla tanı, kayıtlı kedilerle karşılaştır.",
      aiPhoto: "Fotoğraf çek",
      aiGallery: "Galeriden seç",
      healthTitle: "Kedi sağlığı ve destek",
      healthDesc: "Aşı, kısırlaştırma, kuduz ve sağlık notları.",
      communityTitle: "Topluluğa katıl",
      communityDesc: "Gönüllüler, etkinlikler ve daha fazlası.",
      communityMark: "Birlikte daha güçlüyüz",
      navReport: "Kedi bildir",
      navReportShort: "Kedi bildir",
      justNow: "az önce",
      hoursAgo: "{n} sa önce",
      hoursAgoLong: "{n} saat önce",
      minutesAgoLong: "{n} dk önce",
      pickPlaceAria: "Mahalleni seç",
      pickPlaceCta: "Mahalleni seç",
      pickPlaceTitle: "Mahalleni seç",
      pickPlaceSub: "Anasayfa ve Mahallem ekranları bu seçime göre çalışır.",
      pickPlaceNear: "Konumuna göre",
      pickPlaceClose: "Kapat",
      pickPlaceSave: "Kaydet",
      pickPlaceSaving: "Kaydediliyor\u2026",
      yesterday: "dün",
      daysAgo: "{n} gün önce",
    },
    kayipIlan: {
      title: "Kayıp Kedi",
      lost: "KAYIP",
      odul: "ödül",
      poster: "Afiş hazırla",
      posterTitle: "Kayıp kedi afişi",
      posterErr: "Afiş oluşturulamadı. Biraz sonra tekrar dene.",
      share: "Paylaş",
      shareTitle: "Kayıp kedi aranıyor 🔍",
      shareCopied: "Bağlantı kopyalandı — istediğin yere yapıştır.",
      shareErr: "Paylaşılamadı. Biraz sonra tekrar dene.",
      seen: "Bu kediyi gördüm",
      seenSub: "Haritada konumunu bildir",
      aiTitle: "Benzer kedi mi gördün?",
      aiDesc: "Fotoğraf çek veya galeriden seç, AI karşılaştırsın.",
      whereTitle: "Nerede olabilir?",
      whereSub: "Tahmini arama alanı",
      openMap: "Haritayı aç",
      teamTitle: "Arama ekibi",
      teamCount: "{n} kişi arıyor",
      teamJoin: "Aramaya katıl",
      teamLeave: "Aramadan ayrıl",
      photoCount: "Fotoğraf",
      seenCount: "Görülme bildirimi",
      owner: "İlan sahibi",
      call: "Ara",
      message: "Mesaj",
      details: "İlan detayları",
      reunited: "Kavuştuk — ilanı kapat",
      bKisir: "Kısır",
      bKisirDegil: "Kısır değil",
      bErkek: "Erkek",
      bDisi: "Dişi",
      bUrkek: "Ürkek",
      bSokulgan: "Sokulgan",
      bSaldirgan: "Tedbirli",
      bSakin: "Sakin",
      noPhoto: "Fotoğraf yok",
      notFound: "İlan bulunamadı.",
      closed: "Bu ilan kapanmış.",
    },
    aramaAlani: {
      title: "Arama Alanı",
      areaTitle: "Tahmini arama alanı",
      areaSub: "Son görüldüğü konuma göre oluşturuldu.",
      allReports: "Tümünü gör",
      directions: "Bu bölgeye yol tarifi al",
      legend: "Harita anahtarı",
      sonGorulen: "Son görüldüğü yer",
      aramaAlaniHalka: "Arama alanı",
      ihbarlar: "Görülen ihbarlar",
      gonulluler: "Gönüllüler",
      reports: "Son ihbarlar",
      noReports: "Henüz ihbar yok. İlk sen ol.",
      cFoto: "Fotoğraf eklendi",
      cBenzer: "Benzer olabilir",
      cEminDegil: "Emin değil",
      addReport: "İhbar ekle",
      join: "Arama ekibine katıl",
      leave: "Arama ekibinden ayrıl",
      team: "{n} kişi arıyor",
      locHidden: "Gönüllülerin yerini görmek için giriş yap",
      away: "{d} ötede",
      noCoords: "Bu ilanda harita konumu yok.",
      closed: "Bu ilan kapanmış.",
      notFound: "İlan bulunamadı.",
      back: "Geri",
    },
    mahallem: {
      anOnerilerAcikGorev: "Bu konular için görev açık",
      anHeroBaslik: "{yer} durum nasıl?",
      anHeroAlt: "Son 30 günün verileri",
      anHeroAltAi: "Son 30 gün ve AI analizi",
      anSkorEtiketi: "Mahalle Sağlık Skoru",
      anSkorBilgiAc: "Skor neyi ölçüyor?",
      anSkorBilgi: "Skor mahallenin imkânlarını ölçer (aşı kaydı, mama ve su noktası, açık acil vaka, çözüm hızı) — kedilerin sağlığını değil.",
      anAzVeri: "az veri",
      anBalonYok: "Henüz özet yok: bu bölgede yeterli ölçüm birikmedi.",
      anDurumKayit: "Kedi kayıtları sürüyor",
      anDurumKayitAlt: "30 günde {n} yeni kayıt",
      anDurumAcilYok: "Açık acil vaka yok",
      anDurumAcilYokAlt: "Bütün vakalar kapandı",
      anDurumSuVar: "Su noktaları var",
      anDurumSuVarAlt: "{n} aktif nokta",
      anDurumMamaVar: "Mama noktaları var",
      anDurumMamaVarAlt: "{n} aktif nokta",
      anDurumBeslenme: "Beslenme desteği yetersiz",
      anDurumBeslenmeAlt: "Yeni nokta gerekli",
      anDurumSu: "Su noktası yetersiz",
      anDurumSuAlt: "Yeni nokta gerekli",
      anDurumGonullu: "Gönüllü desteği düşük",
      anDurumGonulluAlt: "Gönüllü gerekli",
      anDurumAsi: "Aşı takibi zayıf",
      anDurumAsiAlt: "Kayıt az",
      anDurumBarinak: "Kedi evi yok",
      anDurumBarinakAlt: "Barınak gerekli",
      anDurumYarali: "Yaralı kedi bildirimi",
      anDurumYaraliAlt: "Son 30 günde",
      anDurumAcil: "{n} acil vaka bekliyor",
      anDurumAcilBir: "1 acil vaka bekliyor",
      anDurumAcilAlt: "Müdahale gerekli",
      anKonularBaslik: "Şu anda önemli {n} konu",
      anTumunuGor: "Tümünü gör",
      anKonuAcil: "Acil vaka bekliyor",
      anKonuYarali: "Yaralı kedi bildirildi",
      anKonuBeslenme: "Beslenme noktası eksik",
      anKonuSu: "Su noktası eksik",
      anKonuGonullu: "Gönüllü desteği düşük",
      anKonuAsi: "Aşı takibi zayıf",
      anKonuBarinak: "Kedi evi yok",
      anKonuBolgede: "En çok {yer}.",
      anKonuYok: "Ölçülen alanlarda belirgin bir eksik yok.",
      anVeriYok: "Veri alınamadı.",
      anDugmeVakalar: "Vakaları Gör",
      anDugmeGonulluler: "Gönüllüleri Gör",
      anDugmeKediler: "Kedileri Gör",
      anHaritaBaslik: "Mahalle Analiz Haritası",
      anAralikSec: "Dönem seç",
      anAralik7: "Son 7 gün",
      anAralik30: "Son 30 gün",
      anAralik90: "Son 3 ay",
      anAralik365: "Son 1 yıl",
      anAralikTumu: "Tümü",
      anKatmanYogunluk: "Kedi Yoğunluğu",
      anKatmanIhtiyac: "İhtiyaçlar",
      anKatmanAcil: "Acil Vakalar",
      anKatmanBeslenme: "Beslenme",
      anKatmanSu: "Su Noktaları",
      anYogunlukDusuk: "Düşük",
      anYogunlukYuksek: "Yüksek",
      anEtiketKedi: "{n} kedi",
      anEnCokIhtiyac: "En çok ihtiyaç olan bölge:",
      anHaritaBos: "Sınırı çizilebilen bölge yok.",
      anDegisimBaslik: "Son 30 Günde Ne Değişti?",
      anDetayliGor: "Detaylı Gör",
      anSekmeKediler: "Kediler",
      anSekmeAcil: "Acil Vakalar",
      anSekmeKayip: "Kayıp Kediler",
      anSekmeBeslenme: "Beslenme",
      anSekmeSu: "Su Noktaları",
      anSatirKedi: "Kedi kayıtları",
      anSatirAcil: "Acil vakalar",
      anSatirBeslenme: "Beslenme noktaları",
      anSatirSu: "Su noktaları",
      anRozet30: "30 günde",
      anGrafikNotu: "Kayıt tarihine göre: kediler = o günün sonunda kayıtlı toplam; diğerleri = o gün eklenen.",
      anGrafikYok: "Bu dönemde henüz kayıt yok.",
      anOnerilerBaslik: "AI Önerileri",
      anOnerilerAlt: "Mahallemizi daha iyi hale getirmek için öneriler.",
      anTumOneriler: "Tüm önerileri gör",
      anOneriYok: "Şimdilik öneri yok.",
      // ÖNCELİK-1 (18 Eylül 2026) — Mahalle Öncelikleri sayfası.
      ocBaslik: "Mahalle Öncelikleri",
      ocAlt: "Daha iyi bir mahalle için birlikte harekete geçelim.",
      ocGeri: "Geri",
      ocNot: "Bu öncelikler, son 30 gün içinde mahallendeki verilere göre AI analizi ile belirlenmiştir.",
      ocEtiketAcil: "ACİL VAKALAR",
      ocEtiketBeslenme: "BESLENME",
      ocEtiketSu: "SU NOKTALARI",
      ocEtiketGonullu: "GÖNÜLLÜ DESTEĞİ",
      ocEtiketDiger: "DİĞER İHTİYAÇLAR",
      ocAcilBaslik: "Yardım bekleyen kediler",
      ocAcilAlt: "Mahallende {n} acil vaka açık.",
      ocAcilYokBaslik: "Açık acil vaka yok",
      ocAcilYokAlt: "Şu anda müdahale bekleyen vaka yok.",
      ocAcilBirim: "açık vaka",
      ocBeslenmeBaslik: "Mama desteği gerekiyor",
      ocBeslenmeAlt: "{n} bölgede mama noktası yetersiz.",
      ocBeslenmeYokBaslik: "Mama noktaları yeterli",
      ocBeslenmeYokAlt: "{n} mama noktası kayıtlı.",
      ocBeslenmeBirim: "yeni nokta",
      ocSuBaslik: "Su noktaları eksik",
      ocSuAlt: "{n} bölgede su desteği gerekli.",
      ocSuYokBaslik: "Su noktaları yeterli",
      ocSuYokAlt: "{n} su noktası kayıtlı.",
      ocSuBirim: "nokta",
      ocGonulluBaslik: "Desteğe ihtiyacımız var",
      ocGonulluAlt: "{n} kedi için gönüllü aranıyor.",
      ocGonulluYokBaslik: "Gönüllü desteği yeterli",
      ocGonulluYokAlt: "{n} kediye {g} gönüllü bakıyor.",
      ocGonulluBirim: "kedi",
      ocDigerBaslik: "Kayıp ve barınma konuları",
      ocDigerAlt: "{k} aktif kayıt, {b} barınak ihtiyacı.",
      ocDigerYokBaslik: "Kayıp ya da barınma sorunu yok",
      ocDigerYokAlt: "Açık kayıp ilanı ve barınak ihtiyacı yok.",
      ocDigerBirim: "kayıt",
      ocDugmeGorevler: "Görevleri Gör",
      ocDugmeBolgeler: "Bölgeleri Gör",
      ocDugmeDetaylar: "Detayları Gör",
      gvKatBeslenme: "Beslenme",
      gvKatSu: "Su Noktası",
      gvKatKayip: "Kayıp Kedi",
      gvKatSaglik: "Sağlık",
      gvKatKulube: "Kulübe",
      gvDugmeDetay: "Detayları Gör",
      gvSloganSade: "Kediler için birlikte harekete geçelim.",
      gvOlusturBaslik: "Kendi Görevini Oluştur",
      gvOlusturAlt: "Mahallen için yeni bir görev oluştur. Birlikte daha fazla kediye ulaşalım.",
      gvOlusturDugme: "Görev Oluştur",
      gvOneriBaslikKisa: "AI Önerisi",
      gvGorevlerimBag: "Görevlerim · {n}",
      baKatmanKisaAcil: "Acil",
      baKatmanKisaBeslenme: "Beslenme",
      baKatmanKisaSu: "Su",
      baKatmanKisaGonullu: "Gönüllü",
      baKatmanKisaIsi: "Isı",
      gvBaslik: "Görevler",
      gvSlogan: "Mahallendeki kediler için yapabileceğin işler. Küçük bir adım, büyük bir fark.",
      gvElYazisi: "Birlikte daha mutlu kediler.",
      gvSuzTumu: "Tümü",
      gvSuzAcil: "Acil",
      gvSuzBugun: "Bugün",
      gvSuzYakin: "Yakınımda",
      gvFiltrele: "Filtrele",
      gvFiltreTur: "Görev türü",
      gvFiltreDurum: "Durum",
      gvTemizle: "Temizle",
      gvTamam: "Tamam",
      gvTur_yarali_kontrol: "Yaralı kedi",
      gvTur_veteriner_destegi: "Veteriner",
      gvTur_mama_istasyonu: "Mama",
      gvTur_su_kabi: "Su",
      gvTur_kayip_arama: "Kayıp kedi",
      gvTur_kulube: "Kulübe",
      gvHata: "Görevler yüklenemedi.",
      gvOncelikliBaslik: "Öncelikli Görevler",
      gvCipTumu: "Tümü",
      gvCipAcil: "Acil",
      gvCipIhtiyac: "İhtiyaç",
      gvCipDestek: "Destek",
      gvListeGeri: "Mahallem",
      gvListeAlt: "{yer}'deki tüm görevler",
      gvListeSimdi: "Şimdi yapılması gerekenler",
      gvListeSimdiAlt: "Mahalledeki öncelikli görevler",
      gvListeDiger: "Diğer Görevler",
      gvListeDigerAlt: "Mahalledeki diğer görevler",
      gvListeAra: "Ara",
      gvDetayGeri: "Görevler",
      gvDetayAcilRozet: "Acil görev",
      gvDetayBenimRozet: "Görevim",
      gvDetayBittiRozet: "Tamamlandı",
      gvDetayBekliyor: "Müdahale bekliyor",
      gvDetayBekliyorAlt: "Bu görev için acil kontrol gerekiyor.",
      gvDetayUstlendi: "Bu görevi üstlendiniz!",
      gvDetayUstlendiAlt: "Teşekkürler, birlikte güçlüyüz.",
      gvDetayBittiBanner: "Bu görev tamamlandı. Emeğin için teşekkürler!",
      gvDetaySekmeDetay: "Detaylar",
      gvDetaySekmeKonum: "Konum",
      gvDetaySekmeYorum: "Yorumlar",
      gvDetaySonGorulme: "Son görülme",
      gvDetayKonum: "Konum",
      gvDetayDurum: "Durum",
      gvDetayBildiren: "Bildiren",
      gvDetayAciklama: "Açıklama",
      gvDetayHaritada: "Haritada Gör",
      gvDetayUstlen: "Görevi Üstlen",
      gvDetayPaylas: "Paylaş",
      gvDetayTamamla: "Tamamlandı işaretle",
      gvDetayFoto: "Fotoğraf Ekle",
      gvDetayGelisme: "Gelişme Fotoğrafları",
      gvDetayBittiTarih: "Bu görev {tarih} tarihinde tamamlandı",
      gvDetayYorumBos: "Henüz yorum yok.",
      gvDetayEkip: "Ekip ve adımlar",
      gvTumunuGor: "Tümünü Gör",
      gvRozetAcil: "Acil",
      gvRozetOncelikli: "Öncelikli",
      gvDugmeGor: "Görevi Gör",
      gvDugmeUstlen: "Görevi Üstlen",
      gvDugmeKontrol: "Kontrol Et",
      gvDugmeDestek: "Destek Ol",
      gvYakinBaslik: "Yakınımdaki Görevler",
      gvMahalleBaslik: "Mahalledeki Görevler",
      gvOneriBaslik: "Yapay Zeka Önerisi",
      gvOneriDugme: "Önerileri Gör",
      gvBenimBaslik: "Görevlerim",
      gvAktifSayi: "{n} Aktif",
      gvTamamSayi: "{n} Tamamlandı",
      gvBenimBos: "Henüz bir göreve katılmadın.",
      gvBos: "Şu an burada görev yok.",
      gvKonumGerekli: "Yakındaki görevler için konum izni gerekli.",
      gvKonumReddedildi: "Konum izni verilmedi. Ayarlardan açabilirsin.",
      gvDurumAcik: "Açık",
      gvDurumDevam: "Devam Ediyor",
      gvDurumTamam: "Tamamlandı",
      gvDurumIptal: "İptal",
      gvZamanBugun: "Katıldın · Bugün {saat}",
      gvZamanDun: "Katıldın · Dün {saat}",
      gvZamanTarih: "Katıldın · {tarih}",
      gvZamanBugunKisa: "Bugün {saat}",
      gvZamanDunKisa: "Dün {saat}",
      m5Mahallem: "Mahallem",
      m5Paylas: "Paylaş",
      m5GonulluOl: "Gönüllü Ol",
      m5HavaAcik: "Açık",
      m5HavaParcali: "Parçalı Bulutlu",
      m5HavaSisli: "Sisli",
      m5HavaYagmurlu: "Yağmurlu",
      m5HavaKarli: "Karlı",
      m5HavaFirtinali: "Fırtınalı",
      m5HavaBugun: "Bugün {min}° / {max}°",
      m5PanoKedi: "kayıtlı kedi",
      m5EgilimYeni: "yeni",
      m5EgilimBaslik: "Son 30 günde {son} yeni kayıt · önceki 30 günde {once}",
      m5HaritayiAc: "Haritayı Aç",
      baBaslik: "Bölge Analizi",
      baAltBaslik: "Mahallenin ihtiyaçlarını birlikte daha iyi anlayalım.",
      baAralikTumu: "Tümü",
      baAralik7: "7 Gün",
      baAralik30: "30 Gün",
      baAralik90: "3 Ay",
      baAralik365: "1 Yıl",
      baSayacKedi: "Toplam kedi",
      baSayacAcil: "Açık acil vaka",
      baSayacBeslenme: "Beslenme noktası",
      baSayacSu: "Su noktası",
      baSayacGonullu: "Gönüllü katılımı",
      baSayacKediYeni: "Yeni kedi",
      baSayacAcilYeni: "Yeni acil vaka",
      baSayacBeslenmeYeni: "Yeni beslenme noktası",
      baSayacSuYeni: "Yeni su noktası",
      baSayacGonulluYeni: "Yeni gönüllü katılımı",
      baSatirKedi: "{n} kedi",
      baSatirAcil: "{n} acil vaka",
      baSatirBeslenme: "{n} beslenme noktası",
      baSatirSu: "{n} su noktası",
      baSatirGonullu: "{n} gönüllü katılımı",
      baKatmanlar: "Katmanlar",
      baKatmanKedi: "Kediler",
      baKatmanAcil: "Acil vakalar",
      baKatmanBeslenme: "Beslenme noktaları",
      baKatmanSu: "Su noktaları",
      baKatmanGonullu: "Gönüllü görevleri",
      baKatmanIsi: "Isı haritası",
      baCercevele: "Haritayı bölgelere sığdır",
      baIlceNotu: "Kayıtların azında mahalle bilgisi var; bölgeler ilçe olarak gösteriliyor.",
      baHata: "Bölge analizi alınamadı. Sunucu güncellenince görünecek.",
      baHaritaBos: "Konumu olan bölge yok",
      baBolgeOzet: "Bölgelere Göre Özet",
      baTumBolgeler: "Tüm Bölgeleri Gör",
      baKademeNotu: "Sayılar bu aralıkta eklenenler; kademeler tüm zamana göre.",
      baDiger: "Diğer",
      baDigerAlt: "mahallesi bilinmeyen",
      baBolgeyiIncele: "Bölgeyi İncele",
      baBolgeYok: "Gösterilecek bölge yok.",
      baAiBaslik: "Yapay Zeka Bölge Analizi",
      baAiYok: "Bugünün yapay zeka özeti henüz hazır değil.",
      baAiDetay: "Detaylı Analizi Gör",
      baOncelikli: "Öncelikli Bölgeler",
      baNedenYok: "En düşük skor",
      baHaritadaGor: "Kedileri Haritada Gör",
      baHaritadaAlt: "Harita bu bölgelerin ortasında açılır.",
      baTumKediler: "Mahalledeki Tüm Kediler",
      baTumKedilerAlt: "Tüm kedileri liste olarak gör.",
      baKisaKedi: "Toplam Kedi",
      baKisaAcil: "Acil Vaka",
      baKisaBeslenme: "Beslenme",
      baKisaSu: "Su Noktası",
      baKisaGonullu: "Gönüllü",
      baKisaKediYeni: "Yeni Kedi",
      baKisaAcilYeni: "Yeni Acil",
      baKisaBeslenmeYeni: "Yeni Mama",
      baKisaSuYeni: "Yeni Su",
      baKisaGonulluYeni: "Katılım",
      baSatirKisaKedi: "{n} kedi",
      baSatirKisaAcil: "{n} acil",
      baSatirKisaBeslenme: "{n} mama",
      baSatirKisaSu: "{n} su",
      baSatirKisaGonullu: "{n} katılım",
      baIncele: "İncele",
      baAiDetayKisa: "Detaylı Analiz",
      baKademeNotuKisa: "· yeni kayıtlar",
      baIlceNotuKisa: "İlçe düzeyi",
      gdBaslik: "Günlük Detay",
      gdBugun: "Bugün",
      gdOncekiGun: "Önceki gün",
      gdSonrakiGun: "Sonraki gün",
      gdToplamBugun: "Bugün toplam {n} kayıt var.",
      gdToplamGun: "Bu günde toplam {n} kayıt var.",
      gdToplamYok: "Bu günde kayıt yok.",
      gdToplamKarsilastir: "Önceki gün: {once} · bu gün: {simdi}.",
      gdDagilim: "Aktivite Dağılımı",
      gdKisaKedi: "Yeni kedi",
      gdKisaAcil: "Acil",
      gdKisaKayip: "Kayıp",
      gdKisaBeslenme: "Beslenme",
      gdKisaSu: "Su noktası",
      gdKisaGonullu: "Gönüllü",
      gdBolge: "Bölgelere Göre Dağılım",
      gdKonumsuz: "Mahallesi bilinmeyen: {n}",
      gdKonumYok: "Konumlu kayıt yok",
      gdHaritadaGor: "Haritada Gör",
      gdFotoBugun: "Bugünden Fotoğraflar",
      gdFotoGun: "Günün Fotoğrafları",
      gdFotoYok: "Fotoğraf yok",
      gdFazla: "daha",
      gdKayitBugun: "Bugünün Kayıtları",
      gdKayitGun: "Günün Kayıtları",
      gdFiltreHepsi: "Tüm Kayıtlar",
      gdTumunuGoster: "Tümünü göster ({n})",
      gdKayitKedi: "Yeni kedi kaydı",
      gdKayitAcil: "Acil vaka",
      gdKayitKayip: "Kayıp kedi ilanı",
      gdKayitBeslenme: "Yeni beslenme noktası",
      gdKayitSu: "Yeni su noktası",
      gdKayitGonullu: "Gönüllü katılımı",
      gdKatilim: "{n} katılım",
      gdAltBaslik: "Mahallenizi haritada görün.",
      gdAltMetin: "Mahalleniz için birlikte daha güvenli bir yaşam.",
      gdHata: "Günlük detay alınamadı. Sunucu güncellenince görünecek.",
      gdPaylasMetni: "{yer} · {tarih}: sokak kedileri günlük detay.",
      gdGunuAc: "{tarih} günlük detayını aç",
      s7Baslik: "Son 7 Gün",
      s7PaylasMetni: "{yer}: sokak kedileri için son 7 gün.",
      s7GrafikBaslik: "Günlük yeni kayıtlar",
      s7Yeni: "yeni",
      s7Sabit: "değişmedi",
      s7SayacKedi: "Yeni kedi kaydı",
      s7SayacAcil: "Acil vaka",
      s7SayacKayip: "Kayıp kedi ilanı",
      s7SayacBeslenme: "Yeni beslenme noktası",
      s7SayacSu: "Yeni su noktası",
      s7SayacGonullu: "Gönüllü katılımı",
      s7Gunluk: "Günlük Detaylar",
      s7KartKotu: "Son 7 günde {seri} artış var.",
      s7KartIyi: "Son 7 günde {seri} artış var.",
      s7KartSabit: "Önceki 7 güne göre belirgin bir değişim yok.",
      s7KartOnceYok: "Önceki haftayla karşılaştırma sunucu güncellenince görünür.",
      s7KartKarsilastir: "Önceki 7 gün: {once} · bu hafta: {simdi}.",
      s7IstatistikDetay: "İstatistik Detayını Gör",
      s7SeriAcil: "acil vakalarda",
      s7SeriKayip: "kayıp ilanlarında",
      s7SeriKedi: "yeni kedi kayıtlarında",
      s7SeriBeslenme: "yeni beslenme noktalarında",
      s7SeriSu: "yeni su noktalarında",
      s7SeriGonullu: "gönüllü katılımında",
      s7Ac: "Son 7 günü aç",
      zekaGonullu: "Gönüllü desteği gerekli",
      azVeriKisa: "Az veri",
      heroYanSlogan: "Aynı mahalle, daha mutlu kediler.",
      haritaBaslikOzet: "Mahalle Haritası (Özet)",
      lejantAcil: "Acil vakalar",
      lejantKayip: "Kayıp kediler",
      lejantSu: "Su noktaları",
      lejantBeslenme: "Beslenme",
      lejantGonullu: "Gönüllüler",
      zekaSu: "Su noktaları yetersiz",
      zekaMama: "Mama noktası yetersiz",
      zekaAsi: "Aşı takibi zayıf",
      zekaYarali: "Yaralı kedi bildirildi",
      zekaAcil: "Açık acil vaka var",
      zekaBarinak: "Kayıtlı kedi evi yok",
      zekaTamam: "Belirgin eksik yok",
      yasBilinmiyorKisa: "Bilinmiyor",
      dagilimIpucuBilinmiyor: "Kedilerin {p} kadarının yaşı kayıtlı değil.",
      dagilimIpucuCogunluk: "En kalabalık grup: {grup} ({p}).",
      dagilimYakinda: "Sunucu güncellenince görünecek.",
      ustBaslik: "Mahallem",
      paylas: "Paylaş",
      paylasMetni: "{yer}: mahalledeki kediler, ihtiyaçlar ve gönüllüler.",
      paylasKopyalandi: "Bağlantı kopyalandı",
      paylasOlmadi: "Paylaşılamadı",
      sekmeGenel: "Genel Bakış",
      sekmeAnaliz: "Analiz",
      sekmeGorevler: "Görevler",
      sekmeKediler: "Kediler",
      sekmeGonulluler: "Gönüllüler",
      lejantKedi: "Kediler",
      zekaOzet: "Mahallendeki durumu analiz ettik. İşte öne çıkanlar:",
      azVeri: "Henüz az kayıt var — skor hızlı değişebilir.",
      detayliAnaliz: "Detaylı Analizi Gör",
      dagilimBaslik: "Mahalledeki Kedilerin Dağılımı",
      kediKisa: "kedi",
      yasYavru: "Yavru",
      yasYetiskin: "Yetişkin",
      yasYasli: "Yaşlı",
      yasBilinmiyor: "Yaşı bilinmiyor",
      dagilimYok: "Henüz kayıtlı kedi yok.",
      ihtiyacBaslik: "Öne Çıkan İhtiyaçlar",
      ihtiyacYok: "Şu an belirgin bir ihtiyaç yok.",
      oncelikKritik: "Kritik",
      oncelikYuksek: "Yüksek",
      oncelikOrta: "Orta",
      oncelikDusuk: "Düşük",
      son7Baslik: "Son 7 Gün",
      son7Yok: "Son 7 günde yeni kayıt yok.",
      seriKedi: "Kedi",
      seriAcil: "Acil",
      seriKayip: "Kayıp",
      seriBeslenme: "Beslenme",
      seriSu: "Su",
      ctaBaslik: "Birlikte daha güçlü bir mahalle.",
      ctaAlt: "Sen de destek ol, mahallendeki kedilerin hayatını değiştir.",
      ctaDugme: "Gönüllü Ol",
      zekaAlt: "Mahalledeki durumu analiz et, eksikleri gör, harekete geç.",
      zekaBaslik: "Yapay Zeka Analizi",
      panoBeslenme: "beslenme noktası",
      panoGonullu: "aktif gönüllü",
      hizliAcilMerkezi: "Acil Merkezi",
      onemliBaslik: "Yakınındaki önemli durumlar",
      rozetKayip: "KAYIP",
      rozetAcil: "ACİL",
      rozetMama: "MAMA",
      rozetTehlike: "TEHLİKE",
      rozetGonullu: "GÖNÜLLÜ",
      durumYok: "Yakınında şu an önemli bir durum yok.",
      sonFotograflar: "Son fotoğraflar",
      sonAktiviteler: "Son aktiviteler",
      tumuSayili: "Tümü ({n})",
      paylasimYok: "Mahallende henüz paylaşım yok.",
      yakinKediler: "Yakınındaki kediler",
      haritayiAc: "Haritayı aç",
      cipBesleniyor: "Mama noktası",
      cipSu: "Su noktası",
      cipAlisik: "İnsanlara alışık",
      cipBiliniyor: "Mahallede biliniyor",
      haritaEnYakin: "{n} kediden en yakın {k}'ı",
      panoBaslik: "Bugün Mahallende",
      panoAlt: "Daha fazla kedi, daha iyi bir mahalle.",
      panoKedi: "kedi takipte",
      panoAcil: "acil vaka",
      panoKayip: "kayıp kedi",
      panoSu: "su noktası",
      mahallemiGor: "Mahallemi gör",
      hizliBaslik: "Hızlı İşlemler",
      hizliKaydet: "Kedi bildir",
      hizliGordum: "Kedi gördüm",
      hizliAcil: "Acil bildir",
      hizliMamaSu: "Mama / Su ekle",
      title: "Benim Mahallem",
      bolgelerBaslik: "En çok ihtiyacı olan bölgeler",
      heroKedi: "Mahallende {n} kedi kayıtlı.",
      heroYeniKedi: "Son 30 günde {n} yeni kedi kaydedildi.",
      heroYardimBekleyen: "Mahallende {n} kedi yardım bekliyor.",
      enOnemli: "En önemli ihtiyaç:",
      iSu: "Su desteği",
      iMama: "Mama desteği",
      iAsi: "Aşı takibi",
      iYarali: "Yaralı kedi",
      iAcil: "Acil vaka",
      iBarinak: "Barınak",
      iTamam: "Belirgin eksik yok",
      haritaAlt: "Kedileri ve ihtiyaç bölgelerini keşfet.",
      tamEkran: "Tam ekran",
      lYuksek: "Yüksek ihtiyaç",
      lOrta: "Orta",
      lYeterli: "Yeterli",
      lMama: "Mama noktası",
      lSu: "Su noktası",
      tumu: "Tümü",
      tumGorevler: "Tüm görevler",
      ilerlemeTamam: "{n}/{m} tamamlandı",
      kayipRozet: "KAYIP",
      kayipAdsiz: "Kayıp kedi",
      kayipYok: "Yakınında kayıp ilanı yok.",
      azOnce: "az önce",
      saatOnce: "{n} sa önce",
      gunOnce: "{n} gün önce",
      son30: "Son 30 gün",
      sAcil: "Acil vaka",
      sMama: "Mama noktası",
      sSu: "Su noktası",
      sGonullu: "Aktif gönüllü",
      aiBaslik: "AI Mahalle Analizi",
      beta: "BETA",
      aiGor: "AI önerilerini gör",
      haritaBaslik: "Mahalle Haritası",
      kayipBaslik: "Yakınındaki kayıp kediler",
      yardimBaslik: "Mahallene yardım et",
      gorevYok: "Şu an açık görev yok.",
      gorevKatil: "Katıl",
      ilerleme: "{n}/{m} gönüllü",
      skorBaslik: "Mahalle Skoru",
      skorYaklasik: "değişim yaklaşık",
      olculmedi: "—",
      yukleniyor: "Yükleniyor…",
      hata: "Mahalle raporu yüklenemedi.",
      mahalleYok: "Bu sayfayı görmek için ev mahalleni seç.",
      secBaslik: "Mahalleni seç",
      secCumle: "Mahalleni seçince buradaki kediler, ihtiyaçlar ve görevler senin mahallene göre gelir.",
      secKaydet: "Kaydet",
      vazgec: "Vazgeç",
      secHata: "Mahalle kaydedilemedi.",
      tekrarDene: "Tekrar dene",
    },
    bolge: {
      title: "Bölge detayı",
      bolgesi: "{n} Bölgesi",
      tumunuGor: "Tümünü gör",
      detayliAnaliz: "Detaylı analiz",
      tumu: "Tümü",
      gecenAy: "Geçen aya göre {n}",
      yaklasik: "yaklaşık",
      kediAdet: "{n} kedi",
      geri: "Geri",
      kapat: "Kapat",
      son30: "Son 30 gün",
      iKritik: "Kritik ihtiyaç",
      iYuksek: "Yüksek ihtiyaç",
      iOrta: "Orta ihtiyaç",
      iDusuk: "Düşük ihtiyaç",
      sKedi: "Kayıtlı kedi",
      sMama: "Mama noktası",
      sSu: "Su noktası",
      yKuzey: "Kuzey",
      yGuney: "Güney",
      yDogu: "Doğu",
      yBati: "Batı",
      aiBaslik: "AI Bölge Analizi",
      beta: "BETA",
      kedilerBaslik: "Bölgedeki kediler",
      kediYok: "Bu bölgede kayıtlı kedi yok.",
      skorBaslik: "Mahalle Skoru",
      skorYaklasik: "değişim yaklaşık",
      dagilimBaslik: "Bölgesel Dağılım",
      dagilimAdet: "{n} kedi",
      bildirimBaslik: "Son Bildirimler",
      bildirimYok: "Son 30 günde bildirim yok.",
      olculmedi: "—",
      olcumYok: "Bu bölge için henüz günlük ölçüm birikmedi.",
      hata: "Bölge raporu yüklenemedi.",
    },
    mahalleListesi: {
      title: "Mahalleler",
      ara: "Mahalle ara",
      mahalle: "mahalle",
      paylasim: "paylaşım",
      kedi: "kedi",
      alinamadi: "Mahalle listesi alınamadı.",
      tekrarDene: "Tekrar dene",
      aramaBos: "Bu aramaya uyan mahalle yok.",
      hicYok: "Henüz gösterilecek mahalle yok.",
      kuralNotu: "Bu listede paylaşımı, kapsama alanı ya da kayıtlı hayvanı olan mahalleler görünür.",
    },
    aiAnaliz: {
      sekmeOzet: "Özet",
      sekmeDetay: "Detaylı Veriler",
      sekmeOneriler: "Öneriler",
      sonNGun: "Son {n} gün",
      paylas: "Paylaş",
      paylasMetni: "{yer}: sokak kedileri için mahalle analizi.",
      paylasKopyalandi: "Bağlantı kopyalandı",
      paylasOlmadi: "Paylaşılamadı",
      detayBaslik: "Alan bazında skorlar",
      veriYok: "Henüz ölçüm yok.",
      skorEtiketi: "Mahalle Sağlık Skoru",
      genelDurum: "Genel Durum",
      genelSkor: "Mahalledeki imkânlar \"{etiket}\" düzeyinde.",
      genelKediArtti: "30 günde {n} kedi daha kaydedildi.",
      genelKediAzaldi: "Kayıtlı kedi 30 gün öncesine göre {n} az.",
      genelKediSabit: "Kayıtlı kedi sayısı 30 günde değişmedi.",
      genelBulgu: "{n} eksik tespit edildi.",
      genelBulguYok: "Ölçülen alanlarda belirgin eksik yok.",
      farkRozeti: "Geçen aya göre {d} puan",
      aiOzeti: "AI Özeti",
      ozetSablon: "Özet",
      tumu: "Tümü",
      bulgularBaslik: "Tespit Edilen Önemli Bulgular",
      bulguAcil: "Açık acil vakalar",
      bulguYarali: "Yaralı kedi bildirimleri",
      bulguMama: "Beslenme noktası yetersiz",
      bulguSu: "Su noktası yetersiz",
      bulguAsi: "Aşı takibi zayıf",
      bulguBarinak: "Kedi evi yok",
      bulguGonullu: "Gönüllü desteği gerekiyor",
      eylemHaritada: "Haritada incele",
      eylemGorev: "Görev oluştur",
      eylemDetaylar: "Detayları gör",
      eylemGonullu: "Gönüllü çağır",
      eylemVakalar: "Vakaları gör",
      son7AcilEk: "Son 7 günde {n} yeni.",
      bulguYok: "Ölçülen alanlarda belirgin eksik yok.",
      haritadaGorBag: "Harita üzerinde gör",
      bolgeBaslik: "Bölge Bazlı Analiz",
      kademeMukemmel: "Mükemmel",
      kademeIyi: "İyi",
      kademeGelistir: "Geliştirilmeli",
      kademeKritik: "Kritik",
      ilceDuzeyiNotu: "İlçe düzeyi",
      notSu: "Su noktası az",
      notMama: "Beslenme eksik",
      notAsi: "Aşı takibi",
      notYarali: "Yaralı bildirimi",
      notAcil: "Açık acil",
      notBarinak: "Kedi evi yok",
      notGonullu: "Gönüllü gerekli",
      notTamam: "Genel durum iyi",
      bolgeYok: "Bölge skorları yeterli konum verisi birikince görünür.",
      aralikSec: "Aralık seç",
      trendBaslik: "Trend Analizi",
      seriKedi: "Kedi",
      seriAcil: "Acil",
      seriKayip: "Kayıp",
      seriBeslenme: "Beslenme",
      seriSu: "Su",
      trendBirikiyor: "Günlük ölçüm birikiyor; grafik iki gün sonra görünür.",
      oneriBaslik: "AI Önerisi",
      bakilanBaslik: "Bu analiz nelere baktı",
      bakilanKedi: "Kayıtlı kedi",
      bakilanSu: "Su noktası",
      bakilanMama: "Mama noktası",
      bakilanKulube: "Kedi evi",
      bakilanAcil: "Acil durumdaki kedi",
      bakilanYarali: "Yaralı bildirimi (30 gün)",
      oneriAlinamadi: "Öneriler alınamadı — sunucu cevap vermedi. Bu, eksik yok demek DEĞİL.",
      tekrarDene: "Tekrar dene",
      acikGorevNotu: "Aynı türde açık bir görev varken o öneri burada çıkmaz.",
      sayacKedi: "kedi",
      sayacSu: "su noktası",
      sayacMama: "mama noktası",
      sayacKulube: "kedi evi",
      sayacYarali: "yaralı bildirimi (30 gün)",
      sekmeBolge: "Bölge Analizi",
      sekmeKedi: "Kedi Tanıma",
      ozetBaslikPano: "Genel değerlendirme",
      ozetCumle: "{yer}'de mahalle durumu {durum}. {n} öncelikli eksik bulundu.",
      durumBilinmiyor: "ölçülmedi",
      yerYok: "Mahalle seçilmedi",
      balon: "Daha iyi bir mahalle mümkün!",
      eksiklerBaslik: "Öncelikli eksikler",
      eksikSu: "Su noktaları yetersiz",
      eksikMama: "Beslenme noktaları yetersiz",
      eksikYarali: "Yaralı kedi bildirimleri",
      eksikKulube: "Kayıtlı kedi evi yok",
      eksikVet: "Acil bakım gereken kediler",
      bugunBaslik: "Bugün yapabileceğin 3 şey",
      eylemSu: "Su noktası ekle",
      eylemMama: "Beslenme noktası kur",
      eylemYarali: "Yaralı kediyi kontrol et",
      eylemKulube: "Kedi evi yap",
      eylemVet: "Veteriner desteği al",
      haritadaGor: "Haritada gör",
      eylemDetay: "Detayları gör",
      eylemListe: "Listeyi gör",
      yogunBolge: "Mahallede kedilerin en yoğun olduğu bölge",
      gorevOlustur: "Görev oluştur",
      etkiOnEk: "Beklenen etki:",
      eksikYok: "Şu an öncelikli bir eksik görünmüyor.",
      title: "Yapay Zeka Analizi",
      beta: "BETA",
      sekmeTani: "Fotoğraf ile Tanı",
      sekmeSonuc: "Analiz Sonuçları",
      sekmeIstatistik: "İstatistikler",
      taniBaslik: "Bir fotoğrafla kediyi tanı",
      taniAciklama: "Fotoğrafı yükle; kayıtlı kedilerle ve açık kayıp ilanlarıyla karşılaştırılır.",
      taniGit: "Fotoğrafla tanımaya git",
      sonAnalizler: "Son analizler",
      analizYok: "Henüz analiz yapmadın.",
      olcumBaslamadi: "Analiz geçmişi yeni tutulmaya başladı; öncesi kayıtlı değil.",
      eslesti: "Eşleşti",
      eslesmedi: "Eşleşmedi",
      adsizIlan: "Adı belirtilmemiş",
      ozetBaslik: "Bu bölgede analiz özeti",
      sAnaliz: "Analiz",
      sEslesme: "Eşleşme",
      sYeniKedi: "Yeni kedi",
      oran: "{n}/{m} analiz eşikte kaldı",
      oranNotu: "Bu bir doğruluk oranı değil: eşleşenlerin kaçının doğru olduğu ölçülmedi.",
      son30: "Son 30 gün",
      yukleniyor: "Yükleniyor…",
      hata: "Analizler yüklenemedi.",
    },
    kayipFormu: {
      baslik: "Kedi hakkında",
      adEtiket: "Adı",
      adYerTutucu: "örn. Boncuk",
      adIpucu: "Komşuların seslenerek arayacağı ad — her şeyden çok işe yarar.",
      kisirEtiket: "Kısır",
      kEvet: "Evet",
      kHayir: "Hayır",
      cinsiyetEtiket: "Cinsiyet",
      cErkek: "Erkek",
      cDisi: "Dişi",
      cBilinmiyor: "Bilmiyorum",
      yasEtiket: "Yaş",
      yasIpucu: "örn. 1,5 yaşında",
      mizacEtiket: "Mizaç",
      mUrkek: "Ürkek",
      mSokulgan: "Sokulgan",
      mSaldirgan: "Saldırgan",
      mSakin: "Sakin",
      iletisimEtiket: "İletişim (isteğe bağlı)",
      iletisimIpucu: "Yalnızca Ara düğmesinin arkasında ve girişli kişilere gösterilir. Açıklamaya yazma.",
      notNumaraUyarisi: "Açıklamada telefon numarası var gibi. Onu iletişim alanına taşı — böylece açıkta görünmez.",
    },
    kayipBildir: {
      title: "Kayıp Kedi Bildirimi",
      subtitle: "Fotoğrafları ve kedinin tarifini ekle. Konum bir sonraki adımda.",
      fotoZorunlu: "En az bir fotoğraf gerekli — bulan kişinin karşılaştırabileceği tek şey o.",
      varsa: "varsa",
      renkEtiket: "Rengi",
      konumTitle: "Son görüldüğü yer ve zaman",
      konumSubtitle: "Haritada noktayı işaretle. Olmadan ilanın yakındakilere ulaşamaz.",
      konumumuKullan: "Mevcut konumumu kullan",
      konumAliniyor: "Konum alınıyor…",
      konumZorunlu: "Konum zorunlu. GPS açılmıyorsa haritaya dokunarak yeri işaretle.",
      konumIsaretlendi: "Konum işaretlendi",
      konumYok: "Konum yok",
      yaricapEtiket: "Arama yarıçapı",
      yaricapIpucu: "Kayıp ev kedisi ilk günler çoğunlukla birkaç yüz metre içinde saklanır.",
      sonGorulmeEtiket: "Son görüldüğü",
      ekEtiket: "Ek bilgi",
      ekYerTutucu: "İşe yarayacak her şey: bir alışkanlığı, genelde saklandığı yer, güvendiği kişi…",
      mahalleEtiket: "Mahalle",
      duzenle: "Düzenle",
      ozetAdsiz: "Adı yazılmamış kedi",
      yayinla: "Kayıp ilanını yayınla",
      yayinlaniyor: "Yayınlanıyor…",
      errSend: "İlan yayınlanamadı. Tekrar dene.",
      okTitle: "İlanın yayında",
      okDesc: "Yakındakilere haber gitti. Her görülme sana ulaşacak.",
      okView: "İlanımı aç",
      okList: "Tüm kayıp kediler",
      acikIlanBaslik: "Açık bir ilanın var",
      acikIlanMetin: "İkinci bir ilan arama gücünü böler. Bu aynı kedi mi?",
      acikIlanGit: "O ilanı aç",
      acikIlanBaska: "Hayır, bu başka bir kedi",
      vazgec: "Vazgeç",
      renkYerTutucu: "örn. tekir, göğsü beyaz",
      ayirtEtiket: "Ayırt edici özellikler",
      ayirtYerTutucu: "Tasma, yara izi, kulak çentiği, yürüyüşü…",
    },
    kayipListe: {
      odul: "ödül",
      baslik: "Kayıp Kediler",
      rozet: "KAYIP",
      ilanAc: "Kayıp ilanı ver",
      fotoAra: "Fotoğrafla ara",
      yukleniyor: "Yükleniyor…",
      yeniden: "Tekrar dene",
      hata: "İlanlar yüklenemedi.",
      bos: "Şu an açık kayıp ilanı yok — iyi haber.",
      yakindaYok: "Yakınında ilan yok, daha uzaktakiler gösteriliyor.",
    },
    acilDetay: {
      durumBaslik: "Kedinin durumu",
      aciklamaBaslik: "Açıklama",
      bildirenBaslik: "Bildiren",
      bildirdi: "{n} bildirdi",
      konumBaslik: "Konum",
      haritadaAc: "Haritada aç",
      paylas: "Paylaş",
      sil: "Bildirimi sil",
      silOnayBaslik: "Bu bildirimi silmek istiyor musun?",
      silOnayMetin: "Listeden ve haritadan kaybolur. Geri alınamaz.",
      silEvet: "Evet, sil",
      vazgec: "Vazgeç",
      siliniyor: "Siliniyor…",
      silHata: "Bildirim silinemedi.",
      silYetki: "Yalnızca bildirimi yapan kişi silebilir.",
      fotoSayaci: "{a} / {b}",
      yorumlar: "Yorumlar",
      yorumYok: "Henüz yorum yok — komşular burada koordine olur.",
      yorumYaz: "Yorum yaz…",
      yorumGonder: "Gönder",
      yorumGidiyor: "Gönderiliyor…",
      yorumHata: "Yorum gönderilemedi.",
      yorumOkunmadi: "Yorumlar yüklenemedi.",
      yorumGiris: "Yorum yazmak için giriş yap.",
      yorumSil: "Yorumu sil",
      yorumSilHata: "Yorum silinemedi.",
      begen: "Destekle",
      begenHata: "İşlem tamamlanamadı.",
    },
    anasayfaAlt: {
      baslik: "Sokak Kedileri hakkında",
      hakkinda: "Hakkımızda",
      yardim: "Yardım",
      gizlilik: "Gizlilik",
      sartlar: "Kullanım Şartları",
      telif: "Sokak kedileri için sevgiyle yapıldı.",
    },
    ihbarFormu: {
      title: "İhbar ekle",
      sub: "Kediyi nerede gördün?",
      locTitle: "Konum",
      locUse: "Mevcut konumumu kullan",
      locPicked: "Konum işaretlendi",
      locHelp: "İğneyi taşımak için haritaya dokun.",
      locNeeded: "Konum zorunlu — konumsuz bir ihbar haritaya çizilemez.",
      noteTitle: "Ne gördün?",
      notePh: "Beyaz arabanın altında, çok ürkekti. Parka doğru koştu.",
      photoTitle: "Fotoğraf (isteğe bağlı)",
      photoAdd: "Fotoğraf ekle",
      photoRemove: "Fotoğrafı kaldır",
      photoHint: "Fotoğraf, ihbarını kanıta çevirir — AI onu karşılaştırabilir.",
      guvenTitle: "Ne kadar eminsin?",
      gBenzer: "Benzer olabilir",
      gEminDegil: "Emin değil",
      gFoto: "Fotoğraf eklendi",
      guvenFotoNote: "Fotoğrafla birlikte ihbarın “Fotoğraf eklendi” olarak işaretlenir.",
      send: "İhbarı gönder",
      sending: "Gönderiliyor…",
      okTitle: "İhbar gönderildi 🧡",
      okBody: "İlan sahibine haber verildi. Teşekkürler.",
      okBack: "Arama alanına dön",
      errSend: "İhbar gönderilemedi. Lütfen tekrar dene.",
      back: "Geri",
    },
    aiKars: {
      title: "AI Karşılaştırma",
      seenSimilar: "Benzer kedi mi gördün?",
      seenSimilarDesc: "Bir kedi gördüysen fotoğrafını çek veya galeriden seç. Yapay zeka kayıp kediyle karşılaştırsın.",
      camera: "Kamera",
      gallery: "Galeri",
      aiNote: "AI yardımcıdır. Son kararı sen ver.",
      resultTitle: "Karşılaştırma sonucu",
      new: "Yeni",
      yourPhoto: "Senin fotoğrafın",
      lostCat: "Kayıp kedi",
      levelHigh: "Yüksek benzerlik",
      levelMid: "Kısmen benziyor",
      levelLow: "Düşük benzerlik",
      levelHighDesc: "Bu kedi kayıp kediye oldukça benziyor.",
      levelMidDesc: "Bazı özellikler tutuyor, bazıları tutmuyor.",
      levelLowDesc: "Özellikler büyük ölçüde tutmuyor.",
      fPattern: "Tüy deseni",
      fMarks: "Ayırt edici işaretler",
      fVisual: "Genel görünüm",
      fAge: "Yaş aralığı",
      fLocation: "Konum yakınlığı",
      stateSimilar: "benziyor",
      statePartial: "kısmen benziyor",
      stateDifferent: "farklı",
      stateUnmeasured: "ölçülemedi",
      report: "Bu kediyi ihbar et",
      unsureTitle: "Emin değil misin?",
      unsureDesc: "Yine de ihbar edebilirsin. Her ihbar değerlidir.",
      tipsTitle: "Daha iyi sonuç için ipuçları",
      tip1: "Kedinin yüzünü net çek",
      tip2: "Mümkünse farklı açılardan fotoğraf çek",
      tip3: "İyi ışıkta çekim yap",
      tip4: "Çevresini de göster",
      comparing: "Karşılaştırılıyor…",
      errCompare: "Karşılaştırma yapılamadı. Tekrar dene.",
      notMeasuredNote: "Yapay zekanın ölçemediği özellikler böyle işaretlenir — ölçülememek, uymamak demek değildir.",
    },
    acil: {
      merkezHeroNot: "Her kedi önemli",
      merkezBaslik: "Acil Merkezi",
      merkezHeroBaslik: "Birlikte hayat kurtaralım",
      merkezHeroAlt: "Yaralı, tehlikede veya yardıma muhtaç kediler için birlikte hareket edelim.",
      merkezVakalariGor: "Acil vakaları gör",
      merkezVakalariGorAlt: "Şu anda yardım bekleyen kedileri incele.",
      merkezVakaBildir: "Acil vaka bildir",
      merkezVakaBildirAlt: "Yeni bir acil durum oluştur.",
      merkezYakinVakalar: "Yakınımdaki acil vakalar",
      merkezKategoriler: "Acil vaka kategorileri",
      merkezKatYarali: "Yaralı kediler",
      merkezKatTehlike: "Tehlikeli bölgeler",
      merkezKatKayip: "Kayıp kediler",
      merkezKatMamaSu: "Mama / Su",
      merkezKatGonullu: "Gönüllü desteği",
      merkezAltBaslik: "Her ihbar bir hayat kurtarabilir.",
      merkezAltMetin: "Şüpheli durumları da bildirerek kedilerin daha güvenli mahallelerde yaşamasına yardımcı olun.",
      merkezVakaYok: "Yakınında şu an açık acil vaka yok.",
      title: "Acil Bildir",
      subtitle: "Yardıma muhtaç bir kedi mi gördün? Hızlıca bildir, en yakın gönüllülere ulaşalım.",
      safetyNote: "Acil durumlarda lütfen kendi güvenliğini önceliklendir. Kendini tehlikeye atma.",
      stepInfo: "Bilgi",
      stepLocation: "Konum",
      stepSend: "Gönder",
      catInjured: "Yaralı kedi",
      catInjuredDesc: "Yaralı, hasta, acı çeken kedi",
      catLost: "Kayıp kedi bildir",
      catLostDesc: "Kedin kayboldu — arama ilanı aç",
      catFood: "Mama / Su ihtiyacı",
      catFoodDesc: "Beslenme noktası yok veya desteğe ihtiyaç var",
      catDanger: "Tehlikeli bölge",
      catDangerDesc: "İnşaat, trafik, zehirlenme riski vb.",
      catVolunteer: "Gönüllü desteği",
      catVolunteerDesc: "Taşıma, geçici sahiplenme, yerinde yardım gerektiğinde",
      injuredTitle: "Yaralı Kedi Bildirimi",
      injuredDesc: "Gördüğün kedinin durumunu paylaş. En kısa sürede gönüllülere haber verelim.",
      injuredPlaceholder: "Kedinin durumu, davranışı, yaralanması…",
      severityLabel: "Acil durum seviyesi",
      severityHigh: "Acil",
      severityMid: "Orta",
      severityLow: "Düşük",
      foodTitle: "Mama / Su İhtiyacı",
      foodDesc: "Bu bölgede mama ya da su ihtiyacı olan kediler için bildirim oluştur.",
      needLabel: "İhtiyaç türü",
      needFood: "Mama",
      needWater: "Su",
      needBoth: "İkisi de",
      foodPlaceholder: "Örn. Bu bölgede sokak kedileri için mama desteğine ihtiyaç var…",
      dangerTitle: "Tehlikeli Bölge",
      dangerDesc: "Kediler için risk oluşturan bir bölge mi var? Diğer hayvanseverler de haberdar olsun.",
      dangerTypeLabel: "Tehlike türü",
      multiSelect: "Birden fazla seçilebilir",
      dTraffic: "Trafik riski",
      dConstruction: "İnşaat / yıkım",
      dPoison: "Zehirlenme riski",
      dViolence: "Şiddet / kötü muamele",
      dWell: "Kuyu / kapalı alan",
      dOther: "Diğer",
      dangerPlaceholder: "Bölge hakkında detay ekle…",
      volunteerTitle: "Gönüllü Desteği",
      volunteerDesc: "Bu kedi için gönüllü desteğine mi ihtiyaç var? Talebini paylaş, yakınındaki gönüllüler haberdar olsun.",
      supportLabel: "Destek türü",
      sTransport: "Taşıma desteği",
      sFoster: "Geçici sahiplenme",
      sOnSite: "Yerinde yardım",
      sVet: "Veteriner desteği",
      sSupplies: "Malzeme desteği",
      sOther: "Diğer",
      volunteerPlaceholder: "Ne tür bir desteğe ihtiyaç var?",
      photoLabel: "Fotoğraf ekle",
      optional: "(opsiyonel)",
      photoTake: "Kamera ile çek",
      photoPick: "Galeriden seç",
      photoMax: "En fazla {n} fotoğraf",
      descLabel: "Açıklama",
      minChars: "En az {n} karakter",
      continue: "Devam et",
      back: "Geri",
      locTitle: "Konum Seç",
      locDesc: "Kedinin bulunduğu konumu haritada işaretle veya mevcut konumunu kullan.",
      locUseCurrent: "Mevcut konumumu kullan",
      locAddress: "Adres (opsiyonel)",
      locNeighborhood: "Mahalle",
      locNeighborhoodHint: "örn. Moda, Kadıköy",
      locUnsupported: "Bu tarayıcı konumu desteklemiyor.",
      locDenied: "Konum izni verilmedi. Mahalle adını el ile yazabilirsin.",
      locFailed: "Konum alınamadı. Haritaya dokun ya da mahalleyi yaz.",
      okNotified: "gönüllüye haber gitti",
      errSend: "Bildirim gönderilemedi. Tekrar dene.",
      errLogin: "Göndermek için giriş yap. Formun korunuyor.",
      errNetwork: "Bağlantı yok. Ağını kontrol edip tekrar dene.",
      errList: "Liste yüklenemedi.",
      retry: "Tekrar dene",
      detayGiris: "Bu bildirimi görmek için giriş yap.",
      detayYok: "Bu bildirim artık yok.",
      detayKonumsuz: "Bu bildirimde konum kayıtlı değil.",
      detayBildiren: "Bildiren: {ad}",
      detaySilmeYok: "Kendi bildirimini silme henüz eklenmedi.",
      locRequired: "Konum gerekli — yardıma gidenler yeri bulabilsin.",
      reviewTitle: "Bildirimi Gözden Geçir",
      reviewDesc: "Her şey doğru mu? Göndermeden önce kontrol et.",
      rType: "Tür",
      rLocation: "Konum",
      rPhotos: "Fotoğraflar ({n})",
      submit: "Bildirimi gönder",
      okTitle: "Bildirim gönderildi!",
      okDesc: "Teşekkürler! En yakın gönüllülere haber verildi. Durumu takip edebilirsin.",
      okMap: "Haritada gör",
      okList: "Acil vakaları görüntüle",
      okAgain: "Başka bir bildirim yap",
      okAvgLabel: "Ortalama yanıt süresi",
      okAvgMinutes: "{n} dakika",
      listTitle: "Acil Vakalar",
      fAll: "Tümü",
      fInjured: "Yaralı",
      fFood: "Mama / Su",
      fDanger: "Tehlike",
      fVolunteer: "Gönüllü",
      badgeHigh: "ACİL",
      badgeMid: "ORTA",
      badgeLow: "DÜŞÜK",
      badgeNew: "YENİ",
      listEmpty: "Yakınında açık acil vaka yok — iyi haber.",
      errNoCategory: "Önce ne bildirdiğini seç.",
      sending: "Gönderiliyor…",
      kaydet: "Kaydet",
      kaydedildi: "Kaydedildi",
      kaydettiklerim: "Kaydettiklerim",
      kaydetHata: "İşlem tamamlanamadı.",
      kaydetBos: "Henüz kaydettiğin vaka yok. Takip etmek için kalbe dokun.",
      kaydetGiris: "Vakaları kaydetmek için giriş yap.",
      yakinFiltre: "{n} km çevrendeki vakalar gösteriliyor.",
      tumunuGoster: "Tümünü göster",
      yakiniGoster: "Yalnızca yakındakiler",
      tumVakalar: "Tüm açık vakalar gösteriliyor.",
    },
    footer: {
      discover: "Keşfet",
      community: "Topluluk",
      connect: "İletişim",
      brandDescription:
        "Sokak kedilerinin güvenli, sağlıklı ve sevgi dolu bir yaşama kavuşması için gönüllüleri, bakım noktalarını ve acil destekleri bir araya getiriyoruz.",
    },
    assistant: {
      name: "Pati Asistan",
      readyStatus: "Hazır · önce ücretsiz",
      napStatus: "Yarına dek kestiriyor 😴",
      placeholder: "Sokak kedileri hakkında sor…",
      napHint: "Kestiriyorum — yarın görüşürüz!",
      suggestionsTitle: "Bunlardan birini dene:",
      disclaimer: "YZ tavsiye · veterinerin yerini tutmaz",
      networkError: "Şu an internet biraz huysuz 🐾 birazdan tekrar dener misin?",
      guestPrompt: "Merhaba! Seninle sohbet etmeyi çok isterim ama önce seni tanımam gerek. Giriş yap veya katıl — ücretsiz, birkaç saniye sürer.",
      guestLoginCta: "Sohbet için giriş yap",
    },
    nav: {
      home: "Anasayfa",
      database: "Kedi Veritabanı",
      identify: "Yapay Zeka Tanı",
      identifyShort: "YZ Tanı",
      map: "Harita",
      sightings: "Acil Bildir",
      emergencies: "Acil Bildir",
      volunteers: "Gönüllüler",
      feeding: "Besleme Noktaları",
      leaderboard: "Sıralama",
      photoContest: "Foto Yarışması",
      badges: "Rozetler",
      safety: "Güvenlik",
      observationHouse: "Gözlem Evi",
      neighborhood: "Benim Mahallem",
      messages: "Mesajlar",
      notifications: "Bildirimler",
      dashboard: "Panel",
      profile: "Profil",
      login: "Giriş yap",
      register: "Katıl",
      logout: "Çıkış yap",
      addCat: "Kedi ekle",
      admin: "Admin",
      adminEmergencies: "Acil Kuyruğu",
    },
    landing: {
      overline: "İstanbul · Sokak Hayvanları",
      heroTitle: "Her sokak kedisinin bir hikayesi var.",
      heroSubtitle:
        "Tanı, takip et ve korunmalarına destek ol.",
      liveBadge: "Topluluk aktif",
      ctaPrimary: "Bir kediyi kaydet",
      ctaSecondary: "Kedi Veritabanı",
      statCats: "Kayıtlı kedi",
      statSightings: "Bildirilen acil vaka",
      statFeeding: "Besleme noktası",
      statVaccinated: "Aşılı",
      featuresOverline: "Neler yapabilirsin",
      f1Title: "Yapay zeka ile foto tanıma",
      f1Desc:
        "Bir foto yükle; yapay zeka tüyü, cinsi, yaşı ve ayırt edici özellikleri yazsın, ardından yerel veritabanıyla eşleştirsin.",
      f2Title: "Mahalle haritası",
      f2Desc:
        "İstanbul genelinde kedileri, acil vakaları ve besleme noktalarını işaretle. İlçeye, renge, sağlık durumuna göre filtrele.",
      f3Title: "Sağlık ve aşılar",
      f3Desc:
        "Kuduz, FVRCP, kısırlaştırma ve notları takip et. Gönüllüleri ve veterinerleri gerçek kayıtlarla destekle.",
      f4Title: "Acil vaka bildirimi",
      f4Desc:
        "Yardıma muhtaç bir kedi mi gördün? Tek bir konum iğnesi — ya da foto ve konum birlikte — yakındaki gönüllüleri ve veterinerleri 30 saniyede uyarır.",
      finalCta: "Mahalleninin kedilerine bugün yardım et.",
      finalCtaSub: "Başlamak için tek bir foto yeterli.",
    },
    auth: {
      loginTitle: "Tekrar hoş geldin",
      registerTitle: "Hesabını oluştur",
      registerSuccess: "Hesabın oluşturuldu!",
      email: "E-posta",
      password: "Parola",
      name: "Ad Soyad",
      role: "Hesap türü",
      rolePublic: "Halk — Gez ve acil vaka bildir",
      roleVolunteer: "Gönüllü — Kedileri ve besleme noktalarını yönet",
      roleAdmin: "Yönetici — Tam erişim",
      volunteerType: "Gönüllü uzmanlığı",
      volunteerTypeVet: "Gönüllü veteriner — yaralı sokak kedilerini tedavi et",
      volunteerTypeIndividual: "Gönüllü birey — besle, kolla, kurtar",
      submitLogin: "Giriş yap",
      submitRegister: "Hesap oluştur",
      switchToRegister: "Hesabın yok mu? Katıl",
      switchToLogin: "Hesabın var mı? Giriş yap",
      forgotPassword: "Şifreni mi unuttun?",
      forgotLink: "Şifremi unuttum",
      backToLogin: "Giriş ekranına dön",
      forgotTitle: "Şifreni sıfırla",
      forgotIntro: "E-posta adresini gir, sana yeni şifre belirleme bağlantısı gönderelim.",
      forgotSubmit: "Sıfırlama bağlantısı gönder",
      forgotSentBody: "Bu e-postaya kayıtlı bir hesap varsa, sıfırlama bağlantısı yola çıktı. Gelen kutunu (ve spam klasörünü) kontrol et.",
      forgotDevTitle: "Geliştirici modu",
      forgotDevBody: "E-posta servisi henüz yapılandırılmadı — devam etmek için aşağıdaki bağlantıyı kullan:",
      newPassword: "Yeni şifre",
      confirmPassword: "Şifreyi tekrar gir",
      resetTitle: "Yeni şifre belirle",
      resetIntro: "Hatırlayabileceğin bir şey seç — en az 8 karakter.",
      resetSubmit: "Şifreyi güncelle",
      resetSuccess: "Şifre güncellendi. Şimdi giriş yapabilirsin.",
      resetFailed: "Bir şeyler ters gitti — yeni bir sıfırlama bağlantısı iste.",
      resetMinLen: "Şifre en az 8 karakter olmalı.",
      resetMismatch: "Şifreler eşleşmiyor.",
      resetNoToken: "Bu bağlantıda token eksik. Yeniden sıfırlama maili iste.",
      invalid: "Geçersiz bilgiler",
      emailTaken: "Bu e-posta zaten kayıtlı",
    },
    cats: {
      title: "Kedi Veritabanı",
      loadMore: "Daha fazla göster",
      subtitle: "Topluluğumuzdaki tüm kayıtlı kedilere göz at.",
      search: "İsim, renk, cins ile ara…",
      filterNeighborhood: "Mahalle",
      filterHealth: "Sağlık durumu",
      add: "Kedi ekle",
      empty: "Henüz kedi yok. İlk ekleyen sen ol!",
      details: "Detaylar",
      visits: "ziyaret",
      aiPortraitShortcut: "Bu kediye AI portre üret",
      aiPortraitRegenerate: "AI portresini yenile",
      aiPortraitShortcutHint: "Bu kedinin gerçek fotoğraflarından stüdyo benzeri portre oluştur",
      aiBadgeHasTooltip: "AI portresi mevcut",
      aiBadgeMissingTooltip: "AI portresi yok — açıp üretebilirsin",
      health: {
        healthy: "Sağlıklı",
        minor_issues: "Hafif sorunlar",
        needs_attention: "Müdahale gerekli",
        critical: "Kritik",
        injured: "Yaralı",
        sick: "Hasta",
        recovering: "İyileşiyor",
        unknown: "Bilinmiyor",
      },
    },
    addCat: {
      title: "Kedi ekle",
      subtitle: "Bir foto yükle — yapay zeka detayları sana göre dolduracak.",
      uploadCta: "Foto yükle",
      analyzing: "Yapay zeka fotoyu inceliyor…",
      analyzeAgain: "Tekrar analiz et",
      name: "İsim",
      color: "Renk",
      pattern: "Desen",
      breed: "Cins",
      age: "Tahmini yaş",
      size: "Boy",
      features: "Ayırt edici özellikler",
      neighborhood: "Mahalle / İlçe",
      lat: "Enlem",
      lng: "Boylam",
      health: "Sağlık durumu",
      spayed: "Kısırlaştırılmış",
      notes: "Notlar",
      save: "Kediyi kaydet",
      success: "Kedi kaydedildi!",
      error: "Kedi kaydedilemedi",
      aiError: "Yapay zeka analizi başarısız",
      tooLarge: "Fotoğraf çok büyük (maks. 6 MB). Lütfen daha küçük bir kare seçin.",
      invalidFields: "Zorunlu alanlar eksik veya hatalı (konum / isim).",
      serverDown: "Profil oluşturulamadı, lütfen konum veya zorunlu alanları kontrol edin.",
    },
    identify: {
      // TUR-10: bu yedi anahtar ayri bir 'identify' blogundaydi.
      // Ayni nesnede ayni anahtar iki kez yazilinca JS ikincisini
      // alir ve birincisini TAMAMEN atar — birlestirmez. Sonuc:
      // AI Kimlik ekraninda kullanici 'identify.registerCat' gibi
      // ham anahtarlar goruyordu. Uc dilde de boyleydi.
      registerCat: "Bu kediyi kaydet",
      registerDog: "Bu köpeği kaydet",
      noMatch: "Eşleşme bulamadık",
      noMatchCat: "Bu kedi kayıtlı olabilir ama biz bulamamış olabiliriz.",
      noMatchDog: "Bu köpek kayıtlı olabilir ama biz bulamamış olabiliriz.",
      reallyNewCat: "Bu gerçekten yeni bir kedi",
      reallyNewDog: "Bu gerçekten yeni bir köpek",

      title: "Yapay Zeka Tanıma",
      subtitle:
        "Bir sokak kedisi fotosu yükle. Yapay zeka onu tanımlasın ve veritabanında olası eşleşmeleri bulsun.",
      upload: "Kedi fotosu yükle",
      analyze: "Analiz et ve eşleştir",
      analyzing: "Yapay zeka fotoya bakıyor…",
      neighborhood: "Mahalleye göre filtre (opsiyonel)",
      analysis: "YZ analizi",
      matches: "Olası eşleşmeler",
      noMatches: "Benzer kedi bulunamadı. Yeni kedi olarak ekleyelim mi?",
      addAsNew: "Yeni kedi olarak kaydet",
      score: "Benzerlik",
      topMatch: "En iyi eşleşme",
      otherMatches: "Diğer olası eşleşmeler",
      matchFound: "Eşleşme bulundu",
      health: {
        title: "Sağlık değerlendirmesi",
        statusLabel: "Sağlık durumu",
        urgencyLabel: "Öncelik",
        issuesLabel: "Görülen belirtiler",
        adviceLabel: "Hemen ne yapmalı",
        bodyLabel: "Vücut kondisyonu",
        statuses: {
          healthy: "Sağlıklı",
          minor_issues: "Hafif sorunlar",
          needs_attention: "Müdahale gerekli",
          critical: "Kritik",
          unknown: "Değerlendirilemedi",
        },
        urgencies: {
          URGENT: "Acil — hemen veterinere",
          MEDIUM: "Orta — 1–3 gün içinde veterinere",
          STABLE: "Stabil",
          UNKNOWN: "Belirsiz",
        },
        body: {
          emaciated: "Çok zayıf",
          thin: "Zayıf",
          ideal: "İdeal",
          overweight: "Kilolu",
          unknown: "Bilinmiyor",
        },
        disclaimer: "Yalnızca YZ değerlendirmesi — veterinerin yerini tutmaz.",
      },
    },
    liveIdentify: {
      overline: "Canlı YZ",
      title: "Kameranı kedinin yüzüne tut",
      subtitle: "Sokak kedisinin yüzüne nişan al — canlı kamerandan yapay zeka tanıyıp veritabanıyla eşleştirsin.",
      tryLive: "Kamera ile dene",
      startCamera: "Kamerayı başlat",
      stopCamera: "Kamerayı durdur",
      scanNow: "Şimdi tara",
      pauseAuto: "Otomatik taramayı duraklat",
      resumeAuto: "Otomatik taramayı sürdür",
      autoOn: "Otomatik tarama açık",
      autoOff: "Manuel mod",
      cameraOff: "Kamera kapalı — Başlat'a dokun.",
      aimAtCat: "Kedi yüzüne nişan al, yapay zeka analiz etmeye başlasın…",
      startToBegin: "Başlamak için Kamerayı başlat'a dokun.",
      freeHint: "Ücretsiz plan: her denemede bir kez Tara'ya bas (aylık 10 YZ hakkından 1'ini kullanır). Sürekli otomatik tarama için Premium'a yükselt.",
      upsell: "Premium ile otomatik tarama",
    },
    scanCat: {
      overline: "YZ Profil Sihirbazı",
      title: "Kedi vesikalık çek",
      subtitle: "Kamerayı kediye tut. Yapay zeka önce veritabanını tarar, sonra üç açıdan (ön, yan ve üst) seni yönlendirerek kusursuz bir profil oluşturur.",
      navLabel: "Vesikalık Çek",
      startCamera: "Kamerayı başlat",
      startCameraNative: "Fotoğrafları çek (3)",
      pickFromGallery: "Galeriden 3 fotoğraf seç",
      galleryFallbackHint: "Kamera açılamazsa galeriden 3 fotoğraf seçerek de profil oluşturabilirsin.",
      galleryPrimaryHint: "Galeriden 3 fotoğraf seçerek kedi profili oluştur. AI eşleşme, otomatik kırpma ve Premium portreler aynı şekilde çalışır.",
      galleryNeed3: "Lütfen tam olarak 3 fotoğraf seç.",
      tryCameraBeta: "Kamerayı dene",
      cameraUnavailableFallback: "Kamera açılamadı, galeriden 3 fotoğraf seçebilirsin.",
      startUnavailableFallback: "Kamera başlatılamadı, galeriden fotoğraf seçebilirsin.",
      partialFallback: "Sadece {n}/3 fotoğraf alındı. Kalanı için galeriye geçiyoruz.",
      cameraCannotOpen: "Kamera açılamadı. Lütfen telefon kamera uygulamasının çalıştığını kontrol edin veya galeriden fotoğraf seçin.",
      nativeCaptureStep: "Fotoğraf alındı",
      nativeAngleStart: "Açı çekiliyor",
      partialCaptures: "{n}/3 fotoğraf alındı. Kalan açılar için tekrar deneyin.",
      buttonTapped: "Kamera butonuna basıldı",
      stopCamera: "Kamerayı durdur",
      cameraDenied: "Kamera izni reddedildi. Lütfen tarayıcı ayarlarından bu site için kamerayı açın.",
      cameraDeniedNative: "Kamera izni reddedildi. Telefon ayarlarından kamera iznini açın.",
      cameraDeniedPWA: "Kamera izni PWA uygulamasında alınamadı. Kamera ile tarama yapmak için Chrome'da açabilirsiniz.",
      cameraUnsupported: "Bu cihaz kamera API'sini desteklemiyor.",
      cameraGenericError: "Kameraya erişilemedi.",
      openInChrome: "Chrome'da aç",
      openAppSettings: "Ayarları aç",
      cameraRetry: "Tekrar dene",
      cameraWaiting: "Kamera izni bekleniyor…",
      matching: "Veritabanında aranıyor…",
      matchFound: "Eşleşme bulundu!",
      matchPrompt: "Aynı kedi mi?",
      matchYes: "Evet, profili aç",
      matchNo: "Hayır, yeni profil",
      notFound: "Profil bulunamadı",
      notFoundSub: "Yeni bir tane oluşturalım — üç açı, yapay zeka rehberli.",
      startWizard: "Şimdi taramaya başla",
      stages: {
        front: "Ön görünüm",
        side: "Yan görünüm",
        top: "Üst görünüm",
      },
      stageHint: {
        front: "Kamerayı doğrudan kedinin yüzüne tut. İki göz de görünsün.",
        side: "Yana geç — tam profil, tek göz/kulak görünsün.",
        top: "Kamerayı başının üzerinden, kafatasına bakacak şekilde tut.",
      },
      captured: "Yakalandı!",
      retake: "Yeniden çek",
      tooFast: "Sabit tut…",
      holdCatOnScreen: "Kediyi ekranda tut",
      timeoutCloser: "Biraz yaklaş",
      timeoutSteady: "Kamerayı sabit tut",
      timeoutLight: "Işık yetersiz — daha aydınlık ortama geç",
      timeoutShade: "Güneş yansıması fazla — gölgeye geç",
      collectingFrames: "En iyi kare seçiliyor…",
      locationCaptured: "Konum alındı",
      locationNeighborhoodOnly: "Mahalle bilgisi ile devam",
      locationMissing: "Konum yok — GPS al veya mahalle yaz",
      locationRequired: "Konumu GPS ile al veya mahalle yaz.",
      locating: "Konum alınıyor…",
      gpsRefresh: "GPS Güncelle",
      gpsLocked: "Konum alındı",
      gpsDenied: "Konum izni reddedildi. Telefon ayarlarından izin ver veya mahalle yaz.",
      gpsUnavailable: "Konum alınamadı (sinyal yok). Mahalle ile devam edebilirsin.",
      gpsTimeout: "Konum zaman aşımına uğradı. Mahalle ile devam edebilirsin.",
      gpsError: "Konum alınamadı. Mahalle ile devam edebilirsin.",
      gpsUnsupported: "Bu cihaz konum servisini desteklemiyor.",
      autoStarting: "Kedi algılandı, tarama başlıyor…",
      autoDetectActive: "Kedi algılanıyor… Lütfen yüzünü kadrajda tut. Tarama otomatik başlayacak.",
      autoProfileTitle: "Otomatik Profil Foto",
      autoFree: "Ücretsiz",
      autoProcessing: "İşleniyor…",
      premiumOnly: "Premium",
      aiPortraitSectionTitle: "AI Profil Portresi",
      aiPortraitOpenBtn: "AI ile vesikalık oluştur",
      aiPortraitFreeHint: "Premium üye olunca AI ile karşıdan vesikalık portre oluşturabilirsin.",
      aiPortraitTitle: "AI ile vesikalık oluştur",
      aiPortraitOverline: "Premium özellik",
      aiPortraitBody: "Gerçek 3 fotoğrafını referans alarak kedinin karşıdan çekilmiş vesikalık benzeri tek bir portresini AI oluşturur. Kimliği (renk, desen, gözler) korur. Bu işlem 1 AI portre hakkı kullanır.",
      aiPortraitLoading: "Hak bilgisi alınıyor…",
      aiPortraitQuota: "Kalan hakkın",
      aiPortraitTierLabel: "Üyelik",
      aiPortraitFreeNotice: "AI portresi Premium ve Süper Premium üyelere açık. Ücretsiz otomatik profil foto zaten oluşturuldu.",
      aiPortraitConfirm: "Oluştur",
      aiPortraitGenerating: "Üretiliyor…",
      aiPortraitReady: "AI profil portresi hazır!",
      aiPortraitCached: "Önbellekten geldi — hak harcanmadı.",
      aiPortraitFailed: "AI portresi oluşturulamadı.",
      aiPortraitNotEnoughPhotos: "AI profil portresi oluşturmak için yeterli kaliteli fotoğraf yok. Lütfen kediyi daha yakın ve net çekin.",
      aiBadge: "AI",
      originalPhotosTitle: "Gerçek Fotoğraflar",
      blockedAi: "Yapay zeka üretimi görsel engellendi.",
      blockedInappropriate: "Uygunsuz görsel tespit edildi.",
      finalize: "Profili tamamla",
      finalizeTitle: "Son bir adım",
      finalizeSub: "Kediye bir isim ver (mahalleyi biliyorsan onu da yaz).",
      submit: "Profili oluştur",
      submitting: "Kaydediliyor…",
      done: "Profil oluşturuldu!",
      doneSub: "Üç açı da galeriye eklendi.",
      goProfile: "Profili aç",
      manualCapture: "Şimdi çek",
      angleOf: "{n}/3 açı",
      voiceOn: "Ses açık",
      voiceOff: "Ses kapalı",
      directions: {
        left: "Sola çevir",
        right: "Sağa çevir",
        up: "Yukarı kaldır",
        down: "Aşağı indir",
        closer: "Yaklaş",
        farther: "Biraz uzaklaş",
        center: "Mükemmel, sabit dur",
        unclear: "Kedi yüzüne nişan al",
      },
      overallProgress: "Profil tamamlanma",
      bestPhotoChosen: "YZ en iyi ön kareyi profil olarak seçti",
      detectorLoading: "Cihaz içi YZ yükleniyor… (tek seferlik)",
      detectorReady: "Cihaz içi YZ hazır · ücretsiz tarama",
    },
    safety: {
      overline: "Topluluk Bakımı",
      title: "Kedi Güvenlik Rehberi",
      subtitle:
        "Bir sokak kedisine yaklaşmadan, beslemeden veya kurtarmadan önce bu kısa pratik kuralları oku. Hem seni hem de kediyi korurlar.",
      disclaimerTitle: "Tıbbi uyarı",
      disclaimerText:
        "Bu sayfa gönüllüler için genel güvenlik eğitimidir, tıbbi tavsiye değildir. Herhangi bir ısırık, derin tırmık, şişlik veya kediyle temasın ardından ortaya çıkan ateş için 24 saat içinde lütfen bir sağlık uzmanına başvur.",
      footerNote:
        "Dikkatli ol. Bir sokak kedisine yapabileceğin en iyi iyilik, onlara yardım etmeye devam edebilecek kadar sağlıklı kalmaktır.",
      before: {
        eyebrow: "Yaklaşmadan önce",
        title: "Güvenli yaklaşma",
        intro:
          "İstanbul'daki çoğu sokak kedisi insanlara hoşgörülüdür ama her kedinin sınırı vardır. Beden dilini okumak ve yavaş hareket etmek olayların %90'ını önler.",
        items: [
          {
            h: "Yavaşça yaklaş, yumuşak bakış, kafadan yukarıdan uzanma",
            p: "Yere çök, parmağını uzat ve önce koklamasına izin ver. Kafasının üstüne doğrudan el uzatmak savunma tepkisini tetikler. Yavaş göz kırpma, kedi dilinde 'tehdit değilim' anlamına gelir.",
          },
          {
            h: "Kuyruk ve kulakları oku",
            p: "Savrulan kuyruk, geriye yatık kulaklar, yandan duran beden, alçak homurtu = uzaklaş. Ucu hafif kıvrık dik kuyruk = arkadaşça selam.",
          },
          {
            h: "İlk seferinde mesafeli besle",
            p: "Mamayı bırak ve 1-2 metre geri çekil. Üstüne eğilmeden yemesine izin ver. Elden besleme, kedinin sana güvenmeye karar verdikten sonraki adımdır.",
          },
          {
            h: "Anne kedinin yavrularına asla uzanma",
            p: "En tatlı anne bile yavrularını korur. Birkaç gün en az 2 metreden gözle; yavrular hastaysa veya terk edilmişse veterinere ya da bir gönüllüye haber ver.",
          },
          {
            h: "Kurtarma için kapalı ayakkabı ve uzun kol giy",
            p: "Yaralı veya sıkışmış kediler panikler. Kalın kumaş derin tırmıklara karşı koruma sağlar. Yanına bir havlu al — kediyi sarmak ikinizin de stresini azaltır.",
          },
        ],
      },
      scratch: {
        eyebrow: "Tırmık sonrası",
        title: "Kedi seni tırmalarsa",
        intro:
          "Yüzeysel tırmıklar genelde temiz iyileşir. Ama kedi tırnakları Bartonella henselae (Kedi Tırmığı Hastalığı) gibi bakteriler taşıyabilir. Hızlı ve titiz yıkama enfeksiyonların çoğunu önler.",
        items: [
          {
            h: "5+ dakika akan suyun altında durula",
            p: "Yarayı serin ya da ılık musluk suyunun altında tut ve birkaç dakika kanamasına izin ver — kan, bakterinin dışarı atılmasına yardım eder.",
          },
          {
            h: "En az 15 dakika sabunla yıka",
            p: "Sade sabun yeterli, özel bir şey gerekmez. Yaranın içine ve etrafına iyice köpük uygula.",
          },
          {
            h: "Temiz gazlı bezle kanamayı durdur",
            p: "Steril gazlı bez veya temiz bir bezle hafif baskı uygula. Pamuk kullanma — lifleri yaraya yapışır.",
          },
          {
            h: "Antiseptik sür ve gevşek kapat",
            p: "Povidon iyot (Batticon) veya klorheksidin iyi çalışır. Yapışmayan bir sargı kullan ve her gün değiştir.",
          },
          {
            h: "24 saat içinde bir şey ters görünürse doktora git",
            p: "Yayılan kızarıklık, şişlik, irin, ateş, şişen lenf bezleri — en yakın Aile Sağlığı Merkezi'ne veya hastaneye git. Tırmık tarihini ve kedinin sağlıklı görünüp görünmediğini söyle.",
          },
        ],
        warning:
          "Son tetanos hatırlatma aşın 10 yıldan eskiyse — veya hatırlamıyorsan — bir doz iste. Aile Sağlığı Merkezi'nde ücretsizdir.",
      },
      bite: {
        eyebrow: "Isırık sonrası",
        title: "Kedi seni ısırırsa",
        intro:
          "Isırıklar tırmıklardan daha ciddidir. Kedi dişleri derine batar ve dokuya bakteri enjekte eder. Türkiye'de kuduz profilaksisi de gerçek bir değerlendirmedir. Her ısırığı tıbbi bir olay olarak ele al.",
        items: [
          {
            h: "15+ dakika akan suyun altında durula",
            p: "En önemli adım budur. Uzun ve güçlü durulama, enfeksiyon ve kuduz riskini ciddi biçimde düşürür. Kanın akmasını teşvik etmek için bölgeye hafifçe bastır.",
          },
          {
            h: "Sabunla yıka ve antiseptik uygula",
            p: "En az 15 dakika sabun + su, ardından povidon iyot. Sıkı bandaj yapma — ısırık yarasının havaya ihtiyacı var.",
          },
          {
            h: "Aynı gün hastaneye veya sağlık ocağına git",
            p: "Doktor derinliği, konumu ve enfeksiyon riskini değerlendirir. Kedi ısırıkları için neredeyse her zaman antibiyotik reçete edilir.",
          },
          {
            h: "Kuduz profilaksisi konusunda sor",
            p: "Kedi tanıdık değilse, anormal davranıyorsa ya da 10 gün gözlemlenemiyorsa, temas sonrası kuduz aşısı gerekebilir. Türkiye'deki devlet hastanelerinde ücretsizdir.",
          },
          {
            h: "Kediyi tanıyorsan gözlem altında tut",
            p: "Seni ısıran sağlıklı bir mahalle/ev kedisi 10 gün boyunca gözlenmelidir. Sağlıklı kalırsa kuduz dışlanır. Kedinin yerini ve fotoğrafını bu uygulamaya kaydet, gerekirse yardımcı olur.",
          },
        ],
        warning:
          "Ele, yüze veya eklem yakınına alınan ısırıklar yüksek risklidir. Geciktirme — aynı gün acil servise git.",
      },
      contacts: {
        eyebrow: "Acil",
        title: "Bilmen gereken telefonlar",
        subtitle:
          "Türkiye'de bu hatlar ücretsiz, 7/24, Türkçe yanıtlanır (112'de çoğunlukla İngilizce de mevcuttur).",
        l112: "Acil Çağrı Merkezi · ambulans, itfaiye, polis",
        l184: "Sağlık Bakanlığı · sağlık danışma hattı",
        l153: "Belediye · hayvan hizmetleri",
      },
      ctaTitle: "Tırmalandın veya ısırıldın mı?",
      ctaSubtitle: "Olayı kaydet, 10 günlük kuduz gözlem süresini takip edelim. Hem sen unutmayasın hem de gerekirse topluluk yardım edebilsin.",
      ctaScratch: "Tırmalandım",
      ctaBite: "Isırıldım",
    },
    incident: {
      title: "Olay Kaydı Aç",
      subtitle: "10 günlük gözlem süresini başlat. Durumu sonra güncelleyebilirsin (kedi sağlıklı, belirti çıktı, tedavi tamam).",
      types: { scratch: "Tırmık", bite: "Isırık" },
      severity: "Ciddiyet",
      sev: { mild: "Hafif — yüzeysel", moderate: "Orta — kanama var", severe: "Ciddi — derin / çoklu" },
      cat: "Hangi kedi (opsiyonel)",
      catPlaceholder: "Bir kedi seç veya boş bırak",
      catHint: "Kediyi seçmek, topluluğun 10 günlük gözlemi açıkça takip etmesini sağlar.",
      unknownCat: "Bilmiyorum / tanımadığım bir sokak kedisi",
      date: "Olay tarihi",
      location: "Nerede (opsiyonel)",
      locationPlaceholder: "ör. Beşiktaş, Akaretler Sokağı",
      notes: "Notlar",
      notesPlaceholder: "Ne oldu? Yarayı yıkadın mı? Belirti var mı?",
      photo: "Yara veya kedi fotoğrafı (opsiyonel)",
      privacyNote: "Notların ve fotoğrafın sadece sana özeldir. Yalnızca seçtiğin kedi (varsa) profilinde 'gözlem altında' rozeti ve sayacı görünür — kişisel bilgi paylaşılmaz.",
      useGps: "Konumumu kullan (yakındaki gönüllülere bildirim)",
      gpsCaptured: "Konum alındı. Yakındaki topluluk üyeleri bilgilendirilecek.",
      gpsHint: "Bu bölgedeki gönüllüler bildirim olarak görecek.",
      submit: "10 günlük gözlemi başlat",
      submitting: "Kaydediliyor…",
      created: "Olay kaydedildi. 10 günlük gözlem başladı.",
      error: "Olay kaydedilemedi.",
      loginRequired: "Lütfen önce giriş yap.",
      observation: {
        title: "Aktif Gözlemler",
        subtitle: "Isırık/tırmık sonrası 10 gün boyunca hem kendinde hem de kedide olağandışı davranışları izle.",
        empty: "Aktif gözlem yok.",
        daysLeft: "{n} gün kaldı",
        complete: "Gözlem tamamlandı",
        scratchOn: "Tırmık",
        biteOn: "Isırık",
        markHealthy: "Kedi sağlıklı kaldı",
        markSymptoms: "Belirti çıktı",
        markResolved: "Tedavi tamam",
        statusUpdated: "Durum güncellendi.",
        statuses: {
          observing: "Gözleniyor",
          cat_healthy: "Kedi sağlıklı kaldı ✓",
          symptoms_appeared: "Belirti çıktı",
          incident_developed: "Komplikasyon gelişti",
          incident_resolved: "Tedavi tamam ✓",
        },
        bannerObserving: "{n} aktif gözlem",
        bannerObservingPlural: "{n} aktif gözlem",
        notified: "komşu bilgilendirildi",
        feedbackList: "Topluluk geri bildirimleri",
      },
      catBadge: {
        observing: "Gözlem altında",
        sub: "{n} kişi izliyor — {days} gün kaldı",
      },
      feedbackPage: {
        eyebrow: "Topluluk geri bildirimi",
        notFound: "Olay bulunamadı.",
        intro: "Yakınındaki bir kullanıcı bu tırmık/ısırığı bildirdi. Kediyi gördüysen veya faydalı bir şey biliyorsan kısa bir gözlem paylaş — bildiren kişinin 10 günlük gözlem sürecini güvenle tamamlamasına yardım eder.",
        communityReplies: "topluluk yanıtı",
        observationLabel: "Ne gözlemledin?",
        options: {
          cat_seen_healthy: "Kediyi gördüm, sağlıklı görünüyor",
          cat_not_seen: "Kediyi göremedim",
          cat_seen_concerning: "Gördüm, bir şeyler ters görünüyor",
          other: "Başka gözlem",
        },
        notePlaceholder: "Başka bir şey? Saat, yer, kedi ne yapıyordu…",
        photo: "Kedinin fotoğrafı (opsiyonel)",
        submit: "Geri bildirim gönder",
        thanks: "Topluluğa yardım ettiğin için teşekkürler!",
        thanksSub: "Bildiren kişiye gözlemin iletildi.",
        othersTitle: "Diğer gözlemler",
      },
    },
    observationHouse: {
      overline: "Topluluk Güvenliği",
      title: "Gözlem Evi",
      subtitle: "Bildirilen tırmık veya ısırık sonrası 10 günlük gözlem süresindeki tüm kediler. Hızlı bir gözlem bırakarak komşularına yardım et.",
      allTypes: "Tümü",
      neighborhoodPlaceholder: "Mahalleye göre filtrele…",
      urgentOnly: "Sadece acil (≤3 gün)",
      nearbyOn: "Yakınımda",
      nearbyOff: "Yakın filtre kapalı",
      results: "sonuç",
      empty: "Şu anda aktif gözlem yok. 🐾",
      unknownCat: "Bilinmeyen kedi",
      locationUnknown: "Konum paylaşılmamış",
      loadFailed: "Gözlemler yüklenemedi.",
      gpsUnavailable: "Konum hizmeti desteklenmiyor.",
      gpsActive: "5 km çevrene odaklanıldı.",
      gpsFailed: "Konum alınamadı.",
    },
    weekly: {
      eyebrow: "E-posta Özeti",
      title: "Haftalık Gözlem Özeti",
      subtitle: "Her Pazartesi 09:00'da — kapsama bölgendeki bu haftanın olay ve gözlemlerinin kişiselleştirilmiş özeti.",
      on: "Açık",
      off: "Kapalı",
      optedIn: "Haftalık özet açıldı.",
      optedOut: "Haftalık özet kapatıldı.",
      noScope: "Önce profilinden kapsama bölgesi belirle ki özetini mahallene göre hazırlayabilelim.",
      statNew: "Yeni olay",
      statActive: "Aktif",
      statUrgent: "Acil",
      statFeedbacks: "Geri bildirim",
      highlightsTitle: "Bu haftanın öne çıkan vakaları",
      sendTest: "Test gönder",
      sending: "Gönderiliyor…",
      scheduleHint: "Açıkken her Pazartesi 09:00'da otomatik gönderilir.",
      testSentEmail: "Test gönderildi! Gelen kutunu kontrol et.",
      testSentInAppOnly: "Uygulama içi bildirim oluşturuldu. Bu sunucuda e-posta devre dışı.",
      error: "Özet gönderilemedi.",
    },
    map: {
      title: "İstanbul Kedi Haritası",
      subtitle:
        "Her iğne; bilinen bir kedi, bir acil vaka ya da bir besleme noktasıdır.",
      layerCats: "Kediler",
      layerSightings: "Acil Vakalar",
      layerFeeding: "Besleme noktaları",
      layerObservations: "Gözlem Evi",
      layerVaccinated: "Aşılı",
      layerCommunity: "Topluluk",
      tabMap: "Harita",
      tabNeighborhood: "Benim Mahallem",
      locate: "Bul",
    },
    sightings: {
      title: "Acil Vakalar",
      subtitle: "İstanbul'da yardıma muhtaç kediler için topluluk bildirimleri.",
      report: "Acil vaka bildir",
      notes: "Ne oluyor?",
      reporter: "Bildiren",
      when: "Ne zaman",
      submit: "Acil bildirimi gönder",
      success: "Bildirdiğin için teşekkürler — yakındaki gönüllüler bilgilendirildi.",
      pendingReview: "Teşekkürler — bildirimin admin onayında, onaylanınca yayına çıkacak.",
      empty: "Henüz acil vaka bildirimi yok.",
      locationOnly: "Ya da hızlıca konum işaretle",
      photoAndLocation: "Foto eklersen ekipler kediyi daha rahat tanır",
    },
    volunteers: {
      title: "Gönüllüler",
      subtitle: "İstanbul sokaklarını kediler için güvende tutan insanlar.",
      vetsTitle: "Gönüllü Veterinerler",
      vetsSubtitle: "Yaralı sokak kedilerini ücretsiz tedavi eden lisanslı veterinerler.",
      individualsTitle: "Gönüllü Bireyler",
      individualsSubtitle: "Besleyen, kollayan, kurtaran komşular.",
      noVets: "Henüz gönüllü veteriner katılmadı.",
      noIndividuals: "Henüz gönüllü birey katılmadı.",
      becomeVolunteer: "Gönüllü ol",
    },
    feeding: {
      title: "Besleme Noktaları",
      subtitle: "Gönüllülerin kedileri beslediği yerler.",
      add: "Besleme noktası ekle",
      name: "Nokta adı",
      schedule: "Plan (örn. Pzt/Çar/Cum 19:00)",
      notes: "Notlar",
      empty: "Henüz besleme noktası yok.",
      success: "Besleme noktası eklendi",
      active: "Aktif",
      inactive: "Pasif",
      statusLabel: "Durum",
      photo: "Noktanın fotoğrafı",
      findNearest: "En yakını bul",
      nearestFound: "En yakın nokta",
      nearestBadge: "Sana en yakın",
      noNearest: "Besleme noktası bulunamadı.",
      geoUnsupported: "Tarayıcın konum servisini desteklemiyor.",
      geoDenied: "En yakın noktayı bulmak için konuma izin ver.",
      openDirections: "Haritada aç",
      maintainedBy: "Bakımı yapan",
      requireNameLoc: "Ad ve GPS koordinatları zorunlu",
    },
    dashboard: {
      title: "Panel",
      ilkKediBaslik: "İlk kedini ekle",
      ilkKediAlt: "Fotoğrafını çektiğin sokak kedileri, başkalarının onları bulmasına, beslemesine ve sahip çıkmasına yardım ediyor.",
      recentCats: "Son eklenenler",
      welcome: "Tekrar hoş geldin",
      myRole: "Rolün",
      quickAdd: "Hızlı ekle",
      addCat: "Kedi ekle",
      identify: "YZ tanıma",
      reportSighting: "Acil vaka bildir",
      addFeeding: "Besleme noktası ekle",
    },
    profile: {
      title: "Profilim",
      settingsTitle: "Ayarlar ve yardım",
      settingsSubtitle: "Hesabını, bildirimleri, dili ve gizliliği tek yerden yönet.",
      aiPortraitBannerTitle: "AI Profil Portresi",
      aiPortraitBannerRemaining: "Bu ay kalan hakkın",
      aiPortraitBannerUnlimited: "Sınırsız",
      aiPortraitBannerCta: "Yeni portre oluştur",
      aiPortraitBannerUsed: "{used}/{limit} kullanıldı",
      aiPortraitBannerExhausted: "Bu ayki hakların doldu — gelecek ay yenilenecek.",
      aiCoverageTitle: "Topluluk AI portre tamamlaması",
      aiCoverageSubtitle: "{total} kedinin {with} tanesinde AI portresi var",
      aiCoverageEmpty: "AI portre ekleyen ilk kişi sen ol!",
      myAccount: "Hesabım",
      myAccountHint: "Panel, kedilerim, katkılarım",
      notificationSettings: "Bildirim ayarları",
      notificationSettingsHint: "Ne, nasıl ve ne zaman alacağını seç",
      countryTitle: "Ülke",
      countryHint: "Hangi topluluğa dahilsin",
      languageTitle: "Dil seçimi",
      languageHint: "Arayüz dilini seç",
      privacyKvkk: "Gizlilik ve KVKK",
      privacyKvkkHint: "Veri koruma hakların ve çerezler",
      terms: "Kullanım Şartları",
      termsHint: "Uygulamayı kullanma kuralları",
      contact: "İletişim",
      contactHint: "destek@sokakkedileri.tr",
      helpFaq: "Yardım ve SSS",
      helpFaqHint: "Başlangıç, sık sorulanlar, iletişim",
      safetyHint: "Sokak kedisi güvenlik rehberleri",
      points: "Pati Puanı",
      badgesEarned: "kazanılan rozet",
      seeAllBadges: "Tüm rozetleri gör",
      nextBadge: "Sıradaki rozet",
      nextLevel: "Sıradaki seviye",
      nextLevelIn: "Sıradaki seviyeye",
      contributions: "katkı",
      notificationsTitle: "Bildirimler",
      notificationsHint: "Son güncellemeler ve yanıtlar",
      notificationsEmpty: "Henüz bildirim yok",
      seeAllNotifications: "Tüm bildirimleri gör",
      settingsGroup: "Ayarlar",
      settingsGroupHint: "Dil, tema, izinler",
      helpInfoGroup: "Yardım ve bilgi",
      helpInfoHint: "Nasıl çalışır, SSS, hakkımızda, gizlilik",
      howItWorks: "Nasıl çalışır?",
      howItWorksHint: "3 adımda özellikleri keşfet",
      aboutUs: "Hakkımızda",
      aboutUsHint: "Misyonumuz, ekibimiz ve iletişim",
      themeTitle: "Tema",
      themeHint: "Açık veya koyu arayüz",
      themeLight: "Açık",
      themeDark: "Koyu",
      locationPermTitle: "Konum izni",
      locationPermHint: "Yakındaki kedileri önermek için kullanılır",
      notifPermTitle: "Bildirim izni",
      notifPermHint: "Acil vaka uyarıları alabilmen için",
      permRequest: "İzin ver",
      permGranted: "İzinli",
      permDenied: "Engellendi",
      pointsShort: "🐾",
      catsDescribed: "Tanıdığın kedi",
      sightingsReported: "Bildirilen acil vaka",
      feedingSpots: "Besleme noktası",
      vaccinations: "Aşı kaydı",
      // TUR-10: eskiden bu da `howItWorks` idi ve ayni nesnedeki
      // yardim baglantisinin metnini eziyordu (JS ikinciyi alir).
      // Farkli kavram, farkli anahtar.
      pointsHowItWorks: "Puanlama nasıl işliyor",
      ruleCat: "Yeni bir kediyi tanıt: +5 Pati Puanı",
      ruleFeeding: "Besleme noktası ekle: +3 Pati Puanı",
      ruleVaccination: "Aşı kaydı gir: +10 Pati Puanı",
      ruleSighting: "Acil vaka bildir: +2 Pati Puanı",
      namingNote: "Bir kediyi ilk tanıyan kişi, ona isim verme ayrıcalığını kazanır.",
      myCats: "Tanıttığın kediler",
      noCatsYet: "Henüz bir kediyi tanıtmadın.",
      rank: "Sıra",
      privilegesTitle: "Bu seviyede açılan ayrıcalıklar",
      nextTier: "ulaşmak için",
      keepGoing: "Bir kediyi ilk tanıyan sen ol — 5 Pati Puanı kazan ve tanıyan rozetini aç.",
      photoUpdated: "Fotoğraf güncellendi",
      photoError: "Fotoğraf güncellenemedi",
    },
    common: {
      showPassword: "Şifreyi göster",
      hidePassword: "Şifreyi gizle",
      deletePhoto: "Fotoğrafı sil",
      cancel: "Vazgeç",
      save: "Kaydet",
      delete: "Sil",
      edit: "Düzenle",
      back: "Geri",
      required: "Zorunlu",
      loading: "Yükleniyor…",
      locateMe: "Konumumu kullan",
      tryAi: "YZ ile dene",
      describedBy: "Tanıyan",
      optional: "opsiyonel",
    },
    leaderboard: {
      title: "Topluluk Sıralaması",
      subtitle: "Bir kediyi ilk tanıyan sen ol. Sıralamada yüksel. Ayrıcalıklar aç.",
      contributor: "Katkıda Bulunan",
      badge: "Seviye",
      contributions: "Katkılar",
      points: "Pati Puanı",
      empty: "Henüz katkıda bulunan yok — ilk sen ol!",
      you: "Sen",
      tabVolunteers: "Gönüllüler",
      tabNeighborhoods: "Mahalle AI Portresi",
      hoodEmpty: "Henüz yeterli AI portresi yok. İlk üreten mahalle senin olsun!",
      hoodWithAi: "AI portresi",
      hoodCoverage: "Kaplama",
      hoodTopContributors: "Bu hafta en çok katkı sağlayanlar",
    },
    messenger: {
      title: "Mesajlar",
      empty: "Henüz yazışman yok. Gönüllüler dizininden birine mesaj gönder.",
      selectThread: "Sohbeti başlatmak için bir yazışma seç.",
      placeholder: "Mesaj yaz…",
      send: "Gönder",
    },
    notifications: {
      title: "Bildirimler",
      subtitle: "Yakın acil vakalar, üstlenilen ihbarların ve yeni mesajların.",
      empty: "Hepsi okundu.",
      markAllRead: "Hepsini okundu işaretle",
      markRead: "Okundu",
      open: "Aç",
    },
    severity: {
      minor: "Hafif",
      urgent: "Acil",
      critical: "Kritik",
      label: "Aciliyet",
    },
    emergencyActions: {
      claim: "Yola çıktım",
      claimed: "Üstlenen",
      resolve: "Çözüldü olarak işaretle",
      resolved: "Çözüldü",
      status: "Durum",
      open: "Açık",
      message: "Mesaj",
    },
    emergencyCategories: {
      injured: "Yaralı kedi",
      injuredDesc: "Yara, topallama veya acı çeken kedi",
      food: "Mama / su ihtiyacı",
      foodDesc: "Etrafta mama ya da su olmayan kediler",
      danger: "Tehlikeli bölge",
      dangerDesc: "İnşaat, trafik veya hava tehdidi",
      volunteer: "Gönüllü çağır",
      volunteerDesc: "Taşıma, geçici sahiplenme gibi destek gerekli",
      lost: "Kayıp Kedi",
      lostDesc: "Fotoğrafla ara — AI tüm kayıtlı kedileri tarar",
    },
    premium: {
      // TUR-10: Profile.jsx `t("premium.active") || "Aktif"` diyordu;
      // anahtar olmayinca Ingilizce ve Endonezce arayuze TURKCE "Aktif" siziyordu.
      active: "Aktif",
      navLink: "Premium",
      title: "Premium üyelik",
      subtitle: "Sınırsız yapay zeka, özel temalar, doğrulama rozeti ve favori besleme noktaları — kediler için.",
      priceLine: "Aylık ₺59",
      cta: "Yükselt — Aylık ₺59",
      comingSoonPlay: "Yakında Google Play'de",
      comingSoonHint: "Premium üyelik, Android uygulamamız Google Play'de yayınlandıktan sonra satın alınabilir olacak. Yayına çıktığımız gün buradan haber vereceğiz.",
      ctaActive: "Premium üyesisin",
      perksHeading: "Açılan ayrıcalıklar",
      perk1: "Sınırsız kedi tanımlama hakkı (ücretsiz: ayda {limit})",
      perk2: "Sınırsız kedi fotoğrafı beğenme",
      perk3: "Premium üyeler arasında özel mesajlaşma",
      perk4: "Acil bildirimlerinde öncelik",
      perk5: "Premium rozeti — toplulukta sözün daha güçlü",
      perk6: "Resmi gönüllülük programına başvuru",
      perk7: "Favori besleme alanlarını kaydet",
      activeUntil: "Şu tarihe kadar geçerli",
      usedThisMonth: "Bu ay kullanılan YZ",
      remaining: "kaldı",
      unlimited: "Sınırsız",
      themeTitle: "Tema",
      themeWarm: "Sıcak Taş",
      themeMidnight: "Gece",
      themeSage: "Adaçayı",
      themeBosphorus: "Boğaz Mavisi",
      verificationTitle: "Doğrulama rozeti",
      verificationDesc: "Premium üyeler, kuruluşlarının yönetici tarafından doğrulanması için başvurabilir.",
      verificationOrgLabel: "Kuruluş adı (belediye, dernek, veteriner kliniği…)",
      verificationRequest: "Doğrulama iste",
      verificationPending: "Yönetici onayı bekleniyor",
      verificationVerified: "Doğrulandı ✓",
      verificationOrgIs: "Doğrulanan kurum",
      adminApprovals: "Bekleyen doğrulama istekleri",
      adminApprove: "Onayla",
      adminEmpty: "Bekleyen doğrulama isteği yok.",
      paymentSuccess: "Premium'a hoş geldin!",
      paymentSuccessSub: "İstanbul'un sokak kedilerini desteklediğin için teşekkürler. Ayrıcalıkların aktif.",
      paymentChecking: "Ödemen doğrulanıyor…",
      paymentExpired: "Ödeme oturumun sona erdi. Lütfen tekrar dene.",
      paymentCancelled: "Ödeme iptal edildi.",
      paymentCancelSub: "Herhangi bir ücret alınmadı. İstediğin zaman tekrar deneyebilirsin.",
      backHome: "Panele dön",
      tryAgain: "Tekrar dene",
      checkoutError: "Ödeme başlatılamadı. Lütfen tekrar dene.",
      quotaReachedTitle: "Aylık ücretsiz YZ limiti doldu",
      quotaReachedSub: "Sınırsız tanımlama için Premium'a yükselt veya yeni ayı bekle.",
      seePricing: "Premium'u gör",
      favoriteAdd: "Kaydet",
      favoriteRemove: "Kayıtlı",
      favoritePremiumOnly: "Besleme noktası kaydetmek Premium ayrıcalığı.",
    },
    lounge: {
      overline: "Premium Lounge",
      title: "Premium Lounge",
      subtitle: "İstanbul'un kedilerini koruyan diğer premium destekçilerle tanış. Buradaki herkese doğrudan mesaj atabilirsin.",
      memberCount: "premium üye",
      empty: "Premium Lounge'un ilk üyesi sen ol!",
      message: "Mesaj",
      youAreHere: "Bu sensin",
      vet: "Veteriner",
      gatedTitle: "Premium Lounge yalnızca Premium üyelere",
      gatedSub: "Aylık ₺59'a yükselt ve doğrulanmış gönüllüler ile veterinerlerle birebir sohbet et.",
    },
    city: {
      generating: "Şehrin çiziliyor…",
      aiGenerated: "YZ sahnesi",
    },
    coverage: {
      title: "Kapsama alanları",
      heading: "Sorumluluk aldığın bölgeler",
      subtitle: "Yaşadığın ya da sorumluluk hissettiğin ilçeleri seç. Acil vakaları ve besleme noktalarını öncelikle bu bölgelerde gösterelim.",
      empty: "İlk bölgeni seçerek yakınındaki acil vakaları görmeye başla.",
      addCity: "Şehir ekle",
      removeCity: "Şehri kaldır",
      districtsCount: "ilçe seçili",
      save: "Kapsamayı kaydet",
      saved: "Kapsama kaydedildi",
    },
    trending: {
      overline: "Pati Holü",
      title: "Haftanın patileri",
      subtitle: "Şu an en çok ziyaret edilen sokak kedileri. Her 25 ziyarette kediyi ilk tanıtan kişiye +5 Pati Puanı.",
      week: "Hafta",
      month: "Ay",
      all: "Tüm zamanlar",
      seeAll: "Tüm kedileri gör",
    },
    badges: {
      overline: "Koleksiyon",
      title: "Pati Rozetleri",
      subtitle: "Pati temalı koleksiyon rozetleri. Kedilere yardım ettikçe kazanırsın. Detayını görmek için rozete tıkla.",
      collected: "Kazanılan",
      earnedOn: "Kazanıldı",
      filterAll: "Tümü",
      filterEarned: "Kazanılan",
      filterLocked: "Kilitli",
      emptyFilter: "Bu filtrede rozet yok.",
      next: "Sıradaki",
      allEarned: "Tüm rozetler kazanıldı!",
      celebrationTitle: "Yeni rozet kazandın",
      howToTitle: "Nasıl kazanılır",
      howToFallback: "Toplulukta aktif olmaya devam et — rozet açılacak.",
      pageOverline: "Koleksiyon",
      pageTitle: "Pati Rozetleri",
      pageSubtitle: "Rozet üzerine tıklayıp nasıl kazanılacağını gör. Kedilere yardım ederek, fotoğraflayarak ve topluluğa katkı sağlayarak kazan.",
      shareBtn: "Paylaş",
      shareText: "Pati uygulamasında {badge} rozetini kazandım! 🐾",
      remainingMetric: {
        cats: { one: "kedi daha", many: "kedi daha" },
        feeding: { one: "besleme noktası daha", many: "besleme noktası daha" },
        vax: { one: "aşı daha", many: "aşı daha" },
        sightings: { one: "acil bildirim daha", many: "acil bildirim daha" },
        claimed: { one: "vakaya daha yetiş", many: "vakaya daha yetiş" },
        messages: { one: "mesaj daha", many: "mesaj daha" },
        districts: { one: "ilçe daha", many: "ilçe daha" },
        cities: { one: "şehir daha", many: "şehir daha" },
      },
      category: {
        describer: "Tanıtıcı",
        feeding: "Besleme",
        health: "Sağlık",
        emergency: "Acil",
        fame: "Şöhret",
        coverage: "Kapsama",
        social: "Sosyal",
        engagement: "Katılım",
        special: "Özel",
        paw: "Pati",
      },
    },
    donate: {
      navLink: "Bağış",
      overline: "Canlı Şeffaflık",
      title: "Bizi iyileştir, onları iyileştir",
      subtitle: "Her kuruş izlenebilir. Bağışların veteriner kontrollerine, ameliyatlara ve İstanbul sokak kedilerine ikinci bir şansa nasıl dönüştüğünü açıkça gör.",
      transparencyTag: "%100 açık defter — her tedavi doğrulanmış veterinerlerce kaydedilir",
      totalRaised: "Toplam toplanan",
      totalSpent: "Tedavi için harcanan",
      balance: "Şu an mevcut",
      catsHelped: "Yardım edilen kedi",
      donors: "bağışçı",
      treatments: "tedavi",
      availableForCats: "Bir sonraki kedi için hazır",
      uniqueCats: "Farklı kedi sayısı",
      makeDonation: "Bağış yap",
      everyKurusCounts: "Her kuruş bir kediye gider.",
      amount: "Tutar",
      yourName: "İsmin (herkese görünür)",
      email: "E-posta",
      note: "Not",
      donateNow: "Bağışla",
      securedBy: "iyzico ile güvende · Kart bilgini saklamıyoruz.",
      iyzicoPending: "Bağışlar kaydediliyor ancak iyzico hesap doğrulaması tamamlanana kadar kartla ödeme geçici olarak duraklatıldı. Bilgilerin saklandı; en kısa sürede sana ulaşacağız.",
      pendingMerchant: "Bağış kaydedildi — kısa süre içinde teyit edeceğiz.",
      thankYou: "Teşekkürler 🐾",
      error: "Bağış kaydedilemedi. Lütfen tekrar dene.",
      invalidAmount: "Geçerli bir tutar gir",
      treatmentLedger: "Canlı tedavi defteri",
      treatmentLedgerDesc: "Yalnızca admin onaylı veterinerler kayıt ekleyebilir. Her gider, yardım ettiği kedinin yanına işlenir.",
      noTreatmentsYet: "Henüz tedavi kaydı yok — ilk sen yardım et.",
      spent: "Harcandı",
      more: "kayıt daha",
      recentDonors: "Son bağışlar",
      noDonorsYet: "İlk bağış senden olabilir.",
      severity: {
        mild: "Hafif",
        moderate: "Orta",
        severe: "Şiddetli",
        critical: "Kritik",
      },
      tStatus: {
        planned: "Planlandı",
        in_progress: "Devam ediyor",
        completed: "Tamamlandı",
      },
    },
    treatments: {
      title: "Tedavi defteri",
      subtitle: "Her ziyaret, her kuruş — yalnızca admin onaylı veterinerler tarafından eklenir.",
      totalSpent: "Bu kedi için harcanan toplam",
      records: "kayıt",
      noneYet: "Henüz tedavi kaydı yok.",
      add: "Tedavi ekle",
      added: "Tedavi kaydedildi",
      condition: "Durum / hastalık",
      details: "Tedavi detayları (ilaçlar, dozaj, kontrol…)",
      detailsPlaceholder: "Teşhisi ve yapılanları olabildiğince ayrıntılı yaz.",
      cost: "Maliyet",
      date: "Tarih",
      severityLabel: "Şiddet",
      statusLabel: "Durum",
      clinicName: "Klinik / belediye (opsiyonel)",
      save: "Kaydet",
    },
    apply: {
      overline: "Gönüllü Programı",
      title: "Gönüllü ol",
      subtitle: "İstanbul sokak kedileri için neden gönüllü olmak istediğini anlat. Önce AI ön taraması yapıyor (spam/troll/uygunsuz filtresi), sonra admin kararı veriyor.",
      cardTitle: "Gönüllü ol",
      cardSubtitle: "Kedi bakım araçlarına eriş · 2 dk başvuru + admin onayı",
      motivationLabel: "Neden gönüllü olmak istiyorsun?",
      motivationPlaceholder: "Sokak kedileriyle deneyimini, mahallesini ve gönüllü olarak ne yapmak istediğini birkaç cümleyle anlat…",
      minChars: "En az 20 karakter",
      aiNotice: "AI başvurunu spam/troll sinyalleri için tarayacak. Son karar her zaman admin tarafından verilir.",
      submitBtn: "Başvuruyu gönder",
      submitted: "Başvuru gönderildi — admin'e iletildi",
      error: "Bir sorun oluştu",
      tooShort: "Lütfen en az 20 karakter yaz",
      backToProfile: "Profile dön",
      aiScore: "AI skoru",
      statusPending: "Başvurun inceleme aşamasında",
      statusPendingHint: "Admin başvurunu kısa sürede inceleyecek. Karar verildiğinde bildirim alacaksın.",
      statusRejected: "Başvurun onaylanmadı",
    },
    admin: {
      overline: "Admin",
      title: "Gönüllü başvuruları",
      subtitle: "AI her başvuruyu ön taramadan geçirir. Senin işin: gönüllü erişimi vermeden önce son insan kontrolü.",
      forbiddenTitle: "Sadece admin erişimi",
      forbiddenSubtitle: "Bu sayfayı görüntüleme yetkin yok.",
      aiSummary: "AI değerlendirmesi",
      empty: "Bu filtrede başvuru yok.",
      approveBtn: "Onayla",
      rejectBtn: "Reddet",
      approvedToast: "Başvuru onaylandı — gönüllü erişimi verildi.",
      rejectedToast: "Başvuru reddedildi.",
      bulkRejectTitle: "AI {count} başvuruyu yüksek risk işaretledi",
      bulkRejectSubtitle: "Tek tıkla hepsini reddet — başvuranlara bildirim gider.",
      bulkRejectBtn: "Toplu reddet",
      bulkRejectConfirm: "{count} kırmızı işaretli başvuruyu reddetmek istediğine emin misin? Geri alınamaz.",
      bulkRejectedToast: "{count} başvuru toplu reddedildi.",
      notePlaceholder: "Başvurana (görünür) opsiyonel not…",
      adminNoteLabel: "Admin notu",
      tab: {
        pending: "Beklemede",
        approved: "Onaylandı",
        rejected: "Reddedildi",
      },
      stats: {
        daysOld: "günlük",
        cats: "kedi",
        sightings: "acil",
        feeding: "besleme",
      },
    },
    adminEmerg: {
      overline: "Acil moderasyonu",
      title: "Acil inceleme kuyruğu",
      subtitle: "AI fotoğraf ve metni tarar. Güvenilmeyen kullanıcıların acilleri burada admin onayı bekler.",
      gotoApplications: "Gönüllü başvurularına git",
      empty: "Bu filtrede acil yok.",
      approveBtn: "Yayınla",
      rejectBtn: "Reddet",
      approvedToast: "Acil yayında.",
      rejectedToast: "Acil reddedildi.",
      tab: {
        pending: "Beklemede",
        approved: "Yayında",
        rejected: "Reddedildi",
      },
      photoVerdict: "Fotoğraf AI",
      textVerdict: "Metin AI",
      notACat: "Kedi değil",
      aiGenerated: "AI üretimi",
      photoshopped: "Photoshop",
      photoClean: "Fotoğraf temiz",
      confidence: "Güven",
      redWarning: "AI bunu yüksek risk işaretledi — yayınlamadan önce dikkatli incele.",
    },
    photos: {
      archive: "Foto arşivi",
      galleryTitle: "Foto arşivi",
      gallerySubtitle: "Bu kediye ait topluluğun paylaştığı tüm fotoğraflar. Pati atmak için tıkla.",
      addPhoto: "Foto ekle",
      uploaded: "Fotoğraf yüklendi",
      uploadFailed: "Yükleme başarısız",
      emptyGallery: "Henüz fotoğraf yok — ilk paylaşan sen ol!",
      loginRequired: "Fotoğraf yüklemek için giriş yap",
      loginToLike: "Pati atmak için giriş yap",
      deleteConfirm: "Bu fotoğrafı silmek istiyor musun?",
      deleted: "Fotoğraf silindi",
      monthlyQuotaUsed: "Bu ay {used}/{limit} pati kullandın",
      unlimitedQuota: "Sınırsız pati (Premium)",
      upgradeForUnlimited: "Sınırsız için Premium ol",
      aiNotice: "Fotoğraflar AI ile taranır: AI üretimi, photoshop veya uygunsuz içerik engellenir.",
    },
    photoContest: {
      overline: "Aylık foto yarışması",
      title: "Ayın Fotoğrafı",
      subtitle: "Her ay en çok pati alan ilk 3 fotoğraf 'Ayın Fotoğrafçısı' rozeti kazanır. Fotoğrafa tıkla, kedisine git.",
      empty: "Bu ay henüz pati alan fotoğraf yok — podyuma ilk sen çık!",
      by: "fotoğrafçı:",
      honorableMentions: "Mansiyon (4-10)",
    },
  },
};

const I18nContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

// Ülkenin kendi (ana) dili. Bir ülke açıldığında buraya kendi dili yazılır;
// çevirisi henüz yoksa aşağıdaki defaultLang() İngilizce'ye düşer (Türkçe'ye
// ASLA). Diller "dalga dalga" eklenir: translations{}'a yeni bir blok gelince
// bu haritaya bir satır eklemek yeter.
const COUNTRY_LANG = {
  tr: "tr",
  id: "id",
  br: "pt-br",
  es: "es",
};

// Kayıtlı seçim yoksa ülkeye göre varsayılan dil. Türkiye → tr; kendi dili
// çevrilmiş bir ülke → o dil; başka herkes → İngilizce (uluslararası yüz).
// Kritik: Türkiye dışındaki hiçbir ülke Türkçe açılmaz.
function defaultLang() {
  try {
    const c = getCountry();
    const native = COUNTRY_LANG[c];
    if (native && translations[native]) return native;
    return c === "tr" ? "tr" : "en";
  } catch {
    return "tr";
  }
}

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(
    () => localStorage.getItem("lang") || localStorage.getItem("sk_lang") || defaultLang(),
  );
  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
    // Sekme başlığı da dile göre: TR yüzü Sokak Kedileri, uluslararası yüz StrayPaw.
    try {
      document.title = lang === "tr"
        ? "Sokak Kedileri · İstanbul Sokak Hayvanları"
        : "StrayPaw · Stray cats of your neighbourhood";
    } catch { /* önemsiz */ }
    // Keep the legacy "sk_lang" system (Landing / Messenger / Profile use it for
    // their local copy) in sync with i18n, so ONE toggle switches the WHOLE app.
    // Those pages already listen for a "storage" event on sk_lang — writing the
    // key and dispatching the event makes them update live. This is the fix for
    // "English mode still shows Turkish" on the sk_lang-based pages.
    try {
      // Gerçek dil kodunu taşı (tr/en/id/…). Eski sk_lang tüketicileri (Landing/
      // Messenger/Profile) kendi bloğu olmayan bir dili İngilizce'ye düşürür —
      // Türkçe'ye değil. Yalnızca "tr" Türkçe'yi seçtirir.
      const sk = lang;
      localStorage.setItem("sk_lang", sk);
      window.dispatchEvent(new StorageEvent("storage", { key: "sk_lang", newValue: sk }));
    } catch { /* StorageEvent unsupported — pages still sync on next focus */ }
  }, [lang]);
  const t = (path) => {
    const parts = path.split(".");
    // Aktif dilde ara; bulunamazsa İngilizce'ye düş (Türkçe'ye değil), o da
    // yoksa anahtarı döndür. Böylece çevirisi eksik bir dil (dalga dalga
    // eklenirken) Türkçe sızdırmaz — uluslararası yüz İngilizce'ye düşer.
    const lookup = (dict) => {
      let cur = dict;
      for (const p of parts) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else return undefined;
      }
      return typeof cur === "string" ? cur : undefined;
    };
    const hit = lookup(translations[lang]);
    if (hit !== undefined) return hit;
    const en = lookup(translations.en);
    return en !== undefined ? en : path;
  };
  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
